import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';

// API base URL
const API_BASE = '/api';

// =============================================================================
// AUTH CONTEXT & UTILITIES
// =============================================================================

const AuthContext = createContext(null);

// Get stored auth data
function getStoredAuth() {
  try {
    const token = localStorage.getItem('access_token');
    const user = localStorage.getItem('user');
    if (token && user) {
      return { token, user: JSON.parse(user) };
    }
  } catch (e) {}
  return null;
}

// Store auth data
function storeAuth(token, user, refreshToken) {
  localStorage.setItem('access_token', token);
  localStorage.setItem('user', JSON.stringify(user));
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
}

// Clear auth data
function clearAuth() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  localStorage.removeItem('refresh_token');
}

// Auth Provider component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const stored = getStoredAuth();
    if (stored) {
      setToken(stored.token);
      setUser(stored.user);
      // Verify token is still valid, fall back to refresh token if expired
      fetch(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${stored.token}` }
      })
        .then(res => {
          if (res.ok) return res.json();
          // Access token expired - try refresh token
          const refreshToken = localStorage.getItem('refresh_token');
          if (!refreshToken) return Promise.reject('no_refresh');
          return fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
          }).then(r => r.ok ? r.json() : Promise.reject('refresh_failed'))
            .then(data => {
              storeAuth(data.access_token, data.user, data.refresh_token);
              setToken(data.access_token);
              return { user: data.user };
            });
        })
        .then(data => {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        })
        .catch(() => {
          clearAuth();
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');

    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }

    storeAuth(data.access_token, data.user, data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);
    return { success: true };
  };

  const verify2FA = async (tempToken, code) => {
    const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ temp_token: tempToken, code })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || '2FA verification failed');

    storeAuth(data.access_token, data.user, data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);
    return { success: true };
  };

  const requestMagicLink = async (email) => {
    const res = await fetch(`${API_BASE}/auth/magic-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to send magic link');
    return data;
  };

  const verifyMagicLink = async (linkToken) => {
    const res = await fetch(`${API_BASE}/auth/magic-link/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: linkToken })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Invalid magic link');
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }

    storeAuth(data.access_token, data.user, data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);
    return { success: true };
  };

  // The 6-digit code from the sign-in email (the same email also carries the magic link).
  const verifyLoginCode = async (email, code) => {
    const res = await fetch(`${API_BASE}/auth/login-code/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Invalid or expired code');
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }

    storeAuth(data.access_token, data.user, data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);
    return { success: true };
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {}
    clearAuth();
    setToken(null);
    setUser(null);
  };

  const requestPasswordReset = async (email) => {
    const res = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to send reset email');
    return data;
  };

  const resetPassword = async (resetToken, password) => {
    const res = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Password reset failed');
    return data;
  };

  const signup = async (fields) => {
    const res = await fetch(`${API_BASE}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Signup failed');

    storeAuth(data.access_token, data.user, data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    signup,
    logout,
    verify2FA,
    requestMagicLink,
    verifyMagicLink,
    verifyLoginCode,
    requestPasswordReset,
    resetPassword,
    hasRole: (requiredRole) => {
      if (!user) return false;
      const hierarchy = { super_admin: 100, admin: 80, manager: 60, viewer: 40, api_only: 20 };
      return (hierarchy[user.role] || 0) >= (hierarchy[requiredRole] || 0);
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

// Protected Route wrapper
function ProtectedRoute({ children, requiredRole }) {
  const { isAuthenticated, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return children;
}

// Fetch wrapper with error handling AND auth
async function api(endpoint, options = {}) {
  const token = localStorage.getItem('access_token');
  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      ...options,
    });
    if (res.status === 401) {
      // Token expired, clear auth
      clearAuth();
      window.location.href = '/login';
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  } catch (err) {
    console.error('API Error:', err);
    throw err;
  }
}

// Read every row of a paged list endpoint (e.g. '/domains?is_active=true') by offset.
// Stops when the API reports has_more === false. An older API without has_more is paged
// until it returns a short page. The offset advances by the rows actually returned, so a
// server that caps limit below pageSize is still read to the end. Rows are de-duplicated
// by id, since new domains are inserted at the top of the newest-first order while we page.
async function apiAll(endpoint, key = 'domains', pageSize = 1000) {
  const sep = endpoint.endsWith('?') ? '' : endpoint.includes('?') ? '&' : '?';
  const rows = [];
  const seen = new Set();
  let last = {};
  let offset = 0;
  for (let page = 0; page < 1000; page++) {
    last = (await api(`${endpoint}${sep}limit=${pageSize}&offset=${offset}`)) || {};
    const batch = Array.isArray(last[key]) ? last[key] : [];
    for (const row of batch) {
      if (row && row.id != null) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
      }
      rows.push(row);
    }
    offset += batch.length;
    if (batch.length === 0) break;
    if (typeof last.has_more === 'boolean') {
      if (!last.has_more) break;
    } else {
      const served = Number(last.limit) > 0 ? Math.min(pageSize, Number(last.limit)) : pageSize;
      if (batch.length < served) break;
    }
  }
  return { ...last, [key]: rows, count: rows.length, offset: 0, has_more: false };
}

// Format price
const formatPrice = (price) => {
  if (!price) return 'Call for Price';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
};

// Format date
const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Format number
const formatNumber = (num) => {
  if (!num) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

// Icons (simple SVG components)
const Icons = {
  Dashboard: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>,
  Globe: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>,
  Car: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>,
  Activity: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  Plus: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
  Search: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
  Refresh: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
  Trash: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  ExternalLink: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
  Check: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
  X: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
  Clock: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  ChevronLeft: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>,
  ChevronRight: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
  Grid: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>,
  List: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>,
  DollarSign: () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Info: () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Key: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>,
  Download: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
  Copy: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
  Code: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>,
  Play: () => <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>,
  Rss: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 11-2 0 1 1 0 012 0z" /></svg>,
  Users: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
  User: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  Settings: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  Logout: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>,
  Mail: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  Lock: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
  Phone: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>,
  Shield: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
  Building: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
  FolderOpen: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" /></svg>,
  Eye: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
  EyeOff: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>,
  Book: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
  Database: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>,
  Folder: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>,
  Edit: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
  Sparkles: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>,
  Zap: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
  ArrowRight: () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>,
  Target: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2}/><circle cx="12" cy="12" r="6" strokeWidth={2}/><circle cx="12" cy="12" r="2" strokeWidth={2}/></svg>,
  MapPin: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  Bell: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
  TrendingUp: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
  TrendingDown: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>,
  Map: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
  Minus: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>,
  Brain: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
  BarChart2: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  Cpu: () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>,
  Bandage: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 4 4 10l10 10 6-6"/>
      <path d="m14 4 6 6-10 10-6-6"/>
      <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
      <circle cx="9.5" cy="14.5" r="0.6" fill="currentColor"/>
      <circle cx="14.5" cy="9.5" r="0.6" fill="currentColor"/>
    </svg>
  ),
};

// MotorsDynamic brand mark, slanted gradient bars on a black squircle.
// Single shared gradient ID per mounted instance is fine since SVG IDs scope
// to the document; passing `idSuffix` lets callers be safe when multiple are
// rendered at once.
let mdLogoIdCounter = 0;
function MDLogo({ size = 44, radius, style, ...rest }) {
    const idRef = useRef(`md-logo-grad-${++mdLogoIdCounter}`);
    const id = idRef.current;
    const r = radius ?? Math.round(size * 0.27);
    return (
        <svg width={size} height={size} viewBox="0 0 28 28"
             xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
             style={{ borderRadius: r, ...(style || {}) }} {...rest}>
            <defs>
                <linearGradient id={id} x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0" stopColor="#FF5BA4"/>
                    <stop offset=".5" stopColor="#7A4DFF"/>
                    <stop offset="1" stopColor="#22D3EE"/>
                </linearGradient>
            </defs>
            <rect width="28" height="28" rx="6" fill="#000"/>
            <g fill={`url(#${id})`}>
                <path d="M3 22 L11 6 L14 6 L6 22 Z" opacity=".45"/>
                <path d="M9 22 L17 6 L20 6 L12 22 Z" opacity=".75"/>
                <path d="M15 22 L23 6 L26 6 L18 22 Z"/>
            </g>
        </svg>
    );
}

// =============================================================================
// CHANGELOG / WHAT'S NEW SYSTEM
// =============================================================================

const CHANGELOG_ENTRIES = [
  // ─── May 2026 ────────────────────────────────────────────────────────────
  {
    id: 'may-2026-per-key-usage',
    date: '2026-05-11',
    type: 'feature',
    title: 'Per-API-key usage stats',
    summary: 'See request volume + error counts per API token with a 30-day sparkline. Tokens issued before May are grandfathered as unlimited.',
    link: '/api'
  },
  {
    id: 'may-2026-usage-billing',
    date: '2026-05-11',
    type: 'feature',
    title: 'Usage-based billing',
    summary: 'New billing dashboard: daily usage sparkline, hard caps, threshold alerts, Stripe Customer Portal. Set a cap and we\'ll page you before you blow through it.',
    link: '/settings'
  },
  {
    id: 'may-2026-white-label',
    date: '2026-05-10',
    type: 'feature',
    title: 'White-label branding',
    summary: 'Agencies can now upload their own logo + favicon, set primary/accent colors, override the company name, and customize the support email.',
    link: '/settings'
  },
  {
    id: 'may-2026-saml-sso',
    date: '2026-05-10',
    type: 'feature',
    title: 'SAML SSO',
    summary: 'Configure a SAML 2.0 identity provider (Okta, Azure AD, OneLogin) per tenant. Users in your domain skip the password screen entirely.',
    link: '/settings'
  },
  {
    id: 'may-2026-totp-2fa',
    date: '2026-05-10',
    type: 'feature',
    title: 'TOTP two-factor authentication',
    summary: 'Enroll your authenticator app (Google Authenticator, 1Password, Authy) for stronger sign-in security. Recovery codes included.',
    link: '/settings'
  },
  {
    id: 'may-2026-signup',
    date: '2026-05-10',
    type: 'feature',
    title: 'Self-serve signup + 7-day trial',
    summary: 'New customers can spin up an agency from /signup, get 7 days free, then add a payment method to continue.',
    link: '/signup'
  },
  {
    id: 'may-2026-sdks',
    date: '2026-05-10',
    type: 'feature',
    title: 'Python & Go SDKs',
    summary: 'Official SDKs for Python and Go shipped, joining the existing TypeScript SDK. All three default to the /v1 base URL with retries on 5xx + 429.',
    link: '/api'
  },
  {
    id: 'may-2026-self-healing',
    date: '2026-05-09',
    type: 'feature',
    title: 'Self-healing scrapers',
    summary: 'When a dealer site changes, the platform detects the failure, writes a parser fix, opens a PR, and ships it, typically within minutes. Email approval gates protect sensitive changes.',
    link: '/healing'
  },
  {
    id: 'may-2026-dealer-venom',
    date: '2026-05-09',
    type: 'improvement',
    title: 'Dealer Venom + Dealer Alchemist support',
    summary: 'Two more CMS platforms supported, courtesy of the self-healer\'s first auto-shipped parser fix.',
    link: '/scraper-health'
  },
  {
    id: 'may-2026-vla-finance',
    date: '2026-05-08',
    type: 'feature',
    title: 'Price-tiered finance terms in Google VLA',
    summary: 'Configure different finance APR and term combinations per price band. Your VLA feed picks the right tier for each VIN automatically.',
    link: '/vla'
  },

  // ─── April 2026 ──────────────────────────────────────────────────────────
  {
    id: 'apr-2026-priority-scraping',
    date: '2026-04-06',
    type: 'feature',
    title: 'Dealer discovery + priority scraping',
    summary: 'Find dealers by location/brand from the dashboard and push any domain to the front of the scrape queue.',
    link: '/competitive'
  },

  // ─── March 2026 ──────────────────────────────────────────────────────────
  {
    id: 'mar-2026-scheduled-reports',
    date: '2026-03-28',
    type: 'feature',
    title: 'Scheduled email reports',
    summary: 'Weekly or monthly emailed snapshots per dealer. Pick recipients, send hour, and whether to include the summary.',
    link: '/reports'
  },
  {
    id: 'mar-2026-daily-digests',
    date: '2026-03-28',
    type: 'feature',
    title: 'Daily email digests',
    summary: 'Get one daily email per dealer with aging alerts, price-change alerts, and scraper-health rollups. Configurable per-user thresholds.',
    link: '/settings'
  },
  {
    id: 'mar-2026-scraper-health',
    date: '2026-03-28',
    type: 'feature',
    title: 'Scraper health dashboard',
    summary: 'Live view of every CMS\'s success rate, per-domain status, and recent jobs. Acts as the upstream signal for self-healing.',
    link: '/scraper-health'
  },
  {
    id: 'mar-2026-trade-matchmaking',
    date: '2026-03-28',
    type: 'feature',
    title: 'Inter-dealer trade matchmaking',
    summary: 'Opt-in trade preferences plus automatic VIN-to-VIN matching across dealers in your agency.',
    link: '/trade-matches'
  },
  {
    id: 'mar-2026-pricing-recs',
    date: '2026-03-28',
    type: 'feature',
    title: 'Pricing recommendations + seasonal patterns',
    summary: 'Sell-fast / margin / equilibrium pricing recommendations per VIN, with projected days-to-sell. Seasonal patterns and F&I benchmarks for every segment.',
    link: '/market-intelligence'
  },
  {
    id: 'mar-2026-supply-demand',
    date: '2026-03-28',
    type: 'feature',
    title: 'Supply & demand imbalance detection',
    summary: 'Surface segments where local supply is light and demand is hot, and where the opposite is true. Includes a pricing-power signal.',
    link: '/market-intelligence'
  },
  {
    id: 'mar-2026-listing-quality',
    date: '2026-03-28',
    type: 'feature',
    title: 'Listing quality auditor',
    summary: 'Score every VDP against a configurable rubric (description, photos, key specs). Get actionable per-VIN fixes and an estimated revenue impact.',
    link: '/quality'
  },
  {
    id: 'mar-2026-tradein',
    date: '2026-03-28',
    type: 'feature',
    title: 'Trade-in valuation',
    summary: 'Market-comparable trade-in estimates with low/mid/high range and adjustments for condition. Useful for sales-floor instant offers.',
    link: '/dealers'
  },
  {
    id: 'mar-2026-webhooks',
    date: '2026-03-28',
    type: 'feature',
    title: 'Webhook event subscriptions',
    summary: 'Subscribe to vehicle.added, vehicle.price_changed, vehicle.removed, domain.scrape_completed and more. Deliveries are HMAC-SHA256 signed.',
    link: '/webhooks'
  },
  {
    id: 'mar-2026-portfolio',
    date: '2026-03-28',
    type: 'feature',
    title: 'Agency portfolio dashboard',
    summary: 'Roll-up view across every dealer you manage: aging, alerts, engagement coverage, market positioning, and cross-dealer conflicts.',
    link: '/portfolio'
  },
  {
    id: 'mar-2026-vla-smart-feed',
    date: '2026-03-28',
    type: 'feature',
    title: 'Smart VLA feed + dynamic labels',
    summary: 'VLA feed now auto-includes/excludes vehicles based on engagement and quadrant classification, with dynamic custom labels per VIN.',
    link: '/vla'
  },
  {
    id: 'mar-2026-time-decay',
    date: '2026-03-28',
    type: 'feature',
    title: 'Time-decay bidding + reactive boosts',
    summary: 'Bid curves that taper as a VIN ages on the lot, plus reactive bid boosts when engagement spikes. Includes search-term optimization suggestions.',
    link: '/vla'
  },
  {
    id: 'mar-2026-market-position',
    date: '2026-03-28',
    type: 'feature',
    title: 'Market price positioning + gap analysis',
    summary: 'Per-VIN market rank with comparables and a price delta to median. Inventory gap analysis ranks the segments most worth stocking.',
    link: '/market'
  },
  {
    id: 'mar-2026-engagement-quadrants',
    date: '2026-03-28',
    type: 'feature',
    title: 'Engagement scoring + Stars / Fix-It / Boost / Push-or-Cut matrix',
    summary: 'GA4-powered per-VIN engagement score combined with days-on-lot puts every vehicle in a strategic quadrant with a recommended action.',
    link: '/engagement'
  },
  {
    id: 'mar-2026-ad-efficiency',
    date: '2026-03-28',
    type: 'feature',
    title: 'Ad spend efficiency reports + ammo cards',
    summary: 'On-demand reports tying ad spend to engagement outcomes. Sales-floor ammo cards: a printable VIN-level "why this car right now" sheet.',
    link: '/reports'
  },
  {
    id: 'mar-2026-ga4',
    date: '2026-03-28',
    type: 'feature',
    title: 'GA4 dealer connection',
    summary: 'Connect a dealer\'s Google Analytics 4 property via OAuth. VDP views and engagement signals flow into every per-VIN insight.',
    link: '/settings'
  },
  {
    id: 'mar-2026-aging-alerts',
    date: '2026-03-28',
    type: 'feature',
    title: 'Days-on-lot tracking + competitor price-change alerts',
    summary: 'Configurable aging thresholds with dismissible alerts, plus real-time notifications when a tracked competitor changes price on a matching VIN.',
    link: '/alerts'
  }
];

const ChangelogContext = createContext(null);

function ChangelogProvider({ children }) {
  const STORAGE_KEY = 'motorsdynamic_changelog_seen';
  const [lastSeenId, setLastSeenId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [showPopup, setShowPopup] = useState(false);

  const hasUnseen = !lastSeenId || CHANGELOG_ENTRIES[0]?.id !== lastSeenId;
  const unseenCount = hasUnseen ? CHANGELOG_ENTRIES.findIndex(e => e.id === lastSeenId) : 0;
  const actualCount = lastSeenId === null ? CHANGELOG_ENTRIES.length : (unseenCount === -1 ? CHANGELOG_ENTRIES.length : unseenCount);

  useEffect(() => {
    if (hasUnseen && !showPopup) {
      const timer = setTimeout(() => setShowPopup(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [hasUnseen, showPopup]);

  const markSeen = useCallback(() => {
    if (CHANGELOG_ENTRIES[0]) {
      setLastSeenId(CHANGELOG_ENTRIES[0].id);
      try { localStorage.setItem(STORAGE_KEY, CHANGELOG_ENTRIES[0].id); } catch {}
    }
  }, []);

  const openPopup = useCallback(() => { setShowPopup(true); markSeen(); }, [markSeen]);
  const closePopup = useCallback(() => { setShowPopup(false); markSeen(); }, [markSeen]);

  return (
    <ChangelogContext.Provider value={{ entries: CHANGELOG_ENTRIES, hasUnseen, unseenCount: actualCount, showPopup, openPopup, closePopup }}>
      {children}
    </ChangelogContext.Provider>
  );
}

function useChangelog() {
  return useContext(ChangelogContext);
}

function WhatsNewButton() {
  const ctx = useChangelog();
  if (!ctx) return null;
  const { hasUnseen, unseenCount, openPopup } = ctx;
  return (
    <button className="whats-new-btn" onClick={openPopup} title="What's New">
      <Icons.Sparkles />
      <span>What's New</span>
      {hasUnseen && unseenCount > 0 && <span className="whats-new-badge">{unseenCount}</span>}
    </button>
  );
}

function ChangelogPopup() {
  const ctx = useChangelog();
  const navigate = useNavigate();
  if (!ctx || !ctx.showPopup) return null;
  const { entries, closePopup } = ctx;

  const handleClick = (link) => { closePopup(); if (link) navigate(link); };

  return (
    <>
      <div className="changelog-overlay" onClick={closePopup} />
      <div className="changelog-popup">
        <div className="changelog-header">
          <h2><Icons.Sparkles /> What's New</h2>
          <button onClick={closePopup} className="changelog-close"><Icons.X /></button>
        </div>
        <div className="changelog-content">
          {entries.map((entry, i) => (
            <div key={entry.id} className={`changelog-item ${i === 0 ? 'latest' : ''}`} onClick={() => handleClick(entry.link)}>
              <div className="changelog-item-header">
                <span className={`changelog-badge ${entry.type}`}>
                  {entry.type === 'feature' ? <><Icons.Sparkles /> New</> : <><Icons.Zap /> Improved</>}
                </span>
                <span className="changelog-date">{new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
              <h3>{entry.title}</h3>
              <p>{entry.summary}</p>
              {entry.link && <span className="changelog-link">Learn more <Icons.ArrowRight /></span>}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// Sidebar Navigation
function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasRole } = useAuth();
  const [stats, setStats] = useState({ domains: 0, vehicles: 0, pendingJobs: 0 });
  const [competitiveAlertCount, setCompetitiveAlertCount] = useState(0);
  const [healingOpenCount, setHealingOpenCount] = useState(0);

  useEffect(() => {
    api('/stats').then(setStats).catch(() => {});
    api('/competitive/alerts?unread=true').then(data => {
      setCompetitiveAlertCount(data.unread_count ?? (data.alerts || []).length);
    }).catch(() => {});
    if (hasRole('super_admin')) {
      api('/healing/incidents?tab=active').then(d => setHealingOpenCount(d.stats?.open_count || 0)).catch(() => {});
    }
    const interval = setInterval(() => {
      api('/stats').then(setStats).catch(() => {});
      api('/competitive/alerts?unread=true').then(data => {
        setCompetitiveAlertCount(data.unread_count ?? (data.alerts || []).length);
      }).catch(() => {});
      if (hasRole('super_admin')) {
        api('/healing/incidents?tab=active').then(d => setHealingOpenCount(d.stats?.open_count || 0)).catch(() => {});
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [hasRole]);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (onClose) onClose();
  }, [location.pathname]);

  const navSections = [
    {
      items: [
        { path: '/', icon: Icons.Dashboard, label: 'Dashboard' },
      ]
    },
    {
      label: 'Inventory',
      items: [
        { path: '/dealers', icon: Icons.Building, label: 'Dealers' },
        { path: '/domains', icon: Icons.Globe, label: 'Domains', count: stats.domains },
        { path: '/vehicles', icon: Icons.Car, label: 'Vehicles', count: stats.vehicles },
      ]
    },
    {
      label: 'Advertising',
      items: [
        { path: '/feeds', icon: Icons.Rss, label: 'Feed Builder' },
        { path: '/vla', icon: Icons.Target, label: 'VLA Feed' },
        { path: '/reports', icon: Icons.Mail, label: 'Reports' },
      ]
    },
    {
      label: 'Intelligence',
      items: [
        { path: '/competitive', icon: Icons.Target, label: 'Competitive Intel', badge: 'competitiveAlerts' },
        { path: '/specials', icon: Icons.DollarSign, label: 'OEM Specials' },
        { path: '/specials/builder', icon: Icons.Plus, label: 'Offer Builder' },
        { path: '/market-intelligence', icon: Icons.Brain, label: 'Market Intelligence' },
        { path: '/alerts', icon: Icons.Bell, label: 'Alert Inbox' },
        { path: '/engagement', icon: Icons.TrendingUp, label: 'Engagement Scores' },
        { path: '/market', icon: Icons.BarChart2, label: 'Price Positioning' },
        { path: '/quality', icon: Icons.Check, label: 'Listing Quality' },
      ]
    },
    {
      label: 'Analytics',
      items: [
        { path: '/portfolio', icon: Icons.BarChart2, label: 'Portfolio Overview' },
        { path: '/ammo-cards', icon: Icons.DollarSign, label: 'Ammo Cards' },
        { path: '/imbalance', icon: Icons.BarChart2, label: 'Market Imbalances' },
        { path: '/seasonal', icon: Icons.TrendingUp, label: 'Seasonal Trends' },
        { path: '/fi', icon: Icons.DollarSign, label: 'F&I Benchmarks' },
        { path: '/trade', icon: Icons.Activity, label: 'Trade Matches' },
      ]
    },
    {
      label: 'Operations',
      items: [
        { path: '/jobs', icon: Icons.Activity, label: 'Jobs', count: stats.pendingJobs > 0 ? stats.pendingJobs : null },
        { path: '/scraper-health', icon: Icons.BarChart2, label: 'Scraper Health' },
        ...(hasRole('super_admin') ? [{ path: '/healing', icon: Icons.Bandage, label: 'Self-Healing', count: healingOpenCount > 0 ? healingOpenCount : null }] : []),
      ]
    },
    {
      label: 'Integrations',
      items: [
        { path: '/ga4', icon: Icons.BarChart2, label: 'GA4 Connect' },
        { path: '/webhooks', icon: Icons.Code, label: 'Webhooks' },
        { path: '/api', icon: Icons.Code, label: 'API Explorer' },
        { path: '/mcp', icon: Icons.Cpu, label: 'MCP Server' },
      ]
    },
  ];

  if (hasRole('admin')) {
    navSections.push({
      label: 'Admin',
      items: [
        { path: '/organization', icon: Icons.Building, label: 'Organization' },
        { path: '/users', icon: Icons.Users, label: 'Users' },
      ]
    });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`} aria-label="Main navigation">
        <div className="sidebar-header">
          <div className="logo">
            <svg className="logo-icon" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <linearGradient id="md-logo-grad" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0" stopColor="#FF5BA4"/>
                  <stop offset=".5" stopColor="#7A4DFF"/>
                  <stop offset="1" stopColor="#22D3EE"/>
                </linearGradient>
              </defs>
              <rect width="28" height="28" rx="6" fill="#000"/>
              <g fill="url(#md-logo-grad)">
                <path d="M3 22 L11 6 L14 6 L6 22 Z" opacity=".45"/>
                <path d="M9 22 L17 6 L20 6 L12 22 Z" opacity=".75"/>
                <path d="M15 22 L23 6 L26 6 L18 22 Z"/>
              </g>
            </svg>
            <div className="logo-text">
              <span className="logo-title">MotorsDynamic</span>
              <span className="logo-version">V3</span>
            </div>
          </div>
        </div>
        <nav className="sidebar-nav" role="navigation" aria-label="Primary navigation">
          {navSections.map((section, si) => (
            <div key={si} className="nav-section">
              {section.label && (
                <div className="nav-section-label">{section.label}</div>
              )}
              {section.items.map(({ path, icon: Icon, label, count, badge }) => {
                const isActive = path === '/competitive'
                  ? location.pathname.startsWith('/competitive')
                  : path === '/market-intelligence'
                    ? location.pathname.startsWith('/market-intelligence')
                    : location.pathname === path;
                const badgeCount = badge === 'competitiveAlerts' ? competitiveAlertCount : null;
                const displayCount = badgeCount || count;
                return (
                  <Link
                    key={path}
                    to={path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span aria-hidden="true"><Icon /></span>
                    <span>{label}</span>
                    {displayCount !== null && displayCount !== undefined && displayCount !== 0 && (
                      <span className={`nav-count ${badge === 'competitiveAlerts' && badgeCount > 0 ? 'nav-count-alert' : ''}`}>{formatNumber(displayCount)}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <WhatsNewButton />
          {user && (
            <div className="sidebar-user">
              <div className="user-info">
                <div className="user-avatar">
                  {user.first_name ? user.first_name[0] : user.email[0].toUpperCase()}
                </div>
                <div className="user-details">
                  <span className="user-name">{user.first_name ? `${user.first_name} ${user.last_name || ''}` : user.email}</span>
                  <span className="user-role">{user.role.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="user-actions">
                <Link to="/settings" className="user-action-btn" title="Settings">
                  <Icons.Settings />
                </Link>
                <button onClick={handleLogout} className="user-action-btn" title="Logout">
                  <Icons.Logout />
                </button>
              </div>
            </div>
          )}
          <div className="system-status" role="status" aria-live="polite">
            <div className="status-dot active" aria-hidden="true"></div>
            <span>System Online</span>
          </div>
        </div>
      </aside>
    </>
  );
}

// Mobile Header with hamburger menu
function MobileHeader({ onMenuClick }) {
  return (
    <header className="mobile-header">
      <button className="mobile-menu-btn" onClick={onMenuClick} aria-label="Open menu">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12h18M3 6h18M3 18h18" />
        </svg>
      </button>
      <span className="mobile-header-title">MotorsDynamic</span>
    </header>
  );
}

// Dashboard Page
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentVehicles, setRecentVehicles] = useState([]);
  const [topDealers, setTopDealers] = useState([]);
  const [cmsDist, setCmsDist] = useState([]);
  const [cadence, setCadence] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/stats').catch(() => ({})),
      api('/jobs?limit=8').catch(() => ({ jobs: [] })),
      api('/vehicles?limit=8').catch(() => ({ vehicles: [] })),
      api('/dealers/top').catch(() => ({ dealers: [] })),
      api('/domains/cms-distribution').catch(() => ({ distribution: [] })),
      api('/jobs/cadence').catch(() => ({ days: [] })),
    ]).then(([statsData, jobsData, vehiclesData, dealersData, cmsData, cadenceData]) => {
      setStats(statsData || {});
      setRecentJobs(jobsData?.jobs || []);
      setRecentVehicles(vehiclesData?.vehicles || []);
      setTopDealers(dealersData?.dealers || []);
      setCmsDist(cmsData?.distribution || []);
      setCadence(cadenceData?.days || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  // ───── Derived values + realistic fallbacks ─────
  const hero = recentVehicles[0];
  const totalFleet = stats?.vehicles ?? 55531;
  const fleetDelta = stats?.vehicles24h ?? 0;
  const fleetTrend = stats?.fleetTrend30d || [];
  // Build a normalized SVG path from the 30-day trend (or empty if no data)
  const fleetSparkPath = (() => {
    if (!fleetTrend.length) return null;
    const vals = fleetTrend.map(d => d.fleet);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const step = 200 / Math.max(1, fleetTrend.length - 1);
    return fleetTrend.map((d, i) => {
      const x = i * step;
      const y = 36 - ((d.fleet - min) / range) * 32; // map to 4-36 range (4px headroom)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  })();
  const fleet30dStart = fleetTrend[0]?.fleet ?? null;
  const fleet30dPctChange = fleet30dStart ? ((totalFleet - fleet30dStart) / fleet30dStart) * 100 : 0;
  const totalDomains = stats?.domains ?? 89;
  const onlineDomains = stats?.onlineDomains ?? totalDomains;
  const successRate = stats?.successRate ?? 99.6;
  const jobsOk = stats?.jobsOk ?? 744;
  const jobsRetry = stats?.jobsRetry ?? 1;
  const jobsFailed = stats?.jobsFailed ?? 0;
  const avgDuration = stats?.avgDurationSec ?? 4.2;
  const avgPrice = stats?.avgPrice ?? 40554;
  const newCount = stats?.new_count ?? Math.round(totalFleet * 0.406);
  const usedCount = stats?.used_count ?? Math.round(totalFleet * 0.316);
  const cpoCount = stats?.cpo_count ?? (totalFleet - newCount - usedCount);
  const newPct = (newCount / totalFleet) * 100;
  const usedPct = (usedCount / totalFleet) * 100;
  const cpoPct = (cpoCount / totalFleet) * 100;

  // Inventory by category (cars / commercial / rv / moto)
  const invByType  = stats?.inventoryByType || {};
  const carCount   = invByType.car        ?? 0;
  const rvCount    = invByType.rv         ?? 0;
  const motoCount  = invByType.moto       ?? 0;
  const commCount  = invByType.commercial ?? 0;
  const invTotal   = carCount + rvCount + motoCount + commCount;
  const pctOf      = (n) => (invTotal > 0 ? (n / invTotal) * 100 : 0);
  const carPct     = pctOf(carCount);
  const commPct    = pctOf(commCount);
  const rvPct      = pctOf(rvCount);
  const motoPct    = pctOf(motoCount);
  const healingResolved = stats?.healingResolvedToday ?? 3;
  const healingOpen = stats?.healingOpen ?? 0;
  const healingSpend = stats?.healingSpendToday ?? 8.97;
  const jobs24h = stats?.jobs24h ?? 745;
  const competitiveGroups = stats?.competitiveGroups ?? 12;

  // Top dealers w/ fallback that mirrors the prototype
  const dealersList = topDealers.length > 0 ? topDealers.slice(0, 6) : [
    { id: 1, name: 'Shorkey Kia N.Huntingdon', cms: 'dealeron', vehicle_count: 719 },
    { id: 2, name: 'Shorkey Kia Wexford', cms: 'dealeron', vehicle_count: 718 },
    { id: 3, name: '#1 Cochran Kia Robinson', cms: 'teamvelocity', vehicle_count: 264 },
    { id: 4, name: 'South Hills Kia', cms: 'dealer-inspire', vehicle_count: 260 },
    { id: 5, name: 'Monroeville Kia', cms: 'dealercom', vehicle_count: 157 },
    { id: 6, name: 'Legacy Kia W. Mifflin', cms: 'dealeron', vehicle_count: 135 },
  ];

  // CMS distribution fallback
  const cmsTop = (cmsDist.length > 0 ? cmsDist : [
    { cms: 'dealeron', count: 22, pct: 25 },
    { cms: 'dealercom', count: 18, pct: 20 },
    { cms: 'dealer-inspire', count: 15, pct: 17 },
    { cms: 'teamvelocity', count: 11, pct: 12 },
  ]).slice(0, 4);
  const cmsTopTotal = cmsTop.reduce((s, c) => s + (c.count || 0), 0);
  const cmsOthers = Math.max(0, totalDomains - cmsTopTotal);
  const cmsOthersPct = totalDomains > 0 ? Math.round((cmsOthers / totalDomains) * 100) : 26;
  const cmsDotColors = ['var(--c2)', 'var(--c3)', 'var(--c1)', 'var(--c4)'];

  // 7-day cadence - fallback %
  const cadenceData = cadence.length === 7 ? cadence : [
    { day: 'Sun', pct: 65, color: 'violet' },
    { day: 'Mon', pct: 80, color: 'violet' },
    { day: 'Tue', pct: 100, color: 'pink' },
    { day: 'Wed', pct: 74, color: 'violet' },
    { day: 'Thu', pct: 62, color: 'violet' },
    { day: 'Fri', pct: 50, color: 'violet' },
    { day: 'Sat', pct: 73, color: 'cyan' },
  ];

  // Live activity - last 5 jobs
  const liveJobs = recentJobs.slice(0, 5);

  const labelForJob = (job) => {
    const t = job.job_type;
    if (t === 'cms_detect') return 'CMS detect';
    if (t === 'scrape') return 'Scrape';
    if (t === 'vin_decode') return 'VIN decode';
    if (t === 'verify') return 'Verify';
    return t || 'Job';
  };
  const borderForJob = (job) => {
    if (job.status === 'completed') return 'var(--c4)';
    if (job.status === 'failed') return 'var(--c1)';
    if (job.status === 'running') return 'var(--c2)';
    return 'rgba(255,255,255,.15)';
  };
  const timeShort = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const heroVehicleTitle = hero
    ? `${hero.year || ''} ${hero.make || ''} ${hero.model || ''}`.trim() || 'Latest vehicle'
    : '2026 Silverado 1500';
  const heroDealerName = hero?.dealer_name || hero?.domain_name || 'Diehl Kia of Beaver';
  const heroCms = hero?.cms || 'dealer-venom';
  const heroTrim = hero?.trim || 'RST';
  const heroMileage = hero?.mileage != null ? `${formatNumber(hero.mileage)} mi` : '8 mi';
  const heroColor = hero?.exterior_color || 'Black';
  const heroPrice = hero?.price ?? 48477;
  const heroVin = hero?.vin || '1GCUKEED3TZ377135';
  const heroImg = hero?.images?.[0];

  return (
    <div className="page v2-page">
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
        <div style={{ minWidth: 0 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Dashboard · live
          </div>
          <h1 style={{ fontSize: 38, lineHeight: 1, fontWeight: 500, letterSpacing: '-0.03em', color: '#fff', margin: 0 }}>
            Overview.&nbsp;
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.85)' }}>
              {healingResolved > 0 ? `${healingResolved} lots came back online today.` : 'All lots holding green.'}
            </span>
          </h1>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <span className="v2-chip"><span className="v2-pulse-dot"></span>{onlineDomains}/{totalDomains} online</span>
          <Link to="/healing" className="v2-chip">Self-healing · {healingOpen} open</Link>
          <Link to="/jobs" className="v2-chip">{formatNumber(jobs24h)} jobs / 24h</Link>
          <Link to="/competitive" className="v2-chip">{competitiveGroups} competitive groups</Link>
          <Link to="/domains" className="v2-chip v2-chip-active"><Icons.Plus /> Add domain</Link>
        </div>
      </div>

      {/* Bento grid */}
      <div className="v2-bento" style={{ marginBottom: 24 }}>

        {/* Hero "freshly scraped" widget removed, subsequent widgets reflow into the slot. */}

        {/* 2. Total fleet - 30d trend, 24h variance */}
        <Link to="/vehicles" className="v2-tile v2-tile-cyan" style={{ gridColumn: 'span 3', gridRow: 'span 2', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>total fleet · 30d trend</div>
            {fleet30dStart != null && (
              <span className="v2-mono" style={{ fontSize: 10, color: fleet30dPctChange >= 0 ? 'var(--c4)' : 'var(--c1)' }}>
                {fleet30dPctChange >= 0 ? '▲' : '▼'} {Math.abs(fleet30dPctChange).toFixed(1)}%
              </span>
            )}
          </div>
          <div className="v2-num" style={{ fontSize: 44, fontWeight: 300 }}>{formatNumber(totalFleet)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
            <span className={fleetDelta >= 0 ? 'v2-c4' : 'v2-c1'}>
              {fleetDelta >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(fleetDelta))}
            </span>
            <span>· 24h net</span>
          </div>
          <svg viewBox="0 0 200 40" preserveAspectRatio="none" className="v2-spark" style={{ marginTop: 'auto', width: '100%', height: 32, opacity: 0.9 }}>
            <defs><linearGradient id="v2-spark-fleet" x1="0" x2="1"><stop offset="0" stopColor="#22D3EE"/><stop offset="1" stopColor="#A6FF54"/></linearGradient></defs>
            {fleetSparkPath && (
              <path d={fleetSparkPath} fill="none" stroke="url(#v2-spark-fleet)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        </Link>

        {/* 3. Avg price */}
        <div className="v2-tile" style={{ gridColumn: 'span 3', gridRow: 'span 2' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 8 }}>avg listed price</div>
          <div className="v2-num" style={{ fontSize: 40, fontWeight: 300 }}>{formatPrice(avgPrice)}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 4 }}>across {formatNumber(totalFleet)} vehicles</div>
          <div style={{ marginTop: 'auto', paddingTop: 12 }}>
            <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', border: '1px solid rgba(255,255,255,.1)' }}>
              <div style={{ width: `${newPct}%`, background: 'var(--c2)' }}></div>
              <div style={{ width: `${usedPct}%`, background: 'var(--c3)' }}></div>
              <div style={{ width: `${cpoPct}%`, background: 'var(--c5)' }}></div>
            </div>
            <div className="v2-mono" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,.55)' }}>
              <span>NEW {formatNumber(newCount)}</span>
              <span>USED {formatNumber(usedCount)}</span>
              <span>CPO {formatNumber(cpoCount)}</span>
            </div>
          </div>
        </div>

        {/* 3b. Inventory by type */}
        <div className="v2-tile" style={{ gridColumn: 'span 3', gridRow: 'span 2' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 8 }}>inventory by type</div>
          <div className="v2-num" style={{ fontSize: 40, fontWeight: 300 }}>{formatNumber(invTotal)}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 4 }}>
            cars · trucks · RV · moto
          </div>
          <div style={{ marginTop: 'auto', paddingTop: 12 }}>
            <div style={{ display: 'flex', height: 10, gap: 1, borderRadius: 999, overflow: 'hidden', border: '1px solid rgba(255,255,255,.1)' }}>
              <div style={{ flex: `${carPct} 1 0`,  minWidth: 2, background: 'var(--c3)' }} title={`Cars · ${formatNumber(carCount)}`} />
              <div style={{ flex: `${commPct} 1 0`, minWidth: 2, background: 'var(--c5)' }} title={`Commercial · ${formatNumber(commCount)}`} />
              <div style={{ flex: `${rvPct} 1 0`,   minWidth: 2, background: 'var(--c4)' }} title={`RV · ${formatNumber(rvCount)}`} />
              <div style={{ flex: `${motoPct} 1 0`, minWidth: 2, background: 'var(--c1)' }} title={`Moto · ${formatNumber(motoCount)}`} />
            </div>
            <div className="v2-mono" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,.55)', gap: 4, flexWrap: 'wrap' }}>
              <span><span className="v2-c3">●</span> CAR {formatNumber(carCount)}</span>
              <span><span style={{ color: 'var(--c5)' }}>●</span> COMM {formatNumber(commCount)}</span>
              <span><span className="v2-c4">●</span> RV {formatNumber(rvCount)}</span>
              <span><span className="v2-c1">●</span> MOTO {formatNumber(motoCount)}</span>
            </div>
          </div>
        </div>

        {/* 4. Self-healing */}
        <Link to="/healing" className="v2-tile v2-tile-acid" style={{ gridColumn: 'span 3', gridRow: 'span 2', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>self-healing</div>
            <span className="v2-mono v2-c4" style={{ fontSize: 11 }}>{healingOpen === 0 ? 'all clear' : `${healingOpen} open`}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 4 }}>
            <div className="v2-num" style={{ fontSize: 40, fontWeight: 300, lineHeight: 1 }}>{healingResolved}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', paddingBottom: 4 }}>resolved today</div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <span className="v2-chip" style={{ fontSize: 10, padding: '2px 8px' }}>Diehl Beaver</span>
            <span className="v2-chip" style={{ fontSize: 10, padding: '2px 8px' }}>Diehl Hermitage</span>
            <span className="v2-chip" style={{ fontSize: 10, padding: '2px 8px' }}>Castle Chevy</span>
          </div>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 'auto', paddingTop: 8 }}>
            spent <span style={{ color: '#fff' }}>${healingSpend.toFixed(2)}</span> · cap $50/day
          </div>
        </Link>

        {/* 5. Top dealers */}
        <Link to="/dealers" className="v2-tile" style={{ gridColumn: 'span 3', gridRow: 'span 4', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>top dealerships</div>
            <span className="v2-chip">{dealersList.length} of {totalDomains}</span>
          </div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
            {dealersList.map((d, i) => {
              const rankColors = [
                { bg: 'rgba(255,91,164,.18)', fg: 'var(--c1)' },
                { bg: 'rgba(122,77,255,.18)', fg: 'var(--c2)' },
                { bg: 'rgba(34,211,238,.18)', fg: 'var(--c3)' },
              ];
              const rc = rankColors[i];
              const dim = i >= 3 ? { opacity: 0.9 - (i - 3) * 0.1 } : {};
              return (
                <li key={d.id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, ...dim }}>
                  <span style={{ width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, background: rc?.bg || 'rgba(255,255,255,.05)', color: rc?.fg || 'rgba(255,255,255,.55)' }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="truncate" style={{ fontSize: 13, fontWeight: i < 3 ? 500 : 400 }}>{d.name}</div>
                    <div className="v2-mono truncate" style={{ fontSize: 10, color: 'rgba(255,255,255,.4)' }}>{d.cms || '-'}</div>
                  </div>
                  <div className="v2-num" style={{ fontSize: i < 3 ? 14 : 13, fontWeight: i < 3 ? 600 : 400, flexShrink: 0 }}>{formatNumber(d.vehicle_count || 0)}</div>
                </li>
              );
            })}
          </ol>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 12, color: 'rgba(255,255,255,.65)' }}>
            All {totalDomains} dealerships <span className="v2-arrow">↗</span>
          </div>
        </Link>

        {/* 6. Western PA map */}
        <Link to="/competitive" className="v2-tile v2-tile-glow" style={{ gridColumn: 'span 6', gridRow: 'span 3', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>dealer footprint · western PA</div>
              <div className="v2-serif" style={{ fontSize: 26, marginTop: 4 }}>Western Pennsylvania</div>
            </div>
            <span className="v2-chip" style={{ flexShrink: 0 }}>{competitiveGroups} active groups</span>
          </div>
          <div style={{ position: 'relative', height: 180, flex: 1 }}>
            <svg viewBox="0 0 600 200" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              <defs>
                <radialGradient id="v2-rg" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(255,91,164,.5)" />
                  <stop offset="100%" stopColor="rgba(255,91,164,0)" />
                </radialGradient>
              </defs>
              <g stroke="rgba(255,255,255,.06)" strokeWidth="1">
                <path d="M0,40 L600,40 M0,80 L600,80 M0,120 L600,120 M0,160 L600,160" />
                <path d="M120,0 L120,200 M240,0 L240,200 M360,0 L360,200 M480,0 L480,200" />
              </g>
              <path d="M40,160 C150,140 220,180 320,150 S500,100 580,120" fill="none" stroke="rgba(34,211,238,.3)" strokeWidth="2" />
              <circle cx="300" cy="115" r="50" fill="url(#v2-rg)" />
              <circle cx="300" cy="115" r="6" fill="#FF5BA4" />
              <text x="312" y="119" fontFamily="Inter" fontWeight="600" fill="rgba(255,255,255,.85)" fontSize="11">Pittsburgh</text>
              <g fill="#22D3EE">
                <circle cx="240" cy="135" r="5" /><circle cx="265" cy="100" r="5" />
                <circle cx="335" cy="95" r="5" /><circle cx="370" cy="125" r="5" />
                <circle cx="200" cy="105" r="4" /><circle cx="220" cy="160" r="4" />
                <circle cx="395" cy="80" r="4" /><circle cx="430" cy="105" r="4" />
                <circle cx="155" cy="80" r="3.5" /><circle cx="280" cy="160" r="3.5" />
                <circle cx="465" cy="135" r="3.5" /><circle cx="510" cy="90" r="3.5" />
                <circle cx="100" cy="125" r="3" /><circle cx="540" cy="55" r="3" />
              </g>
              <text x="40" y="30" fill="rgba(255,255,255,.45)" fontFamily="JetBrains Mono" fontSize="10">40.44°N · 79.99°W</text>
            </svg>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 'auto', paddingTop: 12, fontSize: 12, color: 'rgba(255,255,255,.55)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ whiteSpace: 'nowrap' }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, marginRight: 6, verticalAlign: 'middle', background: '#FF5BA4' }}></span>your lots</span>
              <span style={{ whiteSpace: 'nowrap' }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, marginRight: 6, verticalAlign: 'middle', background: '#22D3EE' }}></span>competitor lots</span>
            </div>
            <span style={{ whiteSpace: 'nowrap' }}>Open competitive intel <span className="v2-arrow">↗</span></span>
          </div>
        </Link>

        {/* 7. Platform mix */}
        <div className="v2-tile" style={{ gridColumn: 'span 3', gridRow: 'span 2' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>platform mix</div>
            <span className="v2-mono v2-c4" style={{ fontSize: 10 }}>+venom NEW</span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            {cmsTop.map((c, i) => {
              const pct = c.pct ?? (totalDomains > 0 ? Math.round((c.count / totalDomains) * 100) : 0);
              return (
                <li key={c.cms || i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: cmsDotColors[i] }}></span>
                  <span className="truncate" style={{ flex: 1 }}>{c.cms || '-'}</span>
                  <span className="v2-mono" style={{ color: 'rgba(255,255,255,.55)', flexShrink: 0 }}>{formatNumber(c.count || 0)} · {pct}%</span>
                </li>
              );
            })}
            {cmsOthers > 0 && (
              <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'rgba(255,255,255,.55)' }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: 'rgba(255,255,255,.15)' }}></span>
                <span className="truncate" style={{ flex: 1 }}>+ others</span>
                <span className="v2-mono" style={{ flexShrink: 0 }}>{formatNumber(cmsOthers)} · {cmsOthersPct}%</span>
              </li>
            )}
          </ul>
        </div>

        {/* 8. Live activity */}
        <Link to="/jobs" className="v2-tile" style={{ gridColumn: 'span 3', gridRow: 'span 4', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>live activity</div>
            <span className="v2-chip"><span className="v2-pulse-dot"></span>live</span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            {liveJobs.length === 0 && (
              <li style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', padding: '8px 0' }}>No recent activity</li>
            )}
            {liveJobs.map((job) => {
              const detail = job.vehicles_found ? `${formatNumber(job.vehicles_found)} ok` : (job.status || '-');
              return (
                <li key={job.id} style={{ borderLeft: `2px solid ${borderForJob(job)}`, paddingLeft: 10 }}>
                  <div className="truncate" style={{ fontSize: 11, lineHeight: 1.2 }}>
                    {labelForJob(job)} · {job.domain_name || 'system'}
                  </div>
                  <div className="v2-mono truncate" style={{ fontSize: 9, color: 'rgba(255,255,255,.45)', lineHeight: 1.2, marginTop: 2 }}>
                    {timeShort(job.created_at)} · {detail}
                  </div>
                </li>
              );
            })}
          </ul>
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 11, color: 'rgba(255,255,255,.65)' }}>
            Open jobs feed <span className="v2-arrow">↗</span>
          </div>
        </Link>

        {/* 9. Success rate gauge */}
        <div className="v2-tile v2-tile-cyan" style={{ gridColumn: 'span 3', gridRow: 'span 2' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 8 }}>scraper success · 24h</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div className="v2-ring" style={{ '--p': successRate, width: 76, height: 76, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 62, height: 62, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper-2, #13131a)' }}>
                <div style={{ textAlign: 'center', lineHeight: 1 }}>
                  <div className="v2-num" style={{ fontSize: 18, fontWeight: 600 }}>
                    {Number(successRate).toFixed(1)}<span style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="v2-mono" style={{ flex: 1, fontSize: 11, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: 'rgba(255,255,255,.55)' }}>{formatNumber(jobsOk)} ok</span><span className="v2-c4">▲</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: 'rgba(255,255,255,.55)' }}>{jobsRetry} retry</span><span style={{ color: 'rgba(255,255,255,.55)' }}>→</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: 'rgba(255,255,255,.55)' }}>{jobsFailed} failed</span><span className="v2-c4">●</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.1)' }}><span style={{ color: 'rgba(255,255,255,.55)' }}>avg</span><span>{avgDuration}s</span></div>
            </div>
          </div>
        </div>

        {/* 10. 7-day cadence */}
        <div className="v2-tile" style={{ gridColumn: 'span 6', gridRow: 'span 2' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>7-day cadence · jobs by day</div>
            <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>peak Mon · 1.2K</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flex: 1, minHeight: 56 }}>
            {cadenceData.map((d, i) => {
              const grad = d.color === 'pink'
                ? 'linear-gradient(180deg, var(--c1), rgba(255,91,164,.2))'
                : d.color === 'cyan'
                  ? 'linear-gradient(180deg, var(--c3), rgba(34,211,238,.2))'
                  : 'linear-gradient(180deg, rgba(122,77,255,.7), rgba(122,77,255,.2))';
              return <div key={i} style={{ flex: 1, height: `${d.pct}%`, background: grad, borderRadius: '6px 6px 0 0' }} />;
            })}
          </div>
          <div className="v2-mono" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,.45)', textAlign: 'center' }}>
            {cadenceData.map((d, i) => (
              <span key={i}>{d.day}{i === cadenceData.length - 1 ? ' ●' : ''}</span>
            ))}
          </div>
        </div>

      </div>

      {/* Marquee */}
      <div className="v2-ticker-wrap" style={{ marginLeft: -24, marginRight: -24, marginTop: 8 }}>
        <div className="v2-marquee v2-mono" style={{ padding: '12px 0', fontSize: 11, color: 'rgba(255,255,255,.7)' }}>
          <span><span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.95)' }}>tonight</span> · all {healingResolved} unknown CMSs resolved</span>
          <span className="v2-c4">▲</span>
          <span>fleet {formatNumber(totalFleet)} · ▲ {formatNumber(fleetDelta)} · 24h</span>
          <span className="v2-c1">▲</span>
          <span>self-heal session ${healingSpend.toFixed(2)} · {((healingSpend / 1500) * 100).toFixed(1)}% of monthly cap</span>
          <span className="v2-c3">●</span>
          <span>{onlineDomains}/{totalDomains} domains green</span>
          <span className="v2-c4">▲</span>
          <span>{formatNumber(jobs24h)} jobs · 24h · {Number(successRate).toFixed(1)}% ok</span>
          <span className="v2-c5">★</span>
          <span>top: {dealersList[0]?.name} · {formatNumber(dealersList[0]?.vehicle_count || 0)}</span>
          {/* Duplicate for seamless scroll */}
          <span><span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.95)' }}>tonight</span> · all {healingResolved} unknown CMSs resolved</span>
          <span className="v2-c4">▲</span>
          <span>fleet {formatNumber(totalFleet)} · ▲ {formatNumber(fleetDelta)} · 24h</span>
          <span className="v2-c1">▲</span>
          <span>self-heal session ${healingSpend.toFixed(2)} · {((healingSpend / 1500) * 100).toFixed(1)}% of monthly cap</span>
          <span className="v2-c3">●</span>
          <span>{onlineDomains}/{totalDomains} domains green</span>
          <span className="v2-c4">▲</span>
          <span>{formatNumber(jobs24h)} jobs · 24h · {Number(successRate).toFixed(1)}% ok</span>
          <span className="v2-c5">★</span>
          <span>top: {dealersList[0]?.name} · {formatNumber(dealersList[0]?.vehicle_count || 0)}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="v2-mono" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, padding: '24px 0 8px', fontSize: 12, color: 'rgba(255,255,255,.45)' }}>
        <div>motorsdynamic v3.0 · {totalDomains} domains · primary lot: <span className="v2-uline-soft">{dealersList[0]?.name}</span></div>
        <div style={{ textAlign: 'right' }}>v2 bento · live</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, link }) {
  const content = (
    <div className={`stat-card ${color}`}>
      <div className="stat-icon"><Icon /></div>
      <div className="stat-content">
        <div className="stat-value">{typeof value === 'number' ? formatNumber(value) : value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );

  return link ? <Link to={link} style={{ textDecoration: 'none' }}>{content}</Link> : content;
}

// Domains Page
function Domains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [scanning, setScanning] = useState({});
  const [stats, setStats] = useState(null);
  const [showCmsDropdown, setShowCmsDropdown] = useState(false);
  const [cmsSearch, setCmsSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const loadMoreTriggerRef = useRef(null);
  const domainsRef = useRef([]);
  const limit = 50;

  const urlParams = new URLSearchParams(location.search);
  const filters = {
    status: urlParams.get('status') || 'active',
    cms_type: urlParams.get('cms_type'),
    error: urlParams.get('error') === 'true',
  };

  // Load stats for filter counts (load all domains without pagination)
  useEffect(() => {
    apiAll('/domains').then(data => {
      const allDomains = data.domains || [];
      const activeCount = allDomains.filter(d => d.is_active).length;
      const inactiveCount = allDomains.filter(d => !d.is_active).length;
      const errorCount = allDomains.filter(d => d.last_error).length;

      const cmsCounts = {};
      allDomains.forEach(d => {
        let cms = d.cms_type || 'Unknown';
        if (cms && cms.toLowerCase() === 'unknown') cms = 'Unknown';
        cmsCounts[cms] = (cmsCounts[cms] || 0) + 1;
      });

      // Total inventory across all domains
      const totalInventory = allDomains.reduce((sum, d) => {
        const c = d.vehicle_counts || {};
        return sum + (c.total || ((c.new || 0) + (c.used || 0) + (c.cpo || 0)));
      }, 0);

      // Recently scraped (24h)
      const recentlyScraped = allDomains.filter(d =>
        d.last_scraped_at && (Date.now() - new Date(d.last_scraped_at).getTime()) < 24 * 3600 * 1000
      ).length;

      setStats({
        total: allDomains.length,
        status: { active: activeCount, inactive: inactiveCount, error: errorCount },
        cms_types: cmsCounts,
        total_inventory: totalInventory,
        recently_scraped: recentlyScraped,
      });
    }).catch(() => {});
  }, []);

  const loadDomains = useCallback((reset = true) => {
    if (reset) {
      setLoading(true);
      setDomains([]);
      domainsRef.current = [];
    } else {
      setLoadingMore(true);
    }

    const shouldLoadAll = filters.error || filters.cms_type === 'Unknown';
    const offset = reset ? 0 : domainsRef.current.length;
    const params = new URLSearchParams();
    if (!shouldLoadAll) {
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
    }

    if (filters.status === 'active') params.append('is_active', 'true');
    else if (filters.status === 'inactive') params.append('is_active', 'false');

    if (filters.cms_type && filters.cms_type !== 'Unknown') {
      params.append('cms_type', filters.cms_type);
    }

    const request = shouldLoadAll ? apiAll(`/domains?${params}`) : api(`/domains?${params}`);
    request.then(data => {
      let newDomains = data.domains || [];

      if (filters.cms_type === 'Unknown') {
        newDomains = newDomains.filter(d => !d.cms_type);
      }
      if (filters.error) {
        newDomains = newDomains.filter(d => d.last_error);
      }

      if (reset) {
        domainsRef.current = newDomains;
        setDomains(newDomains);
      } else {
        domainsRef.current = [...domainsRef.current, ...newDomains];
        setDomains([...domainsRef.current]);
      }

      let calculatedTotal = data.total || 0;
      if (filters.error || filters.cms_type === 'Unknown') {
        calculatedTotal = newDomains.length;
      }
      setTotal(calculatedTotal);

      if (filters.error || filters.cms_type === 'Unknown') {
        setHasMore(false);
      } else {
        setHasMore(newDomains.length === limit && domainsRef.current.length < calculatedTotal);
      }

      setLoading(false);
      setLoadingMore(false);
    }).catch(() => {
      setLoading(false);
      setLoadingMore(false);
    });
  }, [filters.status, filters.cms_type, filters.error]);

  useEffect(() => { loadDomains(true); }, [filters.status, filters.cms_type, filters.error]);

  // Close CMS dropdown when clicking outside
  useEffect(() => {
    if (!showCmsDropdown) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.cms-filter-v2')) {
        setShowCmsDropdown(false);
        setCmsSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCmsDropdown]);

  // Infinite scroll
  useEffect(() => {
    if (!loadMoreTriggerRef.current || loading || loadingMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadDomains(false);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    observer.observe(loadMoreTriggerRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadDomains, domains.length]);

  const setFilter = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value) params.set(key, value);
    else params.delete(key);
    navigate(`/domains?${params.toString()}`);
  };

  const setErrorFilter = (value) => {
    const params = new URLSearchParams(location.search);
    if (value) params.set('error', 'true');
    else params.delete('error');
    navigate(`/domains?${params.toString()}`);
  };

  const clearFilters = () => navigate('/domains');

  const cmsTypes = stats?.cms_types ? Object.keys(stats.cms_types).sort((a, b) => {
    if (a === 'Unknown') return -1;
    if (b === 'Unknown') return 1;
    return a.localeCompare(b);
  }) : [];

  const filteredCmsTypes = cmsTypes.filter(cmsType =>
    !cmsSearch || cmsType.toLowerCase().includes(cmsSearch.toLowerCase())
  );

  const selectedCmsType = filters.cms_type || 'All CMS Types';
  const hasActiveFilters = filters.status !== 'active' || filters.cms_type || filters.error;

  const handleRefresh = () => loadDomains(true);

  const triggerScan = async (id) => {
    setScanning(s => ({ ...s, [id]: true }));
    await api(`/domains/${id}/scan`, { method: 'POST' });
    setTimeout(() => setScanning(s => ({ ...s, [id]: false })), 2000);
  };

  const deactivateDomain = async (id, cleanDomain) => {
    if (window.confirm(`Deactivate "${cleanDomain}"? The domain data will be preserved.`)) {
      await api(`/domains/${id}`, { method: 'DELETE' });
      loadDomains(true);
    }
  };

  const goToVehicles = (domainId, condition) => {
    const params = new URLSearchParams({ domain_id: domainId });
    if (condition) params.append('condition', condition);
    navigate(`/vehicles?${params.toString()}`);
  };

  // Helpers
  function fmtRelative(ts) {
    if (!ts) return 'never';
    const diffMs = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // CMS color (deterministic, palette-driven)
  function cmsColor(cms) {
    if (!cms || cms === 'Unknown') return { bg: 'rgba(148,163,184,.14)', fg: '#cbd5e1' };
    const map = {
      'dealercom':            { bg: 'rgba(34,211,238,.16)',  fg: '#a5f3fc' },
      'dealer-inspire':       { bg: 'rgba(255,91,164,.16)',  fg: '#fbcfe8' },
      'dealeron':             { bg: 'rgba(122,77,255,.18)',  fg: '#c4b5fd' },
      'teamvelocity':         { bg: 'rgba(166,255,84,.16)',  fg: '#d9f99d' },
      'dealer-venom':         { bg: 'rgba(255,200,87,.18)',  fg: '#fde68a' },
      'dealer-eprocess':      { bg: 'rgba(244,114,182,.14)', fg: '#fbcfe8' },
    };
    const key = cms.toLowerCase().replace(/_/g, '-');
    if (map[key]) return map[key];
    // hash to palette for unknown CMS
    const palette = [
      { bg: 'rgba(34,211,238,.14)',  fg: '#a5f3fc' },
      { bg: 'rgba(122,77,255,.16)',  fg: '#c4b5fd' },
      { bg: 'rgba(166,255,84,.14)',  fg: '#d9f99d' },
      { bg: 'rgba(255,91,164,.14)',  fg: '#fbcfe8' },
      { bg: 'rgba(255,200,87,.14)',  fg: '#fde68a' },
    ];
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  }

  // Top CMS platforms for KPI
  const topCms = stats?.cms_types
    ? Object.entries(stats.cms_types)
        .filter(([k]) => k !== 'Unknown')
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
    : [];

  // Statuses for KPI
  const statusCounts = stats?.status || { active: 0, inactive: 0, error: 0 };
  const totalDomains = stats?.total || 0;

  return (
    <div className="page-content">
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          DOMAINS · {totalDomains.toLocaleString()} TRACKED{cmsTypes.length ? ` · ${cmsTypes.length} CMS PLATFORMS` : ''}{stats?.recently_scraped != null ? ` · ${stats.recently_scraped} SCRAPED IN 24H` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Domains.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Every dealer URL we crawl, in one console.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={handleRefresh} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
              <Icons.Refresh /> Refresh
            </button>
            <button onClick={() => setShowModal(true)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
              ＋ Add domain
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 22 }}>
        {/* Total */}
        <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>total domains</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
            <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>
              {totalDomains.toLocaleString()}
            </div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'var(--c4)', paddingBottom: 8 }}>
              {statusCounts.active.toLocaleString()} active
            </div>
          </div>
        </div>

        {/* Status breakdown */}
        <div className="v2-tile" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>status breakdown</div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c4)' }}>ACTIVE</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 500, color: '#fff' }}>{statusCounts.active.toLocaleString()}</div>
            </div>
            <div>
              <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>INACTIVE</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>{statusCounts.inactive.toLocaleString()}</div>
            </div>
            <div>
              <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c1)' }}>ERROR</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 500, color: 'var(--c1)' }}>{statusCounts.error.toLocaleString()}</div>
            </div>
          </div>
          {/* Stacked bar */}
          {totalDomains > 0 && (
            <div style={{ display: 'flex', height: 4, marginTop: 12, borderRadius: 99, overflow: 'hidden', background: 'rgba(255,255,255,0.06)' }}>
              <div style={{ width: `${(statusCounts.active / totalDomains) * 100}%`, background: 'var(--c4)' }} />
              <div style={{ width: `${(statusCounts.inactive / totalDomains) * 100}%`, background: 'rgba(255,255,255,0.3)' }} />
              <div style={{ width: `${(statusCounts.error / totalDomains) * 100}%`, background: 'var(--c1)' }} />
            </div>
          )}
        </div>

        {/* Top CMS */}
        <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>top CMS platforms</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{cmsTypes.length} total</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {topCms.length === 0 ? (
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>-</div>
            ) : topCms.map(([name, count]) => {
              const pct = totalDomains ? (count / totalDomains) * 100 : 0;
              const c = cmsColor(name);
              return (
                <div key={name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11, marginBottom: 2 }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{name}</span>
                    <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)' }}>{count} · {pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: c.fg, opacity: 0.9 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coverage */}
        <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>inventory tracked</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>
            {(stats?.total_inventory || 0).toLocaleString()}
          </div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>vehicles · all domains</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>scraped 24h</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="v2-num" style={{ fontSize: 14, color: '#fff' }}>{stats?.recently_scraped || 0}</span>
              <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>/ {totalDomains}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="v2-tile" style={{ padding: 14, marginBottom: 14, display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginRight: 4, letterSpacing: '0.06em' }}>STATUS</span>
        <button onClick={() => setFilter('status', 'all')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.status === 'all' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.02)', color: filters.status === 'all' ? '#fff' : 'rgba(255,255,255,0.6)' }}>
          All
        </button>
        <button onClick={() => setFilter('status', 'active')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.status === 'active' ? 'rgba(166,255,84,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.status === 'active' ? 'rgba(166,255,84,0.4)' : undefined, color: filters.status === 'active' ? 'var(--c4)' : 'rgba(255,255,255,0.6)' }}>
          Active {stats && <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{statusCounts.active}</span>}
        </button>
        <button onClick={() => setFilter('status', 'inactive')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.status === 'inactive' ? 'rgba(255,200,87,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.status === 'inactive' ? 'rgba(255,200,87,0.35)' : undefined, color: filters.status === 'inactive' ? '#fde68a' : 'rgba(255,255,255,0.6)' }}>
          Inactive {stats && <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{statusCounts.inactive}</span>}
        </button>
        <button onClick={() => setErrorFilter(!filters.error)} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.error ? 'rgba(255,91,164,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.error ? 'rgba(255,91,164,0.4)' : undefined, color: filters.error ? 'var(--c1)' : 'rgba(255,255,255,0.6)' }}>
          Error {stats && <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{statusCounts.error}</span>}
        </button>

        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)', margin: '0 6px' }} />

        <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginRight: 4, letterSpacing: '0.06em' }}>CMS</span>
        <div className="cms-filter-v2" style={{ position: 'relative' }}>
          <button onClick={() => setShowCmsDropdown(!showCmsDropdown)} className="v2-chip"
                  style={{ cursor: 'pointer', background: filters.cms_type ? 'rgba(122,77,255,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.cms_type ? 'rgba(122,77,255,0.35)' : undefined, color: filters.cms_type ? '#c4b5fd' : 'rgba(255,255,255,0.7)' }}>
            {selectedCmsType} <span style={{ marginLeft: 4, opacity: 0.6 }}>▾</span>
          </button>
          {showCmsDropdown && (
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100, background: 'rgba(15,15,22,0.97)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, padding: 8, width: 240, maxHeight: 360, display: 'flex', flexDirection: 'column' }}>
              <input
                type="text"
                placeholder="Search CMS types..."
                value={cmsSearch}
                onChange={e => setCmsSearch(e.target.value)}
                autoFocus
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff', padding: '6px 10px', fontSize: 12, marginBottom: 6, fontFamily: 'inherit' }}
              />
              <div style={{ overflowY: 'auto', flex: 1 }}>
                <button onClick={() => { setFilter('cms_type', null); setShowCmsDropdown(false); setCmsSearch(''); }}
                        style={{ width: '100%', textAlign: 'left', background: !filters.cms_type ? 'rgba(255,255,255,0.06)' : 'transparent', color: '#fff', padding: '6px 10px', borderRadius: 8, border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                  <span>All CMS Types</span>
                  <span className="v2-mono" style={{ opacity: 0.5 }}>{totalDomains}</span>
                </button>
                {filteredCmsTypes.map(cmsType => (
                  <button key={cmsType} onClick={() => { setFilter('cms_type', cmsType); setShowCmsDropdown(false); setCmsSearch(''); }}
                          style={{ width: '100%', textAlign: 'left', background: filters.cms_type === cmsType ? 'rgba(122,77,255,0.15)' : 'transparent', color: filters.cms_type === cmsType ? '#c4b5fd' : 'rgba(255,255,255,0.85)', padding: '6px 10px', borderRadius: 8, border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{cmsType}</span>
                    <span className="v2-mono" style={{ opacity: 0.5 }}>{stats?.cms_types?.[cmsType] || 0}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {hasActiveFilters && (
          <button onClick={clearFilters} className="v2-chip"
                  style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', marginLeft: 'auto' }}>
            ✕ Clear filters
          </button>
        )}
      </div>

      {/* Table / Empty */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING DOMAINS…</span>
        </div>
      ) : domains.length === 0 ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 28, color: '#fff', marginBottom: 10 }}>
            {hasActiveFilters ? 'No domains match these filters.' : 'No domains tracked yet.'}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>
            {hasActiveFilters ? 'Try clearing filters or broaden the search.' : 'Add your first dealership domain to start scraping inventory.'}
          </div>
          {hasActiveFilters ? (
            <button onClick={clearFilters} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.05)' }}>
              ✕ Clear filters
            </button>
          ) : (
            <button onClick={() => setShowModal(true)} style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
              ＋ Add your first domain
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <th style={{ textAlign: 'left', padding: '12px 18px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Domain</th>
                    <th style={{ textAlign: 'left', padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>CMS</th>
                    <th style={{ textAlign: 'right', padding: '12px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>New</th>
                    <th style={{ textAlign: 'right', padding: '12px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Used</th>
                    <th style={{ textAlign: 'right', padding: '12px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>CPO</th>
                    <th style={{ textAlign: 'left', padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Last scraped</th>
                    <th style={{ textAlign: 'left', padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Status</th>
                    <th style={{ padding: '12px 18px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d, idx) => {
                    const counts = d.vehicle_counts || { new: 0, used: 0, cpo: 0, total: 0 };
                    const cms = d.cms_type || 'Unknown';
                    const c = cmsColor(cms);
                    const cleanName = d.clean_domain || (d.url || '').replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
                    return (
                      <tr key={d.id} style={{
                        borderBottom: idx === domains.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.04)',
                        transition: 'background 0.15s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Domain */}
                        <td style={{ padding: '14px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                              background: `linear-gradient(135deg, ${c.fg}40, ${c.fg}10)`,
                              border: `1px solid ${c.fg}25`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: c.fg
                            }}>
                              {(cleanName[0] || '?').toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                                {cleanName}
                              </div>
                              {d.url && (
                                <a href={d.url} target="_blank" rel="noopener noreferrer"
                                   className="v2-mono"
                                   style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {d.url.replace(/^https?:\/\//, '').slice(0, 38)}{d.url.replace(/^https?:\/\//, '').length > 38 ? '…' : ''} <Icons.ExternalLink />
                                </a>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* CMS */}
                        <td style={{ padding: '14px 12px' }}>
                          {d.cms_type ? (
                            <span className="v2-chip" style={{ background: c.bg, borderColor: 'transparent', color: c.fg, fontSize: 10 }}>
                              {d.cms_type}
                            </span>
                          ) : (
                            <span className="v2-chip" style={{ background: 'rgba(255,200,87,0.10)', borderColor: 'rgba(255,200,87,0.25)', color: '#fde68a', fontSize: 10 }}>
                              Detecting…
                            </span>
                          )}
                        </td>

                        {/* New / Used / CPO */}
                        {[
                          { key: 'new', val: counts.new, color: 'var(--c4)' },
                          { key: 'used', val: counts.used, color: 'var(--c3)' },
                          { key: 'cpo', val: counts.cpo, color: 'var(--c2)' },
                        ].map(col => (
                          <td key={col.key} style={{ padding: '14px 8px', textAlign: 'right' }}>
                            {col.val > 0 ? (
                              <button onClick={() => goToVehicles(d.id, col.key)} title={`View ${col.key} vehicles`}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 600, color: col.color, fontFeatureSettings: '"tnum"' }}>
                                {col.val.toLocaleString()}
                              </button>
                            ) : (
                              <span className="v2-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>-</span>
                            )}
                          </td>
                        ))}

                        {/* Last scraped */}
                        <td style={{ padding: '14px 12px' }}>
                          <span className="v2-mono" style={{ fontSize: 11, color: d.last_scraped_at ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.25)' }}>
                            {d.last_scraped_at ? fmtRelative(d.last_scraped_at) : 'never'}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '14px 12px' }}>
                          {d.last_error ? (
                            <span className="v2-chip" title={d.last_error}
                                  style={{ background: 'rgba(255,91,164,0.12)', borderColor: 'rgba(255,91,164,0.35)', color: 'var(--c1)', fontSize: 10 }}>
                              <span className="v2-pulse-dot v2-pulse-dot--magenta" /> Error
                            </span>
                          ) : d.is_active ? (
                            <span className="v2-chip"
                                  style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                              <span className="v2-pulse-dot" /> Active
                            </span>
                          ) : (
                            <span className="v2-chip"
                                  style={{ background: 'rgba(255,200,87,0.10)', borderColor: 'rgba(255,200,87,0.25)', color: '#fde68a', fontSize: 10 }}>
                              Inactive
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button onClick={() => triggerScan(d.id)} disabled={scanning[d.id]}
                                    title="Trigger scan" aria-label={`Trigger scan for ${cleanName}`}
                                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 6, color: 'rgba(255,255,255,0.7)', cursor: scanning[d.id] ? 'wait' : 'pointer', display: 'inline-flex', opacity: scanning[d.id] ? 0.5 : 1 }}
                                    className={scanning[d.id] ? 'spinning' : ''}>
                              <Icons.Refresh />
                            </button>
                            <button onClick={() => deactivateDomain(d.id, cleanName)}
                                    title="Deactivate domain" aria-label={`Deactivate ${cleanName}`}
                                    style={{ background: 'rgba(255,91,164,0.06)', border: '1px solid rgba(255,91,164,0.20)', borderRadius: 8, padding: 6, color: '#fbcfe8', cursor: 'pointer', display: 'inline-flex' }}>
                              <Icons.Trash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px', flexWrap: 'wrap', gap: 8 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
              Showing {domains.length.toLocaleString()} of {total.toLocaleString()}
            </div>
            {hasMore && (
              <div ref={loadMoreTriggerRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {loadingMore && (
                  <>
                    <div className="spinner" style={{ width: 12, height: 12 }} />
                    <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>loading more…</span>
                  </>
                )}
              </div>
            )}
            {!hasMore && domains.length > 0 && (
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>· all loaded ·</div>
            )}
          </div>
        </>
      )}

      {showModal && <AddDomainModal onClose={() => setShowModal(false)} onSuccess={loadDomains} />}
    </div>
  );
}

function AddDomainModal({ onClose, onSuccess }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await api('/domains', { method: 'POST', body: JSON.stringify({ url }) });
      if (result.error) setError(result.error);
      else { onSuccess(); onClose(); }
    } catch (err) {
      setError('Failed to add domain');
    }
    setLoading(false);
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '11px 14px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} className="v2-tile" style={{ width: 480, maxWidth: '92vw', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>NEW DOMAIN</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 className="v2-serif" style={{ fontSize: 26, fontWeight: 400, color: '#fff', margin: 0 }}>Add a domain</h2>
          <button onClick={onClose} aria-label="Close dialog"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Domain *</label>
          <input type="text" placeholder="cochrantoyota.com" value={url} onChange={e => setUrl(e.target.value)} required autoFocus
            style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>enter the dealership domain (e.g., cochrantoyota.com)</div>

          {error && (
            <div style={{ background: 'rgba(255,91,164,0.10)', border: '1px solid rgba(255,91,164,0.30)', color: 'var(--c1)', borderRadius: 12, padding: '10px 14px', fontSize: 12, marginTop: 16 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="v2-chip" style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>Cancel</button>
            <button type="submit" disabled={loading}
              style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: loading ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
              {loading ? 'Adding…' : 'Add domain'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PriceStackDisplay({ priceStack }) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!priceStack) return null;

  // Parse if it's a string
  const stack = typeof priceStack === 'string' ? JSON.parse(priceStack) : priceStack;

  // Get the key pricing items to display
  const items = [];

  // Check if it's an array format (Dealer Inspire style: [{label, amount}] or DealerCom style: [{label, value}])
  if (Array.isArray(stack)) {
    for (const item of stack) {
      const itemValue = item.amount !== undefined ? item.amount : item.value;
      if (item.label && itemValue !== undefined) {
        items.push({ label: item.label, value: itemValue });
      }
    }
  } else {
    // Object format (Team Velocity style: {MSRP: 54930, discount: -1648})
    // Standard keys to look for (in order of display)
    const keyMap = {
      'MSRP': 'MSRP',
      'msrp': 'MSRP',
      'Dealer Discount': 'Discount',
      'discount': 'Discount',
      'Rebate': 'Rebate',
      'rebate': 'Rebate',
      'Sale Price': 'Sale Price',
      'salePrice': 'Sale Price',
      'Final Price': 'Final Price',
      'price': 'Price',
      'Internet Price': 'Internet Price',
      'internetPrice': 'Internet Price'
    };

    // Build display items
    const seen = new Set();
    for (const [key, label] of Object.entries(keyMap)) {
      if (stack[key] !== undefined && !seen.has(label)) {
        seen.add(label);
        items.push({ label, value: stack[key] });
      }
    }

    // Add any other keys not in our map
    for (const [key, value] of Object.entries(stack)) {
      if (typeof value === 'number' && !keyMap[key]) {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        if (!seen.has(label)) {
          seen.add(label);
          items.push({ label, value });
        }
      }
    }
  }

  if (items.length === 0) return '-';

  return (
    <div
      className="price-stack-wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button className="price-stack-btn">
        <Icons.DollarSign /> {items.length} items
      </button>
      {showTooltip && (
        <div className="price-stack-tooltip">
          <div className="price-stack-header">Price Breakdown</div>
          {items.map((item, i) => (
            <div key={i} className={`price-stack-row ${item.value < 0 || item.label.toLowerCase().includes('discount') || item.label.toLowerCase().includes('savings') ? 'discount' : ''}`}>
              <span className="price-stack-label">{item.label}</span>
              <span className="price-stack-value">
                {item.value < 0 ? '-' : ''}{formatPrice(Math.abs(item.value))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Price History Tooltip - Shows price trend on hover
function PriceHistoryTooltip({ vin, currentPrice, children }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const fetchHistory = async () => {
    if (history || loading) return;
    setLoading(true);
    try {
      const data = await api(`/vehicles/${vin}/price-history`);
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch price history:', err);
      setHistory([]);
    }
    setLoading(false);
  };

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    }
    setShowTooltip(true);
    fetchHistory();
  };

  // Calculate price change
  const getPriceChange = () => {
    if (!history || history.length < 2) return null;
    const firstPrice = history[0].price;
    const lastPrice = history[history.length - 1].price;
    const change = lastPrice - firstPrice;
    const percentChange = ((change / firstPrice) * 100).toFixed(1);
    return { change, percentChange, increased: change > 0 };
  };

  const priceChange = history ? getPriceChange() : null;

  // SVG Trendline chart
  const renderTrendline = () => {
    if (!history || history.length < 2) return null;
    const prices = history.map(h => h.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;

    const width = 220;
    const height = 80;
    const padding = { top: 10, right: 10, bottom: 25, left: 10 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Generate points for the line
    const points = prices.map((price, i) => {
      const x = padding.left + (i / (prices.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - ((price - min) / range) * chartHeight;
      return { x, y, price, date: history[i].recordedAt };
    });

    // Create SVG path
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    // Area fill path
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;

    // Determine trend color
    const trendUp = prices[prices.length - 1] > prices[0];
    const trendColor = trendUp ? '#ef4444' : '#22c55e';

    return (
      <div className="price-trendline">
        <svg width={width} height={height} className="trendline-svg">
          {/* Grid lines */}
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="rgba(255,255,255,0.1)" />
          <line x1={padding.left} y1={padding.top + chartHeight} x2={width - padding.right} y2={padding.top + chartHeight} stroke="rgba(255,255,255,0.1)" />

          {/* Area fill */}
          <path d={areaPath} fill={trendColor} fillOpacity="0.15" />

          {/* Line */}
          <path d={linePath} fill="none" stroke={trendColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

          {/* Data points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4" fill={trendColor} stroke="#1a1a2e" strokeWidth="2" />
              {/* Show first and last price labels */}
              {(i === 0 || i === points.length - 1) && (
                <text
                  x={p.x}
                  y={padding.top + chartHeight + 15}
                  textAnchor={i === 0 ? 'start' : 'end'}
                  fill="rgba(255,255,255,0.6)"
                  fontSize="10"
                >
                  {formatPrice(p.price)}
                </text>
              )}
            </g>
          ))}
        </svg>
        <div className="trendline-dates">
          <span>{new Date(history[0].recordedAt).toLocaleDateString()}</span>
          <span>{new Date(history[history.length - 1].recordedAt).toLocaleDateString()}</span>
        </div>
      </div>
    );
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 150);
  };

  const handleTooltipEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="price-history-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showTooltip && ReactDOM.createPortal(
        <div
          className="price-history-tooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="price-history-header">
            Price History
            {history && history.length > 1 && (
              <span className="price-history-count">{history.length} changes</span>
            )}
          </div>
          {loading ? (
            <div className="price-history-loading">Loading...</div>
          ) : history && history.length > 1 ? (
            <>
              {renderTrendline()}
              {priceChange && (
                <div className={`price-change-summary ${priceChange.increased ? 'increased' : 'decreased'}`}>
                  {priceChange.increased ? '↑' : '↓'} {formatPrice(Math.abs(priceChange.change))}
                  <span className="price-change-percent">({priceChange.percentChange}%)</span>
                </div>
              )}
            </>
          ) : (
            <div className="price-history-empty">No price changes recorded</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// Vehicles Page - Enhanced with Filters and Status
function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [stats, setStats] = useState(null);
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);
  const [domainSearch, setDomainSearch] = useState('');
  const [detailVehicle, setDetailVehicle] = useState(null);
  const limit = 50;
  const location = useLocation();
  const navigate = useNavigate();
  const loadMoreTriggerRef = useRef(null);

  const urlParams = new URLSearchParams(location.search);
  const filters = {
    domain_id: urlParams.get('domain_id'),
    condition: urlParams.get('condition'),
    status: urlParams.get('status') || 'active',
    make: urlParams.get('make'),
    is_cpo: urlParams.get('is_cpo'),
  };

  useEffect(() => {
    api('/vehicles/stats').then(setStats).catch(() => {});
  }, []);

  const loadVehicles = useCallback((reset = true) => {
    if (reset) {
      setLoading(true);
      setVehicles([]);
    } else {
      setLoadingMore(true);
    }

    const offset = reset ? 0 : vehicles.length;
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      status: filters.status,
    });
    if (filters.domain_id) params.append('domain_id', filters.domain_id);
    if (filters.condition) params.append('condition', filters.condition);
    if (filters.make) params.append('make', filters.make);
    if (filters.is_cpo) params.append('is_cpo', filters.is_cpo);
    if (search) params.append('search', search);

    api(`/vehicles?${params}`).then(data => {
      const newVehicles = data.vehicles || [];
      if (reset) {
        setVehicles(newVehicles);
      } else {
        setVehicles(prev => [...prev, ...newVehicles]);
      }
      setTotal(data.total || 0);
      setHasMore(newVehicles.length === limit && (reset ? newVehicles.length : vehicles.length + newVehicles.length) < data.total);
      setLoading(false);
      setLoadingMore(false);
    }).catch(() => {
      setLoading(false);
      setLoadingMore(false);
    });
  }, [vehicles.length, filters.domain_id, filters.condition, filters.status, filters.make, filters.is_cpo, search]);

  useEffect(() => { loadVehicles(true); }, [filters.domain_id, filters.condition, filters.status, filters.make, filters.is_cpo, search]);

  useEffect(() => {
    if (!loadMoreTriggerRef.current || loading || loadingMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadVehicles(false);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    observer.observe(loadMoreTriggerRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadVehicles]);

  // Close domain dropdown when clicking outside
  useEffect(() => {
    if (!showDomainDropdown) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.domain-filter-v2')) {
        setShowDomainDropdown(false);
        setDomainSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDomainDropdown]);

  const setFilter = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value) params.set(key, value);
    else params.delete(key);
    navigate(`/vehicles?${params.toString()}`);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    navigate('/vehicles');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleRefresh = () => loadVehicles(true);

  const selectedDomain = stats?.domains?.find(d => d.id === parseInt(filters.domain_id));
  const filteredDomains = stats?.domains?.filter(d =>
    !domainSearch || (d.clean_domain || '').toLowerCase().includes(domainSearch.toLowerCase())
  ) || [];

  const hasActiveFilters = filters.domain_id || filters.condition || filters.make || filters.is_cpo || filters.status !== 'active' || search;

  const exportCSV = () => {
    if (!vehicles.length) return;
    const cols = ['vin','stock_number','year','make','model','trim','condition','price','msrp','mileage','exterior_color','interior_color','engine','transmission','drivetrain','fuel_type','body_type','clean_domain','first_seen_at','last_seen_at'];
    const header = cols.join(',');
    const rows = vehicles.map(v => cols.map(c => {
      const val = v[c] ?? '';
      return typeof val === 'string' && val.includes(',') ? `"${val.replace(/"/g,'""')}"` : val;
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `vehicles-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  function fmtRelative(ts) {
    if (!ts) return 'never';
    const diffMs = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // KPI math
  const activeCount = stats?.status?.active || 0;
  const removedCount = stats?.status?.removed || 0;
  const cpoCount = stats?.status?.cpo || 0;
  const newCount = stats?.conditions?.new || 0;
  const usedCount = stats?.conditions?.used || 0;
  const domainCount = stats?.domains?.length || 0;
  const conditionTotal = newCount + usedCount;
  const newPct = conditionTotal ? (newCount / conditionTotal) * 100 : 0;
  const usedPct = conditionTotal ? (usedCount / conditionTotal) * 100 : 0;

  const topDomains = (stats?.domains || [])
    .filter(d => d.activeCount > 0)
    .sort((a, b) => b.activeCount - a.activeCount)
    .slice(0, 4);

  return (
    <div className="page-content">
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          VEHICLES · {activeCount.toLocaleString()} ACTIVE{conditionTotal ? ` · ${newCount.toLocaleString()} NEW · ${usedCount.toLocaleString()} USED` : ''}{cpoCount ? ` · ${cpoCount.toLocaleString()} CPO` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Inventory.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Every VIN we're watching, in real time.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={exportCSV} disabled={!vehicles.length} className="v2-chip"
                    style={{ cursor: vehicles.length ? 'pointer' : 'not-allowed', background: 'rgba(255,255,255,0.04)', opacity: vehicles.length ? 1 : 0.5 }}>
              <Icons.Download /> Export CSV
            </button>
            <button onClick={handleRefresh} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
              <Icons.Refresh /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 22 }}>
        <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>active inventory</div>
          <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>
            {activeCount.toLocaleString()}
          </div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
            {(activeCount + removedCount).toLocaleString()} total tracked · {removedCount.toLocaleString()} removed
          </div>
        </div>

        <div className="v2-tile" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>new vs used</div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c4)' }}>NEW</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 500, color: '#fff' }}>{newCount.toLocaleString()}</div>
            </div>
            <div>
              <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c3)' }}>USED</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 500, color: 'rgba(255,255,255,0.85)' }}>{usedCount.toLocaleString()}</div>
            </div>
            <div>
              <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c2)' }}>CPO</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 500, color: '#c4b5fd' }}>{cpoCount.toLocaleString()}</div>
            </div>
          </div>
          {conditionTotal > 0 && (
            <div style={{ display: 'flex', height: 4, marginTop: 12, borderRadius: 99, overflow: 'hidden', background: 'rgba(255,255,255,0.06)' }}>
              <div style={{ width: `${newPct}%`, background: 'var(--c4)' }} />
              <div style={{ width: `${usedPct}%`, background: 'var(--c3)' }} />
            </div>
          )}
        </div>

        <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>biggest lots</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{domainCount} domains</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {topDomains.length === 0 ? (
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>-</div>
            ) : topDomains.map((d, i) => {
              const max = topDomains[0]?.activeCount || 1;
              const pct = (d.activeCount / max) * 100;
              const colors = ['var(--c3)', 'var(--c4)', 'var(--c1)', 'var(--c5)'];
              return (
                <button key={d.id} onClick={() => setFilter('domain_id', d.id)}
                        style={{ background: 'transparent', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11, marginBottom: 2 }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{d.clean_domain}</span>
                    <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{d.activeCount.toLocaleString()}</span>
                  </div>
                  <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: colors[i % colors.length], opacity: 0.85 }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>current view</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>
            {total.toLocaleString()}
          </div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
            results match filters
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>loaded</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="v2-num" style={{ fontSize: 14, color: '#fff' }}>{vehicles.length.toLocaleString()}</span>
              <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>/ {total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="v2-tile" style={{ padding: 14, marginBottom: 14, display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginRight: 4, letterSpacing: '0.06em' }}>STATUS</span>
        <button onClick={() => setFilter('status', 'active')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.status === 'active' ? 'rgba(166,255,84,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.status === 'active' ? 'rgba(166,255,84,0.4)' : undefined, color: filters.status === 'active' ? 'var(--c4)' : 'rgba(255,255,255,0.6)' }}>
          Active {stats && <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{activeCount}</span>}
        </button>
        <button onClick={() => setFilter('status', 'removed')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.status === 'removed' ? 'rgba(255,91,164,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.status === 'removed' ? 'rgba(255,91,164,0.4)' : undefined, color: filters.status === 'removed' ? 'var(--c1)' : 'rgba(255,255,255,0.6)' }}>
          Removed {stats && <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{removedCount}</span>}
        </button>
        <button onClick={() => setFilter('status', 'all')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.status === 'all' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.02)', color: filters.status === 'all' ? '#fff' : 'rgba(255,255,255,0.6)' }}>
          All
        </button>

        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)', margin: '0 6px' }} />

        <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginRight: 4, letterSpacing: '0.06em' }}>CONDITION</span>
        <button onClick={() => setFilter('condition', null)} className="v2-chip"
                style={{ cursor: 'pointer', background: !filters.condition ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)', color: !filters.condition ? '#fff' : 'rgba(255,255,255,0.6)' }}>
          All
        </button>
        <button onClick={() => setFilter('condition', 'new')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.condition === 'new' ? 'rgba(166,255,84,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.condition === 'new' ? 'rgba(166,255,84,0.4)' : undefined, color: filters.condition === 'new' ? 'var(--c4)' : 'rgba(255,255,255,0.6)' }}>
          New {stats?.conditions?.new ? <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{stats.conditions.new}</span> : null}
        </button>
        <button onClick={() => setFilter('condition', 'used')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.condition === 'used' ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.condition === 'used' ? 'rgba(34,211,238,0.4)' : undefined, color: filters.condition === 'used' ? 'var(--c3)' : 'rgba(255,255,255,0.6)' }}>
          Used {stats?.conditions?.used ? <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{stats.conditions.used}</span> : null}
        </button>
        <button onClick={() => setFilter('is_cpo', filters.is_cpo === 'true' ? null : 'true')} className="v2-chip"
                style={{ cursor: 'pointer', background: filters.is_cpo === 'true' ? 'rgba(122,77,255,0.15)' : 'rgba(255,255,255,0.02)', borderColor: filters.is_cpo === 'true' ? 'rgba(122,77,255,0.4)' : undefined, color: filters.is_cpo === 'true' ? '#c4b5fd' : 'rgba(255,255,255,0.6)' }}>
          CPO only {cpoCount > 0 && filters.is_cpo === 'true' ? <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{cpoCount}</span> : null}
        </button>

        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)', margin: '0 6px' }} />

        <div className="domain-filter-v2" style={{ position: 'relative' }}>
          <button onClick={() => setShowDomainDropdown(!showDomainDropdown)} className="v2-chip"
                  style={{ cursor: 'pointer', background: filters.domain_id ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.02)', borderColor: filters.domain_id ? 'rgba(34,211,238,0.4)' : undefined, color: filters.domain_id ? 'var(--c3)' : 'rgba(255,255,255,0.7)' }}>
            <Icons.Globe /> {selectedDomain ? selectedDomain.clean_domain : 'All domains'} <span style={{ marginLeft: 4, opacity: 0.6 }}>▾</span>
          </button>
          {showDomainDropdown && (
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100, background: 'rgba(15,15,22,0.97)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, padding: 8, width: 280, maxHeight: 360, display: 'flex', flexDirection: 'column' }}>
              <input
                type="text"
                placeholder="Search domains..."
                value={domainSearch}
                onChange={e => setDomainSearch(e.target.value)}
                autoFocus
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff', padding: '6px 10px', fontSize: 12, marginBottom: 6, fontFamily: 'inherit' }}
              />
              <div style={{ overflowY: 'auto', flex: 1 }}>
                <button onClick={() => { setFilter('domain_id', null); setShowDomainDropdown(false); }}
                        style={{ width: '100%', textAlign: 'left', background: !filters.domain_id ? 'rgba(255,255,255,0.06)' : 'transparent', color: '#fff', padding: '6px 10px', borderRadius: 8, border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                  <span>All Domains</span>
                  <span className="v2-mono" style={{ opacity: 0.5 }}>{activeCount.toLocaleString()}</span>
                </button>
                {filteredDomains.map(d => (
                  <button key={d.id} onClick={() => { setFilter('domain_id', d.id); setShowDomainDropdown(false); }}
                          style={{ width: '100%', textAlign: 'left', background: filters.domain_id === String(d.id) ? 'rgba(34,211,238,0.12)' : 'transparent', color: filters.domain_id === String(d.id) ? 'var(--c3)' : 'rgba(255,255,255,0.85)', padding: '6px 10px', borderRadius: 8, border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{d.clean_domain}</span>
                    <span className="v2-mono" style={{ opacity: 0.5 }}>{(d.activeCount || 0).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSearch} style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '1 1 220px', minWidth: 220, maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 12, color: 'rgba(255,255,255,0.4)', display: 'flex', pointerEvents: 'none' }}><Icons.Search /></span>
          <input
            type="text"
            placeholder="VIN, make, model, stock#…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 999, color: '#fff', padding: '7px 36px 7px 36px', fontSize: 12, width: '100%', fontFamily: 'inherit' }}
          />
          {searchInput && (
            <button type="button" onClick={() => { setSearchInput(''); setSearch(''); }}
                    style={{ position: 'absolute', right: 10, background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, display: 'flex' }}>
              <Icons.X />
            </button>
          )}
        </form>

        {hasActiveFilters && (
          <button onClick={clearFilters} className="v2-chip"
                  style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', marginLeft: 'auto' }}>
            ✕ Clear filters
          </button>
        )}
      </div>

      {/* Table / Empty */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING INVENTORY…</span>
        </div>
      ) : vehicles.length === 0 ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 28, color: '#fff', marginBottom: 10 }}>
            {hasActiveFilters ? 'No vehicles match these filters.' : 'No vehicles in inventory yet.'}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>
            {hasActiveFilters ? 'Try clearing filters or broaden the search.' : 'Add a domain and wait for the first scrape to complete.'}
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.05)' }}>
              ✕ Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {['Vehicle', 'VIN / Stock', 'Price', 'Specs', 'Domain', 'Seen', 'Status', ''].map((h, i) => (
                      <th key={i} style={{ textAlign: i === 2 ? 'right' : 'left', padding: '12px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v, idx) => {
                    const condClass = v.condition?.toLowerCase() === 'new' ? 'new' : (v.condition?.toLowerCase() === 'certified' || v.is_cpo) ? 'cpo' : 'used';
                    const condColor = condClass === 'new' ? { bg: 'rgba(166,255,84,0.10)', fg: 'var(--c4)' }
                                    : condClass === 'cpo' ? { bg: 'rgba(122,77,255,0.15)', fg: '#c4b5fd' }
                                    : { bg: 'rgba(34,211,238,0.10)', fg: 'var(--c3)' };
                    const specs = [v.drivetrain, v.fuel_type, v.transmission, v.body_type].filter(Boolean);
                    const colors = [v.exterior_color, v.interior_color].filter(Boolean).join(' / ');

                    return (
                      <tr key={v.id} style={{
                        borderBottom: idx === vehicles.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.04)',
                        opacity: v.is_active ? 1 : 0.55,
                        transition: 'background 0.15s'
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Vehicle */}
                        <td style={{ padding: '12px 14px', minWidth: 280 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div onClick={() => v.images?.length > 0 && setSelectedVehicle(v)}
                                 title={v.images?.length > 0 ? `View ${v.images.length} photos` : 'No photos'}
                                 style={{ width: 56, height: 42, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', position: 'relative', cursor: v.images?.length > 0 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {v.images?.[0] ? (
                                <img src={v.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ color: 'rgba(255,255,255,0.25)' }}><Icons.Car /></span>
                              )}
                              {v.images?.length > 1 && (
                                <span className="v2-mono" style={{ position: 'absolute', bottom: 2, right: 2, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 9, padding: '0 4px', borderRadius: 4 }}>
                                  {v.images.length}
                                </span>
                              )}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {[v.year, v.make, v.model].filter(Boolean).join(' ') || '-'}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                {(v.condition || v.is_cpo) && (
                                  <span className="v2-chip" style={{ background: condColor.bg, borderColor: 'transparent', color: condColor.fg, fontSize: 9, padding: '1px 6px' }}>
                                    {v.condition || 'CPO'}{v.is_cpo && v.condition?.toLowerCase() !== 'certified' ? ' · CPO' : ''}
                                  </span>
                                )}
                                {v.trim && (
                                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                                    {v.trim}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* VIN / Stock */}
                        <td style={{ padding: '12px 14px' }}>
                          <button onClick={() => setDetailVehicle(v)} title="View vehicle details"
                                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'block', textAlign: 'left' }}>
                            <div className="v2-mono" style={{ fontSize: 11, color: 'var(--c4)', whiteSpace: 'nowrap' }}>
                              {v.vin || '-'}
                            </div>
                          </button>
                          {v.stock_number && (
                            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                              #{v.stock_number}
                            </div>
                          )}
                        </td>

                        {/* Price */}
                        <td style={{ padding: '12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {v.price ? (
                            <div>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <PriceHistoryTooltip vin={v.vin} currentPrice={v.price}>
                                  <span className="v2-num" style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{formatPrice(v.price)}</span>
                                </PriceHistoryTooltip>
                                {v.price_change_count >= 2 && (
                                  <span className="v2-mono" title={`${v.price_change_count} price changes`}
                                        style={{ background: 'rgba(255,200,87,0.15)', color: 'var(--c5)', fontSize: 9, padding: '1px 5px', borderRadius: 99, border: '1px solid rgba(255,200,87,0.25)' }}>
                                    Δ{v.price_change_count}
                                  </span>
                                )}
                              </div>
                              {v.msrp && v.msrp > v.price && (
                                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textDecoration: 'line-through', marginTop: 2 }}>
                                  {formatPrice(v.msrp)}
                                </div>
                              )}
                              {v.price_stack && (
                                <div style={{ marginTop: 4 }}>
                                  <PriceStackDisplay priceStack={v.price_stack} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>
                          )}
                        </td>

                        {/* Specs */}
                        <td style={{ padding: '12px 14px', minWidth: 220 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {v.mileage != null && (
                              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
                                {formatNumber(v.mileage)} mi
                              </div>
                            )}
                            {colors && (
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                                {colors}
                              </div>
                            )}
                            {specs.length > 0 && (
                              <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220, letterSpacing: '0.04em' }}>
                                {specs.map(s => s.toUpperCase()).join(' · ')}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Domain */}
                        <td style={{ padding: '12px 14px', minWidth: 160 }}>
                          {v.clean_domain ? (
                            <button onClick={() => setFilter('domain_id', v.domain_id)}
                                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                              <div style={{ fontSize: 12, color: 'var(--c3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                                {v.clean_domain}
                              </div>
                              {(v.domain_city || v.domain_state) && (
                                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                                  {[v.domain_city, v.domain_state].filter(Boolean).join(', ')}
                                </div>
                              )}
                            </button>
                          ) : (
                            <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>-</span>
                          )}
                        </td>

                        {/* Seen */}
                        <td style={{ padding: '12px 14px', minWidth: 110 }}>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>
                            {fmtRelative(v.last_seen_at)}
                          </div>
                          <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                            first {fmtRelative(v.first_seen_at)}
                          </div>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '12px 14px' }}>
                          {v.is_active ? (
                            <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                              <span className="v2-pulse-dot" /> Active
                            </span>
                          ) : (
                            <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.10)', borderColor: 'rgba(255,91,164,0.30)', color: 'var(--c1)', fontSize: 10 }}>
                              Removed{v.removed_at ? ` · ${fmtRelative(v.removed_at)}` : ''}
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            {v.video_url && (
                              <a href={v.video_url} target="_blank" rel="noopener noreferrer"
                                 title="Watch video" aria-label={`Watch video for ${v.year} ${v.make} ${v.model}`}
                                 style={{ background: 'rgba(255,91,164,0.08)', border: '1px solid rgba(255,91,164,0.20)', borderRadius: 8, padding: 6, color: '#fbcfe8', display: 'inline-flex', textDecoration: 'none' }}>
                                <Icons.Play />
                              </a>
                            )}
                            {v.url && (
                              <a href={v.url} target="_blank" rel="noopener noreferrer"
                                 title="View on dealer site" aria-label={`View ${v.year} ${v.make} ${v.model} on dealer site`}
                                 style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 6, color: 'rgba(255,255,255,0.7)', display: 'inline-flex', textDecoration: 'none' }}>
                                <Icons.ExternalLink />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px', flexWrap: 'wrap', gap: 8 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
              Showing {vehicles.length.toLocaleString()} of {total.toLocaleString()} vehicles
            </div>
            {hasMore && (
              <div ref={loadMoreTriggerRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {loadingMore && (
                  <>
                    <div className="spinner" style={{ width: 12, height: 12 }} />
                    <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>loading more…</span>
                  </>
                )}
              </div>
            )}
            {!hasMore && vehicles.length > 0 && (
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>· all loaded ·</div>
            )}
          </div>
        </>
      )}

      {selectedVehicle && (
        <PhotoGalleryModal vehicle={selectedVehicle} onClose={() => setSelectedVehicle(null)} />
      )}

      {detailVehicle && (
        <VehicleDetailPanel
          vehicle={detailVehicle}
          onClose={() => setDetailVehicle(null)}
          onViewPhotos={v => { setDetailVehicle(null); setSelectedVehicle(v); }}
        />
      )}
    </div>
  );
}

function PhotoGalleryModal({ vehicle, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = vehicle.images || [];

  const goToPrev = () => setCurrentIndex(i => (i > 0 ? i - 1 : images.length - 1));
  const goToNext = () => setCurrentIndex(i => (i < images.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length]);

  if (images.length === 0) return null;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 4 }}>
              VIN {vehicle.vin}{vehicle.stock_number ? ` · STOCK #${vehicle.stock_number}` : ''}
            </div>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', fontWeight: 400 }}>
              {vehicle.year} {vehicle.make} {vehicle.model}
              {vehicle.trim && <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)', marginLeft: 8 }}>{vehicle.trim}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 36, height: 36, borderRadius: 10, flexShrink: 0 }}><Icons.X /></button>
        </div>

        {/* Main image */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 60px', minHeight: 0, position: 'relative' }}>
          <button onClick={goToPrev}
            style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,77,255,0.20)'; e.currentTarget.style.borderColor = 'rgba(122,77,255,0.40)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}>
            <Icons.ChevronLeft />
          </button>
          <img
            src={images[currentIndex]}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - Photo ${currentIndex + 1}`}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 12 }}
          />
          <button onClick={goToNext}
            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,77,255,0.20)'; e.currentTarget.style.borderColor = 'rgba(122,77,255,0.40)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}>
            <Icons.ChevronRight />
          </button>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 14, backdropFilter: 'blur(12px)' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', flexShrink: 0, fontWeight: 700 }}>
            {currentIndex + 1} <span style={{ color: 'rgba(255,255,255,0.35)' }}>/ {images.length}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            {images.map((img, idx) => (
              <button key={idx} onClick={() => setCurrentIndex(idx)}
                style={{ width: 56, height: 42, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'rgba(255,255,255,0.04)', border: idx === currentIndex ? '2px solid var(--c3)' : '1px solid rgba(255,255,255,0.10)', cursor: 'pointer', padding: 0, opacity: idx === currentIndex ? 1 : 0.6, transition: 'opacity 0.15s' }}>
                <img src={img} alt={`Thumbnail ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VehicleDetailPanel({ vehicle: v, onClose, onViewPhotos }) {
  const [priceHistory, setPriceHistory] = useState([]);
  const [phLoading, setPhLoading] = useState(true);

  useEffect(() => {
    if (!v?.vin) return;
    api(`/vehicles/${v.vin}/price-history`).then(data => {
      setPriceHistory(data.history || data || []);
      setPhLoading(false);
    }).catch(() => setPhLoading(false));
  }, [v?.vin]);

  const formatPriceLocal = n => n ? '$' + parseInt(n).toLocaleString() : '-';
  const formatDateLocal = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  const specs = [
    ['Year', v.year], ['Make', v.make], ['Model', v.model], ['Trim', v.trim],
    ['Condition', v.condition], ['Mileage', v.mileage ? parseInt(v.mileage).toLocaleString() + ' mi' : null],
    ['Engine', v.engine], ['Transmission', v.transmission], ['Drivetrain', v.drivetrain],
    ['Fuel type', v.fuel_type], ['Body', v.body_type], ['Doors', v.doors],
    ['MPG city', v.mpg_city], ['MPG hwy', v.mpg_hwy],
    ['Ext color', v.exterior_color], ['Int color', v.interior_color],
    ['Stock #', v.stock_number], ['Domain', v.clean_domain],
    ['First seen', formatDateLocal(v.first_seen_at)], ['Last seen', formatDateLocal(v.last_seen_at)],
  ].filter(([, val]) => val);

  const maxPH = priceHistory.length ? Math.max(...priceHistory.map(p => p.price || 0)) : 1;
  const minPH = priceHistory.length ? Math.min(...priceHistory.map(p => p.price || 0)) : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }} onClick={onClose} />
      <div className="v2-tile" style={{ position: 'relative', width: 540, maxWidth: '95vw', display: 'flex', flexDirection: 'column', overflowY: 'auto', borderRadius: '20px 0 0 20px', borderRight: 'none', padding: 0, background: 'linear-gradient(180deg, rgba(15,15,22,0.98), rgba(10,10,14,0.98))' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, position: 'sticky', top: 0, background: 'rgba(15,15,22,0.96)', backdropFilter: 'blur(16px)', zIndex: 1, flexShrink: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 4 }}>VIN {v.vin}</div>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', fontWeight: 400, lineHeight: 1.2 }}>
              {v.year} {v.make} {v.model}
              {v.trim && <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.65)', marginLeft: 6 }}>{v.trim}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {v.images?.length > 0 && (
              <button onClick={() => onViewPhotos(v)} className="v2-chip"
                style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.08)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 11 }}>
                <Icons.Play /> {v.images.length}
              </button>
            )}
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 32, height: 32, borderRadius: 10 }}>×</button>
          </div>
        </div>

        <div style={{ padding: '20px 24px', flex: 1 }}>
          {/* Pricing tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: v.msrp ? '1fr 1fr auto' : '1fr auto', gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
            <div className="v2-tile v2-tile-acid" style={{ padding: '14px 16px', textAlign: 'center' }}>
              <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>PRICE</div>
              <div className="v2-num" style={{ fontSize: 22, fontWeight: 700, color: 'var(--c4)' }}>{formatPriceLocal(v.price)}</div>
            </div>
            {v.msrp && (
              <div className="v2-tile" style={{ padding: '14px 16px', textAlign: 'center' }}>
                <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>MSRP</div>
                <div className="v2-num" style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{formatPriceLocal(v.msrp)}</div>
              </div>
            )}
            {v.url && (
              <a href={v.url} target="_blank" rel="noopener noreferrer" className="v2-chip"
                style={{ alignSelf: 'center', whiteSpace: 'nowrap', cursor: 'pointer', background: 'rgba(34,211,238,0.10)', borderColor: 'rgba(34,211,238,0.30)', color: 'var(--c3)', textDecoration: 'none' }}>
                Listing ↗
              </a>
            )}
          </div>

          {/* Price history */}
          <div className="v2-tile v2-tile-cyan" style={{ padding: 16, marginBottom: 18 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12 }}>PRICE HISTORY</div>
            {phLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <div className="spinner" style={{ width: 18, height: 18, margin: '0 auto' }} />
              </div>
            ) : priceHistory.length < 2 ? (
              <div className="v2-mono" style={{ textAlign: 'center', padding: 16, color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>NO PRICE CHANGES RECORDED</div>
            ) : (
              <div>
                <div style={{ position: 'relative', height: 80 }}>
                  <svg width="100%" height="80" viewBox={`0 0 ${priceHistory.length * 40} 80`} preserveAspectRatio="none">
                    <polyline
                      points={priceHistory.map((p, i) => {
                        const x = (i / (priceHistory.length - 1)) * (priceHistory.length * 40);
                        const y = 8 + ((maxPH - (p.price || 0)) / (maxPH - minPH || 1)) * 64;
                        return `${x},${y}`;
                      }).join(' ')}
                      fill="none" stroke="var(--c4)" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round"
                    />
                    {priceHistory.map((p, i) => {
                      const x = (i / (priceHistory.length - 1)) * (priceHistory.length * 40);
                      const y = 8 + ((maxPH - (p.price || 0)) / (maxPH - minPH || 1)) * 64;
                      return <circle key={i} cx={x} cy={y} r="3" fill="var(--c4)" vectorEffect="non-scaling-stroke" />;
                    })}
                  </svg>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.45)', fontFamily: "'JetBrains Mono', monospace" }}>
                  <span>{formatDateLocal(priceHistory[0]?.recorded_at)}</span>
                  <span style={{ color: 'var(--c4)', fontWeight: 700 }}>{formatPriceLocal(priceHistory[priceHistory.length - 1]?.price)}</span>
                  <span>{formatDateLocal(priceHistory[priceHistory.length - 1]?.recorded_at)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Specs */}
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10, textTransform: 'uppercase' }}>Specifications</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            {specs.map(([label, val]) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                <span style={{ fontSize: 13, color: '#fff', marginTop: 3, fontFamily: ['Year', 'Mileage', 'Doors', 'MPG city', 'MPG hwy', 'Stock #', 'First seen', 'Last seen'].includes(label) ? "'JetBrains Mono', monospace" : 'inherit' }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VehicleCard({ vehicle: v }) {
  const [imageIndex, setImageIndex] = useState(0);
  const images = v.images || [];

  return (
    <div className="vehicle-card">
      <div className="vehicle-image-container">
        {images.length > 0 ? (
          <>
            <img src={images[imageIndex]} alt={`${v.year} ${v.make} ${v.model}`} className="vehicle-image" />
            {images.length > 1 && (
              <div className="image-nav">
                <button onClick={() => setImageIndex(i => i > 0 ? i - 1 : images.length - 1)}>
                  <Icons.ChevronLeft />
                </button>
                <span>{imageIndex + 1}/{images.length}</span>
                <button onClick={() => setImageIndex(i => i < images.length - 1 ? i + 1 : 0)}>
                  <Icons.ChevronRight />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="vehicle-image no-image">
            <Icons.Car />
            <span>No Image</span>
          </div>
        )}
        {v.condition && (
          <span className={`condition-badge ${v.condition}`}>{v.condition}</span>
        )}
      </div>
      <div className="vehicle-details">
        <div className="vehicle-header">
          <h3>{v.year} {v.make} {v.model}</h3>
          {v.trim && <span className="vehicle-trim">{v.trim}</span>}
        </div>
        <div className="vehicle-price">{formatPrice(v.price)}</div>
        <div className="vehicle-specs">
          {v.mileage && <span>{formatNumber(v.mileage)} mi</span>}
          {v.exterior_color && <span>{v.exterior_color}</span>}
          {v.transmission && <span>{v.transmission}</span>}
        </div>
        <div className="vehicle-vin">VIN: {v.vin}</div>
      </div>
    </div>
  );
}

// Jobs Page
function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cancelling, setCancelling] = useState(null);

  const fetchJobs = () => {
    const params = filter !== 'all' ? `?status=${filter}&limit=100` : '?limit=100';
    api(`/jobs${params}`).then(data => {
      setJobs(data.jobs || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [filter]);

  const cancelJob = async (jobId) => {
    if (!window.confirm(`Cancel job #${jobId}?`)) return;
    setCancelling(jobId);
    try {
      await api(`/jobs/${jobId}/cancel`, { method: 'POST' });
      fetchJobs();
    } catch (err) {
      alert('Failed to cancel job: ' + (err.message || 'Unknown error'));
    }
    setCancelling(null);
  };

  const statusCounts = jobs.reduce((acc, job) => {
    acc[job.status] = (acc[job.status] || 0) + 1;
    return acc;
  }, {});

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '-';
    const seconds = Math.round((new Date(completedAt) - new Date(startedAt)) / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatJobStats = (job) => {
    const parts = [];
    if (job.vehicles_found) parts.push(`${job.vehicles_found} found`);
    if (job.vehicles_new) parts.push(`${job.vehicles_new} new`);
    if (job.vehicles_updated) parts.push(`${job.vehicles_updated} updated`);
    if (job.vehicles_removed) parts.push(`${job.vehicles_removed} removed`);
    return parts.length > 0 ? parts.join(' · ') : '-';
  };

  function statusChip(status) {
    const map = {
      completed:  { bg: 'rgba(166,255,84,0.10)', border: 'rgba(166,255,84,0.30)', fg: 'var(--c4)' },
      processing: { bg: 'rgba(34,211,238,0.12)', border: 'rgba(34,211,238,0.30)', fg: 'var(--c3)' },
      running:    { bg: 'rgba(34,211,238,0.12)', border: 'rgba(34,211,238,0.30)', fg: 'var(--c3)' },
      pending:    { bg: 'rgba(255,200,87,0.12)', border: 'rgba(255,200,87,0.30)', fg: 'var(--c5)' },
      failed:     { bg: 'rgba(255,91,164,0.12)', border: 'rgba(255,91,164,0.30)', fg: 'var(--c1)' },
      cancelled:  { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', fg: 'rgba(255,255,255,0.5)' },
    };
    const m = map[status] || { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', fg: 'rgba(255,255,255,0.55)' };
    return (
      <span className="v2-mono" style={{ background: m.bg, color: m.fg, border: `1px solid ${m.border}`, borderRadius: 99, padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>
        {(status || '').toUpperCase()}
      </span>
    );
  }

  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  const total = jobs.length;
  const running = (statusCounts.processing || 0) + (statusCounts.running || 0);

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING JOBS…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          JOBS · {total} {filter === 'all' ? 'TOTAL' : filter.toUpperCase()}{running > 0 ? ` · ${running} RUNNING` : ''}
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Jobs.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Live scraping activity, refreshing every 5 seconds.
          </span>
        </h1>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, flexWrap: 'wrap', overflowX: 'auto' }}>
        {['all', 'completed', 'processing', 'pending', 'failed'].map(status => (
          <button key={status} onClick={() => setFilter(status)}
            style={{
              whiteSpace: 'nowrap',
              background: filter === status ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: filter === status ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: filter === status ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6
            }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && statusCounts[status] > 0 && (
              <span className="v2-mono" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', borderRadius: 99, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>{statusCounts[status]}</span>
            )}
          </button>
        ))}
      </div>

      {jobs.length === 0 ? (
        <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No jobs found.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>{filter !== 'all' ? 'Try a different filter.' : 'Jobs will appear here when you add domains and scrapers run.'}</div>
        </div>
      ) : (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead><tr>{['ID', 'Type', 'Domain', 'Status', 'URLs', 'Results', 'Errors', 'Duration', 'Started', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {jobs.map(job => {
                  const isLive = ['pending', 'processing', 'running'].includes(job.status);
                  return (
                    <tr key={job.id} style={{ opacity: job.status === 'failed' ? 0.85 : 1 }}>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.55)' }}>#{job.id}</td>
                      <td style={td}>
                        <span className="v2-chip" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)', fontSize: 10 }}>
                          {job.job_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ ...td, color: '#fff' }} title={job.domain_url}>{job.clean_domain || job.domain_name || '-'}</td>
                      <td style={td}>{statusChip(job.status)}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.7)' }}>
                        {job.urls_found ? (
                          job.status === 'processing' && job.vehicles_found
                            ? <span title={`${job.vehicles_found} of ${job.urls_found} scraped`}>{job.vehicles_found}/{job.urls_found}</span>
                            : job.urls_found
                        ) : '-'}
                      </td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{formatJobStats(job)}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>
                        {job.errors_count > 0 ? (
                          <span style={{ color: 'var(--c1)' }} title={job.error || ''}>{job.errors_count}</span>
                        ) : job.error ? (
                          <span style={{ color: 'var(--c1)' }} title={job.error}>1</span>
                        ) : <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>}
                      </td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>
                        {job.status === 'processing'
                          ? <span style={{ color: 'var(--c3)' }}>running…</span>
                          : <span style={{ color: 'rgba(255,255,255,0.6)' }}>{formatDuration(job.started_at, job.completed_at)}</span>}
                      </td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{job.started_at ? formatDate(job.started_at) : formatDate(job.created_at)}</td>
                      <td style={td}>
                        {isLive && (
                          <button onClick={() => cancelJob(job.id)} disabled={cancelling === job.id} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10, opacity: cancelling === job.id ? 0.5 : 1 }}>
                            {cancelling === job.id ? 'Cancelling…' : '✕ Kill'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange, total, showing }) {
  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <div className="pagination-info">
        Showing {showing} of {formatNumber(total)} vehicles
      </div>
      <div className="pagination-controls">
        <button
          className="btn btn-icon"
          disabled={page === 0}
          onClick={() => onPageChange(0)}
        >
          First
        </button>
        <button
          className="btn btn-icon"
          disabled={page === 0}
          onClick={() => onPageChange(p => p - 1)}
        >
          <Icons.ChevronLeft />
        </button>
        <span className="pagination-current">
          Page {page + 1} of {totalPages}
        </span>
        <button
          className="btn btn-icon"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(p => p + 1)}
        >
          <Icons.ChevronRight />
        </button>
        <button
          className="btn btn-icon"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(totalPages - 1)}
        >
          Last
        </button>
      </div>
    </div>
  );
}

// Loading State
function LoadingState() {
  return (
    <div className="loading-state">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  );
}

// Empty State
function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="empty-state">
      <div className="empty-icon"><Icon /></div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

// API Page - Token Management & Postman Download
function ApiPage() {
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const activeTab = urlTab || 'explorer';
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [tokenName, setTokenName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [testApiKey, setTestApiKey] = useState('');
  const [testParams, setTestParams] = useState({});
  const [testBody, setTestBody] = useState('');
  const [testResponse, setTestResponse] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ domains: true, vehicles: true, feeds: true, jobs: true, users: true });

  const apiEndpoints = {
    domains: {
      name: 'Domains',
      description: 'Manage dealer websites and scraping',
      icon: 'Globe',
      endpoints: [
        { method: 'GET', path: '/api/domains', description: 'List all domains', params: [
          { name: 'is_active', type: 'boolean', description: 'Filter by active status (true/false)' },
          { name: 'cms_type', type: 'string', description: 'Filter by CMS type' },
          { name: 'limit', type: 'number', description: 'Items per page (default: 100)' },
          { name: 'offset', type: 'number', description: 'Pagination offset' }
        ]},
        { method: 'GET', path: '/api/domains/:domain/vehicles', description: 'Get vehicles by domain (e.g., cochrantoyota.com)', params: [
          { name: 'domain', type: 'string', required: true, in: 'path', description: 'Clean domain (e.g., cochrantoyota.com)' },
          { name: 'status', type: 'string', description: 'Vehicle status: active, removed, all (default: active)' },
          { name: 'active_from', type: 'string', description: 'Only vehicles active since (ISO date)' },
          { name: 'active_to', type: 'string', description: 'Only vehicles active until (ISO date)' },
          { name: 'condition', type: 'string', description: 'Filter: new, used, cpo' },
          { name: 'has_images', type: 'boolean', description: 'Only vehicles with images (true/false)' },
          { name: 'has_price', type: 'boolean', description: 'Only vehicles with price (true/false)' },
          { name: 'min_price', type: 'number', description: 'Minimum price' },
          { name: 'max_price', type: 'number', description: 'Maximum price' },
          { name: 'min_year', type: 'number', description: 'Minimum year' },
          { name: 'max_year', type: 'number', description: 'Maximum year' },
          { name: 'make', type: 'string', description: 'Filter by make' },
          { name: 'model', type: 'string', description: 'Filter by model' },
          { name: 'limit', type: 'number', description: 'Items per page (default: 100, max: 1000)' },
          { name: 'offset', type: 'number', description: 'Pagination offset' }
        ]},
        { method: 'GET', path: '/api/domains/id/:id', description: 'Get domain by ID', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Domain ID' }
        ]},
        { method: 'POST', path: '/api/domains', description: 'Add a new domain', body: {
          url: { type: 'string', required: true, description: 'Domain (e.g., cochrantoyota.com)' }
        }},
        { method: 'DELETE', path: '/api/domains/:id', description: 'Deactivate a domain (cannot delete)', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Domain ID' }
        ]},
        { method: 'POST', path: '/api/domains/:id/reactivate', description: 'Reactivate a domain', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Domain ID' }
        ]},
        { method: 'POST', path: '/api/domains/:id/scan', description: 'Trigger domain scan', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Domain ID' }
        ]}
      ]
    },
    vehicles: {
      name: 'Vehicles',
      description: 'Access vehicle inventory data',
      icon: 'Car',
      endpoints: [
        { method: 'GET', path: '/api/vehicles', description: 'List vehicles (paginated)', params: [
          { name: 'domain', type: 'string', description: 'Filter by clean domain (e.g., cochrantoyota.com)' },
          { name: 'status', type: 'string', description: 'Vehicle status: active, removed, all (default: active)' },
          { name: 'active_from', type: 'string', description: 'Only vehicles active since (ISO date)' },
          { name: 'active_to', type: 'string', description: 'Only vehicles active until (ISO date)' },
          { name: 'condition', type: 'string', description: 'Filter: new, used, cpo' },
          { name: 'has_images', type: 'boolean', description: 'Only vehicles with images (true/false)' },
          { name: 'has_price', type: 'boolean', description: 'Only vehicles with price (true/false)' },
          { name: 'make', type: 'string', description: 'Filter by make' },
          { name: 'model', type: 'string', description: 'Filter by model' },
          { name: 'year_min', type: 'number', description: 'Minimum year' },
          { name: 'year_max', type: 'number', description: 'Maximum year' },
          { name: 'price_min', type: 'number', description: 'Minimum price' },
          { name: 'price_max', type: 'number', description: 'Maximum price' },
          { name: 'limit', type: 'number', description: 'Items per page (default: 50, max: 500)' },
          { name: 'offset', type: 'number', description: 'Pagination offset' }
        ]},
        { method: 'GET', path: '/api/vehicles/stats', description: 'Get vehicle statistics', params: [
          { name: 'domain_id', type: 'number', description: 'Filter by domain ID' }
        ]},
        { method: 'GET', path: '/api/vehicles/:vin', description: 'Get vehicle by VIN', params: [
          { name: 'vin', type: 'string', required: true, in: 'path', description: 'Vehicle VIN (17 chars)' }
        ]},
        { method: 'GET', path: '/api/vehicles/:vin/price-history', description: 'Get vehicle price history', params: [
          { name: 'vin', type: 'string', required: true, in: 'path', description: 'Vehicle VIN' }
        ]}
      ]
    },
    feeds: {
      name: 'Feeds',
      description: 'Manage data feeds and custom apps',
      icon: 'Database',
      endpoints: [
        { method: 'GET', path: '/api/feeds', description: 'List all feeds', params: [] },
        { method: 'GET', path: '/api/feeds/:id', description: 'Get feed details', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Feed ID' }
        ]},
        { method: 'POST', path: '/api/feeds', description: 'Create a new feed', body: {
          app_id: { type: 'number', required: true, description: 'App ID for feed format' },
          domain_ids: { type: 'array', required: true, description: 'Array of domain IDs' },
          name: { type: 'string', description: 'Feed name' },
          filters: { type: 'object', description: 'Vehicle filters (condition, price range, etc.)' }
        }},
        { method: 'PUT', path: '/api/feeds/:id', description: 'Update feed', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Feed ID' }
        ], body: {
          name: { type: 'string', description: 'Feed name' },
          filters: { type: 'object', description: 'Vehicle filters' },
          is_active: { type: 'boolean', description: 'Enable/disable feed' }
        }},
        { method: 'DELETE', path: '/api/feeds/:id', description: 'Delete feed', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Feed ID' }
        ]},
        { method: 'POST', path: '/api/feeds/:id/regenerate', description: 'Regenerate feed token', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Feed ID' }
        ]},
        { method: 'GET', path: '/api/feeds/:id/vehicles', description: 'Preview feed vehicles', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Feed ID' },
          { name: 'limit', type: 'number', description: 'Items to return (default: 50)' },
          { name: 'offset', type: 'number', description: 'Offset for pagination' }
        ]},
        { method: 'GET', path: '/api/feeds/fields', description: 'Get available feed fields', params: [] },
        { method: 'GET', path: '/api/feeds/apps', description: 'List feed apps/templates', params: [] },
        { method: 'GET', path: '/api/feeds/apps/:id', description: 'Get app details', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'App ID' }
        ]},
        { method: 'POST', path: '/api/feeds/apps', description: 'Create custom app', body: {
          name: { type: 'string', required: true, description: 'App name' },
          format: { type: 'string', required: true, description: 'Output format: csv, xml, json' },
          description: { type: 'string', description: 'App description' }
        }},
        { method: 'PUT', path: '/api/feeds/apps/:id', description: 'Update app', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'App ID' }
        ]},
        { method: 'DELETE', path: '/api/feeds/apps/:id', description: 'Delete app', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'App ID' }
        ]},
        { method: 'POST', path: '/api/feeds/apps/:id/columns', description: 'Add column to app', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'App ID' }
        ], body: {
          name: { type: 'string', required: true, description: 'Column name' },
          source_field: { type: 'string', required: true, description: 'Source field from vehicle data' },
          formula: { type: 'string', description: 'Optional transformation formula' }
        }}
      ]
    },
    jobs: {
      name: 'Jobs',
      description: 'Monitor scraping jobs and queues',
      icon: 'Activity',
      endpoints: [
        { method: 'GET', path: '/api/jobs', description: 'List recent jobs', params: [
          { name: 'page', type: 'number', description: 'Page number' },
          { name: 'limit', type: 'number', description: 'Items per page' },
          { name: 'status', type: 'string', description: 'Filter: pending, running, completed, failed' },
          { name: 'domain_id', type: 'number', description: 'Filter by domain' }
        ]},
        { method: 'GET', path: '/api/jobs/:id', description: 'Get job details', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Job ID' }
        ]},
        { method: 'POST', path: '/api/jobs/:id/cancel', description: 'Cancel a job', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'Job ID' }
        ]},
        { method: 'GET', path: '/api/jobs/queues/stats', description: 'Get queue statistics', params: [] }
      ]
    },
    users: {
      name: 'Users',
      description: 'User management (admin only)',
      icon: 'Users',
      endpoints: [
        { method: 'GET', path: '/api/users', description: 'List all users', params: [] },
        { method: 'GET', path: '/api/users/:id', description: 'Get user details', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'User ID' }
        ]},
        { method: 'POST', path: '/api/users', description: 'Create/invite user', body: {
          email: { type: 'string', required: true, description: 'User email' },
          first_name: { type: 'string', description: 'First name' },
          last_name: { type: 'string', description: 'Last name' },
          user_role: { type: 'string', required: true, description: 'Role: viewer, manager, admin' }
        }},
        { method: 'PUT', path: '/api/users/:id', description: 'Update user', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'User ID' }
        ]},
        { method: 'DELETE', path: '/api/users/:id', description: 'Deactivate user', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'User ID' }
        ]},
        { method: 'POST', path: '/api/users/:id/reactivate', description: 'Reactivate user', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'User ID' }
        ]},
        { method: 'POST', path: '/api/users/:id/resend-invite', description: 'Resend invite email', params: [
          { name: 'id', type: 'number', required: true, in: 'path', description: 'User ID' }
        ]}
      ]
    },
    tokens: {
      name: 'API Tokens',
      description: 'Manage programmatic access keys + per-key usage',
      icon: 'Key',
      endpoints: [
        { method: 'GET', path: '/api/tokens', description: 'List your API tokens (secrets redacted)', params: [] },
        { method: 'POST', path: '/api/tokens', description: 'Create a token, plaintext returned ONCE', body: {
          name: { type: 'string', required: true, description: 'Human-readable label' },
          expires_in_days: { type: 'number', description: 'Optional expiry (null = never)' }
        }},
        { method: 'DELETE', path: '/api/tokens/:id', description: 'Revoke a token', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/tokens/:id/usage', description: 'Per-day usage sparkline data', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'days', type: 'number', description: 'Default 30, max 90' }
        ]}
      ]
    },
    webhooks: {
      name: 'Webhooks',
      description: 'Subscribe to real-time inventory events (HMAC-signed)',
      icon: 'Webhook',
      endpoints: [
        { method: 'GET', path: '/api/webhooks', description: 'List subscriptions + supported events', params: [] },
        { method: 'POST', path: '/api/webhooks', description: 'Create webhook (secret returned once)', body: {
          dealer_id: { type: 'number', required: true },
          url: { type: 'string', required: true, description: 'HTTPS callback URL' },
          events: { type: 'array', required: true, description: 'e.g. ["vehicle.added", "vehicle.price_changed"]' },
          description: { type: 'string' }
        }},
        { method: 'GET', path: '/api/webhooks/:id', description: 'Webhook detail + recent deliveries', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'PUT', path: '/api/webhooks/:id', description: 'Update', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'DELETE', path: '/api/webhooks/:id', description: 'Delete', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/webhooks/:id/test', description: 'Queue a test delivery', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/webhooks/:id/deliveries', description: 'Delivery history (paginated)', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'page', type: 'number' },
          { name: 'limit', type: 'number', description: 'Max 100' },
          { name: 'status', type: 'string', description: 'pending | delivered | failed' }
        ]}
      ]
    },
    competitive: {
      name: 'Competitive Intelligence',
      description: 'Track competitors, compare pricing, monitor inventory deltas',
      icon: 'Target',
      endpoints: [
        { method: 'GET', path: '/api/competitive/places/autocomplete', description: 'Google Places dealer search', params: [
          { name: 'input', type: 'string', required: true, description: 'Min 2 chars' }
        ]},
        { method: 'GET', path: '/api/competitive/places/:place_id', description: 'Place detail + OEM brand detection', params: [
          { name: 'place_id', type: 'string', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/competitive/dealers', description: 'List tracked dealers', params: [
          { name: 'is_primary', type: 'boolean', description: 'Filter to your own dealers' }
        ]},
        { method: 'POST', path: '/api/competitive/dealers', description: 'Add a tracked dealer', body: {
          google_place_id: { type: 'string' },
          name: { type: 'string', required: true },
          oem_brand: { type: 'string' },
          is_primary: { type: 'boolean', description: 'True for your own dealerships' }
        }},
        { method: 'DELETE', path: '/api/competitive/dealers/:id', description: 'Remove a tracked dealer', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/competitive/groups', description: 'List competitor groups', params: [] },
        { method: 'POST', path: '/api/competitive/groups', description: 'Create group (auto-runs discovery)', body: {
          primary_dealer_id: { type: 'number', required: true },
          oem_brand: { type: 'string' },
          radius_miles: { type: 'number', description: 'Default 30' },
          name: { type: 'string' }
        }},
        { method: 'GET', path: '/api/competitive/groups/:id', description: 'Group detail + members + inventory', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/competitive/groups/:id/discover', description: 'Re-run auto-discovery', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/market-share', description: 'Per-dealer market share', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'make', type: 'string' },
          { name: 'model', type: 'string' },
          { name: 'window', type: 'string', description: 'current | 30d | 90d | 365d' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/pricing', description: 'Price comparison by make/model', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'make', type: 'string', required: true },
          { name: 'model', type: 'string' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/heatmap', description: 'Inventory heatmap by ZIP', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/activity', description: 'Recent adds/removals/price changes', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'days', type: 'number', description: 'Default 7' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/timeline', description: 'Daily inventory timeline', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'days', type: 'number', description: 'Default 30' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/market-intelligence', description: 'Primary inventory + market pricing + competitor matches', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'condition', type: 'string' },
          { name: 'limit', type: 'number', description: 'Default 1000, max 5000' }
        ]},
        { method: 'GET', path: '/api/competitive/groups/:id/listings', description: 'Own + competitor listings filtered', params: [
          { name: 'id', type: 'number', required: true, in: 'path' },
          { name: 'make', type: 'string', required: true },
          { name: 'model', type: 'string' }
        ]},
        { method: 'GET', path: '/api/competitive/alerts', description: 'List competitive alerts', params: [
          { name: 'group_id', type: 'number' },
          { name: 'unread', type: 'boolean' },
          { name: 'limit', type: 'number', description: 'Default 50' }
        ]},
        { method: 'PUT', path: '/api/competitive/alerts/:id/read', description: 'Mark alert read', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'PUT', path: '/api/competitive/alerts/read-all', description: 'Mark all alerts read', params: [] },
        { method: 'GET', path: '/api/competitive/alerts/preferences', description: 'Get alert preferences', params: [] },
        { method: 'PUT', path: '/api/competitive/alerts/preferences', description: 'Update alert preferences', body: {
          email_enabled: { type: 'boolean' },
          new_competitor_alert: { type: 'boolean' },
          inventory_change_threshold: { type: 'number' },
          price_change_threshold: { type: 'number' }
        }}
      ]
    },
    dealers: {
      name: 'Dealer Analytics',
      description: 'Aging, scores, quadrants, market position, gaps, pricing recs',
      icon: 'BarChart3',
      endpoints: [
        { method: 'GET', path: '/api/dealers/aging', description: 'Agency-wide aging summary per dealer', params: [] },
        { method: 'GET', path: '/api/dealers/aging-alerts', description: 'All undismissed aging alerts', params: [] },
        { method: 'GET', path: '/api/dealers/top', description: 'Top dealers by inventory size', params: [
          { name: 'limit', type: 'number', description: 'Default 10, max 50' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/aging', description: 'Per-dealer aging report', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'threshold', type: 'number', description: 'Minimum days on lot' },
          { name: 'page', type: 'number' },
          { name: 'limit', type: 'number', description: 'Max 100' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/aging/config', description: 'Get aging thresholds', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'PUT', path: '/api/dealers/:dealerId/aging/config', description: 'Replace aging thresholds', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/dealers/:dealerId/aging/alerts/:alertId/dismiss', description: 'Dismiss aging alert', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'alertId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/alerts/price-changes', description: 'Competitor price-change alerts', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'unread_only', type: 'boolean' },
          { name: 'page', type: 'number' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/scores', description: 'GA4 engagement scores per VIN', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'min_score', type: 'number' },
          { name: 'max_score', type: 'number' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/:vin/engagement', description: 'Engagement detail for a VIN', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'vin', type: 'string', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/quadrants', description: 'Stars / Fix-It / Boost / Push-or-Cut matrix', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'quadrant', type: 'string', description: 'stars | fix_it | boost | push_or_cut' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/positions', description: 'Market positions for all VINs', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'position_label', type: 'string', description: 'below_market | at_market | above_market' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/:vin/market-position', description: 'Market position + comparables', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'vin', type: 'string', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/gaps', description: 'Inventory gap opportunities', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/quality', description: 'Listing quality summary', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/quality/vehicles', description: 'Per-VIN quality list (paginated)', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'page', type: 'number' },
          { name: 'sort', type: 'string', description: 'asc | desc' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/:vin/quality', description: 'Quality score for a VIN', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'vin', type: 'string', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/dealers/:dealerId/inventory/quality/audit', description: 'Trigger manual quality audit', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/dealers/:dealerId/trade-in/valuation', description: 'Trade-in value estimate', body: {
          year: { type: 'number', required: true },
          make: { type: 'string', required: true },
          model: { type: 'string', required: true },
          mileage: { type: 'number', required: true },
          condition: { type: 'string', required: true, description: 'excellent | good | fair | poor' }
        }},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/pricing-recommendations', description: 'Sell-fast / margin / equilibrium prices', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/market/imbalances', description: 'Supply/demand imbalances', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'pricing_power', type: 'string', description: 'high | medium | low | none' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/market/seasonal', description: 'Seasonal demand patterns', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/fi/benchmarks', description: 'F&I payment benchmarks', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]}
      ]
    },
    vla: {
      name: 'VLA Feeds',
      description: 'Google Vehicle Listing Ads, config, labels, overrides',
      icon: 'Megaphone',
      endpoints: [
        { method: 'GET', path: '/api/vla/dealers/:dealerId/feeds/vla', description: 'VLA feed metadata + URL', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/vla/dealers/:dealerId/feeds/vla/history', description: 'Generation history', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'limit', type: 'number', description: 'Default 50, max 200' }
        ]},
        { method: 'GET', path: '/api/vla/dealers/:dealerId/feeds/vla/config', description: 'Get VLA config', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'PUT', path: '/api/vla/dealers/:dealerId/feeds/vla/config', description: 'Update VLA config, set payment_config or installment payments will be missing', body: {
          feed_name: { type: 'string' },
          include_stars: { type: 'boolean' },
          include_fix_it: { type: 'boolean' },
          include_boost: { type: 'boolean' },
          include_push_or_cut: { type: 'boolean' },
          exclude_above_market: { type: 'boolean' },
          refresh_interval_hours: { type: 'number' },
          payment_config: { type: 'object', description: 'Required for installment payment columns' }
        }},
        { method: 'POST', path: '/api/vla/dealers/:dealerId/feeds/vla/generate', description: 'Force regenerate', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/vla/dealers/:dealerId/feeds/vla/overrides', description: 'List manual VIN overrides', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/vla/dealers/:dealerId/feeds/vla/overrides', description: 'Upsert an override', body: {
          vin: { type: 'string', required: true },
          force_include: { type: 'boolean', required: true },
          override_reason: { type: 'string' }
        }},
        { method: 'DELETE', path: '/api/vla/dealers/:dealerId/feeds/vla/overrides/:vin', description: 'Remove override', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'vin', type: 'string', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/vla/dealers/:dealerId/inventory/labels', description: 'Label distributions', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/feeds/time-decay/config', description: 'Time-decay bid curve', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/feeds/reactive-boost/config', description: 'Reactive boost config', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/inventory/boosts', description: 'Per-VIN boost multipliers', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/dealers/:dealerId/feeds/search-terms', description: 'Search-term optimization suggestions', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]}
      ]
    },
    ga4: {
      name: 'GA4 Integration',
      description: 'Connect Google Analytics 4 to enrich inventory with engagement signals',
      icon: 'TrendingUp',
      endpoints: [
        { method: 'GET', path: '/api/ga4/connect/:dealerId', description: 'Begin OAuth (returns Google consent URL)', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/ga4/status/:dealerId', description: 'Connection status + GA4 properties', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/ga4/sync/:dealerId/:connectionId', description: 'Trigger manual sync', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'connectionId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/ga4/disconnect/:dealerId/:connectionId', description: 'Disconnect a property', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'connectionId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/ga4/analytics/:dealerId', description: 'Aggregated analytics + per-VIN engagement', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' },
          { name: 'from', type: 'string', description: 'ISO date' },
          { name: 'to', type: 'string', description: 'ISO date' },
          { name: 'vin', type: 'string' }
        ]}
      ]
    },
    portfolio: {
      name: 'Portfolio (Agency)',
      description: 'Agency-wide rollups across every dealer you manage',
      icon: 'Briefcase',
      endpoints: [
        { method: 'GET', path: '/api/portfolio/summary', description: 'Inventory totals + alert counts + portfolio health', params: [] },
        { method: 'GET', path: '/api/portfolio/dealers', description: 'Per-dealer key metrics rollup', params: [] },
        { method: 'GET', path: '/api/portfolio/conflicts', description: 'Cross-dealer VIN / pricing conflicts', params: [] },
        { method: 'GET', path: '/api/portfolio/benchmarks', description: 'Cross-dealer performance ranking', params: [] }
      ]
    },
    reports: {
      name: 'Reports',
      description: 'Scheduled email reports + on-demand snapshots',
      icon: 'FileText',
      endpoints: [
        { method: 'GET', path: '/api/reports/schedules', description: 'List schedules', params: [] },
        { method: 'POST', path: '/api/reports/schedules', description: 'Create a report schedule', body: {
          dealer_id: { type: 'number', required: true },
          cadence: { type: 'string', required: true, description: 'weekly | monthly' },
          recipients: { type: 'array', required: true },
          send_hour: { type: 'number' }
        }},
        { method: 'PUT', path: '/api/reports/schedules/:id', description: 'Update schedule', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'DELETE', path: '/api/reports/schedules/:id', description: 'Delete schedule', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/reports/schedules/:id/send-now', description: 'Send immediately', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/reports/:dealerId/generate', description: 'Generate one-off report', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/reports/:dealerId/latest', description: 'Latest generated report', params: [
          { name: 'dealerId', type: 'number', required: true, in: 'path' }
        ]}
      ]
    },
    scraperHealth: {
      name: 'Scraper Health',
      description: 'Visibility into per-CMS and per-domain scraping fleet health',
      icon: 'Heart',
      endpoints: [
        { method: 'GET', path: '/api/scraper-health/platforms', description: 'Per-CMS health summary', params: [] },
        { method: 'GET', path: '/api/scraper-health/domains', description: 'Per-domain health', params: [
          { name: 'cms_type', type: 'string' },
          { name: 'status', type: 'string', description: 'healthy | degraded | failed' }
        ]},
        { method: 'GET', path: '/api/scraper-health/domains/:domainId', description: 'Domain detail + job history', params: [
          { name: 'domainId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/scraper-health/domains/:domainId/clear-sitemap-source', description: 'Reset persisted sitemap', params: [
          { name: 'domainId', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/scraper-health/alerts', description: 'Active scraper alerts', params: [] },
        { method: 'POST', path: '/api/scraper-health/alerts/:domainId/dismiss', description: 'Dismiss alert', params: [
          { name: 'domainId', type: 'number', required: true, in: 'path' }
        ]}
      ]
    },
    billing: {
      name: 'Billing & Usage',
      description: 'Plan summary, Stripe portal, usage metering',
      icon: 'CreditCard',
      endpoints: [
        { method: 'GET', path: '/api/billing/summary', description: 'Plan + trial + Stripe readiness', params: [] },
        { method: 'POST', path: '/api/billing/start-checkout', description: 'Begin Stripe Checkout (admin)', params: [] },
        { method: 'POST', path: '/api/billing/portal', description: 'Open Stripe Customer Portal (admin)', params: [] },
        { method: 'POST', path: '/api/billing/cancel', description: 'Schedule cancel at period end', params: [] },
        { method: 'POST', path: '/api/billing/domains/:id/pause', description: 'Pause billing for a domain', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'POST', path: '/api/billing/domains/:id/resume', description: 'Resume billing for a domain', params: [
          { name: 'id', type: 'number', required: true, in: 'path' }
        ]},
        { method: 'GET', path: '/api/billing/usage/current', description: 'Current-period usage + quota', params: [] },
        { method: 'GET', path: '/api/billing/usage/history', description: 'Prior-month rollups', params: [
          { name: 'months', type: 'number', description: 'Default 6, max 24' }
        ]},
        { method: 'GET', path: '/api/billing/usage/daily', description: 'Daily usage sparkline', params: [
          { name: 'days', type: 'number', description: 'Default 30, max 90' }
        ]},
        { method: 'PUT', path: '/api/billing/usage/config', description: 'Update hard cap + alerts', body: {
          hard_cap: { type: 'number', description: 'Null = no cap' },
          alerts_enabled: { type: 'boolean' }
        }}
      ]
    }
  };

  const loadTokens = useCallback(() => {
    api('/tokens').then(data => {
      setTokens(data.tokens || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const createToken = async () => {
    if (!tokenName.trim()) {
      alert('Please enter a token name');
      return;
    }
    setCreating(true);
    try {
      const data = await api('/tokens', {
        method: 'POST',
        body: JSON.stringify({
          name: tokenName,
          expires_in_days: expiresInDays ? parseInt(expiresInDays) : null
        })
      });
      if (data.token) {
        setNewToken(data.token.token);
        setTokenName('');
        setExpiresInDays('');
        setShowCreateForm(false);
        loadTokens();
      }
    } catch (err) {
      alert('Failed to create token');
    }
    setCreating(false);
  };

  const revokeToken = async (id, name) => {
    if (!window.confirm(`Revoke token "${name}"? This cannot be undone.`)) return;
    try {
      await api(`/tokens/${id}`, { method: 'DELETE' });
      loadTokens();
    } catch (err) {
      alert('Failed to revoke token');
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadPostman = async () => {
    try {
      const res = await fetch(`${API_BASE}/postman/collection`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'MotorsDynamic_API.postman_collection.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download Postman collection');
    }
  };

  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const selectEndpoint = (groupKey, index) => {
    const key = `${groupKey}-${index}`;
    if (expandedEndpoint === key) {
      setExpandedEndpoint(null);
    } else {
      setExpandedEndpoint(key);
      setTestParams({});
      setTestBody('');
      setTestResponse(null);

      const endpoint = apiEndpoints[groupKey].endpoints[index];
      if (endpoint.body && (endpoint.method === 'POST' || endpoint.method === 'PUT')) {
        const template = {};
        Object.entries(endpoint.body).forEach(([key, val]) => {
          template[key] = val.type === 'array' ? [] : val.type === 'object' ? {} : val.type === 'number' ? 0 : val.type === 'boolean' ? false : '';
        });
        setTestBody(JSON.stringify(template, null, 2));
      }
    }
  };

  const buildRequestUrl = (endpoint) => {
    let url = endpoint.path;
    (endpoint.params || []).filter(p => p.in === 'path').forEach(param => {
      const value = testParams[param.name] || `:${param.name}`;
      url = url.replace(`:${param.name}`, value);
    });
    const queryParams = (endpoint.params || []).filter(p => p.in !== 'path' && testParams[p.name]);
    if (queryParams.length > 0) {
      const qs = queryParams.map(p => `${p.name}=${encodeURIComponent(testParams[p.name])}`).join('&');
      url += `?${qs}`;
    }
    return url;
  };

  const executeRequest = async (endpoint) => {
    if (!testApiKey) {
      alert('Please enter an API key to test requests');
      return;
    }
    setTestLoading(true);
    setTestResponse(null);

    const url = `${API_BASE}${buildRequestUrl(endpoint).replace('/api', '')}`;
    const startTime = Date.now();

    try {
      const options = {
        method: endpoint.method,
        headers: { 'X-API-Key': testApiKey, 'Content-Type': 'application/json' }
      };
      if ((endpoint.method === 'POST' || endpoint.method === 'PUT') && testBody) {
        options.body = testBody;
      }
      const res = await fetch(url, options);
      const duration = Date.now() - startTime;
      let data;
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        data = await res.text();
      }
      setTestResponse({ status: res.status, statusText: res.statusText, duration, data, headers: Object.fromEntries(res.headers.entries()) });
    } catch (err) {
      setTestResponse({ status: 0, statusText: 'Network Error', duration: Date.now() - startTime, data: { error: err.message } });
    }
    setTestLoading(false);
  };

  // v2 palette method colors
  const getMethodColor = (method) => {
    const colors = {
      GET:    { bg: 'rgba(166,255,84,0.10)',  border: 'rgba(166,255,84,0.30)', fg: 'var(--c4)' },
      POST:   { bg: 'rgba(34,211,238,0.12)',  border: 'rgba(34,211,238,0.30)', fg: 'var(--c3)' },
      PUT:    { bg: 'rgba(255,200,87,0.12)',  border: 'rgba(255,200,87,0.30)', fg: 'var(--c5)' },
      DELETE: { bg: 'rgba(255,91,164,0.12)',  border: 'rgba(255,91,164,0.30)', fg: 'var(--c1)' },
      PATCH:  { bg: 'rgba(122,77,255,0.15)',  border: 'rgba(122,77,255,0.30)', fg: '#c4b5fd' },
    };
    return colors[method] || { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', fg: 'rgba(255,255,255,0.55)' };
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'var(--c4)';
    if (status >= 400 && status < 500) return 'var(--c5)';
    if (status >= 500) return 'var(--c1)';
    return 'rgba(255,255,255,0.5)';
  };

  const renderIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon /> : null;
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };
  const codeBlock = { background: 'rgba(0,0,0,0.40)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: 'var(--c4)', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING API EXPLORER…</span>
        </div>
      </div>
    );
  }

  const totalEndpoints = Object.values(apiEndpoints).reduce((s, g) => s + g.endpoints.length, 0);

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          API EXPLORER · {totalEndpoints} ENDPOINTS · {tokens.length} TOKEN{tokens.length === 1 ? '' : 'S'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            API.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Test endpoints, mint tokens, build integrations.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={downloadPostman} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
              <Icons.Download /> Postman
            </button>
            <button onClick={() => { navigate('/api/tokens'); setShowCreateForm(true); }} className="v2-chip"
              style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
              <Icons.Key /> Generate token
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 22, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
        {[
          { id: 'explorer', label: 'Explorer' },
          { id: 'tokens',   label: 'Tokens' },
          { id: 'guide',    label: 'Guide' },
        ].map(t => (
          <button key={t.id} onClick={() => navigate(`/api/${t.id}`)}
            style={{
              whiteSpace: 'nowrap',
              background: activeTab === t.id ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: activeTab === t.id ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: activeTab === t.id ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 18px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* EXPLORER */}
      {activeTab === 'explorer' && (
        <>
          {/* API key input */}
          <div className="v2-tile" style={{ padding: 14, marginBottom: 14, display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', flexShrink: 0 }}>API KEY</span>
            <input type="password" placeholder="Paste your API key to test endpoints…"
              value={testApiKey} onChange={e => setTestApiKey(e.target.value)}
              style={{ ...inp, flex: 1, minWidth: 260, fontFamily: "'JetBrains Mono', monospace" }} />
            {testApiKey && (
              <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                <Icons.Check /> Ready
              </span>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
            {/* Sidebar */}
            <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
              {Object.entries(apiEndpoints).map(([groupKey, group]) => (
                <div key={groupKey}>
                  <button onClick={() => toggleGroup(groupKey)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: expandedGroups[groupKey] ? 'rgba(255,255,255,0.03)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <span style={{ color: 'var(--c2)', display: 'flex', flexShrink: 0 }}>{renderIcon(group.icon)}</span>
                    <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 600 }}>{group.name}</span>
                    <span className="v2-mono" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', borderRadius: 99, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>{group.endpoints.length}</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', display: 'flex', flexShrink: 0, transform: expandedGroups[groupKey] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}><Icons.ChevronRight /></span>
                  </button>
                  {expandedGroups[groupKey] && (
                    <div style={{ background: 'rgba(0,0,0,0.20)' }}>
                      {group.endpoints.map((endpoint, idx) => {
                        const isActive = expandedEndpoint === `${groupKey}-${idx}`;
                        const c = getMethodColor(endpoint.method);
                        return (
                          <button key={idx} onClick={() => selectEndpoint(groupKey, idx)}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', paddingLeft: 32, background: isActive ? 'rgba(122,77,255,0.10)' : 'transparent', borderLeft: isActive ? '2px solid var(--c2)' : '2px solid transparent', borderTop: 'none', borderRight: 'none', borderBottom: '1px solid rgba(255,255,255,0.03)', color: isActive ? '#fff' : 'rgba(255,255,255,0.7)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                            <span className="v2-mono" style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}`, borderRadius: 6, padding: '1px 6px', fontSize: 9, fontWeight: 700, minWidth: 50, textAlign: 'center', flexShrink: 0 }}>{endpoint.method}</span>
                            <span className="v2-mono" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{endpoint.path.replace('/api', '')}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Main panel */}
            <div>
              {expandedEndpoint ? (() => {
                const [groupKey, idx] = expandedEndpoint.split('-');
                const endpoint = apiEndpoints[groupKey].endpoints[parseInt(idx)];
                const c = getMethodColor(endpoint.method);
                const curlCmd = `curl -X ${endpoint.method} "${window.location.origin}${buildRequestUrl(endpoint)}" \\\n  -H "X-API-Key: ${testApiKey || 'YOUR_API_KEY'}" \\\n  -H "Content-Type: application/json"${testBody ? ` \\\n  -d '${testBody.replace(/\n/g, '')}'` : ''}`;
                return (
                  <div className="v2-tile" style={{ padding: 22 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span className="v2-mono" style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 800, letterSpacing: '0.04em' }}>{endpoint.method}</span>
                      <code className="v2-mono" style={{ fontSize: 13, color: '#fff', flex: 1, minWidth: 200, wordBreak: 'break-all' }}>{endpoint.path}</code>
                      <button onClick={() => executeRequest(endpoint)} disabled={testLoading}
                        style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '8px 20px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: testLoading ? 0.6 : 1, letterSpacing: '0.02em', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
                        {testLoading ? 'Sending…' : 'Send →'}
                      </button>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginTop: 8, marginBottom: 18 }}>{endpoint.description}</p>

                    {endpoint.params && endpoint.params.length > 0 && (
                      <div style={{ marginBottom: 18 }}>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Parameters</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                          {endpoint.params.map((param, pIdx) => (
                            <div key={pIdx} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                                <span className="v2-mono" style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{param.name}</span>
                                {param.required && <span className="v2-mono" style={{ background: 'rgba(255,91,164,0.10)', color: 'var(--c1)', border: '1px solid rgba(255,91,164,0.25)', borderRadius: 4, padding: '1px 5px', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em' }}>REQUIRED</span>}
                                <span className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>{param.type}</span>
                                {param.in === 'path' && <span className="v2-mono" style={{ background: 'rgba(122,77,255,0.10)', color: '#c4b5fd', border: '1px solid rgba(122,77,255,0.25)', borderRadius: 4, padding: '1px 5px', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em' }}>PATH</span>}
                              </div>
                              <input type={param.type === 'number' ? 'number' : 'text'} placeholder={param.description}
                                value={testParams[param.name] || ''}
                                onChange={e => setTestParams(prev => ({ ...prev, [param.name]: e.target.value }))}
                                style={{ ...inp, fontSize: 12 }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {endpoint.body && (
                      <div style={{ marginBottom: 18 }}>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Request body</div>
                        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                          {Object.entries(endpoint.body).map(([key, val]) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', flexWrap: 'wrap' }}>
                              <span className="v2-mono" style={{ fontSize: 11, color: 'var(--c4)', fontWeight: 700 }}>{key}</span>
                              <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{val.type}</span>
                              {val.required && <span className="v2-mono" style={{ background: 'rgba(255,91,164,0.10)', color: 'var(--c1)', border: '1px solid rgba(255,91,164,0.25)', borderRadius: 4, padding: '0 5px', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em' }}>REQUIRED</span>}
                              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{val.description}</span>
                            </div>
                          ))}
                        </div>
                        <textarea value={testBody} onChange={e => setTestBody(e.target.value)}
                          placeholder="Enter JSON body" rows={8}
                          style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, lineHeight: 1.5, resize: 'vertical' }} />
                      </div>
                    )}

                    {/* cURL */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>cURL</div>
                        <button onClick={() => copyToClipboard(curlCmd)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10 }}>
                          <Icons.Copy /> Copy
                        </button>
                      </div>
                      <pre style={codeBlock}>{curlCmd}</pre>
                    </div>

                    {/* Response */}
                    {testResponse && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Response</div>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <span className="v2-num" style={{ color: getStatusColor(testResponse.status), fontSize: 14, fontWeight: 700 }}>
                              {testResponse.status} <span style={{ fontSize: 11, fontWeight: 400 }}>{testResponse.statusText}</span>
                            </span>
                            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{testResponse.duration}ms</span>
                          </div>
                        </div>
                        <pre style={{ ...codeBlock, color: 'rgba(255,255,255,0.85)', maxHeight: 360, overflow: 'auto' }}>
                          {typeof testResponse.data === 'string' ? testResponse.data : JSON.stringify(testResponse.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })() : (
                <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 12, color: 'var(--c3)' }}><Icons.Code /></div>
                  <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 6 }}>Pick an endpoint.</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>Choose one from the sidebar to view docs and test live requests.</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* TOKENS */}
      {activeTab === 'tokens' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {newToken && (
            <div className="v2-tile v2-tile-glow" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 10, color: 'var(--c1)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>⚠ COPY NOW</div>
              <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 12 }}>This token will not be shown again.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(0,0,0,0.40)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, marginBottom: 14 }}>
                <code className="v2-mono" style={{ fontSize: 12, color: 'var(--c4)', flex: 1, wordBreak: 'break-all' }}>{newToken}</code>
                <button onClick={() => copyToken(newToken)} className="v2-chip"
                  style={{ cursor: 'pointer', background: copied ? 'rgba(166,255,84,0.10)' : 'rgba(255,255,255,0.04)', borderColor: copied ? 'rgba(166,255,84,0.30)' : undefined, color: copied ? 'var(--c4)' : 'rgba(255,255,255,0.85)', flexShrink: 0 }}>
                  {copied ? <><Icons.Check /> Copied</> : <><Icons.Copy /> Copy</>}
                </button>
              </div>
              <button onClick={() => setNewToken(null)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
                I've copied the token
              </button>
            </div>
          )}

          {showCreateForm && (
            <div className="v2-tile" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>new token</div>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 16 }}>Generate API token</div>
              <div style={{ display: 'grid', gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>Token name *</label>
                  <input value={tokenName} onChange={e => setTokenName(e.target.value)} placeholder="e.g., Production integration" autoFocus style={inp} />
                </div>
                <div>
                  <label style={labelStyle}>Expires in (days)</label>
                  <input type="number" value={expiresInDays} onChange={e => setExpiresInDays(e.target.value)} placeholder="Leave empty for no expiration" style={inp} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowCreateForm(false)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', padding: '9px 18px', fontSize: 12 }}>Cancel</button>
                <button onClick={createToken} disabled={creating}
                  style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: creating ? 0.6 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
                  {creating ? 'Creating…' : 'Create token'}
                </button>
              </div>
            </div>
          )}

          <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>active tokens</div>
                <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Your API keys</div>
              </div>
              <button onClick={() => setShowCreateForm(true)} className="v2-chip"
                style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
                <Icons.Plus /> New token
              </button>
            </div>
            {tokens.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 6 }}>No tokens yet.</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Generate one to start making API calls.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: 18 }}>
                {tokens.map(t => {
                  const expiry = t.expires_at ? new Date(t.expires_at) : null;
                  const daysLeft = expiry ? Math.ceil((expiry - Date.now()) / 86400000) : null;
                  const expColor = daysLeft == null ? 'rgba(255,255,255,0.5)' : daysLeft < 0 ? 'var(--c1)' : daysLeft < 30 ? 'var(--c5)' : 'var(--c4)';
                  const expLabel = daysLeft == null ? 'Never' : daysLeft < 0 ? 'Expired' : formatDate(t.expires_at);
                  return (
                    <div key={t.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: 'var(--c2)', display: 'flex' }}><Icons.Key /></span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                        {t.rate_limit_per_hour === 0 && (
                          <span className="v2-mono" style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(166,255,84,0.10)', color: 'var(--c4)', border: '1px solid rgba(166,255,84,0.30)', flexShrink: 0 }}>UNLIMITED</span>
                        )}
                      </div>
                      <code className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.30)', padding: '6px 10px', borderRadius: 8, wordBreak: 'break-all' }}>{t.token_preview}</code>

                      {/* Usage at-a-glance */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div>
                          <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.04em' }}>24h</div>
                          <div className="v2-num" style={{ fontSize: 16, color: (t.requests_24h || 0) > 0 ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{formatNumber(t.requests_24h || 0)}</div>
                          {t.errors_24h > 0 && (
                            <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c1)' }}>{t.errors_24h} 4xx</div>
                          )}
                        </div>
                        <div>
                          <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.04em' }}>this period</div>
                          <div className="v2-num" style={{ fontSize: 16, color: (t.requests_period || 0) > 0 ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{formatNumber(t.requests_period || 0)}</div>
                        </div>
                        <div>
                          <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.04em' }}>30d</div>
                          <div className="v2-num" style={{ fontSize: 16, color: (t.requests_30d || 0) > 0 ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{formatNumber(t.requests_30d || 0)}</div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 4, fontSize: 11 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>rate limit</span>
                          <span className="v2-mono" style={{ color: t.rate_limit_per_hour === 0 ? 'var(--c4)' : 'rgba(255,255,255,0.7)', fontWeight: t.rate_limit_per_hour === 0 ? 700 : 400 }}>
                            {t.rate_limit_per_hour === 0 ? 'unlimited' : `${formatNumber(t.rate_limit_per_hour || 1000)}/hr`}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>created</span>
                          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatDate(t.created_at)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>last used</span>
                          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.last_used_at ? formatDate(t.last_used_at) : 'never'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>expires</span>
                          <span className="v2-mono" style={{ color: expColor, fontWeight: 700 }}>{expLabel}</span>
                        </div>
                      </div>
                      <button onClick={() => revokeToken(t.id, t.name)} className="v2-chip"
                        style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', justifyContent: 'center' }}>
                        <Icons.Trash /> Revoke
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* GUIDE */}
      {activeTab === 'guide' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Hero: link out to the full rendered integration guide + Swagger UI */}
          <div className="v2-tile v2-tile-violet" style={{ padding: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 260 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>full documentation</div>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Integration guide</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: 0, lineHeight: 1.5 }}>
                Every endpoint, the partner-integration walkthrough, webhook signing, error codes, SDK examples. Open in a new tab to read alongside the Explorer.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href="/api/docs/guide" target="_blank" rel="noreferrer"
                 className="v2-chip"
                 style={{
                   cursor: 'pointer',
                   background: 'linear-gradient(135deg, rgba(122,77,255,0.20), rgba(34,211,238,0.20))',
                   border: '1px solid rgba(122,77,255,0.40)',
                   color: '#fff', fontWeight: 700, padding: '10px 18px', fontSize: 13,
                   textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8,
                 }}>
                <Icons.Book /> Open guide
              </a>
              <a href="/api/docs" target="_blank" rel="noreferrer"
                 className="v2-chip"
                 style={{
                   cursor: 'pointer', background: 'rgba(255,255,255,0.04)',
                   color: 'rgba(255,255,255,0.85)', padding: '10px 18px', fontSize: 13,
                   textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8,
                 }}>
                <Icons.Code /> Swagger UI
              </a>
              <a href="/api/docs/spec" target="_blank" rel="noreferrer"
                 className="v2-chip"
                 style={{
                   cursor: 'pointer', background: 'rgba(255,255,255,0.04)',
                   color: 'rgba(255,255,255,0.85)', padding: '10px 18px', fontSize: 13,
                   textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8,
                 }}>
                <Icons.Download /> OpenAPI
              </a>
            </div>
          </div>

          <div className="v2-tile" style={{ padding: 22 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>quick start</div>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>Getting started</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {[
                { num: '1', title: 'Generate an API token', body: 'Switch to the Tokens tab and click "New token". Give it a descriptive name.' },
                { num: '2', title: 'Authenticate requests', body: 'Include the token in your request header:', code: 'X-API-Key: your_token_here' },
                { num: '3', title: 'Test in Explorer', body: 'Use the Explorer tab to test endpoints interactively. Enter your API key and send requests.' },
                { num: '4', title: 'Use with Postman', body: 'Download the Postman collection (button in the header), import it, and set the api_token variable.' },
              ].map(s => (
                <div key={s.num} style={{ display: 'flex', gap: 14 }}>
                  <div className="v2-num" style={{
                    width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                    background: 'linear-gradient(135deg, var(--c2), var(--c3))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 16, color: '#fff',
                    boxShadow: '0 4px 12px -4px rgba(122,77,255,0.5)'
                  }}>{s.num}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{s.body}</div>
                    {s.code && (
                      <code className="v2-mono" style={{ display: 'inline-block', marginTop: 8, padding: '6px 12px', background: 'rgba(0,0,0,0.40)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 12, color: 'var(--c4)' }}>{s.code}</code>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
            <div className="v2-tile v2-tile-violet" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>base url</div>
              <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 12 }}>Where to send requests</div>
              <code className="v2-mono" style={{ display: 'inline-block', padding: '10px 14px', background: 'rgba(0,0,0,0.40)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12, color: 'var(--c4)', wordBreak: 'break-all' }}>{window.location.origin}/api</code>
            </div>

            <div className="v2-tile v2-tile-cyan" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>auth</div>
              <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 12 }}>Authentication</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 0, marginBottom: 10 }}>Every request needs the <code className="v2-mono" style={{ color: 'var(--c3)', fontSize: 11, background: 'rgba(0,0,0,0.30)', padding: '1px 6px', borderRadius: 4 }}>X-API-Key</code> header.</p>
              <pre style={codeBlock}>{`curl -X GET "${window.location.origin}/api/domains" \\
  -H "X-API-Key: YOUR_API_TOKEN"`}</pre>
            </div>
          </div>

          <div className="v2-tile" style={{ padding: 22 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>responses</div>
            <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 12 }}>Response format</div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 0, marginBottom: 12 }}>All responses are JSON with consistent shapes:</p>
            <pre style={codeBlock}>{`// Success
{
  "domains": [...],
  "total": 42,
  "page": 1,
  "limit": 50
}

// Error
{
  "error": "Error message here",
  "code": "ERROR_CODE"
}`}</pre>
          </div>

          <div className="v2-tile v2-tile-amber" style={{ padding: 22 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>rate limits</div>
            <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 12 }}>How fast you can hit it</div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 0, marginBottom: 12 }}>API requests are capped at <strong style={{ color: 'var(--c5)' }}>10,000 requests per hour</strong> per token by default (configurable per-token; <code className="v2-mono" style={{ color: 'var(--c4)', fontSize: 11, background: 'rgba(0,0,0,0.30)', padding: '1px 6px', borderRadius: 4 }}>0</code> = unlimited). Rate-limit headers are returned on every response:</p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
              {[
                ['X-RateLimit-Limit',     'total requests allowed'],
                ['X-RateLimit-Remaining', 'requests remaining in window'],
                ['X-RateLimit-Reset',     'time when limit resets'],
              ].map(([k, v]) => (
                <li key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 12 }}>
                  <code className="v2-mono" style={{ color: 'var(--c4)', background: 'rgba(0,0,0,0.30)', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{k}</code>
                  <span style={{ color: 'rgba(255,255,255,0.6)' }}>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedBuilder() {
  const [feeds, setFeeds] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    Promise.all([
      api('/feeds'),
      api('/domains')
    ]).then(([feedsData, domainsData]) => {
      setFeeds(feedsData.feeds || []);
      setDomains(domainsData.domains || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const refreshFeeds = () => {
    api('/feeds').then(data => setFeeds(data.feeds || [])).catch(() => {});
  };

  const deleteFeed = async (id, name) => {
    if (!window.confirm(`Delete feed "${name}"? This cannot be undone.`)) return;
    try {
      await api(`/feeds/${id}`, { method: 'DELETE' });
      refreshFeeds();
    } catch (err) {
      alert('Failed to delete feed');
    }
  };

  const regenerateFeed = async (id) => {
    try {
      await api(`/feeds/${id}/regenerate`, { method: 'POST' });
      refreshFeeds();
      alert('Feed regenerated successfully!');
    } catch (err) {
      alert('Failed to regenerate feed');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const openWizard = async (feed = null) => {
    if (feed) {
      try {
        const data = await api(`/feeds/${feed.id}`);
        const appData = await api(`/feeds/apps/${feed.app_id}`);
        setEditingFeed({
          ...feed,
          ...data.feed,
          columns: appData.columns || [],
          domain_ids: data.domains?.map(d => d.domain_id) || [],
          custom_values: data.domains?.reduce((acc, d) => {
            if (d.custom_values) acc[d.domain_id] = typeof d.custom_values === 'string' ? JSON.parse(d.custom_values) : d.custom_values;
            return acc;
          }, {}) || {}
        });
      } catch (err) {
        alert('Failed to load feed details');
        return;
      }
    } else {
      setEditingFeed(null);
    }
    setShowWizard(true);
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING FEEDS…</span>
        </div>
      </div>
    );
  }

  // KPI math
  const totalFeeds  = feeds.length;
  const activeFeeds = feeds.filter(f => f.is_active).length;
  const totalVehiclesInFeeds = feeds.reduce((sum, f) => sum + (f.last_vehicle_count || 0), 0);
  const formatCounts = feeds.reduce((acc, f) => {
    const fmt = (f.output_format || 'csv').toLowerCase();
    acc[fmt] = (acc[fmt] || 0) + 1;
    return acc;
  }, {});
  const topFormat = Object.entries(formatCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

  function fmtRelative(ts) {
    if (!ts) return 'never';
    const diffMs = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function formatBadge(fmt) {
    const f = (fmt || 'csv').toLowerCase();
    const colors = {
      csv:  { bg: 'rgba(34,211,238,0.12)',  fg: '#a5f3fc' },
      xml:  { bg: 'rgba(122,77,255,0.18)',  fg: '#c4b5fd' },
      json: { bg: 'rgba(166,255,84,0.12)',  fg: '#d9f99d' },
      tsv:  { bg: 'rgba(255,200,87,0.14)',  fg: '#fde68a' },
    };
    return colors[f] || { bg: 'rgba(255,255,255,0.08)', fg: 'rgba(255,255,255,0.85)' };
  }

  return (
    <div className="page-content">
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          FEED BUILDER · {totalFeeds} {totalFeeds === 1 ? 'FEED' : 'FEEDS'}{activeFeeds ? ` · ${activeFeeds} ACTIVE` : ''}{totalVehiclesInFeeds ? ` · ${totalVehiclesInFeeds.toLocaleString()} VEHICLES SYNDICATED` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Feeds.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Inventory, syndicated everywhere it needs to go.
            </span>
          </h1>
          <button onClick={() => openWizard()} className="v2-chip"
                  style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
            <Icons.Plus /> Create feed
          </button>
        </div>
      </div>

      {/* KPIs */}
      {totalFeeds > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 22 }}>
          <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>total feeds</div>
            <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>{totalFeeds.toLocaleString()}</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
              {activeFeeds} active · {totalFeeds - activeFeeds} paused
            </div>
          </div>

          <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>vehicles syndicated</div>
            <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>{totalVehiclesInFeeds.toLocaleString()}</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>across all active feeds</div>
          </div>

          <div className="v2-tile" style={{ padding: 18 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>output formats</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {Object.entries(formatCounts).map(([fmt, count]) => {
                const c = formatBadge(fmt);
                return (
                  <span key={fmt} className="v2-chip" style={{ background: c.bg, borderColor: 'transparent', color: c.fg, fontSize: 11 }}>
                    {fmt.toUpperCase()} <span className="v2-mono" style={{ opacity: 0.7, marginLeft: 4 }}>{count}</span>
                  </span>
                );
              })}
            </div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 'auto', paddingTop: 12 }}>
              {topFormat.toUpperCase()} most used
            </div>
          </div>

          <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>destinations</div>
            <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>{domains.length.toLocaleString()}</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>domains available to feed</div>
          </div>
        </div>
      )}

      {/* Feeds list */}
      {feeds.length === 0 ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 28, color: '#fff', marginBottom: 10 }}>
            No feeds yet.
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>
            Create your first feed to export vehicle inventory data to advertising platforms.
          </div>
          <button onClick={() => openWizard()}
                  style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
            <Icons.Plus /> Create your first feed
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {feeds.map(feed => {
            const fmtColor = formatBadge(feed.output_format);
            const feedUrl = `${window.location.origin}/api/feeds/download/${feed.access_token}`;
            return (
              <div key={feed.id} className="v2-tile" style={{ padding: 18, gap: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <h3 className="v2-serif" style={{ fontSize: 22, fontWeight: 400, color: '#fff', margin: 0, lineHeight: 1.2 }}>
                        {feed.name}
                      </h3>
                      <span className="v2-chip" style={{ background: fmtColor.bg, borderColor: 'transparent', color: fmtColor.fg, fontSize: 10 }}>
                        {(feed.output_format || 'csv').toUpperCase()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {feed.is_active ? (
                        <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                          <span className="v2-pulse-dot" /> Active
                        </span>
                      ) : (
                        <span className="v2-chip" style={{ background: 'rgba(255,200,87,0.10)', borderColor: 'rgba(255,200,87,0.25)', color: '#fde68a', fontSize: 10 }}>
                          Paused
                        </span>
                      )}
                      <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                        <span style={{ color: '#fff' }}>{feed.domain_count || 0}</span> {feed.domain_count === 1 ? 'domain' : 'domains'}
                      </span>
                      <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                        <span style={{ color: '#fff' }}>{(feed.last_vehicle_count || 0).toLocaleString()}</span> vehicles
                      </span>
                      {feed.last_generated_at && (
                        <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                          updated {fmtRelative(feed.last_generated_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => openWizard(feed)} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
                      <Icons.Settings /> Edit
                    </button>
                    <button onClick={() => regenerateFeed(feed.id)} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(122,77,255,0.10)', borderColor: 'rgba(122,77,255,0.30)', color: '#c4b5fd' }}>
                      <Icons.Refresh /> Regenerate
                    </button>
                    <a href={`/api/feeds/download/${feed.access_token}`} target="_blank" rel="noopener noreferrer" className="v2-chip"
                       style={{ textDecoration: 'none', background: 'rgba(34,211,238,0.10)', borderColor: 'rgba(34,211,238,0.30)', color: 'var(--c3)' }}>
                      <Icons.Download /> Download
                    </a>
                    <button onClick={() => deleteFeed(feed.id, feed.name)} className="v2-chip"
                            title="Delete feed"
                            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8' }}>
                      <Icons.Trash />
                    </button>
                  </div>
                </div>

                {/* Feed URL */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.30)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 }}>
                  <code className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {feedUrl}
                  </code>
                  <button onClick={() => copyToClipboard(feedUrl, feed.id)}
                          title={copied === feed.id ? 'Copied!' : 'Copy URL'}
                          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 6, color: copied === feed.id ? 'var(--c4)' : 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}>
                    {copied === feed.id ? <Icons.Check /> : <Icons.Copy />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showWizard && (
        <FeedWizard
          feed={editingFeed}
          domains={domains}
          onClose={() => { setShowWizard(false); setEditingFeed(null); }}
          onSuccess={() => { setShowWizard(false); setEditingFeed(null); refreshFeeds(); }}
        />
      )}
    </div>
  );
}

function FeedWizard({ feed, domains, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(feed?.name || '');
  const [outputFormat, setOutputFormat] = useState(feed?.output_format || 'csv');
  const [selectedDomains, setSelectedDomains] = useState(feed?.domain_ids || []);
  const [columns, setColumns] = useState(feed?.columns || []);
  const [filters, setFilters] = useState(feed?.filters || {});
  const [isActive, setIsActive] = useState(feed?.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fields, setFields] = useState([]);
  const [previewDomain, setPreviewDomain] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [domainSearch, setDomainSearch] = useState('');
  const [fieldSearch, setFieldSearch] = useState('');
  const [editingColumn, setEditingColumn] = useState(null);

  const isEditing = !!feed?.id;

  // Filter domains by search
  const filteredDomains = domains.filter(d =>
    d.clean_domain?.toLowerCase().includes(domainSearch.toLowerCase())
  );

  // Load available fields
  useEffect(() => {
    api('/feeds/fields').then(data => {
      setFields(data.fields || []);
    }).catch(() => {});
  }, []);

  // Set default preview domain when selected domains change
  useEffect(() => {
    if (selectedDomains.length > 0) {
      // If current preview domain is not in selected, pick first selected
      if (!previewDomain || !selectedDomains.includes(previewDomain)) {
        setPreviewDomain(selectedDomains[0]);
      }
    } else {
      setPreviewDomain(null);
    }
  }, [selectedDomains]);

  // Load preview when domain or columns change
  useEffect(() => {
    if (previewDomain && columns.length > 0 && step === 2) {
      loadPreview();
    }
  }, [previewDomain, columns, filters, step]);

  const loadPreview = async () => {
    if (!previewDomain || columns.length === 0) return;
    setPreviewLoading(true);
    try {
      const data = await api('/feeds/preview', {
        method: 'POST',
        body: JSON.stringify({
          domain_ids: [previewDomain],
          columns: columns,
          filters: filters,
          limit: 10
        })
      });
      setPreviewData(data);
    } catch (err) {
      console.error('Preview failed:', err);
    }
    setPreviewLoading(false);
  };

  const addColumn = (sourceField = 'vin') => {
    const field = fields.find(f => f.name === sourceField);
    const newCol = {
      id: Date.now(),
      name: field?.label || sourceField,
      source_type: 'direct',
      source_field: sourceField,
      static_value: '',
      template: '',
      formula: '',
      transform: '',
      default_value: '',
      array_handling: 'first',
      max_array_items: 10,
      display_order: columns.length + 1
    };
    setColumns([...columns, newCol]);
  };

  const updateColumn = (id, updates) => {
    setColumns(columns.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const deleteColumn = (id) => {
    setColumns(columns.filter(c => c.id !== id));
  };

  const moveColumn = (index, direction) => {
    const newColumns = [...columns];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= columns.length) return;
    [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
    setColumns(newColumns.map((c, i) => ({ ...c, display_order: i + 1 })));
  };

  const addCustomColumn = () => {
    const newCol = {
      id: Date.now(),
      name: 'custom_field',
      source_type: 'static',
      source_field: '',
      static_value: '',
      template: '',
      formula: '',
      transform: '',
      default_value: '',
      array_handling: 'first',
      max_array_items: 10,
      display_order: columns.length + 1
    };
    setColumns([...columns, newCol]);
    setEditingColumn(newCol);
  };

  const openColumnConfig = (col) => {
    setEditingColumn({ ...col });
  };

  const saveColumnConfig = () => {
    if (editingColumn) {
      setColumns(columns.map(c => c.id === editingColumn.id ? editingColumn : c));
      setEditingColumn(null);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Feed name is required');
      setStep(1);
      return;
    }
    if (selectedDomains.length === 0) {
      setError('Select at least one domain');
      setStep(1);
      return;
    }
    if (columns.length === 0) {
      setError('Add at least one column');
      setStep(2);
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (isEditing) {
        // Check if the app is official - if so, create a new custom app
        let appIdToUse = feed.app_id;
        const appInfo = await api(`/feeds/apps/${feed.app_id}`);

        if (appInfo.app?.is_official) {
          // Create a new custom app to replace the official one
          const newApp = await api('/feeds/apps', {
            method: 'POST',
            body: JSON.stringify({
              name: `${name} Template`,
              description: `Custom template for ${name}`,
              icon: '📄',
              output_format: outputFormat
            })
          });
          appIdToUse = newApp.app.id;

          // Add columns to new app
          for (const col of columns) {
            await api(`/feeds/apps/${appIdToUse}/columns`, {
              method: 'POST',
              body: JSON.stringify(col)
            });
          }
        } else {
          // Update existing custom app
          await api(`/feeds/apps/${feed.app_id}`, {
            method: 'PUT',
            body: JSON.stringify({ output_format: outputFormat })
          });

          // Delete existing columns and recreate
          const existingCols = await api(`/feeds/apps/${feed.app_id}`);
          for (const col of existingCols.columns || []) {
            await api(`/feeds/apps/${feed.app_id}/columns/${col.id}`, { method: 'DELETE' });
          }
          for (const col of columns) {
            await api(`/feeds/apps/${feed.app_id}/columns`, {
              method: 'POST',
              body: JSON.stringify(col)
            });
          }
        }

        // Update the feed with possibly new app_id
        await api(`/feeds/${feed.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name,
            is_active: isActive,
            filters,
            domain_ids: selectedDomains,
            app_id: appIdToUse
          })
        });
      } else {
        // Create new app for this feed
        const appResult = await api('/feeds/apps', {
          method: 'POST',
          body: JSON.stringify({
            name: `${name} Template`,
            description: `Auto-generated for ${name}`,
            icon: '📄',
            output_format: outputFormat
          })
        });

        // Add columns to app
        for (const col of columns) {
          await api(`/feeds/apps/${appResult.app.id}/columns`, {
            method: 'POST',
            body: JSON.stringify(col)
          });
        }

        // Create feed instance
        await api('/feeds', {
          method: 'POST',
          body: JSON.stringify({
            app_id: appResult.app.id,
            name,
            is_active: isActive,
            update_frequency: 'hourly',
            filters,
            domain_ids: selectedDomains
          })
        });
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to save feed');
    }
    setSaving(false);
  };

  const groupedFields = fields.reduce((acc, f) => {
    const cat = f.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-fullscreen feed-wizard" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Feed' : 'Create New Feed'}</h2>
          <button className="btn btn-icon" onClick={onClose}><Icons.X /></button>
        </div>

        {/* Step Indicator */}
        <div className="wizard-steps">
          <button className={`wizard-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`} onClick={() => setStep(1)}>
            <span className="step-number">1</span>
            <span className="step-label">Setup</span>
          </button>
          <div className="wizard-step-line" />
          <button className={`wizard-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`} onClick={() => setStep(2)}>
            <span className="step-number">2</span>
            <span className="step-label">Columns</span>
          </button>
          <div className="wizard-step-line" />
          <button className={`wizard-step ${step === 3 ? 'active' : ''}`} onClick={() => setStep(3)}>
            <span className="step-number">3</span>
            <span className="step-label">Preview & Save</span>
          </button>
        </div>

        <div className="wizard-content">
          {/* Step 1: Basic Setup */}
          {step === 1 && (
            <div className="wizard-step-content">
              <h3>Feed Setup</h3>
              <p className="text-muted">Configure basic feed settings</p>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Feed Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g., Google Ads Feed"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Output Format</label>
                  <div className="format-toggle">
                    <button
                      className={`format-btn ${outputFormat === 'csv' ? 'active' : ''}`}
                      onClick={() => setOutputFormat('csv')}
                    >
                      CSV
                    </button>
                    <button
                      className={`format-btn ${outputFormat === 'json' ? 'active' : ''}`}
                      onClick={() => setOutputFormat('json')}
                    >
                      JSON
                    </button>
                    <button
                      className={`format-btn ${outputFormat === 'xml' ? 'active' : ''}`}
                      onClick={() => setOutputFormat('xml')}
                    >
                      XML
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Select Domains *</label>
                <p className="form-hint">Choose which dealerships to include in this feed</p>
                <div className="domain-search-wrapper">
                  <input
                    type="text"
                    className="form-input domain-search-input"
                    placeholder="Search domains..."
                    value={domainSearch}
                    onChange={e => setDomainSearch(e.target.value)}
                  />
                  {selectedDomains.length > 0 && (
                    <span className="domain-selected-count">{selectedDomains.length} selected</span>
                  )}
                </div>
                <div className="domain-checkboxes">
                  {filteredDomains.map(d => (
                    <label key={d.id} className="domain-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedDomains.includes(d.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedDomains([...selectedDomains, d.id]);
                          } else {
                            setSelectedDomains(selectedDomains.filter(id => id !== d.id));
                          }
                        }}
                      />
                      <span className="domain-checkbox-label">
                        <strong>{d.clean_domain}</strong>
                        <span className="text-muted">{d.vehicle_count || 0} vehicles</span>
                      </span>
                    </label>
                  ))}
                  {filteredDomains.length === 0 && domainSearch && (
                    <div className="domain-no-results">No domains match "{domainSearch}"</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Filters (Optional)</label>
                <div className="filter-grid">
                  <div>
                    <label className="form-label-sm">Condition</label>
                    <select
                      className="form-select"
                      value={filters.condition || ''}
                      onChange={e => setFilters({ ...filters, condition: e.target.value || undefined })}
                    >
                      <option value="">All</option>
                      <option value="new">New Only</option>
                      <option value="used">Used Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label-sm">Min Price</label>
                    <input
                      type="number"
                      className="form-input"
                      value={filters.min_price || ''}
                      onChange={e => setFilters({ ...filters, min_price: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="Any"
                    />
                  </div>
                  <div>
                    <label className="form-label-sm">Max Price</label>
                    <input
                      type="number"
                      className="form-input"
                      value={filters.max_price || ''}
                      onChange={e => setFilters({ ...filters, max_price: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="Any"
                    />
                  </div>
                  <div>
                    <label className="form-label-sm">Has Images</label>
                    <select
                      className="form-select"
                      value={filters.has_images === true ? 'true' : filters.has_images === false ? 'false' : ''}
                      onChange={e => setFilters({ ...filters, has_images: e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined })}
                    >
                      <option value="">Any</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="wizard-actions">
                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!name.trim() || selectedDomains.length === 0}>
                  Next: Configure Columns
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Column Builder */}
          {step === 2 && (
            <div className="wizard-step-content wizard-columns-v2">
              <div className="column-builder-layout">
                {/* LEFT: Available Fields */}
                <div className="fields-sidebar">
                  <div className="fields-sidebar-header">
                    <h4>Available Fields</h4>
                    <input
                      type="text"
                      className="form-input form-input-sm"
                      placeholder="Search fields..."
                      value={fieldSearch || ''}
                      onChange={e => setFieldSearch(e.target.value)}
                    />
                  </div>
                  <div className="fields-sidebar-content">
                    {Object.entries(groupedFields).map(([category, categoryFields]) => {
                      const filtered = categoryFields.filter(f =>
                        !fieldSearch || f.name.toLowerCase().includes(fieldSearch.toLowerCase()) ||
                        (f.label || '').toLowerCase().includes(fieldSearch.toLowerCase())
                      );
                      if (filtered.length === 0) return null;
                      return (
                        <div key={category} className="field-group">
                          <div className="field-group-header">{category}</div>
                          {filtered.map(field => (
                            <div
                              key={field.name}
                              className="field-item"
                              onClick={() => addColumn(field.name)}
                              title={field.description}
                            >
                              <span className="field-item-name">{field.name}</span>
                              <span className="field-item-type">{field.type}</span>
                              <button className="field-item-add"><Icons.Plus /></button>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {/* Custom Field Option */}
                    <div className="field-group">
                      <div className="field-group-header">Custom</div>
                      <div
                        className="field-item field-item-custom"
                        onClick={() => addCustomColumn()}
                      >
                        <span className="field-item-name">+ Add Custom Column</span>
                        <span className="field-item-type">static/formula</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Preview Table with Column Headers */}
                <div className="preview-main">
                  <div className="preview-main-header">
                    <div className="preview-title">
                      <h4>Feed Preview</h4>
                      <span className="preview-count">{columns.length} columns</span>
                    </div>
                    <div className="preview-controls">
                      <select
                        className="form-select form-select-sm"
                        value={previewDomain || ''}
                        onChange={e => setPreviewDomain(parseInt(e.target.value))}
                      >
                        {selectedDomains.map(domainId => {
                          const d = domains.find(dom => dom.id === domainId);
                          return d ? <option key={d.id} value={d.id}>{d.clean_domain}</option> : null;
                        })}
                      </select>
                      <button className="btn btn-sm btn-secondary" onClick={loadPreview} disabled={previewLoading}>
                        <Icons.Refresh />
                      </button>
                    </div>
                  </div>

                  {columns.length === 0 ? (
                    <div className="preview-empty-state">
                      <Icons.Table />
                      <h4>No columns yet</h4>
                      <p>Click on fields from the left panel to add them as columns</p>
                    </div>
                  ) : (
                    <div className="preview-table-container">
                      <table className="preview-table-v2">
                        <thead>
                          <tr>
                            {columns.map((col, idx) => (
                              <th key={col.id} className="preview-th">
                                <div className="column-header">
                                  <div className="column-header-drag">
                                    <button
                                      className="drag-btn"
                                      onClick={() => moveColumn(idx, -1)}
                                      disabled={idx === 0}
                                      title="Move left"
                                    >←</button>
                                    <button
                                      className="drag-btn"
                                      onClick={() => moveColumn(idx, 1)}
                                      disabled={idx === columns.length - 1}
                                      title="Move right"
                                    >→</button>
                                  </div>
                                  <input
                                    type="text"
                                    className="column-header-name"
                                    value={col.name}
                                    onChange={e => updateColumn(col.id, { name: e.target.value })}
                                    title="Click to rename"
                                  />
                                  <div className="column-header-meta">
                                    <span className="column-source-badge">
                                      {col.source_type === 'static' ? 'Static' :
                                       col.source_type === 'formula' ? 'Formula' :
                                       col.source_field}
                                    </span>
                                  </div>
                                  <div className="column-header-actions">
                                    <button
                                      className="column-action-btn"
                                      onClick={() => openColumnConfig(col)}
                                      title="Configure"
                                    >
                                      <Icons.Settings />
                                    </button>
                                    <button
                                      className="column-action-btn column-action-delete"
                                      onClick={() => deleteColumn(col.id)}
                                      title="Remove"
                                    >
                                      <Icons.X />
                                    </button>
                                  </div>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewLoading ? (
                            <tr>
                              <td colSpan={columns.length} className="preview-loading-cell">
                                Loading preview...
                              </td>
                            </tr>
                          ) : previewData?.rows?.length > 0 ? (
                            previewData.rows.map((row, idx) => (
                              <tr key={idx}>
                                {columns.map(col => (
                                  <td key={col.id}>
                                    {col.source_type === 'static' ? (
                                      <span className="static-value">{col.static_value || '-'}</span>
                                    ) : col.source_type === 'formula' ? (
                                      <span className="formula-value">{row[col.name] ?? '(formula)'}</span>
                                    ) : (
                                      String(row[col.name] ?? '-').substring(0, 100)
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={columns.length} className="preview-no-data">
                                No data available for preview
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="wizard-actions">
                <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
                <button className="btn btn-primary" onClick={() => setStep(3)} disabled={columns.length === 0}>
                  Next: Review & Save
                </button>
              </div>

              {/* Column Configuration Modal */}
              {editingColumn && (
                <div className="modal-overlay" onClick={() => setEditingColumn(null)}>
                  <div className="modal column-config-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Configure Column</h3>
                      <button className="btn btn-icon" onClick={() => setEditingColumn(null)}><Icons.X /></button>
                    </div>
                    <div className="modal-body">
                      <div className="form-group">
                        <label className="form-label">Column Name</label>
                        <input
                          type="text"
                          className="form-input"
                          value={editingColumn.name}
                          onChange={e => setEditingColumn({ ...editingColumn, name: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Data Source</label>
                        <div className="source-type-tabs">
                          <button
                            className={`source-tab ${editingColumn.source_type === 'direct' ? 'active' : ''}`}
                            onClick={() => setEditingColumn({ ...editingColumn, source_type: 'direct' })}
                          >
                            Field
                          </button>
                          <button
                            className={`source-tab ${editingColumn.source_type === 'static' ? 'active' : ''}`}
                            onClick={() => setEditingColumn({ ...editingColumn, source_type: 'static' })}
                          >
                            Static Value
                          </button>
                          <button
                            className={`source-tab ${editingColumn.source_type === 'formula' ? 'active' : ''}`}
                            onClick={() => setEditingColumn({ ...editingColumn, source_type: 'formula' })}
                          >
                            Formula
                          </button>
                        </div>
                      </div>

                      {editingColumn.source_type === 'direct' && (
                        <div className="form-group">
                          <label className="form-label">Source Field</label>
                          <select
                            className="form-select"
                            value={editingColumn.source_field || ''}
                            onChange={e => setEditingColumn({ ...editingColumn, source_field: e.target.value })}
                          >
                            <option value="">Select field...</option>
                            {fields.map(f => (
                              <option key={f.name} value={f.name}>{f.name} - {f.description}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {editingColumn.source_type === 'static' && (
                        <div className="form-group">
                          <label className="form-label">Static Value</label>
                          <input
                            type="text"
                            className="form-input"
                            value={editingColumn.static_value || ''}
                            onChange={e => setEditingColumn({ ...editingColumn, static_value: e.target.value })}
                            placeholder="Enter value to show for all vehicles..."
                          />
                          <p className="form-hint">This value will be the same for every vehicle in the feed.</p>
                        </div>
                      )}

                      {editingColumn.source_type === 'formula' && (
                        <div className="form-group">
                          <label className="form-label">Formula</label>
                          <input
                            type="text"
                            className="form-input"
                            value={editingColumn.formula || ''}
                            onChange={e => setEditingColumn({ ...editingColumn, formula: e.target.value })}
                            placeholder="e.g., {price} * 1.1 or CONCAT({make}, ' ', {model})"
                          />
                          <p className="form-hint">
                            Use {'{field}'} to reference fields. Examples:<br />
                            • {'{price}'} * 1.1 - Add 10% markup<br />
                            • CONCAT({'{year}'}, ' ', {'{make}'}, ' ', {'{model}'}) - Combine fields<br />
                            • IF({'{condition}'} = "new", {'{msrp}'}, {'{price}'}) - Conditional
                          </p>
                        </div>
                      )}

                      <div className="form-group">
                        <label className="form-label">Default Value (if empty)</label>
                        <input
                          type="text"
                          className="form-input"
                          value={editingColumn.default_value || ''}
                          onChange={e => setEditingColumn({ ...editingColumn, default_value: e.target.value })}
                          placeholder="Value to use when field is empty"
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" onClick={() => setEditingColumn(null)}>Cancel</button>
                      <button className="btn btn-primary" onClick={saveColumnConfig}>Save Changes</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review & Save */}
          {step === 3 && (
            <div className="wizard-step-content">
              <h3>Review Your Feed</h3>
              <p className="text-muted">Confirm your feed configuration before saving</p>

              <div className="review-grid">
                <div className="review-section">
                  <h4>Feed Settings</h4>
                  <div className="review-item">
                    <span className="review-label">Name</span>
                    <span className="review-value">{name}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Format</span>
                    <span className="review-value">{outputFormat.toUpperCase()}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Domains</span>
                    <span className="review-value">{selectedDomains.length} selected</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Columns</span>
                    <span className="review-value">{columns.length} columns</span>
                  </div>
                </div>

                <div className="review-section">
                  <h4>Columns</h4>
                  <div className="review-columns">
                    {columns.map((col, idx) => (
                      <span key={col.id} className="review-column">{col.name}</span>
                    ))}
                  </div>
                </div>

                <div className="review-section">
                  <h4>Domains</h4>
                  <div className="review-domains">
                    {selectedDomains.map(domainId => {
                      const d = domains.find(dom => dom.id === domainId);
                      return d ? <span key={d.id} className="review-domain">{d.clean_domain}</span> : null;
                    })}
                  </div>
                </div>

                {Object.keys(filters).filter(k => filters[k] !== undefined).length > 0 && (
                  <div className="review-section">
                    <h4>Filters</h4>
                    {filters.condition && <div className="review-item"><span>Condition:</span> <span>{filters.condition}</span></div>}
                    {filters.min_price && <div className="review-item"><span>Min Price:</span> <span>${filters.min_price}</span></div>}
                    {filters.max_price && <div className="review-item"><span>Max Price:</span> <span>${filters.max_price}</span></div>}
                    {filters.has_images !== undefined && <div className="review-item"><span>Has Images:</span> <span>{filters.has_images ? 'Yes' : 'No'}</span></div>}
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginTop: '24px' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                  />
                  <span>Activate feed immediately</span>
                </label>
              </div>

              {error && <div className="form-error">{error}</div>}

              <div className="wizard-actions">
                <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Feed')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// LOGIN PAGE
// =============================================================================
function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, requestMagicLink, verifyMagicLink, verifyLoginCode, verify2FA, requestPasswordReset, isAuthenticated } = useAuth();

  // Email sign-in code is the default (as on LotPro); the password form stays one click away.
  const [mode, setMode] = useState('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await requestPasswordReset(email);
      setMode('forgot-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) handleMagicLinkVerify(token);
  }, [location.search]);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const handleMagicLinkVerify = async (token) => {
    setLoading(true);
    setError('');
    try {
      const result = await verifyMagicLink(token);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setCode('');
        setMode('2fa');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setMode('2fa');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await requestMagicLink(email);
      setCode('');
      setMode('magic-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginCodeVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await verifyLoginCode(email, code.trim());
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setCode('');
        setMode('2fa');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await verify2FA(tempToken, code);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Shared styles: flat and quiet (no gradients, glows or mono kickers)
  const inputStyle = {
    background: '#111116',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: '#fff',
    padding: '10px 12px 10px 38px',
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s'
  };
  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 6
  };
  const iconWrap = {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%) scale(0.8)',
    color: 'rgba(255,255,255,0.35)',
    display: 'flex',
    pointerEvents: 'none'
  };

  const primaryBtn = {
    background: '#FAFAF6',
    border: '1px solid #FAFAF6',
    borderRadius: 8,
    color: '#0a0a0e',
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s'
  };

  const secondaryBtn = {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 8,
    color: 'rgba(255,255,255,0.85)',
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      color: 'var(--ink, #FAFAF6)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px' }}>
          <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 20 }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <MDLogo size={28} radius={7} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>MotorsDynamic</span>
            </div>

            <h1 style={{ fontSize: 22, fontWeight: 600, color: '#fff', margin: 0, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
              {mode === '2fa' ? 'Two-factor authentication'
                : mode === 'magic-sent' ? 'Check your email'
                : mode === 'forgot' ? 'Reset your password'
                : mode === 'forgot-sent' ? 'Check your email'
                : 'Sign in to MotorsDynamic'}
            </h1>

            {error && (
              <div style={{
                background: 'rgba(255,91,91,0.08)',
                border: '1px solid rgba(255,91,91,0.28)',
                color: '#ff9b9b',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8
              }}>
                <span style={{ display: 'flex', flexShrink: 0, marginTop: 1 }}><Icons.X /></span>
                <span style={{ flex: 1 }}>{error}</span>
              </div>
            )}

            {mode === 'password' && (
              <form onSubmit={handlePasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label htmlFor="email" style={labelStyle}>Email</label>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Icons.Mail /></span>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" style={labelStyle}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Icons.Lock /></span>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                      style={{ ...inputStyle, paddingRight: 42 }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex="-1"
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', padding: 6, display: 'flex' }}
                    >
                      {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading}
                        style={{ ...primaryBtn, opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer', marginTop: 4 }}
>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>or</span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </div>

                <button type="button" onClick={() => setMode('magic')} style={secondaryBtn}>
                  <Icons.Mail /> Email me a sign-in link instead
                </button>

                <div style={{ textAlign: 'center', marginTop: 6 }}>
                  <a href="#"
                     onClick={(e) => { e.preventDefault(); setError(''); setMode('forgot'); }}
                    
                     style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', textDecoration: 'none', cursor: 'pointer' }}>
                    Forgot password?
                  </a>
                </div>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgotPasswordRequest} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5 }}>
                  Enter your email and we'll send you a link to set a new password.
                </p>

                <div>
                  <label htmlFor="forgot-email" style={labelStyle}>Email</label>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Icons.Mail /></span>
                    <input
                      id="forgot-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                  </div>
                </div>

                <button type="submit" disabled={loading}
                        style={{ ...primaryBtn, opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' }}>
                  {loading ? 'Sending…' : 'Send reset link'}
                </button>

                <button type="button" onClick={() => { setError(''); setMode('password'); }}
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 13, padding: 8, fontFamily: 'inherit' }}>
                  Back to password sign-in
                </button>
              </form>
            )}

            {mode === 'forgot-sent' && (
              <div>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, margin: '0 0 12px' }}>
                  If an account exists for <strong style={{ color: '#fff' }}>{email}</strong>, you'll receive a reset link shortly.<br />
                  The link expires in 1 hour.
                </p>
                <button type="button" onClick={() => { setError(''); setMode('password'); }}
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 13, padding: 8, fontFamily: 'inherit' }}>
                  Back to sign-in
                </button>
              </div>
            )}

            {mode === 'magic' && (
              <form onSubmit={handleMagicLinkRequest} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5 }}>
                  We'll email you a sign-in link and a code.
                </p>

                <div>
                  <label htmlFor="magic-email" style={labelStyle}>Email</label>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Icons.Mail /></span>
                    <input
                      id="magic-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                  </div>
                </div>

                <button type="submit" disabled={loading}
                        style={{ ...primaryBtn, opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' }}>
                  {loading ? 'Sending…' : 'Email me a sign-in link'}
                </button>

                <button type="button" onClick={() => setMode('password')}
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 13, padding: 8, fontFamily: 'inherit' }}>
                  Use password instead
                </button>
              </form>
            )}

            {mode === 'magic-sent' && (
              <form onSubmit={handleLoginCodeVerify} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5 }}>
                  If <strong style={{ color: '#fff' }}>{email}</strong> has an account, we've emailed it a sign-in link and a 6-digit code.
                  Click the link, or enter the code here. The code expires in 10 minutes.
                </p>

                <div>
                  <label htmlFor="login-code" style={labelStyle}>Code</label>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Icons.Mail /></span>
                    <input
                      id="login-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="123456"
                      required
                      autoFocus
                      style={{ ...inputStyle, letterSpacing: '0.3em', fontFamily: "'JetBrains Mono', monospace" }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                  </div>
                </div>

                <button type="submit" disabled={loading || code.length !== 6}
                        style={{ ...primaryBtn, opacity: (loading || code.length !== 6) ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' }}>
                  {loading ? 'Verifying…' : 'Sign in'}
                </button>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button type="button" disabled={loading} onClick={handleMagicLinkRequest}
                          style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 13, padding: 8, fontFamily: 'inherit' }}>
                    Resend code
                  </button>
                  <button type="button" onClick={() => { setError(''); setMode('magic'); }}
                          style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 13, padding: 8, fontFamily: 'inherit' }}>
                    Use a different email
                  </button>
                </div>
              </form>
            )}

            {mode === '2fa' && (
              <form onSubmit={handle2FAVerify} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5 }}>
                  Enter the 6-digit code sent to your phone.
                </p>

                <div>
                  <label htmlFor="code" style={labelStyle}>Verification code</label>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Icons.Key /></span>
                    <input
                      id="code"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                      maxLength={6}
                      pattern="[0-9]{6}"
                      autoComplete="one-time-code"
                      style={{
                        ...inputStyle,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 22,
                        letterSpacing: '0.4em',
                        textAlign: 'center',
                        padding: '14px 14px 14px 42px',
                      }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                  </div>
                </div>

                <button type="submit" disabled={loading || code.length !== 6}
                        style={{ ...primaryBtn, opacity: (loading || code.length !== 6) ? 0.5 : 1, cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Verifying…' : 'Verify'}
                </button>

                <button type="button"
                        onClick={() => { setMode('password'); setTempToken(''); setCode(''); }}
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 13, padding: 8, fontFamily: 'inherit' }}>
                  Cancel
                </button>
              </form>
            )}

            {(mode === 'password' || mode === 'magic') && (
              <div style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                New to MotorsDynamic?{' '}
                <Link to="/signup" style={{ color: '#fff', textDecoration: 'underline', textUnderlineOffset: 3, fontWeight: 500 }}>
                  Start a 7-day trial
                </Link>
              </div>
            )}
          </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, padding: '20px 16px', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
        <span>© {new Date().getFullYear()} MotorsDynamic</span>
        <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Privacy</Link>
        <Link to="/terms" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Terms</Link>
      </div>
    </div>
  );
}

function PrivacyPolicyPage() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 600px at 88% -10%, rgba(122,77,255,.15), transparent 60%),
        radial-gradient(900px 500px at -10% 110%, rgba(34,211,238,.08), transparent 60%)
      `,
      color: 'var(--ink, #FAFAF6)',
      fontFamily: 'Inter, system-ui, sans-serif',
      padding: '40px 20px 80px'
    }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, gap: 16, flexWrap: 'wrap' }}>
          <Link to="/login" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
            <MDLogo size={38} radius={11} style={{ boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>MotorsDynamic</div>
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>app.motorsdynamic.com</div>
            </div>
          </Link>
          <Link to="/login" className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', textDecoration: 'none' }}>← Back to login</Link>
        </div>

        <div style={{ marginBottom: 32 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 8 }}>LAST UPDATED · DEC 10, 2025</div>
          <h1 className="v2-serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: '#fff', margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em' }}>Privacy policy.</h1>
        </div>

        <div className="v2-tile v2-legal" style={{ padding: 'clamp(24px, 4vw, 40px)' }}>
          <section>
            <h2>Introduction</h2>
            <p>MotorsDynamic ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our automotive dealership management platform and related services (collectively, the "Service").</p>
            <p>By accessing or using the Service, you agree to this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.</p>
          </section>

          <section>
            <h2>Information we collect</h2>
            <h3>2.1 Personal information</h3>
            <p>We may collect personal information that you voluntarily provide when using our Service, including:</p>
            <ul>
              <li>Name, email address, and phone number</li>
              <li>Company/dealership name and business information</li>
              <li>Account credentials (username and password)</li>
              <li>Billing and payment information</li>
            </ul>
            <h3>2.2 Automatically collected information</h3>
            <p>When you access our Service, we automatically collect certain information, including:</p>
            <ul>
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>IP address and geographic location</li>
              <li>Usage data (pages visited, features used, time spent)</li>
              <li>Log data and analytics information</li>
            </ul>
            <h3>2.3 Vehicle and inventory data</h3>
            <p>Our Service processes vehicle inventory data from dealership websites, which may include:</p>
            <ul>
              <li>Vehicle specifications (make, model, year, VIN, mileage)</li>
              <li>Pricing information</li>
              <li>Vehicle images and descriptions</li>
              <li>Dealership location and contact information</li>
            </ul>
          </section>

          <section>
            <h2>How we use your information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative information, updates, and security alerts</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2>Two-factor authentication (2FA)</h2>
            <p>We offer SMS-based two-factor authentication to enhance account security. When you enable 2FA:</p>
            <ul>
              <li>We collect and store your phone number for verification purposes</li>
              <li>We send verification codes via SMS during login</li>
              <li>SMS messages are sent through our third-party provider (Twilio)</li>
              <li>You may opt out of 2FA at any time through your account settings</li>
            </ul>
            <p>By enabling 2FA, you consent to receiving SMS messages for authentication purposes. Standard messaging rates may apply.</p>
          </section>

          <section>
            <h2>Information sharing and disclosure</h2>
            <p>We may share your information in the following circumstances:</p>
            <ul>
              <li><strong>Service providers:</strong> with third-party vendors who assist in operating our Service (e.g., hosting, analytics, payment processing, SMS delivery)</li>
              <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Legal requirements:</strong> when required by law or to protect our rights and safety</li>
              <li><strong>With your consent:</strong> when you have given us explicit permission</li>
            </ul>
            <p>We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2>Data security</h2>
            <p>We implement appropriate technical and organizational security measures to protect your information, including:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
            </ul>
            <p>However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2>Data retention</h2>
            <p>We retain your information for as long as your account is active or as needed to provide you with our Service. We may also retain and use your information to comply with legal obligations, resolve disputes, and enforce our agreements.</p>
          </section>

          <section>
            <h2>Your rights and choices</h2>
            <p>Depending on your location, you may have certain rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> request a copy of your personal information</li>
              <li><strong>Correction:</strong> request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> request deletion of your personal information</li>
              <li><strong>Opt-out:</strong> opt out of marketing communications</li>
              <li><strong>Data portability:</strong> request your data in a portable format</li>
            </ul>
            <p>To exercise these rights, please contact us at <a href="mailto:privacy@motorsdynamic.io">privacy@motorsdynamic.io</a>.</p>
          </section>

          <section>
            <h2>Children's privacy</h2>
            <p>Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.</p>
          </section>

          <section>
            <h2>Changes to this policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the Service after any changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2>Contact us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact us at:</p>
            <p>
              <strong>MotorsDynamic</strong><br />
              Email: <a href="mailto:privacy@motorsdynamic.io">privacy@motorsdynamic.io</a><br />
              Website: <a href="https://motorsdynamic.io">motorsdynamic.io</a>
            </p>
          </section>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 32, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Link to="/login" className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Back to login</Link>
          <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>·</span>
          <Link to="/terms" className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Terms &amp; conditions</Link>
        </div>
      </div>
    </div>
  );
}

function TermsPage() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 600px at 88% -10%, rgba(122,77,255,.15), transparent 60%),
        radial-gradient(900px 500px at -10% 110%, rgba(34,211,238,.08), transparent 60%)
      `,
      color: 'var(--ink, #FAFAF6)',
      fontFamily: 'Inter, system-ui, sans-serif',
      padding: '40px 20px 80px'
    }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, gap: 16, flexWrap: 'wrap' }}>
          <Link to="/login" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
            <MDLogo size={38} radius={11} style={{ boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>MotorsDynamic</div>
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>app.motorsdynamic.com</div>
            </div>
          </Link>
          <Link to="/login" className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', textDecoration: 'none' }}>← Back to login</Link>
        </div>

        <div style={{ marginBottom: 32 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 8 }}>LAST UPDATED · DEC 10, 2025</div>
          <h1 className="v2-serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: '#fff', margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em' }}>Terms &amp; conditions.</h1>
        </div>

        <div className="v2-tile v2-legal" style={{ padding: 'clamp(24px, 4vw, 40px)' }}>
          <section>
            <h2>Agreement to terms</h2>
            <p>These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and MotorsDynamic ("Company," "we," "us," or "our") governing your access to and use of the MotorsDynamic platform, including any associated websites, applications, and services (collectively, the "Service").</p>
            <p>By creating an account or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.</p>
          </section>

          <section>
            <h2>Description of service</h2>
            <p>MotorsDynamic is a B2B software-as-a-service (SaaS) platform designed for automotive dealerships. Our Service provides:</p>
            <ul>
              <li>Vehicle inventory aggregation and management</li>
              <li>Data feed generation for third-party advertising platforms</li>
              <li>Inventory synchronization and monitoring</li>
              <li>Analytics and reporting tools</li>
              <li>API access for integrations</li>
            </ul>
          </section>

          <section>
            <h2>Account registration</h2>
            <h3>3.1 Eligibility</h3>
            <p>To use our Service, you must:</p>
            <ul>
              <li>Be at least 18 years of age</li>
              <li>Have the authority to bind your organization to these Terms</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
            </ul>
            <h3>3.2 Account security</h3>
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:</p>
            <ul>
              <li>Create a strong, unique password</li>
              <li>Enable two-factor authentication (2FA) when prompted</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Not share your account credentials with others</li>
            </ul>
          </section>

          <section>
            <h2>Acceptable use</h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You shall not:</p>
            <ul>
              <li>Use the Service to violate any applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to the Service or related systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Transmit viruses, malware, or other harmful code</li>
              <li>Use automated tools to access the Service without permission</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Resell, sublicense, or redistribute the Service without authorization</li>
              <li>Use the Service to collect data for competing products</li>
            </ul>
          </section>

          <section>
            <h2>Data and content</h2>
            <h3>5.1 Your data</h3>
            <p>You retain ownership of all data you submit to the Service ("Your Data"). By using the Service, you grant us a limited license to use, process, and store Your Data solely to provide and improve the Service.</p>
            <h3>5.2 Vehicle data</h3>
            <p>The Service aggregates publicly available vehicle inventory data from dealership websites. You represent that you have the right to use any domains or data sources you add to your account.</p>
            <h3>5.3 Data accuracy</h3>
            <p>We strive to provide accurate data but do not guarantee the accuracy, completeness, or reliability of any data provided through the Service. You are responsible for verifying data accuracy before use.</p>
          </section>

          <section>
            <h2>Fees and payment</h2>
            <p>Access to certain features of the Service may require payment of fees. If applicable:</p>
            <ul>
              <li>Fees are specified in your service agreement or on our pricing page</li>
              <li>Payment is due according to the billing cycle selected</li>
              <li>All fees are non-refundable unless otherwise specified</li>
              <li>We reserve the right to modify pricing with 30 days notice</li>
            </ul>
          </section>

          <section>
            <h2>Intellectual property</h2>
            <p>The Service, including its original content, features, and functionality, is owned by MotorsDynamic and is protected by copyright, trademark, and other intellectual property laws. Our trademarks and trade dress may not be used without our prior written consent.</p>
          </section>

          <section>
            <h2>Third-party services</h2>
            <p>The Service may integrate with or contain links to third-party services. We are not responsible for the content, privacy policies, or practices of third-party services. Your use of third-party services is at your own risk.</p>
          </section>

          <section>
            <h2>Disclaimer of warranties</h2>
            <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</p>
            <p>We do not warrant that the Service will be uninterrupted, secure, or error-free, or that defects will be corrected.</p>
          </section>

          <section>
            <h2>Limitation of liability</h2>
            <p>To the maximum extent permitted by law, MotorsDynamic shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or use, arising out of or related to your use of the Service.</p>
            <p>Our total liability shall not exceed the amount paid by you to us in the twelve (12) months preceding the claim.</p>
          </section>

          <section>
            <h2>Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless MotorsDynamic and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service or violation of these Terms.</p>
          </section>

          <section>
            <h2>Termination</h2>
            <p>We may terminate or suspend your account and access to the Service at our sole discretion, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.</p>
            <p>Upon termination, your right to use the Service will immediately cease. Provisions that by their nature should survive termination shall survive.</p>
          </section>

          <section>
            <h2>Modifications to terms</h2>
            <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.</p>
          </section>

          <section>
            <h2>Governing law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the state or federal courts located in Delaware.</p>
          </section>

          <section>
            <h2>Severability</h2>
            <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
          </section>

          <section>
            <h2>Entire agreement</h2>
            <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and MotorsDynamic regarding the Service and supersede all prior agreements and understandings.</p>
          </section>

          <section>
            <h2>Contact information</h2>
            <p>For questions about these Terms, please contact us at:</p>
            <p>
              <strong>MotorsDynamic</strong><br />
              Email: <a href="mailto:legal@motorsdynamic.io">legal@motorsdynamic.io</a><br />
              Website: <a href="https://motorsdynamic.io">motorsdynamic.io</a>
            </p>
          </section>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 32, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Link to="/login" className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Back to login</Link>
          <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>·</span>
          <Link to="/privacy" className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Privacy policy</Link>
        </div>
      </div>
    </div>
  );
}

// Self-serve signup: POST /api/signup creates the agency (7-day trial, no
// card), its first admin user and a session, then lands on API tokens.
function SignupPage() {
  const navigate = useNavigate();
  const { signup, isAuthenticated } = useAuth();

  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', agency_name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !done) navigate('/', { replace: true });
  }, [isAuthenticated, done, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 10) {
      setError('Password must be at least 10 characters');
      return;
    }
    if (!agreed) {
      setError('Please accept the Terms and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      setDone(true);
      await signup({
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
        agency_name: form.agency_name.trim() || null,
      });
      navigate('/api/tokens', { replace: true, state: { welcome: true } });
    } catch (err) {
      setDone(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: '#111116',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: '#fff',
    padding: '10px 12px 10px 38px',
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none'
  };
  const plainInput = { ...inputStyle, padding: '10px 12px' };
  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 6
  };
  const iconWrap = {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%) scale(0.8)',
    color: 'rgba(255,255,255,0.35)',
    display: 'flex',
    pointerEvents: 'none'
  };
  const primaryBtn = {
    background: '#FAFAF6',
    border: '1px solid #FAFAF6',
    borderRadius: 8,
    color: '#0a0a0e',
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: loading ? 'wait' : 'pointer',
    opacity: loading ? 0.6 : 1,
    width: '100%',
    fontFamily: 'inherit'
  };
  const linkStyle = { color: '#fff', textDecoration: 'underline', textUnderlineOffset: 3 };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      color: 'var(--ink, #FAFAF6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 16px',
      boxSizing: 'border-box',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <Link to="/login" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'inherit', textDecoration: 'none', marginBottom: 20 }}>
          <MDLogo size={28} radius={7} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>MotorsDynamic</span>
        </Link>

        <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', margin: '0 0 8px' }}>Start your 7-day trial</h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', margin: '0 0 20px', lineHeight: 1.5 }}>
          Full API, SDK and MCP access. No card needed to start. After the trial, $49 per dealership domain per month.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle} htmlFor="su-email">Work email</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrap}><Icons.Mail /></span>
              <input id="su-email" type="email" required autoComplete="email" value={form.email} onChange={set('email')} style={inputStyle} placeholder="you@company.com" />
            </div>
          </div>

          <div>
            <label style={labelStyle} htmlFor="su-password">Password</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrap}><Icons.Lock /></span>
              <input id="su-password" type={showPassword ? 'text' : 'password'} required minLength={10} autoComplete="new-password" value={form.password} onChange={set('password')} style={{ ...inputStyle, paddingRight: 44 }} placeholder="10+ characters" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', display: 'flex' }}>
                {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 10 }}>
            <div>
              <label style={labelStyle} htmlFor="su-first">First name</label>
              <input id="su-first" autoComplete="given-name" value={form.first_name} onChange={set('first_name')} style={plainInput} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="su-last">Last name</label>
              <input id="su-last" autoComplete="family-name" value={form.last_name} onChange={set('last_name')} style={plainInput} />
            </div>
          </div>

          <div>
            <label style={labelStyle} htmlFor="su-company">Company (optional)</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrap}><Icons.Building /></span>
              <input id="su-company" autoComplete="organization" value={form.agency_name} onChange={set('agency_name')} style={inputStyle} />
            </div>
          </div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
            <span>I agree to the <Link to="/terms" style={linkStyle}>Terms</Link> and <Link to="/privacy" style={linkStyle}>Privacy Policy</Link>.</span>
          </label>

          {error && (
            <div role="alert" style={{ background: 'rgba(255,91,91,0.08)', border: '1px solid rgba(255,91,91,0.28)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#ff9b9b' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={primaryBtn}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>

          <div style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            Already have an account?{' '}
            <Link to="/login" style={linkStyle}>Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();

  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Live password strength check, must match server-side validatePassword()
  const checks = {
    length: password.length >= 8,
    upper:  /[A-Z]/.test(password),
    lower:  /[a-z]/.test(password),
    digit:  /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  const allValid = Object.values(checks).every(Boolean);
  const passwordsMatch = password.length > 0 && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Reset link is missing the token. Request a new one.'); return; }
    if (!allValid) { setError('Password does not meet all requirements.'); return; }
    if (!passwordsMatch) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    color: '#fff',
    padding: '12px 14px 12px 42px',
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s, background 0.15s'
  };
  const labelStyle = {
    display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)',
    marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em',
    fontFamily: "'JetBrains Mono', monospace"
  };
  const iconWrap = {
    position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.4)', display: 'flex', pointerEvents: 'none'
  };
  const primaryBtn = {
    background: 'linear-gradient(135deg, var(--c2), var(--c3))',
    border: 'none', borderRadius: 999, color: '#fff',
    padding: '12px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    width: '100%', boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)',
    letterSpacing: '0.02em', transition: 'opacity 0.15s, transform 0.15s'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 700px at 88% -10%, rgba(122,77,255,.22), transparent 60%),
        radial-gradient(900px 600px at -10% 110%, rgba(34,211,238,.12), transparent 60%)
      `,
      color: 'var(--ink, #FAFAF6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <div className="v2-grain" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1 }} />
      <div style={{
        position: 'relative', zIndex: 2,
        width: '100%', maxWidth: 440,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 20,
        padding: 32,
        display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        <div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', marginBottom: 6 }}>
            {success ? 'PASSWORD UPDATED' : 'RESET PASSWORD'}
          </div>
          <h2 className="v2-serif" style={{ fontSize: 28, fontWeight: 400, color: '#fff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
            {success ? 'You\'re all set.' : 'Set a new password.'}
          </h2>
        </div>

        {error && (
          <div style={{
            background: 'rgba(255,91,164,0.10)',
            border: '1px solid rgba(255,91,164,0.30)',
            color: 'var(--c1)', borderRadius: 12, padding: '10px 14px', fontSize: 12,
            display: 'flex', alignItems: 'flex-start', gap: 8
          }}>
            <span style={{ display: 'flex', flexShrink: 0, marginTop: 1 }}><Icons.X /></span>
            <span style={{ flex: 1 }}>{error}</span>
          </div>
        )}

        {success ? (
          <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
            <div style={{
              width: 56, height: 56,
              borderRadius: 16,
              background: 'linear-gradient(135deg, rgba(34,211,238,0.20), rgba(122,77,255,0.20))',
              border: '1px solid rgba(34,211,238,0.30)',
              margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--c3)'
            }}>
              <Icons.Check />
            </div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, margin: 0 }}>
              Your password has been updated. Redirecting to sign in…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label htmlFor="new-pw" style={labelStyle}>New password</label>
              <div style={{ position: 'relative' }}>
                <span style={iconWrap}><Icons.Lock /></span>
                <input
                  id="new-pw"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  autoComplete="new-password"
                  style={{ ...inputStyle, paddingRight: 42 }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(122,77,255,0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.10)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', padding: 6, display: 'flex' }}>
                  {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm-pw" style={labelStyle}>Confirm password</label>
              <div style={{ position: 'relative' }}>
                <span style={iconWrap}><Icons.Lock /></span>
                <input
                  id="confirm-pw"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                  required
                  autoComplete="new-password"
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = 'rgba(122,77,255,0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.10)'; }}
                />
              </div>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 12,
              padding: 12,
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6,
              fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            }}>
              {[
                ['length', '8+ characters'],
                ['upper',  'Uppercase'],
                ['lower',  'Lowercase'],
                ['digit',  'Number'],
                ['special','Special char'],
                ['match',  'Passwords match'],
              ].map(([key, label]) => {
                const ok = key === 'match' ? passwordsMatch : checks[key];
                return (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    color: ok ? 'var(--c3)' : 'rgba(255,255,255,0.4)'
                  }}>
                    <span style={{ display: 'flex' }}>{ok ? <Icons.Check /> : <Icons.X />}</span>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>

            <button type="submit"
                    disabled={loading || !allValid || !passwordsMatch || !token}
                    style={{ ...primaryBtn, opacity: (loading || !allValid || !passwordsMatch || !token) ? 0.4 : 1, cursor: (loading || !allValid || !passwordsMatch || !token) ? 'not-allowed' : 'pointer', marginTop: 4 }}>
              {loading ? 'Updating…' : 'Update password →'}
            </button>

            <button type="button" onClick={() => navigate('/login')}
                    style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: 12, padding: 8, fontFamily: 'inherit' }}>
              ← Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function InviteAcceptPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [enable2FA, setEnable2FA] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/auth/invite/verify?token=${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.valid) setInviteData(data);
        else setError(data.error || 'Invalid or expired invitation');
      })
      .catch(() => setError('Failed to verify invitation'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!acceptedTerms) { setError('You must accept the Terms & Conditions and Privacy Policy'); return; }
    if (enable2FA && !phone) { setError('Phone number is required for 2FA'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, phone: enable2FA ? phone : null, accepted_terms: acceptedTerms, enable_2fa: enable2FA })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to set up account');
      storeAuth(data.access_token, data.user, data.refresh_token);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '12px 14px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  const Shell = ({ children }) => (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 700px at 88% -10%, rgba(122,77,255,.22), transparent 60%),
        radial-gradient(900px 600px at -10% 110%, rgba(34,211,238,.12), transparent 60%),
        radial-gradient(700px 400px at 50% 130%, rgba(255,91,164,.10), transparent 60%)
      `,
      color: 'var(--ink, #FAFAF6)',
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px',
      position: 'relative'
    }}>
      <div className="v2-grain" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1 }} />
      <div style={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, justifyContent: 'center' }}>
          <MDLogo size={44} radius={12} style={{ boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)' }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>MotorsDynamic</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>app.motorsdynamic.com</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 14px' }} />
          <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, letterSpacing: '0.04em' }}>VERIFYING INVITATION…</div>
        </div>
      </Shell>
    );
  }

  if (!inviteData) {
    return (
      <Shell>
        <div className="v2-tile v2-tile-glow" style={{ padding: 32, textAlign: 'center' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'var(--c1)', letterSpacing: '0.06em', marginBottom: 6 }}>INVITATION ERROR</div>
          <h2 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: '0 0 12px' }}>This link's no good.</h2>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 18 }}>{error}</div>
          <Link to="/login"
            style={{ display: 'inline-block', background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', textDecoration: 'none', borderRadius: 999, padding: '11px 24px', fontSize: 13, fontWeight: 700, boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
            Go to login →
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="v2-tile" style={{ padding: 32 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>ONBOARDING</div>
        <h2 className="v2-serif" style={{ fontSize: 28, fontWeight: 400, color: '#fff', margin: '0 0 18px' }}>Welcome aboard.</h2>

        <div style={{ background: 'rgba(122,77,255,0.10)', border: '1px solid rgba(122,77,255,0.30)', borderRadius: 12, padding: '12px 14px', marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: '#fff', fontWeight: 600, marginBottom: 4 }}>
            Hi <span style={{ color: 'var(--c3)' }}>{inviteData.first_name || inviteData.email}</span>
          </div>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
            invited as <span className="v2-chip" style={{ background: 'rgba(122,77,255,0.18)', borderColor: 'rgba(122,77,255,0.30)', color: '#c4b5fd', fontSize: 10, marginLeft: 4 }}>{(inviteData.role || '').toUpperCase()}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={inviteData.email} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed', fontFamily: "'JetBrains Mono', monospace" }} />
          </div>

          <div>
            <label style={labelStyle}>Create password</label>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 8 characters" required
                style={{ ...inputStyle, paddingRight: 42 }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', padding: 6, display: 'flex' }}>
                {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Confirm password</label>
            <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" required style={inputStyle} />
          </div>

          {/* Security */}
          <div style={{ marginTop: 8 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Security</div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: enable2FA ? 'rgba(166,255,84,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${enable2FA ? 'rgba(166,255,84,0.25)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s' }}>
              <input type="checkbox" checked={enable2FA} onChange={e => setEnable2FA(e.target.checked)} style={{ marginTop: 3, accentColor: 'var(--c4)', width: 16, height: 16, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: enable2FA ? 'var(--c4)' : '#fff' }}>Enable two-factor auth <span className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginLeft: 4, letterSpacing: '0.06em' }}>RECOMMENDED</span></div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>get a code via SMS at sign-in</div>
              </div>
            </label>
          </div>

          {enable2FA && (
            <div>
              <label style={labelStyle}>Phone <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, marginLeft: 4 }}>FOR 2FA</span></label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" required={enable2FA} style={inputStyle} />
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>verification codes go to this number</div>
            </div>
          )}

          {/* Legal */}
          <div style={{ marginTop: 8 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Legal</div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} required style={{ marginTop: 3, accentColor: 'var(--c2)', width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
                I agree to the <Link to="/terms" target="_blank" style={{ color: 'var(--c3)', textDecoration: 'none' }}>Terms &amp; conditions</Link> and <Link to="/privacy" target="_blank" style={{ color: 'var(--c3)', textDecoration: 'none' }}>Privacy policy</Link>
              </span>
            </label>
          </div>

          {error && (
            <div style={{ background: 'rgba(255,91,164,0.10)', border: '1px solid rgba(255,91,164,0.30)', color: 'var(--c1)', borderRadius: 12, padding: '10px 14px', fontSize: 12 }}>{error}</div>
          )}

          <button type="submit" disabled={submitting}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 999, color: '#fff', padding: '13px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1, marginTop: 8, boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
            {submitting ? 'Setting up account…' : 'Complete setup →'}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function OrganizationPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const activeTab = urlTab || 'overview';
  const [agency, setAgency] = useState(null);
  const [groups, setGroups] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showAddDomainsModal, setShowAddDomainsModal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agencyData, domainsData] = await Promise.all([
        api('/agencies/current'),
        api('/domains')
      ]);
      setAgency(agencyData.agency);
      setDomains(domainsData.domains || []);
      if (agencyData.agency?.id) {
        const groupsData = await api(`/agencies/${agencyData.agency.id}/domain-groups`);
        setGroups(groupsData.groups || []);
      }
    } catch (err) {
      console.error('Failed to load organization data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const saveGroup = async (groupData) => {
    try {
      if (editingGroup) {
        await api(`/agencies/${agency.id}/domain-groups/${editingGroup.id}`, { method: 'PUT', body: JSON.stringify(groupData) });
      } else {
        await api(`/agencies/${agency.id}/domain-groups`, { method: 'POST', body: JSON.stringify(groupData) });
      }
      setShowGroupModal(false);
      setEditingGroup(null);
      loadData();
    } catch (err) { console.error('Failed to save group:', err); }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? Domains will remain but will be ungrouped.')) return;
    try {
      await api(`/agencies/${agency.id}/domain-groups/${groupId}`, { method: 'DELETE' });
      loadData();
    } catch (err) { console.error('Failed to delete group:', err); }
  };

  const addDomainsToGroup = async (groupId, domainIds) => {
    try {
      await api(`/agencies/${agency.id}/domain-groups/${groupId}/domains`, { method: 'POST', body: JSON.stringify({ domain_ids: domainIds }) });
      setShowAddDomainsModal(null);
      loadData();
    } catch (err) { console.error('Failed to add domains to group:', err); }
  };

  const removeDomainFromGroup = async (groupId, domainId) => {
    try {
      await api(`/agencies/${agency.id}/domain-groups/${groupId}/domains/${domainId}`, { method: 'DELETE' });
      loadData();
    } catch (err) { console.error('Failed to remove domain from group:', err); }
  };

  const getCleanDomain = (url) => {
    if (!url) return '';
    return url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
  };

  const ungroupedDomains = domains.filter(d => {
    const isInGroup = groups.some(g => g.domains?.some(gd => gd.id === d.id));
    return !isInGroup;
  });

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING ORGANIZATION…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          ORGANIZATION{agency ? ` · ${(agency.name || '').toUpperCase()}` : ''}{agency?.user_count != null ? ` · ${agency.user_count} USERS` : ''}{agency?.domain_count != null ? ` · ${agency.domain_count} DOMAINS` : ''}
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          {agency?.name || 'Organization'}.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Your team, your domains, your groups.
          </span>
        </h1>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 22, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, width: 'fit-content', flexWrap: 'wrap' }}>
        {[{ id: 'overview', label: 'Overview' }, { id: 'groups', label: 'Domain groups' }, { id: 'branding', label: 'Branding' }, { id: 'sso', label: 'SSO' }].map(t => (
          <button key={t.id} onClick={() => navigate(`/organization/${t.id}`)}
            style={{
              whiteSpace: 'nowrap',
              background: activeTab === t.id ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: activeTab === t.id ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: activeTab === t.id ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 18px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && agency && (
        <>
          <div className="v2-tile v2-tile-violet" style={{ padding: 24, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: agency.logo_url ? `url(${agency.logo_url}) center/cover` : 'linear-gradient(135deg, var(--c1), var(--c5))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, fontWeight: 800, color: '#fff', flexShrink: 0,
                boxShadow: '0 8px 24px -8px rgba(255,91,164,0.5)'
              }}>
                {!agency.logo_url && (agency.name?.[0] || 'O')}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="v2-serif" style={{ fontSize: 28, color: '#fff', lineHeight: 1.1 }}>{agency.name}</div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>@{agency.slug}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              {[
                { label: 'Users',   value: agency.user_count || 0,   color: 'var(--c4)' },
                { label: 'Domains', value: agency.domain_count || 0, color: 'var(--c3)' },
                { label: 'Groups',  value: groups.length,            color: 'var(--c5)' },
                { label: 'Feeds',   value: agency.feed_count || 0,   color: '#c4b5fd' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 18px' }}>
                  <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
                  <div className="v2-num" style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="v2-tile" style={{ padding: 22 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>quick actions</div>
            <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 16 }}>Jump to</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              {[
                { icon: Icons.Folder, label: 'Manage groups', onClick: () => navigate('/organization/groups') },
                { icon: Icons.Users,  label: 'Manage users',  to: '/users' },
                { icon: Icons.Globe,  label: 'View domains',  to: '/domains' },
              ].map((a, i) => {
                const inner = (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,77,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(122,77,255,0.30)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
                    <span style={{ color: 'var(--c2)', display: 'flex' }}><a.icon /></span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{a.label}</span>
                    <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.4)' }}>→</span>
                  </div>
                );
                if (a.to) return <Link key={i} to={a.to} style={{ textDecoration: 'none' }}>{inner}</Link>;
                return <button key={i} onClick={a.onClick} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{inner}</button>;
              })}
            </div>
          </div>
        </>
      )}

      {/* GROUPS */}
      {activeTab === 'groups' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>domain groups</div>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginTop: 2 }}>Organize your lots</div>
            </div>
            <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }} className="v2-chip"
              style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
              <Icons.Plus /> Create group
            </button>
          </div>

          {groups.length === 0 ? (
            <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
              <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No groups yet.</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>Create groups to organize your domains and manage user access.</div>
              <button onClick={() => setShowGroupModal(true)}
                style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
                <Icons.Plus /> Create your first group
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
              {groups.map(group => (
                <div key={group.id} className="v2-tile" style={{ padding: 18, borderLeft: `3px solid ${group.color || 'var(--c2)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 99, background: group.color || 'var(--c2)', flexShrink: 0 }} />
                        <h4 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</h4>
                      </div>
                      {group.description && <p className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.4 }}>{group.description}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button onClick={() => { setEditingGroup(group); setShowGroupModal(true); }} title="Edit" className="v2-chip"
                        style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', padding: '4px 8px' }}>
                        <Icons.Edit />
                      </button>
                      <button onClick={() => deleteGroup(group.id)} title="Delete" className="v2-chip"
                        style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', padding: '4px 8px' }}>
                        <Icons.Trash />
                      </button>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{group.domain_count || 0} domains</span>
                      <button onClick={() => setShowAddDomainsModal(group)} className="v2-chip"
                        style={{ cursor: 'pointer', background: 'rgba(34,211,238,0.10)', borderColor: 'rgba(34,211,238,0.30)', color: 'var(--c3)', fontSize: 10 }}>
                        <Icons.Plus /> Add
                      </button>
                    </div>
                    {group.domains && group.domains.length > 0 ? (
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {group.domains.map(domain => (
                          <li key={domain.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
                            <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getCleanDomain(domain.url)}</span>
                            <button onClick={() => removeDomainFromGroup(group.id, domain.id)} title="Remove"
                              style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', display: 'flex', flexShrink: 0, padding: 2 }}>
                              <Icons.X />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>No domains in this group</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {ungroupedDomains.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 8, letterSpacing: '0.06em' }}>UNGROUPED · {ungroupedDomains.length}</div>
              <div className="v2-tile" style={{ padding: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ungroupedDomains.slice(0, 10).map(d => (
                  <span key={d.id} className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', fontSize: 10 }}>{getCleanDomain(d.url)}</span>
                ))}
                {ungroupedDomains.length > 10 && (
                  <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', alignSelf: 'center', marginLeft: 4 }}>+{ungroupedDomains.length - 10} more</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* BRANDING */}
      {activeTab === 'branding' && <BrandingPanel />}

      {/* SSO */}
      {activeTab === 'sso' && <SsoPanel />}

      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          onSave={saveGroup}
          onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
        />
      )}

      {showAddDomainsModal && (
        <AddDomainsToGroupModal
          group={showAddDomainsModal}
          availableDomains={ungroupedDomains}
          onAdd={(domainIds) => addDomainsToGroup(showAddDomainsModal.id, domainIds)}
          onClose={() => setShowAddDomainsModal(null)}
          getCleanDomain={getCleanDomain}
        />
      )}
    </div>
  );
}

function BrandingPanel() {
  const [brand, setBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => { api('/branding/me').then(setBrand).catch(e => setMsg({ type: 'error', text: e.message })); }, []);

  async function save(field, value) {
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const updated = await api('/branding/me', { method: 'PUT', body: JSON.stringify({ [field]: value }) });
      setBrand(updated);
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  }

  if (!brand) return (
    <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto 14px' }} />
      <span className="v2-mono" style={{ color: 'rgba(255,255,255,.4)', fontSize: 11 }}>LOADING BRANDING…</span>
    </div>
  );

  const inp = { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" };
  const row = { display: 'flex', alignItems: 'center', gap: 12 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(166,255,84,.10)' : 'rgba(255,91,164,.12)', border: `1px solid ${msg.type === 'success' ? 'rgba(166,255,84,.30)' : 'rgba(255,91,164,.30)'}`, borderRadius: 12, padding: '11px 16px', fontSize: 13, color: msg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{msg.text}</div>
      )}

      {/* Live preview */}
      <div className="v2-tile v2-tile-violet" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>live preview</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>How your tenant looks today</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', background: 'rgba(0,0,0,.3)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 12 }}>
          {brand.logo_url ? (
            <img alt="" src={brand.logo_url} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#fff', padding: 4 }} />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: 10, background: `linear-gradient(135deg, ${brand.primary_color}, ${brand.accent_color})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 18 }}>
              {(brand.company_name || 'M').charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{brand.company_name}</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>{brand.support_email}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <span style={{ width: 18, height: 18, borderRadius: 6, background: brand.primary_color, border: '1px solid rgba(255,255,255,.10)' }} title={brand.primary_color} />
            <span style={{ width: 18, height: 18, borderRadius: 6, background: brand.accent_color, border: '1px solid rgba(255,255,255,.10)' }} title={brand.accent_color} />
          </div>
        </div>
      </div>

      {/* Identity */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>identity</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>Name, logo, contact</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Company name (shown in nav + email)</label>
            <input style={inp} defaultValue={brand.company_name || ''} placeholder="MotorsDynamic" onBlur={e => e.target.value !== (brand.company_name || '') && save('company_name', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Logo URL (square, 256×256+ recommended)</label>
            <input style={inp} defaultValue={brand.logo_url || ''} placeholder="https://yourbrand.com/logo.svg" onBlur={e => e.target.value !== (brand.logo_url || '') && save('logo_url', e.target.value || null)} />
          </div>
          <div>
            <label style={labelStyle}>Favicon URL (32×32 ICO/PNG)</label>
            <input style={inp} defaultValue={brand.favicon_url || ''} placeholder="https://yourbrand.com/favicon.ico" onBlur={e => e.target.value !== (brand.favicon_url || '') && save('favicon_url', e.target.value || null)} />
          </div>
          <div>
            <label style={labelStyle}>Support email (shown in app + footers)</label>
            <input style={inp} defaultValue={brand.support_email || ''} placeholder="support@yourbrand.com" onBlur={e => e.target.value !== (brand.support_email || '') && save('support_email', e.target.value || null)} />
          </div>
        </div>
      </div>

      {/* Colors */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>palette</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>Primary + accent colors</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Primary (CTAs, links, focus)</label>
            <div style={row}>
              <input type="color" defaultValue={brand.primary_color || '#7A4DFF'} onChange={e => save('primary_color', e.target.value)} style={{ width: 44, height: 44, padding: 0, border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, background: 'transparent', cursor: 'pointer' }} />
              <input style={inp} defaultValue={brand.primary_color || '#7A4DFF'} onBlur={e => e.target.value !== (brand.primary_color || '') && save('primary_color', e.target.value)} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Accent (secondary, highlights)</label>
            <div style={row}>
              <input type="color" defaultValue={brand.accent_color || '#22D3EE'} onChange={e => save('accent_color', e.target.value)} style={{ width: 44, height: 44, padding: 0, border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, background: 'transparent', cursor: 'pointer' }} />
              <input style={inp} defaultValue={brand.accent_color || '#22D3EE'} onBlur={e => e.target.value !== (brand.accent_color || '') && save('accent_color', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Custom domain */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>custom domain</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Host the app under your domain</div>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 18 }}>point a CNAME at <code style={{ color: 'var(--c3)' }}>cname.motorsdynamic.com</code> then add your hostname here</div>
        <input style={inp} defaultValue={brand.brand_custom_domain || ''} placeholder="app.yourbrand.com" onBlur={e => e.target.value !== (brand.brand_custom_domain || '') && save('brand_custom_domain', e.target.value || null)} />
      </div>

      {/* Hide MD branding */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>white-label</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff' }}>Hide MotorsDynamic branding</div>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 4 }}>removes "Powered by MotorsDynamic" footer + email signatures</div>
          </div>
          <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, cursor: 'pointer', flexShrink: 0 }}>
            <input type="checkbox" checked={!!brand.hide_md_branding} onChange={e => save('hide_md_branding', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{ position: 'absolute', inset: 0, background: brand.hide_md_branding ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,.10)', borderRadius: 24, transition: '.2s' }}>
              <span style={{ position: 'absolute', top: 3, left: brand.hide_md_branding ? 21 : 3, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: '.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
            </span>
          </label>
        </div>
      </div>
      {saving && <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', textAlign: 'center' }}>SAVING…</div>}
    </div>
  );
}

function SsoPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [draft, setDraft] = useState({ provider: 'saml', entry_point: '', issuer: '', cert: '', metadata_url: '', enabled: false });

  useEffect(() => {
    api('/sso/configure').then(d => {
      setCfg(d);
      setDraft({
        provider: d.sso_provider || 'saml',
        entry_point: d.sso_entry_point || '',
        issuer: d.sso_issuer || '',
        cert: '', // never returned; only fingerprint
        metadata_url: d.sso_metadata_url || '',
        enabled: !!d.sso_enabled,
      });
    }).catch(e => setMsg({ type: 'error', text: e.message }));
  }, []);

  async function save() {
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const updated = await api('/sso/configure', { method: 'POST', body: JSON.stringify(draft) });
      setCfg(prev => ({ ...prev, ...updated }));
      setDraft(d => ({ ...d, cert: '' }));
      setMsg({ type: 'success', text: updated.sso_enabled ? 'SSO enabled.' : 'SSO config saved.' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    setMsg({ type: 'success', text: 'Copied to clipboard.' });
  }

  if (!cfg) return (
    <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto 14px' }} />
      <span className="v2-mono" style={{ color: 'rgba(255,255,255,.4)', fontSize: 11 }}>LOADING SSO CONFIG…</span>
    </div>
  );

  const inp = { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: "'JetBrains Mono', monospace", outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" };

  function CopyRow({ label, value }) {
    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inp, opacity: .85 }} value={value || ''} readOnly />
          <button onClick={() => copy(value)} className="v2-chip" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Copy</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(166,255,84,.10)' : 'rgba(255,91,164,.12)', border: `1px solid ${msg.type === 'success' ? 'rgba(166,255,84,.30)' : 'rgba(255,91,164,.30)'}`, borderRadius: 12, padding: '11px 16px', fontSize: 13, color: msg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{msg.text}</div>
      )}
      {!cfg.sso?.enabled && (
        <div style={{ background: 'rgba(255,200,87,.08)', border: '1px solid rgba(255,200,87,.20)', borderRadius: 12, padding: '11px 16px', fontSize: 13, color: 'var(--c5)' }}>
          SAML library not installed on this server. {cfg.sso?.note || ''}
        </div>
      )}

      {/* Status banner */}
      <div className={`v2-tile ${cfg.sso_enabled ? 'v2-tile-acid' : ''}`} style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>status</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>
          {cfg.sso_enabled ? 'SSO is enabled' : 'SSO is disabled'}
        </div>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>
          {cfg.sso_enabled
            ? `Users in your tenant can sign in via ${cfg.sso_provider?.toUpperCase()}.`
            : 'Configure your IdP below, then enable.'}
        </div>
      </div>

      {/* SP details (give these to the IdP admin) */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>service provider details</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Give these to your IdP</div>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 18 }}>configure these endpoints in Okta, Azure AD, JumpCloud, etc.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CopyRow label="SSO login URL (for end-users)" value={cfg.sp_login_url} />
          <CopyRow label="SAML ACS / callback URL" value={cfg.sp_callback_url} />
          <CopyRow label="SP metadata URL" value={cfg.sp_metadata_url} />
        </div>
      </div>

      {/* IdP config form */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>identity provider</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>IdP configuration</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Provider</label>
            <select value={draft.provider} onChange={e => setDraft({ ...draft, provider: e.target.value })}
              style={{ ...inp, fontFamily: 'inherit' }}>
              <option value="saml">SAML 2.0</option>
              <option value="oidc">OIDC (coming soon)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>IdP SSO URL (entry_point)</label>
            <input style={inp} value={draft.entry_point} onChange={e => setDraft({ ...draft, entry_point: e.target.value })} placeholder="https://idp.example.com/sso" />
          </div>
          <div>
            <label style={labelStyle}>Issuer / EntityID</label>
            <input style={inp} value={draft.issuer} onChange={e => setDraft({ ...draft, issuer: e.target.value })} placeholder="motorsdynamic-your-slug" />
          </div>
          <div>
            <label style={labelStyle}>
              X.509 certificate {cfg.sso_cert_fingerprint && <span style={{ color: 'var(--c4)', marginLeft: 8 }}>· current SHA-256: {cfg.sso_cert_fingerprint.slice(0, 16)}…</span>}
            </label>
            <textarea rows={8} value={draft.cert} onChange={e => setDraft({ ...draft, cert: e.target.value })}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              style={{ ...inp, resize: 'vertical', minHeight: 120 }} />
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginTop: 4 }}>
              {cfg.sso_cert_fingerprint ? 'leave blank to keep current cert' : 'paste your IdP signing certificate'}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Metadata URL (optional)</label>
            <input style={inp} value={draft.metadata_url} onChange={e => setDraft({ ...draft, metadata_url: e.target.value })} placeholder="https://idp.example.com/metadata" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'rgba(255,255,255,.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,.06)' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>Enable SSO</div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>users will be routed through your IdP on next sign-in</div>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, cursor: 'pointer' }}>
              <input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
              <span style={{ position: 'absolute', inset: 0, background: draft.enabled ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,.10)', borderRadius: 24, transition: '.2s' }}>
                <span style={{ position: 'absolute', top: 3, left: draft.enabled ? 21 : 3, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: '.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
              </span>
            </label>
          </div>
          <button onClick={save} disabled={saving}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: saving ? .6 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,.5)', alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save SSO configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GroupModal({ group, onSave, onClose }) {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [color, setColor] = useState(group?.color || '#22D3EE');

  const colors = ['#22D3EE', '#A6FF54', '#FFC857', '#FF5BA4', '#7A4DFF', '#EC4899', '#06B6D4', '#84CC16'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim(), color });
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '11px 14px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} className="v2-tile" style={{ width: 480, maxWidth: '92vw', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>{group ? 'EDITING GROUP' : 'NEW GROUP'}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: 0 }}>{group ? 'Edit group' : 'Create group'}</h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Group name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g., Premium dealers, West coast" autoFocus required style={inp} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Optional description for this group" rows={2}
              style={{ ...inp, resize: 'vertical', minHeight: 60 }} />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Accent color</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {colors.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  style={{ width: 32, height: 32, borderRadius: 10, background: c, border: color === c ? '2px solid #fff' : '2px solid transparent', cursor: 'pointer', boxShadow: color === c ? `0 0 16px ${c}` : 'none', padding: 0 }} />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="v2-chip" style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>Cancel</button>
            <button type="submit" disabled={!name.trim()}
              style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: !name.trim() ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
              {group ? 'Save changes' : 'Create group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddDomainsToGroupModal({ group, availableDomains, onAdd, onClose, getCleanDomain }) {
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [search, setSearch] = useState('');

  const filteredDomains = availableDomains.filter(d =>
    getCleanDomain(d.url).toLowerCase().includes(search.toLowerCase())
  );

  const toggleDomain = (domainId) => {
    setSelectedDomains(prev => prev.includes(domainId) ? prev.filter(id => id !== domainId) : [...prev, domainId]);
  };

  const handleAdd = () => { if (selectedDomains.length > 0) onAdd(selectedDomains); };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '11px 14px 11px 40px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} className="v2-tile" style={{ width: 560, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
        <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>ADD DOMAINS</div>
            <h3 className="v2-serif" style={{ fontSize: 22, fontWeight: 400, color: '#fff', margin: 0 }}>To "{group.name}"</h3>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
        </div>
        <div style={{ padding: '18px 28px 0', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', display: 'flex', pointerEvents: 'none' }}><Icons.Search /></span>
            <input type="text" placeholder="Search domains…" value={search} onChange={e => setSearch(e.target.value)} style={inp} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 28px' }}>
          {filteredDomains.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>
              {availableDomains.length === 0 ? 'All domains are already in groups.' : 'No domains match your search.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filteredDomains.map(d => {
                const selected = selectedDomains.includes(d.id);
                return (
                  <label key={d.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: selected ? 'rgba(122,77,255,0.10)' : 'rgba(255,255,255,0.03)', border: `1px solid ${selected ? 'rgba(122,77,255,0.30)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={selected} onChange={() => toggleDomain(d.id)}
                      style={{ accentColor: 'var(--c2)', width: 16, height: 16, flexShrink: 0 }} />
                    <span className="v2-mono" style={{ flex: 1, fontSize: 12, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getCleanDomain(d.url)}</span>
                    <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>{d.vehicle_count || 0} vehicles</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.06)', gap: 10, flexShrink: 0 }}>
          <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{selectedDomains.length} selected</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} className="v2-chip" style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>Cancel</button>
            <button type="button" onClick={handleAdd} disabled={selectedDomains.length === 0}
              style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: selectedDomains.length === 0 ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
              Add to group
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersPage() {
  const { hasRole, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '', first_name: '', last_name: '', phone: '', user_role: 'viewer', password: ''
  });
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/users');
      setUsers(data.users);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({ email: user.email, first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '', user_role: user.role, password: '' });
    } else {
      setEditingUser(null);
      setFormData({ email: '', first_name: '', last_name: '', phone: '', user_role: 'viewer', password: '' });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingUser) {
        await api(`/users/${editingUser.id}`, {
          method: 'PUT',
          body: JSON.stringify({ first_name: formData.first_name, last_name: formData.last_name, phone: formData.phone, user_role: formData.user_role })
        });
      } else {
        await api('/users', { method: 'POST', body: JSON.stringify(formData) });
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await api(`/users/${userId}`, { method: 'DELETE' });
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const handleReactivate = async (userId) => {
    try {
      await api(`/users/${userId}/reactivate`, { method: 'POST' });
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const roleOptions = [
    { value: 'viewer',  label: 'Viewer',  desc: 'Read-only access' },
    { value: 'manager', label: 'Manager', desc: 'Manage domains and feeds' },
    { value: 'admin',   label: 'Admin',   desc: 'Full agency access' }
  ];
  if (currentUser?.role === 'super_admin') {
    roleOptions.push({ value: 'super_admin', label: 'Super Admin', desc: 'Platform owner' });
  }

  function roleChip(role) {
    const map = {
      super_admin: { bg: 'rgba(122,77,255,0.18)', border: 'rgba(122,77,255,0.40)', fg: '#c4b5fd' },
      admin:       { bg: 'rgba(122,77,255,0.12)', border: 'rgba(122,77,255,0.30)', fg: '#c4b5fd' },
      manager:     { bg: 'rgba(34,211,238,0.12)', border: 'rgba(34,211,238,0.30)', fg: 'var(--c3)' },
      viewer:      { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', fg: 'rgba(255,255,255,0.7)' },
      api_only:    { bg: 'rgba(255,200,87,0.12)', border: 'rgba(255,200,87,0.30)', fg: 'var(--c5)' },
    };
    const m = map[role] || map.viewer;
    return (
      <span className="v2-mono" style={{ background: m.bg, color: m.fg, border: `1px solid ${m.border}`, borderRadius: 99, padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>
        {(role || '').replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  }

  function getAvatarGradient(name) {
    const grads = [
      'linear-gradient(135deg, var(--c2), var(--c3))',
      'linear-gradient(135deg, var(--c1), var(--c5))',
      'linear-gradient(135deg, var(--c4), var(--c3))',
      'linear-gradient(135deg, var(--c5), var(--c1))',
      'linear-gradient(135deg, var(--c3), var(--c2))',
      'linear-gradient(135deg, #c084fc, var(--c1))'
    ];
    const idx = name ? name.charCodeAt(0) % grads.length : 0;
    return grads[idx];
  }

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  const activeCount = users.filter(u => u.is_active).length;
  const pendingCount = users.filter(u => u.is_active && !u.email_verified).length;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          USERS · {users.length} TEAM {users.length === 1 ? 'MEMBER' : 'MEMBERS'}{activeCount !== users.length ? ` · ${activeCount} ACTIVE` : ''}{pendingCount ? ` · ${pendingCount} PENDING` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Team.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Who's in your organization, and what they can touch.
            </span>
          </h1>
          <button onClick={() => handleOpenModal()} className="v2-chip"
            style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
            <Icons.Plus /> Invite user
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,91,164,0.12)', border: '1px solid rgba(255,91,164,0.30)', borderRadius: 12, padding: '11px 16px', marginBottom: 18, fontSize: 13, color: 'var(--c1)' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING USERS…</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {users.map(user => {
            const inactive = !user.is_active;
            const pending = user.is_active && !user.email_verified;
            return (
              <div key={user.id} className="v2-tile" style={{ padding: 18, opacity: inactive ? 0.55 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                    background: getAvatarGradient(user.first_name || user.email),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 800, color: '#fff'
                  }}>
                    {user.first_name ? user.first_name[0].toUpperCase() : user.email[0].toUpperCase()}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {inactive ? (
                      <span className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>Inactive</span>
                    ) : pending ? (
                      <span className="v2-chip" style={{ background: 'rgba(255,200,87,0.12)', borderColor: 'rgba(255,200,87,0.30)', color: 'var(--c5)', fontSize: 10 }}>
                        <span className="v2-pulse-dot v2-pulse-dot--amber" /> Invited
                      </span>
                    ) : (
                      <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                        <span className="v2-pulse-dot" /> Active
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                    {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Pending User'}
                  </div>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                  {roleChip(user.role)}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14, padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                    <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>last login</span>
                    <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.7)' }}>{user.last_login_at ? formatDate(user.last_login_at) : 'never'}</span>
                  </div>
                  {user.two_factor_enabled && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                      <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>2fa</span>
                      <span className="v2-mono" style={{ color: 'var(--c4)' }}>enabled</span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleOpenModal(user)} className="v2-chip"
                    style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', flex: 1, justifyContent: 'center', fontSize: 11 }}>
                    <Icons.Settings /> Edit
                  </button>
                  {user.id !== currentUser?.id && (
                    user.is_active ? (
                      <button onClick={() => handleDeactivate(user.id)} className="v2-chip"
                        style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 11 }}>
                        Deactivate
                      </button>
                    ) : (
                      <button onClick={() => handleReactivate(user.id)} className="v2-chip"
                        style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 11 }}>
                        Reactivate
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} className="v2-tile" style={{ width: 560, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, gap: 12 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, letterSpacing: '0.06em' }}>{editingUser ? `EDITING ${editingUser.email.toUpperCase()}` : 'NEW USER'}</div>
                <h2 className="v2-serif" style={{ fontSize: 26, fontWeight: 400, color: '#fff', margin: 0 }}>{editingUser ? 'Edit user' : 'Invite teammate'}</h2>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>{editingUser ? 'Update profile and role' : 'Send a magic-link invite or set a password'}</div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
            </div>

            <form onSubmit={handleSave}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {!editingUser && (
                  <div>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Account</div>
                    <label style={labelStyle}>Email *</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="user@example.com" style={inp} />
                  </div>
                )}

                <div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Personal info</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={labelStyle}>First name</label>
                      <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="John" style={inp} />
                    </div>
                    <div>
                      <label style={labelStyle}>Last name</label>
                      <input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Doe" style={inp} />
                    </div>
                  </div>
                  <label style={labelStyle}>Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 (555) 000-0000" style={inp} />
                </div>

                <div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Permissions</div>
                  <label style={labelStyle}>Role *</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {roleOptions.map(opt => {
                      const selected = formData.user_role === opt.value;
                      return (
                        <label key={opt.value}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: selected ? 'rgba(122,77,255,0.10)' : 'rgba(255,255,255,0.02)', border: `1px solid ${selected ? 'rgba(122,77,255,0.40)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s' }}>
                          <input type="radio" name="user_role" value={opt.value} checked={selected}
                            onChange={(e) => setFormData({ ...formData, user_role: e.target.value })}
                            style={{ accentColor: 'var(--c2)' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#c4b5fd' : '#fff' }}>{opt.label}</div>
                            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{opt.desc}</div>
                          </div>
                          {selected && <span style={{ color: 'var(--c2)', display: 'flex' }}><Icons.Check /></span>}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {!editingUser && (
                  <div>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Authentication</div>
                    <label style={labelStyle}>Password (optional)</label>
                    <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Leave blank to send invite email" style={inp} />
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>if blank, the user gets a magic link to set their own password</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} className="v2-chip" style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: saving ? 0.6 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                  {saving ? 'Saving…' : editingUser ? 'Save changes' : 'Send invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const activeTab = urlTab || 'profile';
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [notifPrefs, setNotifPrefs] = useState({
    email_digests_enabled: true,
    digest_frequency: 'daily',
    notify_aging: true,
    notify_price_changes: true,
    notify_scraper_alerts: true
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (activeTab === 'notifications') {
      api('/users/me/notification-prefs').then(data => setNotifPrefs(data)).catch(() => {});
    }
  }, [activeTab]);

  const handleSaveNotifPrefs = async () => {
    setNotifLoading(true);
    setNotifMessage({ type: '', text: '' });
    try {
      const updated = await api('/users/me/notification-prefs', { method: 'PUT', body: JSON.stringify(notifPrefs) });
      setNotifPrefs(updated);
      setNotifMessage({ type: 'success', text: 'Notification preferences saved.' });
    } catch (err) {
      setNotifMessage({ type: 'error', text: err.message || 'Failed to save.' });
    } finally {
      setNotifLoading(false);
    }
  };

  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || ''
  });
  const [passwords, setPasswords] = useState({ current: '', new_password: '', confirm: '' });

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await api(`/users/${user.id}`, { method: 'PUT', body: JSON.stringify(profile) });
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await api('/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password: passwords.current, new_password: passwords.new_password }) });
      setMessage({ type: 'success', text: 'Password changed successfully.' });
      setPasswords({ current: '', new_password: '', confirm: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('This will log you out of all devices. Continue?')) return;
    try {
      await api('/auth/sessions', { method: 'DELETE' });
      await logout();
      navigate('/login');
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  function Toggle({ value, onChange }) {
    return (
      <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, cursor: 'pointer', flexShrink: 0 }}>
        <input type="checkbox" checked={value} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
        <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: value ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,0.10)', borderRadius: 24, transition: '0.2s', boxShadow: value ? '0 0 12px rgba(122,77,255,0.4)' : 'none' }}>
          <span style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: '0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
        </span>
      </label>
    );
  }

  const navItems = [
    { id: 'profile',       label: 'Profile',       icon: Icons.User },
    { id: 'security',      label: 'Security',      icon: Icons.Shield },
    { id: 'billing',       label: 'Billing',       icon: Icons.CreditCard || Icons.User },
    { id: 'notifications', label: 'Notifications', icon: Icons.Bell },
    ...(user?.role === 'super_admin' ? [{ id: 'healing', label: 'Self-healing', icon: Icons.Bandage }] : [])
  ];

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          ACCOUNT SETTINGS{user?.email ? ` · ${user.email.toUpperCase()}` : ''}{user?.role ? ` · ${user.role.toUpperCase().replace('_', ' ')}` : ''}
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Settings.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Profile, security, and how you want to be notified.
          </span>
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 220px) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        {/* Sidebar */}
        <div className="v2-tile" style={{ padding: 8, position: 'sticky', top: 80 }}>
          {navItems.map(item => {
            const isActive = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => navigate(`/settings/${item.id}`)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: isActive ? 'rgba(122,77,255,0.10)' : 'transparent',
                  border: isActive ? '1px solid rgba(122,77,255,0.30)' : '1px solid transparent',
                  borderRadius: 10,
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  marginBottom: 2,
                  transition: 'all 0.15s'
                }}>
                <span style={{ color: isActive ? 'var(--c2)' : 'rgba(255,255,255,0.5)', display: 'flex', flexShrink: 0 }}>
                  <item.icon />
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Main */}
        <div style={{ minWidth: 0 }}>
          {message.text && (
            <div style={{ background: message.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${message.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 14, fontSize: 13, color: message.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{message.text}</div>
          )}

          {/* PROFILE */}
          {activeTab === 'profile' && (
            <div className="v2-tile" style={{ padding: 24 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>profile</div>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Personal info</div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>update your details and contact</div>

              <form onSubmit={handleProfileSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>First name</label>
                    <input type="text" value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} placeholder="First name" style={inp} />
                  </div>
                  <div>
                    <label style={labelStyle}>Last name</label>
                    <input type="text" value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} placeholder="Last name" style={inp} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={user?.email || ''} disabled style={{ ...inp, opacity: 0.55, cursor: 'not-allowed', fontFamily: "'JetBrains Mono', monospace" }} />
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>contact admin to change your email</div>
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+1 (555) 000-0000" style={inp} />
                </div>
                <div style={{ marginTop: 6 }}>
                  <button type="submit" disabled={loading}
                    style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: loading ? 0.6 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                    {loading ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* NOTIFICATIONS */}
          {activeTab === 'notifications' && (
            <div className="v2-tile" style={{ padding: 24 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>notifications</div>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Email digests</div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>control how and when you get pinged</div>

              {notifMessage.text && (
                <div style={{ background: notifMessage.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${notifMessage.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 18, fontSize: 13, color: notifMessage.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{notifMessage.text}</div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>Email digests enabled</div>
                    <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>master toggle for all email digests</div>
                  </div>
                  <Toggle value={notifPrefs.email_digests_enabled} onChange={e => setNotifPrefs({ ...notifPrefs, email_digests_enabled: e.target.checked })} />
                </div>

                <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontWeight: 600, color: '#fff', fontSize: 13, marginBottom: 10 }}>Digest cadence</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['daily', 'weekly'].map(freq => {
                      const selected = notifPrefs.digest_frequency === freq;
                      return (
                        <label key={freq}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 18px', borderRadius: 99, border: `1px solid ${selected ? 'rgba(122,77,255,0.40)' : 'rgba(255,255,255,0.10)'}`, background: selected ? 'rgba(122,77,255,0.10)' : 'rgba(255,255,255,0.02)', transition: '0.15s' }}>
                          <input type="radio" name="digest_frequency" value={freq} checked={selected}
                            onChange={() => setNotifPrefs({ ...notifPrefs, digest_frequency: freq })}
                            style={{ accentColor: 'var(--c2)' }} />
                          <span style={{ color: selected ? '#c4b5fd' : '#fff', fontSize: 12, textTransform: 'capitalize', fontWeight: 600 }}>{freq}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {[
                  { key: 'notify_aging',          label: 'Aging alerts',     desc: 'when vehicles age beyond threshold' },
                  { key: 'notify_price_changes',  label: 'Price changes',    desc: 'when competitor prices move significantly' },
                  { key: 'notify_scraper_alerts', label: 'Scraper failures', desc: 'when a scraper job fails' }
                ].map(({ key, label, desc }) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{label}</div>
                      <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>{desc}</div>
                    </div>
                    <Toggle value={!!notifPrefs[key]} onChange={e => setNotifPrefs({ ...notifPrefs, [key]: e.target.checked })} />
                  </div>
                ))}

                <div style={{ marginTop: 8 }}>
                  <button onClick={handleSaveNotifPrefs} disabled={notifLoading}
                    style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: notifLoading ? 0.6 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                    {notifLoading ? 'Saving…' : 'Save preferences'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* HEALING */}
          {activeTab === 'healing' && user?.role === 'super_admin' && <HealingSettingsPanel />}

          {/* BILLING */}
          {activeTab === 'billing' && <BillingPanel />}

          {/* SECURITY */}
          {activeTab === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Change password */}
              <div className="v2-tile" style={{ padding: 24 }}>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>password</div>
                <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Change password</div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>update your password to keep your account secure</div>
                <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Current password</label>
                    <input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} required placeholder="Enter current password" style={inp} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>New password</label>
                      <input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} required minLength={8} placeholder="Enter new password" style={inp} />
                    </div>
                    <div>
                      <label style={labelStyle}>Confirm</label>
                      <input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} required placeholder="Confirm password" style={inp} />
                    </div>
                  </div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                    8+ characters with uppercase, lowercase, number, and special character
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <button type="submit" disabled={loading}
                      style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: loading ? 0.6 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                      {loading ? 'Updating…' : 'Update password'}
                    </button>
                  </div>
                </form>
              </div>

              {/* 2FA */}
              <div className={`v2-tile ${user?.two_factor_enabled ? 'v2-tile-acid' : ''}`} style={{ padding: 24 }}>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>two-factor</div>
                <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Two-factor authentication</div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>add an extra layer of security</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 36, height: 36, borderRadius: 12, background: user?.two_factor_enabled ? 'rgba(166,255,84,0.15)' : 'rgba(255,255,255,0.06)', border: `1px solid ${user?.two_factor_enabled ? 'rgba(166,255,84,0.30)' : 'rgba(255,255,255,0.10)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: user?.two_factor_enabled ? 'var(--c4)' : 'rgba(255,255,255,0.5)' }}>
                      <Icons.Shield />
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{user?.two_factor_enabled ? '2FA enabled' : '2FA disabled'}</div>
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                        {user?.two_factor_enabled ? 'protected by two-factor auth' : 'enable to require a code at sign-in'}
                      </div>
                    </div>
                  </div>
                  <button className="v2-chip"
                    style={{ cursor: 'pointer',
                      background: user?.two_factor_enabled ? 'rgba(255,91,164,0.06)' : 'rgba(166,255,84,0.10)',
                      borderColor: user?.two_factor_enabled ? 'rgba(255,91,164,0.20)' : 'rgba(166,255,84,0.30)',
                      color: user?.two_factor_enabled ? '#fbcfe8' : 'var(--c4)' }}>
                    {user?.two_factor_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                  </button>
                </div>
              </div>

              {/* TOTP (authenticator app) */}
              <TotpSetupPanel />

              {/* Active sessions */}
              <div className="v2-tile" style={{ padding: 24 }}>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>sessions</div>
                <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Active sessions</div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>manage active sessions across devices</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 36, height: 36, borderRadius: 12, background: 'rgba(255,91,164,0.10)', border: '1px solid rgba(255,91,164,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c1)' }}>
                      <Icons.Globe />
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>All devices</div>
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>log out of all other browsers and devices</div>
                    </div>
                  </div>
                  <button onClick={handleLogoutAll} className="v2-chip"
                    style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8' }}>
                    Log out everywhere
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CompetitiveSetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [oem, setOem] = useState('');
  const [dealerType, setDealerType] = useState('both');
  const [addingDealer, setAddingDealer] = useState(false);
  const [primaryDealer, setPrimaryDealer] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [findingCompetitors, setFindingCompetitors] = useState(false);
  const [addCompetitorMode, setAddCompetitorMode] = useState(false);
  const [compSearchQuery, setCompSearchQuery] = useState('');
  const [compSearchResults, setCompSearchResults] = useState([]);
  const [compSearching, setCompSearching] = useState(false);
  const [startingTracking, setStartingTracking] = useState(false);
  const [error, setError] = useState('');
  const [activeGroupId, setActiveGroupId] = useState(null);
  const searchTimeout = useRef(null);
  const compSearchTimeout = useRef(null);

  useEffect(() => {
    api('/competitive/groups').then(data => {
      const g = data.groups || data || [];
      if (Array.isArray(g) && g.length > 0) {
        navigate(`/competitive/group/${g[0].id}`);
      }
    }).catch(() => {});
  }, []);

  const handleSearch = (val) => {
    setSearchQuery(val);
    setSelectedPlace(null);
    setPlaceDetails(null);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.length < 3) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api(`/competitive/places/autocomplete?input=${encodeURIComponent(val)}`);
        setSearchResults(data.results || []);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 300);
  };

  const handleSelectPlace = async (place) => {
    setSelectedPlace(place);
    setSearchResults([]);
    setSearchQuery(place.name || '');
    setLoadingDetails(true);
    try {
      const data = await api(`/competitive/places/${place.place_id}`);
      setPlaceDetails(data);
      if (data.oem_brand) setOem(data.oem_brand);
      if (data.dealer_type) setDealerType(data.dealer_type);
    } catch { setError('Failed to load place details'); }
    setLoadingDetails(false);
  };

  const handleAddPrimary = async () => {
    if (!placeDetails) return;
    setAddingDealer(true);
    setError('');
    try {
      const res = await api('/competitive/dealers', {
        method: 'POST',
        body: JSON.stringify({
          google_place_id: selectedPlace.place_id,
          name: placeDetails.name,
          address: placeDetails.address || placeDetails.formatted_address,
          city: placeDetails.city,
          state: placeDetails.state,
          postal_code: placeDetails.postal_code,
          lat: placeDetails.lat,
          lon: placeDetails.lng,
          website: placeDetails.website,
          clean_domain: placeDetails.clean_domain,
          oem_brand: oem,
          dealer_type: dealerType,
          is_primary: true
        })
      });
      setPrimaryDealer(res.dealer || res);
      setStep(2);
      setFindingCompetitors(true);
      try {
        const dealer = res.dealer || res;
        const groupRes = await api('/competitive/groups', {
          method: 'POST',
          body: JSON.stringify({
            primary_dealer_id: dealer.id,
            name: `${dealer.name || placeDetails.name} - Competitors`,
            oem_brand: oem || dealer.oem_brand
          })
        });
        const newGroupId = (groupRes.group || groupRes).id;
        setActiveGroupId(newGroupId);
        const compData = await api(`/competitive/groups/${newGroupId}`);
        const members = (compData.members || []).filter(m => !m.is_excluded).map(m => ({ ...m.dealer, _groupId: newGroupId, member_id: m.member_id }));
        setCompetitors(members);
      } catch { setError('Failed to discover competitors. You can add them manually.'); }
      setFindingCompetitors(false);
    } catch (e) {
      setError(e.message || 'Failed to add dealership');
    }
    setAddingDealer(false);
  };

  const handleCompSearch = (val) => {
    setCompSearchQuery(val);
    if (compSearchTimeout.current) clearTimeout(compSearchTimeout.current);
    if (val.length < 3) { setCompSearchResults([]); return; }
    compSearchTimeout.current = setTimeout(async () => {
      setCompSearching(true);
      try {
        const data = await api(`/competitive/places/autocomplete?input=${encodeURIComponent(val)}`);
        setCompSearchResults(data.results || []);
      } catch { setCompSearchResults([]); }
      setCompSearching(false);
    }, 300);
  };

  const handleAddCompetitor = async (place) => {
    setCompSearchResults([]);
    setCompSearchQuery('');
    try {
      const details = await api(`/competitive/places/${place.place_id}`);
      const groupId = activeGroupId || (competitors.length > 0 ? competitors[0]._groupId : null);
      if (!groupId) { setError('No group found. Please restart setup.'); return; }
      const dealerRes = await api('/competitive/dealers', {
        method: 'POST',
        body: JSON.stringify({
          google_place_id: place.place_id, name: details.name, address: details.address,
          city: details.city, state: details.state, postal_code: details.postal_code,
          lat: details.lat, lon: details.lng, website: details.website, clean_domain: details.clean_domain,
          oem_brand: details.oem_brand || '', dealer_type: details.dealer_type || 'new_and_used', is_primary: false
        })
      });
      const dealer = dealerRes.dealer || dealerRes;
      await api(`/competitive/groups/${groupId}/members`, { method: 'POST', body: JSON.stringify({ dealer_id: dealer.id }) });
      setCompetitors(prev => [...prev, { ...dealer, _groupId: groupId }]);
    } catch (e) {
      setError(e.message || 'Failed to add competitor');
    }
    setAddCompetitorMode(false);
  };

  const toggleExclude = (id) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleStartTracking = async () => {
    setStartingTracking(true);
    const groupId = activeGroupId || (competitors.length > 0 ? competitors[0]._groupId : null);
    if (groupId && excludedIds.size > 0) {
      for (const id of excludedIds) {
        try { await api(`/competitive/dealers/${id}`, { method: 'DELETE' }); } catch {}
      }
    }
    setStartingTracking(false);
    navigate(groupId ? `/competitive/group/${groupId}` : '/competitive');
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '11px 14px 11px 40px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          COMPETITIVE INTEL · SETUP · STEP {step} OF 2
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Lock in the field.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Add your dealership, then auto-discover the rivals.
          </span>
        </h1>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,91,164,0.10)', border: '1px solid rgba(255,91,164,0.30)', color: 'var(--c1)', borderRadius: 12, padding: '11px 16px', marginBottom: 18, fontSize: 13 }}>{error}</div>
      )}

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22, padding: '14px 18px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, flexWrap: 'wrap' }}>
        {[
          { num: 1, label: 'Add your dealership' },
          { num: 2, label: 'Discover competitors' }
        ].map((s, i) => {
          const done = step > s.num;
          const active = step === s.num;
          return (
            <React.Fragment key={s.num}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="v2-num" style={{
                  width: 30, height: 30, borderRadius: 10, flexShrink: 0,
                  background: done ? 'linear-gradient(135deg, var(--c4), #a3e635)' : active ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,0.04)',
                  border: active ? 'none' : done ? 'none' : '1px solid rgba(255,255,255,0.10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13, color: (done || active) ? '#fff' : 'rgba(255,255,255,0.5)',
                  boxShadow: active ? '0 4px 12px -4px rgba(122,77,255,0.5)' : 'none'
                }}>{done ? <Icons.Check /> : s.num}</div>
                <span style={{ fontSize: 13, fontWeight: 600, color: active ? '#fff' : done ? 'var(--c4)' : 'rgba(255,255,255,0.45)' }}>{s.label}</span>
              </div>
              {i < 1 && <div style={{ flex: 1, minWidth: 30, height: 1, background: done ? 'var(--c4)' : 'rgba(255,255,255,0.10)' }} />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <div className="v2-tile" style={{ padding: 24, position: 'relative', overflow: 'visible' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>step 1</div>
          <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>Search for your dealership</div>

          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Dealership name or address</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', display: 'flex', pointerEvents: 'none' }}><Icons.Search /></span>
              <input type="text" placeholder="e.g. AutoNation Toyota, 1234 Main St…"
                value={searchQuery} onChange={e => handleSearch(e.target.value)} autoFocus style={inp} />
              {searching && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}><div className="spinner" style={{ width: 14, height: 14 }} /></div>}
              {searchResults.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50, background: 'rgba(15,15,22,0.97)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, maxHeight: 320, overflowY: 'auto' }}>
                  {searchResults.map(r => (
                    <button key={r.place_id} onClick={() => handleSelectPlace(r)}
                      style={{ width: '100%', display: 'flex', gap: 10, padding: '12px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', alignItems: 'flex-start' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(122,77,255,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ color: 'var(--c3)', display: 'flex', flexShrink: 0, marginTop: 1 }}><Icons.MapPin /></span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{r.address}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loadingDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 10 }}>
              <div className="spinner" />
              <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>LOADING DETAILS…</span>
            </div>
          )}

          {placeDetails && !loadingDetails && (
            <div className="v2-tile v2-tile-violet" style={{ padding: 20 }}>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>{placeDetails.name}</div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 10, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <span style={{ color: 'var(--c3)', display: 'flex', flexShrink: 0, marginTop: 1 }}><Icons.MapPin /></span>
                {placeDetails.address || placeDetails.formatted_address}
              </div>
              {placeDetails.clean_domain && (
                <div className="v2-mono" style={{ fontSize: 11, color: 'var(--c3)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icons.Globe /> {placeDetails.clean_domain}
                </div>
              )}
              {placeDetails.phone && (
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icons.Phone /> {placeDetails.phone}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
                <div>
                  <label style={labelStyle}>OEM brand</label>
                  <select value={oem} onChange={e => setOem(e.target.value)} style={{ ...inp, padding: '11px 14px', cursor: 'pointer' }}>
                    <option value="" style={{ background: '#111' }}>Select brand…</option>
                    {OEM_BRANDS.map(b => <option key={b} value={b} style={{ background: '#111' }}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Dealer type</label>
                  <select value={dealerType} onChange={e => setDealerType(e.target.value)} style={{ ...inp, padding: '11px 14px', cursor: 'pointer' }}>
                    <option value="new"  style={{ background: '#111' }}>New only</option>
                    <option value="used" style={{ background: '#111' }}>Used only</option>
                    <option value="both" style={{ background: '#111' }}>New &amp; used</option>
                  </select>
                </div>
              </div>
              <button onClick={handleAddPrimary} disabled={addingDealer}
                style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '11px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: addingDealer ? 0.6 : 1, boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)', letterSpacing: '0.02em', marginTop: 16 }}>
                {addingDealer ? 'Adding…' : 'Add my dealership →'}
              </button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="v2-tile" style={{ padding: 24, position: 'relative', overflow: 'visible' }}>
          {primaryDealer && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'rgba(166,255,84,0.08)', border: '1px solid rgba(166,255,84,0.25)', borderRadius: 12, marginBottom: 20 }}>
              <span style={{ width: 28, height: 28, borderRadius: 99, background: 'rgba(166,255,84,0.20)', border: '1px solid rgba(166,255,84,0.40)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c4)', flexShrink: 0 }}>
                <Icons.Check />
              </span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{primaryDealer.name}</div>
                {primaryDealer.oem_brand && <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{primaryDealer.oem_brand}</div>}
              </div>
            </div>
          )}

          {findingCompetitors ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 14 }}>
              <div className="spinner" />
              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, letterSpacing: '0.04em' }}>FINDING COMPETITORS…</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Searching nearby dealerships with overlapping brands</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>step 2</div>
                  <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginTop: 2 }}>
                    Competitors found <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', fontSize: 16 }}>· {competitors.filter(c => !excludedIds.has(c.id)).length} selected</span>
                  </div>
                </div>
                <button onClick={() => setAddCompetitorMode(!addCompetitorMode)} className="v2-chip"
                  style={{ cursor: 'pointer', background: 'rgba(34,211,238,0.10)', borderColor: 'rgba(34,211,238,0.30)', color: 'var(--c3)' }}>
                  <Icons.Plus /> Add competitor
                </button>
              </div>

              {addCompetitorMode && (
                <div style={{ marginBottom: 16, position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', display: 'flex', pointerEvents: 'none' }}><Icons.Search /></span>
                    <input type="text" placeholder="Search for a dealership to add…"
                      value={compSearchQuery} onChange={e => handleCompSearch(e.target.value)} autoFocus style={inp} />
                    {compSearching && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}><div className="spinner" style={{ width: 14, height: 14 }} /></div>}
                  </div>
                  {compSearchResults.length > 0 && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50, background: 'rgba(15,15,22,0.97)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, maxHeight: 320, overflowY: 'auto' }}>
                      {compSearchResults.map(r => (
                        <button key={r.place_id} onClick={() => handleAddCompetitor(r)}
                          style={{ width: '100%', display: 'flex', gap: 10, padding: '12px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', alignItems: 'flex-start' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(122,77,255,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <span style={{ color: 'var(--c3)', display: 'flex', flexShrink: 0, marginTop: 1 }}><Icons.MapPin /></span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{r.address}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {competitors.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12 }}>
                  <div className="v2-serif" style={{ fontSize: 18, color: '#fff', marginBottom: 4 }}>No competitors discovered yet.</div>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>add competitors manually using the search above</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {competitors.map(c => {
                    const excluded = excludedIds.has(c.id);
                    return (
                      <label key={c.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: excluded ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.03)', border: `1px solid ${excluded ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12, cursor: 'pointer', opacity: excluded ? 0.5 : 1, transition: 'all 0.15s' }}>
                        <input type="checkbox" checked={!excluded} onChange={() => toggleExclude(c.id)}
                          style={{ accentColor: 'var(--c4)', width: 18, height: 18, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.address}</div>
                        </div>
                        {c.oem_brand && (
                          <span className="v2-chip" style={{ background: 'rgba(122,77,255,0.15)', borderColor: 'rgba(122,77,255,0.30)', color: '#c4b5fd', fontSize: 10, flexShrink: 0 }}>{c.oem_brand}</span>
                        )}
                        {c.distance_miles && (
                          <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', flexShrink: 0 }}>{c.distance_miles.toFixed(1)} mi</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 22, gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => { setStep(1); setSearchQuery(''); setPlaceDetails(null); setSelectedPlace(null); }} className="v2-chip"
                  style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', padding: '9px 18px', fontSize: 12 }}>
                  ← Back
                </button>
                <button onClick={handleStartTracking} disabled={startingTracking || competitors.filter(c => !excludedIds.has(c.id)).length === 0}
                  style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (startingTracking || competitors.filter(c => !excludedIds.has(c.id)).length === 0) ? 0.5 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                  {startingTracking ? 'Starting…' : 'Start tracking →'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CompetitiveDashboard() {
  const navigate = useNavigate();
  const { groupId: groupIdParam, tab: urlTab } = useParams();
  const activeTab = urlTab || 'map';
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(groupIdParam ? parseInt(groupIdParam) : null);
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [activities, setActivities] = useState([]);
  const [activityDays, setActivityDays] = useState(7);
  const [activityFilter, setActivityFilter] = useState('all');
  const [marketData, setMarketData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [models, setModels] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [showByZip, setShowByZip] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState({ price_drop: true, new_inventory: true, sold_vehicle: true, market_share: true });
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  useEffect(() => {
    api('/competitive/groups').then(data => {
      const g = data.groups || data || [];
      setGroups(Array.isArray(g) ? g : []);
      if (!activeGroupId && g.length > 0) setActiveGroupId(g[0].id);
      if (g.length === 0) setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeGroupId) return;
    setLoading(true);
    const safe = (p) => p.catch(() => null);
    Promise.all([
      api(`/competitive/groups/${activeGroupId}`),
      safe(api(`/competitive/groups/${activeGroupId}/market-share`)),
      safe(api(`/competitive/alerts?group_id=${activeGroupId}`)),
      safe(api(`/competitive/groups/${activeGroupId}/activity?days=${activityDays}`))
    ]).then(([group, market, alertsData, actData]) => {
      setGroupData(group);
      setModels([...new Set((group.members || []).map(m => m.dealer?.oem_brand).filter(Boolean))]);
      if (market) setMarketData(market);
      const alertsList = alertsData?.alerts || alertsData || [];
      setAlerts(alertsList);
      setUnreadAlertCount(alertsList.filter(a => !a.is_read).length);
      const flatActivities = [
        ...(actData?.added || []).map(v => ({ ...v, event_type: 'new_inventory' })),
        ...(actData?.removed || []).map(v => ({ ...v, event_type: 'sold' })),
        ...(actData?.price_changes || []).map(v => ({ ...v, event_type: 'price_change' }))
      ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setActivities(flatActivities);
      setLoading(false);
    }).catch((e) => { console.error('Load error', e); setLoading(false); });
  }, [activeGroupId]);

  useEffect(() => {
    if (!activeGroupId || !selectedModel) { setPricingData(null); return; }
    api(`/competitive/groups/${activeGroupId}/pricing?make=${encodeURIComponent(selectedModel)}`).then(data => {
      setPricingData(data);
    }).catch(() => setPricingData(null));
  }, [activeGroupId, selectedModel]);

  useEffect(() => {
    if (!activeGroupId) return;
    api(`/competitive/groups/${activeGroupId}/activity?days=${activityDays}`)
      .then(data => {
        const flat = [
          ...(data.added || []).map(v => ({ ...v, event_type: 'new_inventory' })),
          ...(data.removed || []).map(v => ({ ...v, event_type: 'sold' })),
          ...(data.price_changes || []).map(v => ({ ...v, event_type: 'price_change' }))
        ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        const filtered = activityFilter === 'all' ? flat : flat.filter(a => a.event_type === activityFilter);
        setActivities(filtered);
      })
      .catch(() => {});
  }, [activeGroupId, activityDays, activityFilter]);

  useEffect(() => {
    if (activeTab !== 'map' || !groupData) return;
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    if (!apiKey) { console.error('No Google Maps API key'); return; }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__gmapsLoaded`;
      script.async = true;
      script.onerror = () => setMapError(true);
      window.__gmapsLoaded = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      window.__gmapsLoaded = () => setMapLoaded(true);
      const check = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 200);
      setTimeout(() => clearInterval(check), 15000);
      return () => clearInterval(check);
    }
  }, [activeTab, groupData]);

  useEffect(() => {
    if (!mapLoaded || !groupData || activeTab !== 'map' || !mapContainerRef.current) return;
    const google = window.google;
    if (!google || !google.maps) return;

    const members = (groupData.members || []).filter(m => !m.is_excluded);
    const primary = groupData.primary_dealer;
    const allDealers = primary ? [{ ...primary, is_primary: true }, ...members.map(m => ({ ...m.dealer, is_primary: false }))] : members.map(m => m.dealer);
    const center = primary ? { lat: parseFloat(primary.lat), lng: parseFloat(primary.lon) } : { lat: 39.8283, lng: -98.5795 };

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapContainerRef.current, {
        center,
        zoom: 11,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#0a0a0e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0a0e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#7a7a90' }] },
          { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a1a26' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a26' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#08080d' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] }
        ],
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      });
      infoWindowRef.current = new google.maps.InfoWindow();
    } else {
      mapRef.current.setCenter(center);
    }

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (showByZip) {
      const zipGroups = {};
      allDealers.forEach(d => {
        const key = d.postal_code || `${Math.round(parseFloat(d.lat)*100)/100},${Math.round(parseFloat(d.lon)*100)/100}`;
        if (!zipGroups[key]) zipGroups[key] = { lat: parseFloat(d.lat), lng: parseFloat(d.lon), count: 0, dealers: [] };
        zipGroups[key].count += d.inventory_count || 0;
        zipGroups[key].dealers.push(d);
      });
      Object.values(zipGroups).forEach(zg => {
        const radius = Math.max(500, Math.min(5000, zg.count * 20));
        const circle = new google.maps.Circle({
          center: { lat: zg.lat, lng: zg.lng },
          radius,
          fillColor: '#22D3EE',
          fillOpacity: 0.25,
          strokeColor: '#22D3EE',
          strokeWeight: 1,
          map: mapRef.current
        });
        google.maps.event.addListener(circle, 'click', () => {
          infoWindowRef.current.setContent(`<div style="color:#1a1a25;padding:4px"><strong>${zg.dealers.length} dealers</strong><br/>${zg.count} vehicles</div>`);
          infoWindowRef.current.setPosition({ lat: zg.lat, lng: zg.lng });
          infoWindowRef.current.open(mapRef.current);
        });
        markersRef.current.push(circle);
      });
    } else {
      const bounds = new google.maps.LatLngBounds();
      allDealers.forEach(d => {
        const lat = parseFloat(d.lat);
        const lng = parseFloat(d.lon);
        if (isNaN(lat) || isNaN(lng)) return;
        const pos = { lat, lng };
        bounds.extend(pos);
        const isPrimary = d.is_primary;
        const marker = new google.maps.Marker({
          position: pos,
          map: mapRef.current,
          title: d.name,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: isPrimary ? 13 : 9,
            fillColor: isPrimary ? '#FF5BA4' : '#22D3EE',
            fillOpacity: 0.9,
            strokeWeight: 2,
            strokeColor: isPrimary ? '#FF5BA4' : '#22D3EE'
          }
        });
        marker.addListener('click', () => {
          infoWindowRef.current.setContent(
            `<div style="color:#1a1a25;padding:6px;min-width:160px">` +
            `<strong style="font-size:14px">${d.name}</strong>` +
            `<div style="margin:4px 0;font-size:12px;color:#666">${d.address || ''}</div>` +
            `${d.distance_miles ? `<div style="font-size:11px;color:#888;margin-bottom:4px">${d.distance_miles.toFixed(1)} miles away</div>` : ''}` +
            `</div>`
          );
          infoWindowRef.current.open(mapRef.current, marker);
        });
        markersRef.current.push(marker);
      });
      if (allDealers.length > 1) mapRef.current.fitBounds(bounds, 50);
    }
  }, [mapLoaded, groupData, activeTab, showByZip]);

  const markAlertRead = async (alertId) => {
    try {
      await api(`/competitive/alerts/${alertId}/read`, { method: 'PUT' });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
      setUnreadAlertCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api(`/competitive/alerts/read-all`, { method: 'PUT', body: JSON.stringify({ group_id: activeGroupId }) });
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      setUnreadAlertCount(0);
    } catch {}
  };

  const saveAlertPrefs = async () => {
    try {
      await api('/competitive/alerts/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          price_change_alert: alertPrefs.price_drop,
          inventory_change_alert: alertPrefs.new_inventory,
          email_enabled: true
        })
      });
    } catch {}
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  function fmtRelative(ts) {
    if (!ts) return '-';
    const diffMs = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING COMPETITIVE INTEL…</span>
        </div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="page-content">
        <div style={{ marginBottom: 22 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>COMPETITIVE INTELLIGENCE · NO GROUPS YET</div>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Watch the field.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>Set up a group to track competitor inventory and pricing.</span>
          </h1>
        </div>
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 28, color: '#fff', marginBottom: 10 }}>No competitive groups.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>Set up your dealership and start tracking competitors in your market.</div>
          <button onClick={() => navigate('/competitive/setup')}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
            <Icons.Plus /> Get started
          </button>
        </div>
      </div>
    );
  }

  const primaryDealer = groupData?.primary_dealer
    ? { ...groupData.primary_dealer, is_primary: true, inventory_count: groupData.primary_dealer.inventory?.total || 0 }
    : null;
  const competitorDealers = (groupData?.members || [])
    .filter(m => !m.is_excluded)
    .map(m => ({ ...m.dealer, is_primary: false, inventory_count: m.inventory?.total || 0 }));
  const dealers = primaryDealer ? [primaryDealer, ...competitorDealers] : competitorDealers;
  const totalInventory = dealers.reduce((s, d) => s + (d.inventory_count || 0), 0);
  const myInventory = primaryDealer?.inventory_count || 0;
  const marketSharePct = totalInventory > 0 ? ((myInventory / totalInventory) * 100).toFixed(1) : '0';
  const competitorTotal = totalInventory - myInventory;

  const tabs = [
    { id: 'map',      label: 'Map' },
    { id: 'market',   label: 'Market Share' },
    { id: 'pricing',  label: 'Pricing' },
    { id: 'scatter',  label: 'Price vs Mileage' },
    { id: 'activity', label: 'Activity' },
    { id: 'alerts',   label: 'Alerts',  count: unreadAlertCount },
  ];

  const activeGroupName = groups.find(g => g.id === activeGroupId)?.name || `Group #${activeGroupId}`;

  return (
    <div className="page-content">
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          COMPETITIVE · {activeGroupName.toUpperCase()} · {dealers.length} {dealers.length === 1 ? 'DEALER' : 'DEALERS'}{totalInventory ? ` · ${totalInventory.toLocaleString()} VEHICLES IN MARKET` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            The field.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Where you stand against {competitorDealers.length} {competitorDealers.length === 1 ? 'rival' : 'rivals'}.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {groups.length > 1 && (
              <select value={activeGroupId || ''} onChange={e => { setActiveGroupId(parseInt(e.target.value)); navigate(`/competitive/group/${e.target.value}/${activeTab}`); }}
                style={{ ...inp, minWidth: 200, cursor: 'pointer' }}>
                {groups.map(g => <option key={g.id} value={g.id} style={{ background: '#111' }}>{g.name || `Group #${g.id}`}</option>)}
              </select>
            )}
            <button onClick={() => navigate('/competitive/setup')} className="v2-chip"
              style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
              <Icons.Plus /> New group
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>my inventory</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{formatNumber(myInventory)}</div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primaryDealer?.name || '-'}</div>
        </div>
        <div className="v2-tile" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>competitor inventory</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c3)' }}>{formatNumber(competitorTotal)}</div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{competitorDealers.length} {competitorDealers.length === 1 ? 'rival' : 'rivals'}</div>
        </div>
        <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>market share</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{marketSharePct}<span style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>%</span></div>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden', marginTop: 8 }}>
            <div style={{ width: `${Math.min(100, parseFloat(marketSharePct))}%`, height: '100%', background: 'var(--c4)' }} />
          </div>
        </div>
        <div className={`v2-tile ${unreadAlertCount > 0 ? 'v2-tile-glow' : ''}`} style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>unread alerts</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: unreadAlertCount > 0 ? 'var(--c1)' : '#fff' }}>{unreadAlertCount}</div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>of {alerts.length} total</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 22, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => navigate(activeGroupId ? `/competitive/group/${activeGroupId}/${t.id}` : `/competitive/${t.id}`)}
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              whiteSpace: 'nowrap',
              background: activeTab === t.id ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: activeTab === t.id ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: activeTab === t.id ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 0.15s'
            }}>
            {t.label}
            {t.count > 0 && (
              <span className="v2-mono" style={{ background: 'rgba(255,91,164,0.15)', color: 'var(--c1)', borderRadius: 99, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* MAP TAB */}
      {activeTab === 'map' && (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>dealer footprint</div>
              <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>The map of rivals</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="v2-chip" style={{ fontSize: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--c1)', display: 'inline-block' }} /> You
              </span>
              <span className="v2-chip" style={{ fontSize: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--c3)', display: 'inline-block' }} /> Competitors
              </span>
              <button onClick={() => setShowByZip(!showByZip)} className="v2-chip"
                style={{ cursor: 'pointer', background: showByZip ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.04)', borderColor: showByZip ? 'rgba(34,211,238,0.35)' : undefined, color: showByZip ? 'var(--c3)' : 'rgba(255,255,255,0.85)' }}>
                {showByZip ? 'Show markers' : 'Show by ZIP'}
              </button>
            </div>
          </div>
          {mapError ? (
            <div style={{ height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: 'rgba(255,255,255,0.55)' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--c1)" strokeWidth="1.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
              <p style={{ fontWeight: 600, color: '#fff', margin: 0 }}>Map unavailable</p>
              <p style={{ fontSize: 12, margin: 0, textAlign: 'center', maxWidth: 340, color: 'rgba(255,255,255,0.45)' }}>Google Maps failed to load - usually a domain restriction. Works on production.</p>
            </div>
          ) : !process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
            <div style={{ height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: 'rgba(255,255,255,0.55)' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
              <p style={{ fontWeight: 600, color: '#fff', margin: 0 }}>Map not configured</p>
              <p style={{ fontSize: 12, margin: 0, textAlign: 'center', maxWidth: 340 }}>Set <code className="v2-mono" style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4 }}>REACT_APP_GOOGLE_MAPS_API_KEY</code> to enable.</p>
            </div>
          ) : (
            <div ref={mapContainerRef} style={{ height: 540, width: '100%', position: 'relative' }}>
              {!mapLoaded && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
                  <div className="spinner" />
                  <p className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>LOADING MAP…</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* MARKET SHARE TAB */}
      {activeTab === 'market' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="v2-tile">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>market share</div>
                <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Inventory leaderboard</div>
              </div>
              {models.length > 0 && (
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} style={{ ...inp, width: 200, cursor: 'pointer' }}>
                  <option value="" style={{ background: '#111' }}>All Models</option>
                  {models.map(m => <option key={m} value={m} style={{ background: '#111' }}>{m}</option>)}
                </select>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {dealers.sort((a, b) => (b.inventory_count || 0) - (a.inventory_count || 0)).map((d) => {
                const maxCount = Math.max(...dealers.map(x => x.inventory_count || 0), 1);
                const pct = totalInventory > 0 ? (((d.inventory_count || 0) / totalInventory) * 100).toFixed(1) : 0;
                const barWidth = Math.max(2, ((d.inventory_count || 0) / maxCount) * 100);
                return (
                  <div key={d.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr', gap: 12, alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      {d.is_primary && (
                        <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.18)', borderColor: 'rgba(255,91,164,0.35)', color: 'var(--c1)', fontSize: 9, padding: '2px 7px' }}>YOU</span>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: d.is_primary ? 600 : 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                          {formatNumber(d.inventory_count || 0)} vehicles · {pct}%
                        </div>
                      </div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ height: 22, background: 'rgba(255,255,255,0.04)', borderRadius: 99, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ width: `${barWidth}%`, height: '100%', background: d.is_primary ? 'linear-gradient(90deg, var(--c1), var(--c5))' : 'linear-gradient(90deg, var(--c2), var(--c3))', borderRadius: 99 }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>breakdown</div>
              <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>By dealer</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead><tr>{['Dealer', 'Total', 'New', 'Used', 'Avg price', 'Share'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {dealers.sort((a, b) => (b.inventory_count || 0) - (a.inventory_count || 0)).map(d => {
                    const mktDealer = (marketData?.dealers || []).find(md => md.dealer_id === d.id);
                    const newCount = mktDealer?.inventory?.new ?? mktDealer?.new_count ?? 0;
                    const usedCount = mktDealer?.inventory?.used ?? mktDealer?.used_count ?? 0;
                    const avgPrice = mktDealer?.avg_price || mktDealer?.inventory?.avg_price || null;
                    return (
                      <tr key={d.id} style={d.is_primary ? { background: 'rgba(255,91,164,0.05)' } : {}}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {d.is_primary && (
                              <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.18)', borderColor: 'rgba(255,91,164,0.35)', color: 'var(--c1)', fontSize: 9, padding: '1px 6px' }}>YOU</span>
                            )}
                            <span style={{ fontWeight: d.is_primary ? 600 : 400, color: '#fff' }}>{d.name}</span>
                          </div>
                        </td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: '#fff', fontWeight: 600 }}>{formatNumber(d.inventory_count || 0)}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'var(--c4)' }}>{formatNumber(newCount)}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'var(--c3)' }}>{formatNumber(usedCount)}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{avgPrice ? formatPrice(avgPrice) : '-'}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.6)' }}>{totalInventory > 0 ? (((d.inventory_count || 0) / totalInventory) * 100).toFixed(1) : '0'}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* PRICING TAB */}
      {activeTab === 'pricing' && (
        <div className="v2-tile">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>pricing</div>
              <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Price comparison</div>
            </div>
            <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} style={{ ...inp, width: 220, cursor: 'pointer' }}>
              <option value="" style={{ background: '#111' }}>Select a make to compare…</option>
              {models.map(m => <option key={m} value={m} style={{ background: '#111' }}>{m}</option>)}
            </select>
          </div>

          {!selectedModel ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 6 }}>Pick a make above.</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>We'll show pricing comparisons across every dealer in this group.</div>
            </div>
          ) : !pricingData ? (
            <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" /></div>
          ) : pricingData.dealers?.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>No pricing data found for {selectedModel}. Scraping may still be in progress.</div>
          ) : (
            <>
              {(() => {
                const allDealers = pricingData.dealers || [];
                const primary = allDealers.find(d => d.is_primary);
                const myAvg = primary?.pricing?.avg_price || 0;
                const competitors = allDealers.filter(d => !d.is_primary);
                const compAvgs = competitors.map(d => d.pricing?.avg_price || 0).filter(v => v > 0);
                const compAvg = compAvgs.length > 0 ? Math.round(compAvgs.reduce((s, v) => s + v, 0) / compAvgs.length) : 0;
                const allAvgs = allDealers.map(d => d.pricing?.avg_price || 0).filter(v => v > 0);
                const marketAvg = allAvgs.length > 0 ? Math.round(allAvgs.reduce((s, v) => s + v, 0) / allAvgs.length) : 0;
                const diff = myAvg && compAvg ? myAvg - compAvg : null;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
                    {myAvg > 0 && (
                      <div className="v2-tile v2-tile-violet" style={{ padding: 16 }}>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>my avg price</div>
                        <div className="v2-num" style={{ fontSize: 24, fontWeight: 700, color: 'var(--c1)' }}>{formatPrice(myAvg)}</div>
                        {diff !== null && (
                          <div className="v2-mono" style={{ fontSize: 10, marginTop: 4, color: diff < 0 ? 'var(--c4)' : 'var(--c5)' }}>
                            {diff < 0 ? '▼' : '▲'} ${Math.abs(Math.round(diff)).toLocaleString()} vs comp
                          </div>
                        )}
                      </div>
                    )}
                    {compAvg > 0 && (
                      <div className="v2-tile" style={{ padding: 16 }}>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>competitor avg</div>
                        <div className="v2-num" style={{ fontSize: 24, fontWeight: 700, color: 'var(--c3)' }}>{formatPrice(compAvg)}</div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{competitors.length} dealers</div>
                      </div>
                    )}
                    {marketAvg > 0 && (
                      <div className="v2-tile v2-tile-cyan" style={{ padding: 16 }}>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>market avg</div>
                        <div className="v2-num" style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{formatPrice(marketAvg)}</div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{allDealers.length} dealers total</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="v2-tile" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="v2-serif" style={{ fontSize: 16 }}>Per-dealer breakdown</div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                    <thead><tr>
                      <th style={th}>Dealer</th>
                      <th style={{ ...th, textAlign: 'right' }}>Avg</th>
                      <th style={{ ...th, textAlign: 'right' }}>Min</th>
                      <th style={{ ...th, textAlign: 'right' }}>Max</th>
                      <th style={{ ...th, textAlign: 'right' }}>Vehicles</th>
                      <th style={{ ...th, textAlign: 'right' }}>Avg miles</th>
                    </tr></thead>
                    <tbody>
                      {(pricingData.dealers || []).map(d => {
                        const primary = (pricingData.dealers || []).find(x => x.is_primary);
                        const myAvg = primary?.pricing?.avg_price || 0;
                        const diff = d.is_primary || !myAvg ? null : d.pricing.avg_price - myAvg;
                        return (
                          <tr key={d.dealer_id} style={d.is_primary ? { background: 'rgba(255,91,164,0.05)' } : {}}>
                            <td style={td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#fff', fontWeight: d.is_primary ? 600 : 400 }}>{d.name}</span>
                                {d.is_primary && (
                                  <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.18)', borderColor: 'rgba(255,91,164,0.35)', color: 'var(--c1)', fontSize: 9, padding: '1px 6px' }}>YOU</span>
                                )}
                              </div>
                            </td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: '#fff' }}>
                              {formatPrice(d.pricing.avg_price)}
                              {diff !== null && (
                                <div className="v2-mono" style={{ fontSize: 10, marginTop: 2, color: diff > 0 ? 'var(--c1)' : 'var(--c4)' }}>
                                  {diff > 0 ? '▲' : '▼'} ${Math.abs(Math.round(diff)).toLocaleString()}
                                </div>
                              )}
                            </td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: 'var(--c4)', fontSize: 12 }}>{formatPrice(d.pricing.min_price)}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: 'var(--c1)', fontSize: 12 }}>{formatPrice(d.pricing.max_price)}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{d.pricing.vehicle_count.toLocaleString()}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{d.pricing.avg_mileage > 0 ? d.pricing.avg_mileage.toLocaleString() + ' mi' : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>price range</div>
              <div className="v2-serif" style={{ fontSize: 16, marginBottom: 12 }}>by dealer</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(() => {
                  const allDealers = pricingData.dealers || [];
                  const allMins = allDealers.map(d => d.pricing?.min_price || 0).filter(v => v > 0);
                  const allMaxes = allDealers.map(d => d.pricing?.max_price || 0).filter(v => v > 0);
                  const globalMin = allMins.length > 0 ? Math.min(...allMins) : 0;
                  const globalMax = allMaxes.length > 0 ? Math.max(...allMaxes) : 100000;
                  const range = globalMax - globalMin || 1;
                  return allDealers.map(d => {
                    const dMin = d.pricing?.min_price || globalMin;
                    const dMax = d.pricing?.max_price || globalMax;
                    const dAvg = d.pricing?.avg_price || 0;
                    const leftPct = ((dMin - globalMin) / range) * 100;
                    const widthPct = Math.max(2, ((dMax - dMin) / range) * 100);
                    const avgPct = ((dAvg - globalMin) / range) * 100;
                    return (
                      <div key={d.dealer_id} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr minmax(140px, 180px)', gap: 12, alignItems: 'center' }}>
                        <div style={{ fontSize: 12, color: '#fff', fontWeight: d.is_primary ? 600 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {d.is_primary && <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.18)', borderColor: 'rgba(255,91,164,0.35)', color: 'var(--c1)', fontSize: 9, padding: '1px 6px', marginRight: 6 }}>YOU</span>}
                          {d.name}
                        </div>
                        <div style={{ position: 'relative', height: 14 }}>
                          <div style={{ position: 'absolute', inset: 'auto 0 4px 0', height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 99 }} />
                          <div style={{
                            position: 'absolute', left: `${leftPct}%`, width: `${widthPct}%`,
                            top: 4, bottom: 4,
                            background: d.is_primary ? 'linear-gradient(90deg, var(--c1), var(--c5))' : 'linear-gradient(90deg, var(--c2), var(--c3))',
                            borderRadius: 99
                          }} />
                          {dAvg > 0 && (
                            <div title={`Avg: ${formatPrice(dAvg)}`} style={{ position: 'absolute', left: `${avgPct}%`, top: -1, width: 2, height: 16, background: '#fff', borderRadius: 99, transform: 'translateX(-50%)' }} />
                          )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                          <span>{formatPrice(dMin)}</span>
                          <span>{formatPrice(dMax)}</span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* PRICE vs MILEAGE SCATTER TAB */}
      {activeTab === 'scatter' && (
        <PriceMileageScatter groupId={activeGroupId} />
      )}

      {/* ACTIVITY TAB */}
      {activeTab === 'activity' && (
        <div className="v2-tile">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>activity</div>
              <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>What rivals just did</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={activityDays} onChange={e => setActivityDays(parseInt(e.target.value))} style={{ ...inp, width: 130, cursor: 'pointer' }}>
                <option value={3} style={{ background: '#111' }}>Last 3 days</option>
                <option value={7} style={{ background: '#111' }}>Last 7 days</option>
                <option value={14} style={{ background: '#111' }}>Last 14 days</option>
                <option value={30} style={{ background: '#111' }}>Last 30 days</option>
              </select>
              <select value={activityFilter} onChange={e => setActivityFilter(e.target.value)} style={{ ...inp, width: 150, cursor: 'pointer' }}>
                <option value="all" style={{ background: '#111' }}>All activity</option>
                <option value="new_inventory" style={{ background: '#111' }}>New inventory</option>
                <option value="price_change" style={{ background: '#111' }}>Price changes</option>
                <option value="sold" style={{ background: '#111' }}>Sold vehicles</option>
              </select>
            </div>
          </div>

          {activities.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>No activity in the selected period.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {activities.map((act, i) => {
                const eventType = act.event_type || act.type || act.activity_type;
                let badgeColor, borderColor, title;
                switch (eventType) {
                  case 'new_inventory': {
                    const vehicleLabel = [act.year, act.make, act.model].filter(Boolean).join(' ') || 'a vehicle';
                    badgeColor = { bg: 'rgba(166,255,84,0.10)', border: 'rgba(166,255,84,0.30)', fg: 'var(--c4)', label: 'NEW' };
                    borderColor = 'var(--c4)';
                    title = `${act.dealer_name || 'Competitor'} added ${vehicleLabel}`;
                    break;
                  }
                  case 'price_change':
                  case 'price_drop': {
                    const priceDir = (act.new_price != null && act.old_price != null) ? (act.new_price > act.old_price ? 'up' : 'down') : (act.direction || 'down');
                    const priceAmt = (act.new_price != null && act.old_price != null) ? Math.abs(act.new_price - act.old_price) : Math.abs(act.amount || 0);
                    const vehicleLabel2 = [act.year, act.make, act.model].filter(Boolean).join(' ') || act.vehicle || act.description || 'vehicle';
                    badgeColor = priceDir === 'up'
                      ? { bg: 'rgba(255,200,87,0.12)', border: 'rgba(255,200,87,0.30)', fg: 'var(--c5)', label: '▲ PRICE UP' }
                      : { bg: 'rgba(166,255,84,0.10)', border: 'rgba(166,255,84,0.30)', fg: 'var(--c4)', label: '▼ PRICE DROP' };
                    borderColor = priceDir === 'up' ? 'var(--c5)' : 'var(--c4)';
                    title = `${act.dealer_name || 'Competitor'} ${priceDir === 'up' ? 'raised' : 'dropped'} price on ${vehicleLabel2} by $${Math.round(priceAmt).toLocaleString()}`;
                    break;
                  }
                  case 'sold': {
                    const vehicleLabel3 = [act.year, act.make, act.model].filter(Boolean).join(' ') || 'a vehicle';
                    badgeColor = { bg: 'rgba(255,91,164,0.10)', border: 'rgba(255,91,164,0.30)', fg: 'var(--c1)', label: 'SOLD' };
                    borderColor = 'var(--c1)';
                    title = `${act.dealer_name || 'Competitor'} sold ${vehicleLabel3}`;
                    break;
                  }
                  default:
                    badgeColor = null;
                    borderColor = 'rgba(255,255,255,0.15)';
                    title = act.description || act.title || 'Activity recorded';
                }
                return (
                  <div key={act.id || i} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderLeft: `3px solid ${borderColor}`, borderRadius: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        {badgeColor && (
                          <span className="v2-mono" style={{ background: badgeColor.bg, color: badgeColor.fg, border: `1px solid ${badgeColor.border}`, borderRadius: 6, padding: '2px 7px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>{badgeColor.label}</span>
                        )}
                        <span style={{ fontSize: 13, color: '#fff' }}>{title}</span>
                      </div>
                      {act.details && <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{act.details}</div>}
                      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{fmtRelative(act.created_at || act.date || act.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ALERTS TAB */}
      {activeTab === 'alerts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="v2-tile">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>alerts</div>
                <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>
                  Inbox{unreadAlertCount > 0 && (<span style={{ fontStyle: 'italic', color: 'var(--c1)' }}> · {unreadAlertCount} unread</span>)}
                </div>
              </div>
              {unreadAlertCount > 0 && (
                <button onClick={markAllRead} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
                  Mark all read
                </button>
              )}
            </div>

            {alerts.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>No alerts yet. They'll appear as competitor activity is detected.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {alerts.map(a => {
                  const severityMap = {
                    high:    { label: 'High',    bg: 'rgba(255,91,164,0.12)',  border: 'rgba(255,91,164,0.30)',  fg: 'var(--c1)' },
                    medium:  { label: 'Medium',  bg: 'rgba(255,200,87,0.12)',  border: 'rgba(255,200,87,0.30)',  fg: 'var(--c5)' },
                    warning: { label: 'Warning', bg: 'rgba(255,200,87,0.12)',  border: 'rgba(255,200,87,0.30)',  fg: 'var(--c5)' },
                    low:     { label: 'Low',     bg: 'rgba(34,211,238,0.12)',  border: 'rgba(34,211,238,0.30)',  fg: 'var(--c3)' },
                    info:    { label: 'Info',    bg: 'rgba(34,211,238,0.12)',  border: 'rgba(34,211,238,0.30)',  fg: 'var(--c3)' },
                  };
                  const sev = severityMap[a.severity];
                  const isRead = a.is_read;
                  return (
                    <div key={a.id}
                      onClick={() => !isRead && markAlertRead(a.id)}
                      style={{
                        display: 'flex', gap: 12, padding: '12px 14px',
                        background: isRead ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderLeft: !isRead && sev ? `3px solid ${sev.fg}` : '3px solid transparent',
                        borderRadius: 10,
                        cursor: !isRead ? 'pointer' : 'default',
                        opacity: isRead ? 0.7 : 1,
                        alignItems: 'flex-start'
                      }}>
                      <div style={{ width: 8, height: 8, borderRadius: 99, marginTop: 5, flexShrink: 0, background: !isRead ? (sev?.fg || 'var(--c4)') : 'rgba(255,255,255,0.15)', boxShadow: !isRead && sev ? `0 0 6px ${sev.fg}` : 'none' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, color: '#fff', fontWeight: isRead ? 400 : 600 }}>{a.title || a.message}</span>
                          {sev && (
                            <span className="v2-mono" style={{ background: sev.bg, color: sev.fg, border: `1px solid ${sev.border}`, borderRadius: 6, padding: '1px 7px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>{sev.label.toUpperCase()}</span>
                          )}
                        </div>
                        {a.description && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>{a.description}</div>}
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{fmtRelative(a.created_at)}</div>
                      </div>
                      {!isRead && (
                        <button onClick={e => { e.stopPropagation(); markAlertRead(a.id); }} className="v2-chip"
                          style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10, whiteSpace: 'nowrap' }}>
                          Mark read
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>preferences</div>
              <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>What to alert me about</div>
            </div>
            <div>
              {[
                { key: 'price_drop',    label: 'Price changes',     desc: 'When competitors change pricing' },
                { key: 'new_inventory', label: 'New inventory',     desc: 'When competitors add vehicles' },
                { key: 'sold_vehicle',  label: 'Sold vehicles',     desc: 'When competitors sell vehicles' },
                { key: 'market_share',  label: 'Market share shifts', desc: 'When your share changes significantly' },
              ].map(p => (
                <label key={p.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{p.label}</div>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{p.desc}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={alertPrefs[p.key]}
                    onChange={e => setAlertPrefs(prev => ({ ...prev, [p.key]: e.target.checked }))}
                    style={{ accentColor: 'var(--c4)', width: 18, height: 18 }}
                  />
                </label>
              ))}
              <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={saveAlertPrefs}
                  style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                  Save preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PrefToggle({ label, on, onClick }) {
  return (
    <button onClick={onClick} className="v2-chip" style={{ cursor: 'pointer' }}>
      <span style={{
        width: 30, height: 18, borderRadius: 99,
        background: on ? 'var(--c4)' : 'rgba(255,255,255,.1)',
        position: 'relative', transition: 'background .2s', flexShrink: 0,
        display: 'inline-block',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: on ? 14 : 2, width: 14, height: 14, borderRadius: '50%',
          background: on ? '#0a0a0e' : 'rgba(255,255,255,.85)', transition: 'left .2s',
          display: 'inline-block',
        }} />
      </span>
      {label}
    </button>
  );
}

function CompetitiveDonut({ dealers, total, myShare }) {
  const palette = ['var(--c2)', '#9b6bff', 'var(--c3)', '#5dd9f0', '#8ae3f3', 'var(--c4)', '#c5ff7e', 'var(--c5)', '#ffd980', '#ffe6a8', 'rgba(255,255,255,.4)'];
  let acc = 0;
  const stops = [];
  dealers.forEach((d, i) => {
    if (!total) return;
    const pct = ((d.inventory_count || 0) / total) * 100;
    const color = d.is_primary ? 'var(--c1)' : palette[i % palette.length];
    stops.push(`${color} ${acc}% ${acc + pct}%`);
    acc += pct;
  });
  if (acc < 100) stops.push(`rgba(255,255,255,.08) ${acc}% 100%`);
  // Highlight ring offset to your slice
  let yourStart = 0; let yourPct = 0;
  let acc2 = 0;
  for (const d of dealers) {
    const pct = total ? ((d.inventory_count || 0) / total) * 100 : 0;
    if (d.is_primary) { yourStart = acc2; yourPct = pct; break; }
    acc2 += pct;
  }
  return (
    <div style={{ position: 'relative', width: 180, height: 180 }}>
      <div style={{
        width: 180, height: 180, borderRadius: '50%',
        background: `conic-gradient(${stops.join(', ')})`,
      }} />
      {yourPct > 0 && (
        <div style={{
          position: 'absolute', inset: -6, borderRadius: '50%', pointerEvents: 'none',
          background: `conic-gradient(transparent 0% ${yourStart}%, rgba(255,91,164,.55) ${yourStart}% ${yourStart + yourPct}%, transparent ${yourStart + yourPct}% 100%)`,
          WebkitMask: 'radial-gradient(circle, transparent 92px, #000 92px, #000 100px, transparent 100px)',
          mask: 'radial-gradient(circle, transparent 92px, #000 92px, #000 100px, transparent 100px)',
        }} />
      )}
      <div style={{
        position: 'absolute', inset: 32, borderRadius: '50%', background: 'var(--paper-2, #13131a)',
        border: '1px solid rgba(255,255,255,.06)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <div className="v2-mono text-[9px] text-white/45 uppercase tracking-wider">your share</div>
        <div className="v2-num text-[26px] font-light v2-c1">{myShare.toFixed(2)}<span className="text-[14px] text-white/55">%</span></div>
        <div className="v2-mono text-[10px] text-white/55">of {total.toLocaleString()}</div>
      </div>
    </div>
  );
}

function stdDev(arr) {
  if (!arr.length) return 0;
  const m = arr.reduce((s, v) => s + v, 0) / arr.length;
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);
}

function CompetitorChip({ competitor, ourPrice }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const chipRef = useRef(null);
  const hideTimer = useRef(null);

  const priceColor = competitor.price < ourPrice ? MI_POS_COLOR.below : competitor.price > ourPrice ? MI_POS_COLOR.above : MI_POS_COLOR.at;
  const diffPct = ourPrice > 0 ? Math.round(((competitor.price - ourPrice) / ourPrice) * 100) : 0;
  const vehicleUrl = competitor.vehicle_url || competitor.dealer_url;
  const dealerLabel = competitor.dealer_name || cleanDomain(competitor.dealer_url) || 'Competitor';
  const domain = cleanDomain(competitor.dealer_url);

  function show() {
    clearTimeout(hideTimer.current);
    const rect = chipRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const y = spaceBelow < 240 ? rect.top - 6 - 240 : rect.bottom + 6;
      setPos({ x: Math.min(rect.left, window.innerWidth - 268), y });
    }
    setVisible(true);
  }
  function scheduleHide() { hideTimer.current = setTimeout(() => setVisible(false), 160); }
  function cancelHide() { clearTimeout(hideTimer.current); }

  return (
    <span ref={chipRef} style={{ display: 'inline-block' }}>
      <span onMouseEnter={show} onMouseLeave={scheduleHide} className="v2-mono"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, padding: '3px 8px', borderRadius: 6, cursor: 'default',
          background: priceColor + '12', border: `1px solid ${priceColor}40`,
          color: priceColor, fontWeight: 700
        }}>
        {formatPrice(competitor.price)}
        <span style={{ fontSize: 9, opacity: 0.7 }}>{diffPct > 0 ? `+${diffPct}` : diffPct}%</span>
      </span>

      {visible && ReactDOM.createPortal(
        <div onMouseEnter={cancelHide} onMouseLeave={scheduleHide}
          style={{
            position: 'fixed', top: pos.y, left: pos.x, zIndex: 9999, width: 260,
            background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 14, padding: '14px 16px',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.70)',
            fontFamily: 'Inter, sans-serif', pointerEvents: 'all',
          }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div className="v2-serif" style={{ fontWeight: 400, fontSize: 14, color: '#fff', lineHeight: 1.3 }}>{dealerLabel}</div>
              {domain && <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{domain}</div>}
            </div>
            {vehicleUrl && (
              <a href={vehicleUrl} target="_blank" rel="noopener noreferrer" className="v2-mono"
                style={{ flexShrink: 0, fontSize: 9, color: 'var(--c2)', textDecoration: 'none', background: 'rgba(122,77,255,0.12)', border: '1px solid rgba(122,77,255,0.30)', padding: '2px 7px', borderRadius: 6, fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>
                VIEW ↗
              </a>
            )}
          </div>

          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontWeight: 600, color: '#fff', marginBottom: 3 }}>
              {[competitor.year, competitor.make, competitor.model, competitor.trim].filter(Boolean).join(' ')}
            </div>
            {competitor.exterior_color && <div className="v2-mono" style={{ fontSize: 10 }}>· {competitor.exterior_color}</div>}
            {competitor.mileage > 0 && <div className="v2-mono" style={{ fontSize: 10 }}>· {Number(competitor.mileage).toLocaleString()} mi</div>}
            {competitor.condition && <div className="v2-mono" style={{ fontSize: 10, textTransform: 'capitalize' }}>· {competitor.condition}</div>}
            {competitor.days_on_market > 0 && <div className="v2-mono" style={{ fontSize: 10 }}>· listed {competitor.days_on_market}d ago</div>}
          </div>

          <div style={{
            padding: '8px 12px', background: priceColor + '12',
            border: `1px solid ${priceColor}30`, borderRadius: 10,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8
          }}>
            <span className="v2-num" style={{ fontSize: 16, fontWeight: 800, color: priceColor }}>
              {formatPrice(competitor.price)}
            </span>
            <span className="v2-mono" style={{ fontSize: 10, color: priceColor, fontWeight: 700, letterSpacing: '0.02em' }}>
              {diffPct > 0 ? `+$${(competitor.price - ourPrice).toLocaleString()}` : `-$${(ourPrice - competitor.price).toLocaleString()}`} vs yours
            </span>
          </div>

          {competitor.vin && (
            <div className="v2-mono" style={{ marginTop: 8, fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.04em' }}>
              VIN {competitor.vin}
            </div>
          )}
        </div>,
        document.body
      )}
    </span>
  );
}

function PriceMileageScatter({ groupId }) {
  const [filters, setFilters] = useState({ make: '', model: '', trim: '', condition: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null);

  // Pull make/model suggestions from current group's pricing endpoint so the
  // user has working values without typing blind.
  const [suggestions, setSuggestions] = useState({ makes: [], modelsByMake: {} });
  useEffect(() => {
    if (!groupId) return;
    api(`/competitive/groups/${groupId}/pricing`).then(d => {
      const byMake = {};
      const makes = new Set();
      (d.models || []).forEach(m => {
        if (m.make) makes.add(m.make);
        if (m.make && m.model) {
          if (!byMake[m.make]) byMake[m.make] = new Set();
          byMake[m.make].add(m.model);
        }
      });
      const modelsByMake = {};
      Object.keys(byMake).forEach(k => { modelsByMake[k] = Array.from(byMake[k]).sort(); });
      setSuggestions({ makes: Array.from(makes).sort(), modelsByMake });
    }).catch(() => {});
  }, [groupId]);

  async function load() {
    if (!filters.make) {
      setError('Pick a make to plot');
      return;
    }
    setLoading(true); setError(null); setData(null);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      const res = await api(`/competitive/groups/${groupId}/listings?${q}`);
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }

  const inp = { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 12, outline: 'none', fontFamily: 'inherit' };

  // Chart geometry
  const W = 760, H = 420, padL = 60, padR = 20, padT = 20, padB = 50;
  const innerW = W - padL - padR, innerH = H - padT - padB;

  const allPoints = data ? [...data.own, ...data.competitors] : [];
  const validPoints = allPoints.filter(p => p.price > 0 && p.mileage != null && p.mileage >= 0);

  let xMin, xMax, yMin, yMax;
  if (validPoints.length > 0) {
    const prices = validPoints.map(p => p.price);
    const miles = validPoints.map(p => p.mileage);
    xMin = Math.min(...miles);
    xMax = Math.max(...miles);
    yMin = Math.min(...prices);
    yMax = Math.max(...prices);
    // pad a little
    const xp = (xMax - xMin) * 0.05 || 1000;
    const yp = (yMax - yMin) * 0.05 || 1000;
    xMin = Math.max(0, xMin - xp); xMax += xp;
    yMin = Math.max(0, yMin - yp); yMax += yp;
  }

  const xScale = (m) => padL + ((m - xMin) / (xMax - xMin || 1)) * innerW;
  const yScale = (p) => padT + innerH - ((p - yMin) / (yMax - yMin || 1)) * innerH;

  // Tick generation
  function ticks(min, max, count = 5) {
    const out = [];
    for (let i = 0; i <= count; i++) out.push(min + ((max - min) * i) / count);
    return out;
  }
  const formatMoney = n => '$' + Math.round(n).toLocaleString();
  const formatMiles = n => Math.round(n / 1000) + 'k';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 14, alignItems: 'start' }}>
      <div className="v2-tile" style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>price vs mileage</div>
            <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>
              {data?.filters?.make ? `${data.filters.make}${data.filters.model ? ' ' + data.filters.model : ''}${data.filters.trim ? ' ' + data.filters.trim : ''}` : 'Pick a make + model →'}
            </div>
          </div>
          {data && (
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>
              {data.stats?.own_count || 0} yours · {data.stats?.competitor_count || 0} rivals
            </div>
          )}
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>LOADING LISTINGS…</span>
          </div>
        )}

        {!loading && !data && !error && (
          <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,.5)' }}>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 6 }}>Pick a make to start.</div>
            <div className="v2-mono" style={{ fontSize: 11 }}>every local listing plotted on price × mileage, your VINs highlighted</div>
          </div>
        )}

        {error && (
          <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,91,164,.08)', border: '1px solid rgba(255,91,164,.30)', color: 'var(--c1)', fontSize: 13 }}>
            {error}
          </div>
        )}

        {!loading && data && validPoints.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.5)', fontSize: 13 }}>
            No listings with both price + mileage match these filters.
          </div>
        )}

        {!loading && data && validPoints.length > 0 && (
          <div style={{ position: 'relative' }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto"
              style={{ display: 'block', maxHeight: 480 }}
              onMouseLeave={() => setHover(null)}>
              {/* Axes */}
              <line x1={padL} y1={padT} x2={padL} y2={padT + innerH} stroke="rgba(255,255,255,.20)" />
              <line x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH} stroke="rgba(255,255,255,.20)" />

              {/* Y ticks (price) */}
              {ticks(yMin, yMax).map((v, i) => (
                <g key={`y${i}`}>
                  <line x1={padL - 4} y1={yScale(v)} x2={padL + innerW} y2={yScale(v)} stroke="rgba(255,255,255,.06)" />
                  <text x={padL - 8} y={yScale(v) + 4} textAnchor="end" fill="rgba(255,255,255,.55)" fontSize="11" fontFamily="'JetBrains Mono', monospace">{formatMoney(v)}</text>
                </g>
              ))}

              {/* X ticks (mileage) */}
              {ticks(xMin, xMax).map((v, i) => (
                <g key={`x${i}`}>
                  <line x1={xScale(v)} y1={padT + innerH} x2={xScale(v)} y2={padT + innerH + 4} stroke="rgba(255,255,255,.20)" />
                  <text x={xScale(v)} y={padT + innerH + 18} textAnchor="middle" fill="rgba(255,255,255,.55)" fontSize="11" fontFamily="'JetBrains Mono', monospace">{formatMiles(v)} mi</text>
                </g>
              ))}

              {/* Axis labels */}
              <text x={padL + innerW / 2} y={H - 8} textAnchor="middle" fill="rgba(255,255,255,.5)" fontSize="11" fontFamily="'JetBrains Mono', monospace" letterSpacing=".06em">MILEAGE →</text>
              <text x={16} y={padT + innerH / 2} textAnchor="middle" fill="rgba(255,255,255,.5)" fontSize="11" fontFamily="'JetBrains Mono', monospace" letterSpacing=".06em" transform={`rotate(-90, 16, ${padT + innerH / 2})`}>PRICE ↑</text>

              {/* Competitor dots */}
              {data.competitors.filter(p => p.price > 0 && p.mileage != null).map((p, i) => (
                <circle key={`c${i}`}
                  cx={xScale(p.mileage)} cy={yScale(p.price)} r="5"
                  fill="rgba(34,211,238,.55)" stroke="rgba(34,211,238,.85)" strokeWidth="1"
                  style={{ cursor: 'pointer', transition: 'r .12s' }}
                  onMouseEnter={() => setHover(p)}
                  onClick={() => p.url && window.open(p.url, '_blank', 'noopener')} />
              ))}

              {/* Own dots, drawn last so they sit on top */}
              {data.own.filter(p => p.price > 0 && p.mileage != null).map((p, i) => (
                <circle key={`o${i}`}
                  cx={xScale(p.mileage)} cy={yScale(p.price)} r="8"
                  fill="var(--c1)" stroke="#fff" strokeWidth="2"
                  style={{ cursor: 'pointer', filter: 'drop-shadow(0 0 6px rgba(255,91,164,.6))' }}
                  onMouseEnter={() => setHover(p)}
                  onClick={() => p.url && window.open(p.url, '_blank', 'noopener')} />
              ))}
            </svg>

            {/* Tooltip */}
            {hover && (
              <div style={{
                position: 'absolute', top: 18, right: 18,
                background: 'rgba(10,10,14,.95)',
                border: `1px solid ${hover.is_own ? 'var(--c1)' : 'var(--c3)'}`,
                borderRadius: 12, padding: '12px 14px',
                maxWidth: 280, fontSize: 12,
                boxShadow: '0 10px 30px -10px rgba(0,0,0,.7)',
                pointerEvents: 'none',
              }}>
                <div className="v2-mono" style={{ fontSize: 10, color: hover.is_own ? 'var(--c1)' : 'var(--c3)', letterSpacing: '.06em', marginBottom: 4 }}>
                  {hover.is_own ? 'YOUR INVENTORY' : 'COMPETITOR'} · {hover.dealer_name}
                </div>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                  {hover.year} {hover.make} {hover.model} {hover.trim || ''}
                </div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.7)', lineHeight: 1.8 }}>
                  ${Math.round(hover.price).toLocaleString()}<br/>
                  {hover.mileage != null ? `${hover.mileage.toLocaleString()} mi` : '–'}{hover.exterior_color ? ` · ${hover.exterior_color}` : ''}<br/>
                  {hover.days_on_lot != null ? `${hover.days_on_lot} days on lot` : ''}
                </div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', marginTop: 6 }}>click to open source ↗</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 18, marginTop: 10, justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,.55)' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99, background: 'var(--c1)', border: '2px solid #fff', verticalAlign: 'middle', marginRight: 6 }} />your inventory ({data.stats?.own_count || 0})</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99, background: 'rgba(34,211,238,.55)', border: '1px solid rgba(34,211,238,.85)', verticalAlign: 'middle', marginRight: 6 }} />competitors ({data.stats?.competitor_count || 0})</span>
            </div>
          </div>
        )}
      </div>

      {/* Filter side panel */}
      <div className="v2-tile" style={{ padding: 18 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 12 }}>filters</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>Make *</label>
            <input list="md-make-list" style={inp} value={filters.make} onChange={e => setFilters({ ...filters, make: e.target.value })} placeholder="Honda" />
            <datalist id="md-make-list">
              {suggestions.makes.map(m => <option key={m} value={m} />)}
            </datalist>
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>Model</label>
            <input list="md-model-list" style={inp} value={filters.model} onChange={e => setFilters({ ...filters, model: e.target.value })} placeholder="Accord" />
            <datalist id="md-model-list">
              {(suggestions.modelsByMake[filters.make] || []).map(m => <option key={m} value={m} />)}
            </datalist>
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>Trim</label>
            <input style={inp} value={filters.trim} onChange={e => setFilters({ ...filters, trim: e.target.value })} placeholder="Sport" />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>Condition</label>
            <select style={{ ...inp, padding: '8px 10px' }} value={filters.condition} onChange={e => setFilters({ ...filters, condition: e.target.value })}>
              <option value="">Any</option>
              <option value="new">New</option>
              <option value="used">Used</option>
              <option value="cpo">CPO</option>
            </select>
          </div>
          <button onClick={load} disabled={loading || !filters.make}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginTop: 4, opacity: loading || !filters.make ? .5 : 1 }}>
            {loading ? 'Loading…' : 'Plot listings'}
          </button>
        </div>

        {data?.stats?.median_price && (
          <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(255,255,255,.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginBottom: 4 }}>local market median</div>
            <div className="v2-num" style={{ fontSize: 22, color: '#fff' }}>${Math.round(data.stats.median_price).toLocaleString()}</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginTop: 6 }}>
              ${Math.round(data.stats.min_price).toLocaleString()} - ${Math.round(data.stats.max_price).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketIntelligencePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const groupIdParam = location.pathname.match(/\/market-intelligence\/group\/(\d+)/)?.[1];
  const svgRef = useRef(null);
  const simRef = useRef(null);
  const containerRef = useRef(null);

  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(groupIdParam ? parseInt(groupIdParam) : null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [condFilter, setCondFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const PAGE_SIZE = 1000;

  const [simVehicle, setSimVehicle] = useState(null);
  const [simPrice, setSimPrice] = useState(0);
  const [activeDetailTab, setActiveDetailTab] = useState('pricing');

  const [manageOpen, setManageOpen] = useState(false);
  const [groupDetail, setGroupDetail] = useState(null);
  const [groupDetailLoading, setGroupDetailLoading] = useState(false);
  const [excludingId, setExcludingId] = useState(null);

  useEffect(() => {
    api('/competitive/groups')
      .then(res => {
        const g = Array.isArray(res.groups || res) ? (res.groups || res) : [];
        setGroups(g);
        if (!activeGroupId && g.length > 0) setActiveGroupId(g[0].id);
        if (g.length === 0) setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeGroupId) return;
    setLoading(true);
    setSelectedCluster(null);
    const condParam = condFilter ? `&condition=${condFilter}` : '';
    api(`/competitive/groups/${activeGroupId}/market-intelligence?limit=${PAGE_SIZE}&offset=0${condParam}`)
      .then(res => { setData(res); setLoading(false); })
      .catch(() => setLoading(false));
  }, [activeGroupId, condFilter, refreshKey]);

  function openManagePanel() {
    setManageOpen(true);
    if (!activeGroupId) return;
    setGroupDetailLoading(true);
    api(`/competitive/groups/${activeGroupId}`)
      .then(res => { setGroupDetail(res); setGroupDetailLoading(false); })
      .catch(() => setGroupDetailLoading(false));
  }

  async function excludeMember(memberId) {
    setExcludingId(memberId);
    try {
      await api(`/competitive/groups/${activeGroupId}/members/${memberId}`, { method: 'DELETE' });
      setGroupDetail(prev => prev ? {
        ...prev,
        members: prev.members.map(m => m.member_id === memberId ? { ...m, is_excluded: true } : m)
      } : prev);
      setRefreshKey(k => k + 1);
    } catch (e) {}
    setExcludingId(null);
  }

  async function reincludeMember(memberId) {
    setExcludingId(memberId);
    try {
      const member = groupDetail?.members?.find(m => m.member_id === memberId);
      if (member) {
        await api(`/competitive/groups/${activeGroupId}/members`, {
          method: 'POST', body: JSON.stringify({ dealer_id: member.dealer.id })
        });
        setGroupDetail(prev => prev ? {
          ...prev,
          members: prev.members.map(m => m.member_id === memberId ? { ...m, is_excluded: false } : m)
        } : prev);
        setRefreshKey(k => k + 1);
      }
    } catch (e) {}
    setExcludingId(null);
  }

  const isNewInventory = useMemo(() => {
    if (!data?.vehicles?.length) return true;
    const newCount = data.vehicles.filter(v => (v.condition || '').toLowerCase() === 'new').length;
    return newCount / data.vehicles.length > 0.6;
  }, [data]);

  const graphData = useMemo(() => {
    if (!data?.vehicles) return { nodes: [], links: [], modelCount: 0 };

    const vehicles = data.vehicles;

    if (!isNewInventory) {
      const AGE_BUCKETS = [
        { id: '0-30',  label: '0–30d',   min: 0,   max: 30  },
        { id: '31-60', label: '31–60d',  min: 31,  max: 60  },
        { id: '61-90', label: '61–90d',  min: 61,  max: 90  },
        { id: '90+',   label: '90d+',    min: 91,  max: Infinity },
      ];
      function ageBucket(days) {
        return AGE_BUCKETS.find(b => days >= b.min && days <= b.max) || AGE_BUCKETS[3];
      }

      const STATUS_DEFS = [
        { id: 'above', label: 'Above Market', color: MI_POS_COLOR.above },
        { id: 'at',    label: 'At Market',    color: MI_POS_COLOR.at    },
        { id: 'below', label: 'Below Market', color: MI_POS_COLOR.below },
        { id: 'none',  label: 'No Data',      color: MI_POS_COLOR.none  },
      ];

      const statusMap = {};
      for (const v of vehicles) {
        const sid = v.recommendation?.position || 'none';
        if (!statusMap[sid]) statusMap[sid] = { vehicles: [], subMap: {} };
        statusMap[sid].vehicles.push(v);
        const bk = ageBucket(v.days_on_market || 0);
        if (!statusMap[sid].subMap[bk.id]) statusMap[sid].subMap[bk.id] = [];
        statusMap[sid].subMap[bk.id].push(v);
      }

      const filteredStatuses = STATUS_DEFS.filter(s => statusMap[s.id]?.vehicles.length > 0);
      const maxStatusCount = Math.max(1, ...filteredStatuses.map(s => statusMap[s.id].vehicles.length));
      const statusNodes = filteredStatuses.map(s => {
        const count = statusMap[s.id].vehicles.length;
        return {
          id: s.id, type: 'model',
          label: s.label, position: s.id,
          vehicles: statusMap[s.id].vehicles,
          count,
          color: s.color,
          r: 22 + 46 * Math.sqrt(count / maxStatusCount),
        };
      });

      const subNodes = [];
      for (const s of statusNodes) {
        const subMap = statusMap[s.id].subMap;
        const bucketIds = Object.keys(subMap);
        if (bucketIds.length <= 1) continue;
        const maxBucketCount = Math.max(1, ...Object.values(subMap).map(a => a.length));
        for (const bk of AGE_BUCKETS) {
          const bvs = subMap[bk.id];
          if (!bvs?.length) continue;
          subNodes.push({
            id: `${s.id}__${bk.id}`, type: 'trim',
            label: bk.label, parentId: s.id, position: s.id,
            vehicles: bvs, count: bvs.length,
            color: s.color,
            r: 8 + 18 * Math.sqrt(bvs.length / maxBucketCount),
          });
        }
      }

      const nodes = [...statusNodes, ...subNodes];
      const links = [];
      for (const sn of subNodes) links.push({ source: sn.parentId, target: sn.id, strength: 0.9, distance: 85, type: 'spoke' });
      for (let i = 0; i < statusNodes.length - 1; i++) {
        links.push({ source: statusNodes[i].id, target: statusNodes[i + 1].id, strength: 0.05, distance: 260, type: 'make' });
      }
      return { nodes, links, modelCount: statusNodes.length };
    }

    const makeColorIdx = {};
    let colorIdx = 0;
    function dominantPos(a, t, b) {
      if (a + t + b === 0) return 'none';
      if (a >= t && a >= b) return 'above';
      if (b > t) return 'below';
      return 'at';
    }

    const modelMap = {};
    const trimMap = {};
    for (const v of vehicles) {
      const modelId = `${v.make}__${v.model}`;
      const trimKey = v.trim ? v.trim.trim().split(/\s+/)[0].toUpperCase() : 'BASE';
      const trimId = `${modelId}__${trimKey}`;
      if (!modelMap[modelId]) modelMap[modelId] = { make: v.make, model: v.model, vehicles: [], a: 0, t: 0, b: 0, trims: new Set() };
      if (!trimMap[trimId]) trimMap[trimId] = { make: v.make, model: v.model, trim: v.trim || null, trimKey, modelId, vehicles: [], a: 0, t: 0, b: 0 };
      modelMap[modelId].vehicles.push(v);
      modelMap[modelId].trims.add(trimId);
      trimMap[trimId].vehicles.push(v);
      const pos = v.recommendation?.position;
      if (pos === 'above') { modelMap[modelId].a++; trimMap[trimId].a++; }
      else if (pos === 'at')    { modelMap[modelId].t++; trimMap[trimId].t++; }
      else if (pos === 'below') { modelMap[modelId].b++; trimMap[trimId].b++; }
    }

    const modelEntries = Object.values(modelMap);
    const maxModelCount = Math.max(1, ...modelEntries.map(c => c.vehicles.length));
    const modelNodes = modelEntries.map(c => {
      if (!(c.make in makeColorIdx)) makeColorIdx[c.make] = colorIdx++;
      const pos = dominantPos(c.a, c.t, c.b);
      return {
        id: `${c.make}__${c.model}`, type: 'model',
        label: c.model, make: c.make, model: c.model, vehicles: c.vehicles,
        count: c.vehicles.length, position: pos,
        color: MI_POS_COLOR[pos],
        makeColor: MI_COLORS[makeColorIdx[c.make] % MI_COLORS.length],
        r: 18 + 52 * Math.sqrt(c.vehicles.length / maxModelCount),
        trimIds: [...c.trims],
      };
    });

    const trimEntries = Object.values(trimMap).filter(c => {
      const model = modelMap[c.modelId];
      return model && model.trims.size > 1;
    });
    const maxTrimCount = Math.max(1, ...trimEntries.map(c => c.vehicles.length));
    const trimNodes = trimEntries.map(c => {
      const pos = dominantPos(c.a, c.t, c.b);
      return {
        id: `${c.modelId}__${c.trimKey}`, type: 'trim',
        label: c.trimKey, make: c.make, model: c.model, trim: c.trim, trimKey: c.trimKey,
        modelId: c.modelId, vehicles: c.vehicles,
        count: c.vehicles.length, position: pos,
        color: MI_POS_COLOR[pos],
        r: 8 + 20 * Math.sqrt(c.vehicles.length / maxTrimCount),
      };
    });

    const nodes = [...modelNodes, ...trimNodes];
    const links = [];
    for (const tn of trimNodes) links.push({ source: tn.modelId, target: tn.id, strength: 0.9, distance: 80, type: 'spoke' });
    const makeModels = {};
    for (const mn of modelNodes) {
      if (!makeModels[mn.make]) makeModels[mn.make] = [];
      makeModels[mn.make].push(mn.id);
    }
    for (const ids of Object.values(makeModels)) {
      for (let i = 0; i < ids.length - 1; i++) {
        links.push({ source: ids[i], target: ids[i + 1], strength: 0.08, distance: 240, type: 'make' });
      }
    }
    return { nodes, links, modelCount: modelNodes.length };
  }, [data, isNewInventory]);

  // D3 force-directed graph (preserved exactly)
  useEffect(() => {
    if (!svgRef.current || !graphData.nodes.length || !window.d3) return;
    const d3 = window.d3;
    const svgEl = svgRef.current;
    const rect = containerRef.current?.getBoundingClientRect() || { width: 600, height: 500 };
    const W = rect.width || 600;
    const H = Math.max(460, rect.height || 460);
    svgEl.setAttribute('width', W);
    svgEl.setAttribute('height', H);

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    const defs = svg.append('defs');
    const pat = defs.append('pattern').attr('id', 'mi-dots').attr('width', 28).attr('height', 28).attr('patternUnits', 'userSpaceOnUse');
    pat.append('circle').attr('cx', 14).attr('cy', 14).attr('r', 1.2).attr('fill', 'rgba(255,255,255,0.04)');
    const filt = defs.append('filter').attr('id', 'mi-glow').attr('x', '-50%').attr('y', '-50%').attr('width', '200%').attr('height', '200%');
    filt.append('feGaussianBlur').attr('stdDeviation', 5).attr('result', 'blur');
    const fm = filt.append('feMerge');
    fm.append('feMergeNode').attr('in', 'blur');
    fm.append('feMergeNode').attr('in', 'SourceGraphic');

    svg.append('rect').attr('width', W).attr('height', H).attr('fill', '#0a0a0e');
    svg.append('rect').attr('width', W).attr('height', H).attr('fill', 'url(#mi-dots)');

    const nodes = graphData.nodes.map(c => ({
      ...c,
      x: W / 2 + (Math.random() - 0.5) * 180,
      y: H / 2 + (Math.random() - 0.5) * 180,
    }));
    const links = graphData.links;

    const sim = d3.forceSimulation(nodes)
      .force('charge', d3.forceManyBody().strength(d => d.type === 'model' ? -420 : -120))
      .force('center', d3.forceCenter(W / 2, H / 2).strength(0.06))
      .force('collision', d3.forceCollide().radius(d => d.r + (d.type === 'model' ? 20 : 14)).strength(0.85))
      .force('link', d3.forceLink(links).id(d => d.id).distance(d => d.distance || 160).strength(d => d.strength || 0.25));
    simRef.current = sim;

    const linkG = svg.append('g');
    const linkPaths = linkG.selectAll('line')
      .data(links).join('line')
      .attr('stroke', d => d.type === 'spoke' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.06)')
      .attr('stroke-width', d => d.type === 'spoke' ? 1 : 1.5)
      .attr('stroke-dasharray', d => d.type === 'spoke' ? '3,3' : null)
      .attr('opacity', d => d.type === 'spoke' ? 0.8 : 0.5);

    const nodeG = svg.append('g');

    const modelData = nodes.filter(d => d.type === 'model');
    const modelGroups = nodeG.selectAll('g.model-node')
      .data(modelData).join('g').attr('class', 'model-node').attr('cursor', 'pointer')
      .on('click', (evt, d) => {
        const orig = graphData.nodes.find(n => n.id === d.id);
        setSelectedCluster(orig || d); setActiveDetailTab('pricing'); setSimVehicle(null);
      })
      .call(d3.drag()
        .on('start', (evt, d) => { if (!evt.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on('drag', (evt, d) => { d.fx = evt.x; d.fy = evt.y; })
        .on('end', (evt, d) => { if (!evt.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      );

    modelGroups.append('circle').attr('class', 'pulse-ring')
      .attr('r', d => d.r + 12).attr('fill', 'none')
      .attr('stroke', d => d.color).attr('stroke-width', 1.5).attr('opacity', 0.2);

    modelGroups.append('circle')
      .attr('r', d => d.r).attr('fill', d => d.color + '1a')
      .attr('stroke', d => d.color).attr('stroke-width', 2.5)
      .attr('filter', 'url(#mi-glow)');

    modelGroups.append('text')
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
      .attr('font-size', d => d.r > 38 ? 14 : 11).attr('font-weight', '700')
      .attr('fill', d => d.color).attr('font-family', "'JetBrains Mono', monospace")
      .text(d => d.count);

    modelGroups.append('text')
      .attr('text-anchor', 'middle').attr('y', d => d.r + 14)
      .attr('font-size', 10).attr('fill', 'rgba(255,255,255,0.65)')
      .attr('font-family', 'Inter, sans-serif').attr('font-weight', '600')
      .text(d => {
        const lbl = d.label || d.model || '';
        return lbl.length > 14 ? lbl.slice(0, 13) + '…' : lbl;
      });

    modelGroups.append('text')
      .attr('text-anchor', 'middle').attr('y', d => d.r + 26)
      .attr('font-size', 9).attr('fill', 'rgba(255,255,255,0.35)')
      .attr('font-family', 'Inter, sans-serif')
      .text(d => d.make || '');

    const trimData = nodes.filter(d => d.type === 'trim');
    const trimGroups = nodeG.selectAll('g.trim-node')
      .data(trimData).join('g').attr('class', 'trim-node').attr('cursor', 'pointer')
      .on('click', (evt, d) => {
        const orig = graphData.nodes.find(n => n.id === d.id);
        setSelectedCluster(orig || d); setActiveDetailTab('pricing'); setSimVehicle(null);
      })
      .call(d3.drag()
        .on('start', (evt, d) => { if (!evt.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on('drag', (evt, d) => { d.fx = evt.x; d.fy = evt.y; })
        .on('end', (evt, d) => { if (!evt.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      );

    trimGroups.append('circle').attr('class', 'pulse-ring')
      .attr('r', d => d.r + 6).attr('fill', 'none')
      .attr('stroke', d => d.color).attr('stroke-width', 1).attr('opacity', 0.15);

    trimGroups.append('circle')
      .attr('r', d => d.r).attr('fill', d => d.color + '22')
      .attr('stroke', d => d.color).attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,2');

    trimGroups.append('text')
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
      .attr('font-size', 8).attr('font-weight', '700')
      .attr('fill', d => d.color).attr('font-family', "'JetBrains Mono', monospace")
      .text(d => d.count);

    trimGroups.append('text')
      .attr('text-anchor', 'middle').attr('y', d => d.r + 11)
      .attr('font-size', 8).attr('fill', 'rgba(255,255,255,0.45)')
      .attr('font-family', "'JetBrains Mono', monospace")
      .text(d => {
        const lbl = d.label || d.trimKey || '';
        return lbl.length > 8 ? lbl.slice(0, 7) + '…' : lbl;
      });

    sim.alphaMin(0.001).alphaDecay(0.015);

    sim.on('tick', () => {
      for (const n of nodes) {
        n.x = Math.max(n.r + 8, Math.min(W - n.r - 8, n.x));
        n.y = Math.max(n.r + 8, Math.min(H - n.r - 32, n.y));
      }
      linkPaths
        .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
      modelGroups.attr('transform', d => `translate(${d.x},${d.y})`);
      trimGroups.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    function microKick() {
      if (!svgEl.isConnected) return;
      for (const n of nodes) {
        if (!n.fx) { n.vx = (n.vx || 0) + (Math.random() - 0.5) * 0.35; n.vy = (n.vy || 0) + (Math.random() - 0.5) * 0.35; }
      }
      sim.alpha(Math.max(sim.alpha(), 0.03)).restart();
    }
    const kickInterval = setInterval(microKick, 3000);

    modelGroups.each(function(d, i) {
      const ring = d3.select(this).select('.pulse-ring');
      function pulse() {
        if (!svgEl.isConnected) return;
        ring.transition().duration(2200 + Math.random() * 700).ease(d3.easeSinInOut)
          .attr('r', d.r + 26).attr('opacity', 0)
          .transition().duration(300)
          .attr('r', d.r + 12).attr('opacity', 0.2)
          .on('end', pulse);
      }
      setTimeout(pulse, i * 280);
    });

    trimGroups.each(function(d, i) {
      const ring = d3.select(this).select('.pulse-ring');
      function pulse() {
        if (!svgEl.isConnected) return;
        ring.transition().duration(3000 + Math.random() * 1000).ease(d3.easeSinInOut)
          .attr('r', d.r + 12).attr('opacity', 0)
          .transition().duration(400)
          .attr('r', d.r + 6).attr('opacity', 0.15)
          .on('end', pulse);
      }
      setTimeout(pulse, i * 180 + 500);
    });

    return () => { sim.stop(); clearInterval(kickInterval); };
  }, [graphData]);

  function computeSim(vehicle, price) {
    if (!vehicle?.market) return null;
    const { median } = vehicle.market;
    const diffPct = ((price - median) / median) * 100;
    let mult;
    if (diffPct > 15) mult = 2.2;
    else if (diffPct > 5) mult = 1.5;
    else if (diffPct > 0) mult = 1.1;
    else if (diffPct > -5) mult = 0.85;
    else if (diffPct > -15) mult = 0.65;
    else mult = 0.5;
    const estDays = Math.round(45 * mult);
    const sweetPrice = Math.round(median * 0.97);
    const revImpact = Math.round(price - sweetPrice);
    const below = (vehicle.competitors || []).filter(c => c.price < price).length;
    const above = (vehicle.competitors || []).filter(c => c.price >= price).length;
    return { estDays, diffPct: Math.round(diffPct * 10) / 10, revImpact, below, above, sweetPrice };
  }

  function genCurvePoints(vehicle, width, height) {
    if (!vehicle?.market) return [];
    const { median } = vehicle.market;
    const priceMin = median * 0.7, priceMax = median * 1.3;
    const points = [];
    for (let i = 0; i <= 40; i++) {
      const price = priceMin + (priceMax - priceMin) * (i / 40);
      const diffPct = ((price - median) / median) * 100;
      let mult;
      if (diffPct > 15) mult = 2.2;
      else if (diffPct > 5) mult = 1.5;
      else if (diffPct > 0) mult = 1.1;
      else if (diffPct > -5) mult = 0.85;
      else if (diffPct > -15) mult = 0.65;
      else mult = 0.5;
      const days = Math.min(100, Math.round(45 * mult));
      points.push({ x: (price - priceMin) / (priceMax - priceMin) * width, y: height - (days / 100) * height, price, days });
    }
    return points;
  }

  function positionColor(pos) {
    return pos === 'above' ? MI_POS_COLOR.above : pos === 'at' ? MI_POS_COLOR.at : MI_POS_COLOR.below;
  }
  function positionBg(pos) {
    return pos === 'above' ? 'rgba(255,107,53,0.10)' : pos === 'at' ? 'rgba(233,196,106,0.10)' : 'rgba(26,147,111,0.10)';
  }

  const summary = data?.summary;
  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };

  if (groups.length === 0 && !loading) {
    return (
      <div className="page-content">
        <div style={{ marginBottom: 22 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
            MARKET INTELLIGENCE · NO COMPETITOR GROUPS
          </div>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            See the field.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>Set up competitor groups to unlock pricing intelligence.</span>
          </h1>
        </div>
        <div className="v2-tile v2-tile-violet" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No competitor groups yet.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>Set up competitor groups first to unlock market intelligence, price positioning analysis, and real-time alerts.</div>
          <button onClick={() => navigate('/competitive/setup')}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
            Set up competitors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Hero */}
      <div style={{ marginBottom: 16 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          MARKET INTELLIGENCE{data?.primary_dealer?.name ? ` · ${data.primary_dealer.name.toUpperCase()}` : ''}{summary ? ` · ${summary.total} VEHICLES · ${graphData.modelCount} CLUSTERS` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(32px, 4.5vw, 50px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            The field.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Click a cluster - drill into pricing intel.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4 }}>
              {[{ v: '', l: 'All' }, { v: 'new', l: 'New' }, { v: 'used', l: 'Used' }].map(({ v, l }) => (
                <button key={v} onClick={() => setCondFilter(v)} style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: 'none',
                  background: condFilter === v ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: condFilter === v ? '#fff' : 'rgba(255,255,255,0.45)',
                  fontFamily: 'inherit',
                }}>{l}</button>
              ))}
            </div>
            <select value={activeGroupId || ''} onChange={e => { setActiveGroupId(parseInt(e.target.value)); setData(null); setGroupDetail(null); }}
              style={{ ...inp, minWidth: 200, cursor: 'pointer' }}>
              {groups.map(g => <option key={g.id} value={g.id} style={{ background: '#111' }}>{g.primary_dealer_name || g.name}</option>)}
            </select>
            <button onClick={openManagePanel} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
              ⚙ Competitors
            </button>
            <button onClick={() => setRefreshKey(k => k + 1)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
              <Icons.Refresh />
            </button>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Total',         value: summary.total,        color: '#fff' },
            { label: 'Above market',  value: summary.above_market, color: MI_POS_COLOR.above },
            { label: 'At market',     value: summary.at_market,    color: MI_POS_COLOR.at },
            { label: 'Below market',  value: summary.below_market, color: MI_POS_COLOR.below },
            { label: 'No data',       value: summary.no_data,      color: 'rgba(255,255,255,0.4)' },
          ].map(s => (
            <div key={s.label} className="v2-tile" style={{ padding: '14px 18px', textAlign: 'left' }}>
              <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{s.label}</div>
              <div className="v2-num" style={{ fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1 }}>{(s.value ?? 0).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {/* Manage Competitors slide-out panel */}
      {manageOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }} onClick={() => setManageOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="v2-tile" style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 420,
            display: 'flex', flexDirection: 'column',
            background: 'linear-gradient(180deg, rgba(15,15,22,0.98), rgba(10,10,14,0.98))',
            borderRadius: '20px 0 0 20px', borderRight: 'none',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.6)', padding: 0,
            backdropFilter: 'blur(16px)'
          }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 4 }}>MANAGE</div>
                <div className="v2-serif" style={{ fontSize: 22, color: '#fff' }}>Competitors</div>
                {groupDetail?.primary_dealer && (
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>
                    Store · <span style={{ color: 'rgba(255,255,255,0.85)' }}>{groupDetail.primary_dealer.name}</span>
                  </div>
                )}
              </div>
              <button onClick={() => setManageOpen(false)} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1, width: 32, height: 32, borderRadius: 10 }}>×</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
              {groupDetailLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : !groupDetail ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Failed to load group details.</div>
              ) : (
                <>
                  <div className="v2-mono" style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', marginBottom: 10 }}>
                    ACTIVE · {groupDetail.members.filter(m => !m.is_excluded).length}
                  </div>
                  {groupDetail.members.filter(m => !m.is_excluded).map(m => (
                    <div key={m.member_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.dealer.name}</div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {m.dealer.clean_domain && <span>{m.dealer.clean_domain}</span>}
                          {m.inventory?.total > 0 && <span>{m.inventory.total} vehicles</span>}
                          {m.dealer.distance_miles && <span>{Math.round(m.dealer.distance_miles)} mi</span>}
                        </div>
                      </div>
                      {m.dealer.website && (
                        <a href={m.dealer.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--c3)', textDecoration: 'none', flexShrink: 0 }}>↗</a>
                      )}
                      <button onClick={() => excludeMember(m.member_id)} disabled={excludingId === m.member_id} className="v2-chip"
                        style={{ cursor: excludingId === m.member_id ? 'not-allowed' : 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10, opacity: excludingId === m.member_id ? 0.5 : 1, flexShrink: 0 }}>
                        {excludingId === m.member_id ? '…' : 'Remove'}
                      </button>
                    </div>
                  ))}

                  {groupDetail.members.some(m => m.is_excluded) && (
                    <>
                      <div className="v2-mono" style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', marginTop: 18, marginBottom: 10 }}>
                        EXCLUDED · {groupDetail.members.filter(m => m.is_excluded).length}
                      </div>
                      {groupDetail.members.filter(m => m.is_excluded).map(m => (
                        <div key={m.member_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 10, opacity: 0.6 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.dealer.name}</div>
                            {m.dealer.clean_domain && <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>{m.dealer.clean_domain}</div>}
                          </div>
                          <button onClick={() => reincludeMember(m.member_id)} disabled={excludingId === m.member_id} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.06)', borderColor: 'rgba(166,255,84,0.25)', color: 'var(--c4)', fontSize: 10, opacity: excludingId === m.member_id ? 0.5 : 1, flexShrink: 0 }}>
                            {excludingId === m.member_id ? '…' : 'Re-add'}
                          </button>
                        </div>
                      ))}
                    </>
                  )}

                  {groupDetail.members.filter(m => !m.is_excluded).length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                      No active competitors. Add some below.
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
              <button onClick={() => { setManageOpen(false); window.location.href = '/competitive/setup'; }}
                style={{ width: '100%', background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 8, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
                + Add competitors
              </button>
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                Changes apply to all market data immediately
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main two-panel layout */}
      <div style={{ display: 'flex', flex: 1, minHeight: 540, gap: 14, flexWrap: 'wrap' }}>
        {/* Graph canvas */}
        <div className="v2-tile" style={{
          flex: selectedCluster ? '1 1 58%' : '1 1 100%',
          padding: 0, overflow: 'hidden',
          minHeight: 540,
          minWidth: 320,
          transition: 'flex 0.3s ease',
          position: 'relative',
        }}>
          <div ref={containerRef} style={{ position: 'absolute', inset: 0 }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 14 }}>
                <div className="spinner" />
                <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>ANALYZING MARKET DATA…</span>
              </div>
            ) : !data ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Failed to load market data.</span>
                <button onClick={() => setRefreshKey(k => k + 1)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>Retry</button>
              </div>
            ) : graphData.nodes.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 }}>
                <span className="v2-serif" style={{ fontSize: 22, color: '#fff' }}>No inventory data.</span>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>Try a different group or remove the condition filter.</span>
              </div>
            ) : (
              <>
                <svg ref={svgRef} style={{ display: 'block', width: '100%', height: '100%' }} />
                <div style={{
                  position: 'absolute', bottom: 14, left: 14,
                  display: 'flex', gap: 12,
                  background: 'rgba(10,10,14,0.85)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 12, padding: '8px 14px',
                  backdropFilter: 'blur(12px)'
                }}>
                  {Object.entries(MI_POS_COLOR).map(([pos, col]) => (
                    <div key={pos} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: col }} />
                      <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{MI_POS_LABEL[pos]}</span>
                    </div>
                  ))}
                </div>
                {!selectedCluster && (
                  <div style={{
                    position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(10,10,14,0.85)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 99, padding: '6px 14px', fontSize: 11, color: 'rgba(255,255,255,0.6)',
                    whiteSpace: 'nowrap', pointerEvents: 'none',
                    backdropFilter: 'blur(12px)'
                  }}>
                    <span className="v2-mono">Click a node to inspect pricing intelligence</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selectedCluster && (
          <div className="v2-tile" style={{
            flex: '1 1 38%', minWidth: 320, padding: 0,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden', minHeight: 540
          }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0, gap: 10 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: selectedCluster.color, boxShadow: `0 0 10px ${selectedCluster.color}` }} />
                  <span className="v2-serif" style={{ fontSize: 18, color: '#fff' }}>
                    {selectedCluster.label || [selectedCluster.make, selectedCluster.model].filter(Boolean).join(' ')}
                  </span>
                  <span className="v2-mono" style={{
                    fontSize: 9, padding: '2px 8px', borderRadius: 99, fontWeight: 700,
                    color: selectedCluster.color, background: selectedCluster.color + '18',
                    border: `1px solid ${selectedCluster.color}40`, letterSpacing: '0.06em'
                  }}>{(MI_POS_LABEL[selectedCluster.position] || '').toUpperCase()}</span>
                </div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                  {selectedCluster.count} vehicle{selectedCluster.count !== 1 ? 's' : ''} in cluster
                </div>
              </div>
              <button onClick={() => setSelectedCluster(null)} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 28, height: 28, borderRadius: 8, flexShrink: 0 }}>×</button>
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, padding: 4, gap: 4, background: 'rgba(255,255,255,0.02)' }}>
              {[
                { key: 'pricing',   label: 'Pricing intel' },
                { key: 'simulator', label: 'Simulator' },
              ].map(t => (
                <button key={t.key} onClick={() => setActiveDetailTab(t.key)} style={{
                  flex: 1, padding: '8px', background: activeDetailTab === t.key ? 'rgba(255,255,255,0.07)' : 'transparent',
                  border: activeDetailTab === t.key ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
                  borderRadius: 8,
                  color: activeDetailTab === t.key ? '#fff' : 'rgba(255,255,255,0.45)',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
                }}>{t.label}</button>
              ))}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
              {/* PRICING INTEL */}
              {activeDetailTab === 'pricing' && (() => {
                const clusterVehicles = selectedCluster.vehicles;
                const scored = clusterVehicles.map(v => {
                  const diff = v.recommendation?.price_diff_pct;
                  const dom = v.days_on_market || 0;
                  const priceScore = diff !== undefined && diff !== null
                    ? Math.max(0, Math.min(100, Math.round(60 - diff * 2)))
                    : null;
                  const domBonus = priceScore !== null ? Math.max(0, Math.round((1 - Math.min(dom, 90) / 90) * 10)) : 0;
                  const score = priceScore !== null ? Math.min(100, priceScore + domBonus) : null;
                  return { v, score };
                });
                const ranked = [...scored].sort((a, b) => {
                  if (a.score === null && b.score === null) return 0;
                  if (a.score === null) return 1;
                  if (b.score === null) return -1;
                  return b.score - a.score;
                });
                const rankMap = new Map(ranked.map(({ v }, i) => [v.id, i + 1]));
                const scoreMap = new Map(scored.map(({ v, score }) => [v.id, score]));
                const total = clusterVehicles.length;

                return clusterVehicles.map(vehicle => {
                  const r = vehicle.recommendation;
                  const m = vehicle.market;
                  const rank = rankMap.get(vehicle.id);
                  const score = scoreMap.get(vehicle.id);
                  const priceRange = m ? m.max - m.min : 0;
                  const clamp = (val, lo, hi) => Math.max(lo, Math.min(hi, val));
                  const ourPct = m && priceRange > 0 ? clamp(((vehicle.price - m.min) / priceRange) * 100, 2, 98) : 50;
                  const medPct = m && priceRange > 0 ? clamp(((m.median - m.min) / priceRange) * 100, 2, 98) : 50;
                  const ssBandLeft = m && priceRange > 0 ? clamp(((m.median * 0.95 - m.min) / priceRange) * 100, 0, 100) : 40;
                  const ssBandW = m && priceRange > 0 ? clamp(((m.median * 1.05 - m.median * 0.95) / priceRange) * 100, 0, 100) : 20;
                  return (
                    <div key={vehicle.id} style={{
                      marginBottom: 12, padding: '12px 14px',
                      background: 'rgba(255,255,255,0.02)', borderRadius: 12,
                      border: `1px solid ${r ? positionColor(r.position) + '25' : 'rgba(255,255,255,0.06)'}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, flexWrap: 'wrap', color: '#fff' }}>
                            {score !== null && (() => {
                              const medalColor = rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : rank === 3 ? '#CD7F32' : 'rgba(255,255,255,0.4)';
                              const medalBg = rank === 1 ? 'rgba(255,215,0,0.10)' : rank === 2 ? 'rgba(192,192,192,0.10)' : rank === 3 ? 'rgba(205,127,50,0.10)' : 'rgba(255,255,255,0.05)';
                              const medalBorder = rank === 1 ? 'rgba(255,215,0,0.40)' : rank === 2 ? 'rgba(192,192,192,0.40)' : rank === 3 ? 'rgba(205,127,50,0.40)' : 'rgba(255,255,255,0.10)';
                              return (
                                <span title={`Rank ${rank} of ${total} · Score: ${score}/100`} className="v2-mono" style={{
                                  fontSize: 9, fontWeight: 700, color: medalColor,
                                  background: medalBg, border: `1px solid ${medalBorder}`,
                                  padding: '2px 7px', borderRadius: 6, flexShrink: 0
                                }}>#{rank}</span>
                              );
                            })()}
                            {vehicle.year} {vehicle.make} {vehicle.model}
                            {vehicle.url && (
                              <a href={vehicle.url} target="_blank" rel="noopener noreferrer" className="v2-mono" style={{
                                fontSize: 9, color: 'var(--c2)', textDecoration: 'none',
                                background: 'rgba(122,77,255,0.12)', border: '1px solid rgba(122,77,255,0.30)',
                                padding: '2px 7px', borderRadius: 6, fontWeight: 700, flexShrink: 0
                              }}>VIEW ↗</a>
                            )}
                          </div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>
                            {[vehicle.trim, vehicle.exterior_color, vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} mi` : null, vehicle.condition].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div className="v2-num" style={{ fontSize: 18, fontWeight: 700, color: r ? positionColor(r.position) : '#fff' }}>
                            {formatPrice(vehicle.price)}
                          </div>
                          {m && <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>median {formatPrice(m.median)}</div>}
                        </div>
                      </div>

                      {score !== null && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em' }}>MARKET SCORE</span>
                            <span className="v2-num" style={{ fontSize: 11, fontWeight: 700, color: score >= 70 ? MI_POS_COLOR.below : score >= 45 ? MI_POS_COLOR.at : MI_POS_COLOR.above }}>
                              {score}<span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>/100</span>
                            </span>
                          </div>
                          <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: 99, transition: 'width 0.4s ease',
                              width: `${score}%`,
                              background: score >= 70 ? `linear-gradient(90deg,${MI_POS_COLOR.below},#2ecc71)` : score >= 45 ? `linear-gradient(90deg,#b8860b,${MI_POS_COLOR.at})` : `linear-gradient(90deg,#c0392b,${MI_POS_COLOR.above})`
                            }} />
                          </div>
                        </div>
                      )}

                      {m && priceRange > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ position: 'relative', height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3, marginBottom: 5 }}>
                            <div style={{ position: 'absolute', left: `${ssBandLeft}%`, width: `${ssBandW}%`, height: '100%', background: 'rgba(26,147,111,0.30)', borderRadius: 3 }} />
                            {(vehicle.competitors || []).map((c, i) => {
                              const cPct = clamp(((c.price - m.min) / priceRange) * 100, 0, 100);
                              return <div key={i} title={`${c.dealer_name}: ${formatPrice(c.price)}`} style={{ position: 'absolute', left: `${cPct}%`, top: 0, width: 5, height: 5, borderRadius: '50%', marginLeft: -2.5, background: 'rgba(34,211,238,0.7)' }} />;
                            })}
                            <div style={{ position: 'absolute', left: `${medPct}%`, top: -2, width: 2, height: 10, background: MI_POS_COLOR.at, borderRadius: 1 }} />
                            <div style={{ position: 'absolute', left: `${ourPct}%`, top: -4, width: 14, height: 14, borderRadius: '50%', marginLeft: -7, background: r ? positionColor(r.position) : 'rgba(255,255,255,0.5)', border: '2px solid #0d0d1a', zIndex: 2 }} />
                          </div>
                          <div className="v2-mono" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
                            <span>{formatPrice(m.min)}</span>
                            <span style={{ color: MI_POS_COLOR.below }}>sweet ±5%</span>
                            <span>{formatPrice(m.max)}</span>
                          </div>
                        </div>
                      )}

                      {(vehicle.competitors || []).length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                          {vehicle.competitors.slice(0, 8).map((c, i) => (
                            <CompetitorChip key={i} competitor={c} ourPrice={vehicle.price} />
                          ))}
                          {vehicle.competitors.length > 8 && <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', alignSelf: 'center' }}>+{vehicle.competitors.length - 8}</span>}
                        </div>
                      )}

                      {r && (
                        <div style={{
                          padding: '8px 12px', borderRadius: 8, fontSize: 11,
                          background: positionBg(r.position), border: `1px solid ${positionColor(r.position)}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
                        }}>
                          <span style={{ color: positionColor(r.position), fontWeight: 500 }}>{r.action}</span>
                          <button onClick={() => { setSimVehicle(vehicle); setSimPrice(vehicle.price); setActiveDetailTab('simulator'); }}
                            className="v2-mono"
                            style={{ background: 'rgba(122,77,255,0.10)', border: '1px solid rgba(122,77,255,0.30)', cursor: 'pointer', color: '#c4b5fd', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', padding: '3px 9px', borderRadius: 6 }}>
                            Simulate →
                          </button>
                        </div>
                      )}
                      {!m && <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>No competitor matches - unique in your market</div>}
                    </div>
                  );
                });
              })()}

              {/* SIMULATOR */}
              {activeDetailTab === 'simulator' && (() => {
                const clusterVehiclesWithMarket = selectedCluster.vehicles.filter(v => v.market);
                const sv = simVehicle || clusterVehiclesWithMarket[0];
                if (!sv?.market) return (
                  <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                    No vehicles in this cluster have market data yet.
                  </div>
                );
                if (!simVehicle && sv) { setSimVehicle(sv); setSimPrice(sv.price); return null; }
                const { median, min, max } = sv.market;
                const slMin = Math.round(min * 0.82), slMax = Math.round(max * 1.18);
                const price = simPrice || sv.price;
                const sim = computeSim(sv, price);
                if (!sim) return null;
                const diffPct = sim.diffPct;
                const priceColor = diffPct > 10 ? MI_POS_COLOR.above : diffPct < -5 ? MI_POS_COLOR.below : MI_POS_COLOR.at;
                const dayColor = sim.estDays < 25 ? MI_POS_COLOR.below : sim.estDays > 60 ? MI_POS_COLOR.above : MI_POS_COLOR.at;
                const SVG_W = 320, SVG_H = 90;
                const curvePoints = genCurvePoints(sv, SVG_W, SVG_H);
                const cursorX = Math.max(0, Math.min(SVG_W, ((price - slMin) / (slMax - slMin)) * SVG_W));
                const medX = Math.max(0, Math.min(SVG_W, ((median - slMin) / (slMax - slMin)) * SVG_W));

                return (
                  <div>
                    {clusterVehiclesWithMarket.length > 1 && (
                      <select value={sv.id} onChange={e => { const v = clusterVehiclesWithMarket.find(x => String(x.id) === e.target.value); if (v) { setSimVehicle(v); setSimPrice(v.price); } }}
                        style={{ ...inp, width: '100%', marginBottom: 14, cursor: 'pointer' }}>
                        {clusterVehiclesWithMarket.map(v => <option key={v.id} value={v.id} style={{ background: '#111' }}>{v.year} {v.make} {v.model} - {formatPrice(v.price)}</option>)}
                      </select>
                    )}

                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <div className="v2-num" style={{ fontSize: 36, fontWeight: 700, color: priceColor }}>{formatPrice(price)}</div>
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                        vs median {formatPrice(median)} <span style={{ color: priceColor }}>({diffPct > 0 ? '+' : ''}{diffPct}%)</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <input type="range" min={slMin} max={slMax} step={100} value={price}
                        onChange={e => setSimPrice(parseInt(e.target.value))}
                        style={{ width: '100%', accentColor: priceColor }} />
                      <div style={{ position: 'relative', height: 18, marginTop: 2 }}>
                        <div className="v2-mono" style={{ position: 'absolute', left: `${((median - slMin) / (slMax - slMin)) * 100}%`, transform: 'translateX(-50%)', fontSize: 9, color: MI_POS_COLOR.at, whiteSpace: 'nowrap' }}>▲ Median</div>
                        <div className="v2-mono" style={{ position: 'absolute', left: `${((median * 0.97 - slMin) / (slMax - slMin)) * 100}%`, transform: 'translateX(-50%)', fontSize: 9, color: MI_POS_COLOR.below, whiteSpace: 'nowrap', top: 9 }}>★ Sweet</div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 12 }}>
                      {[
                        { label: 'Days to sell', value: sim.estDays + 'd', color: dayColor },
                        { label: 'Undercut by', value: sim.below, color: sim.below > 3 ? MI_POS_COLOR.above : MI_POS_COLOR.below },
                        { label: 'vs sweet spot', value: (sim.revImpact > 0 ? '+' : '') + formatPrice(sim.revImpact), color: sim.revImpact > 0 ? MI_POS_COLOR.above : MI_POS_COLOR.below },
                      ].map(mc => (
                        <div key={mc.label} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px 8px', textAlign: 'center' }}>
                          <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{mc.label}</div>
                          <div className="v2-num" style={{ fontSize: 18, fontWeight: 800, color: mc.color }}>{mc.value}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                      <div className="v2-mono" style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 8, letterSpacing: '0.06em' }}>PRICE → DAYS-TO-SELL</div>
                      <svg width="100%" viewBox={`0 0 ${SVG_W + 24} ${SVG_H + 18}`} style={{ overflow: 'visible' }}>
                        {[25, 50, 75, 100].map(d => {
                          const y = SVG_H - (d / 100) * SVG_H;
                          return <g key={d}>
                            <line x1="0" y1={y} x2={SVG_W} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={0.5} strokeDasharray="3,3" />
                            <text x={SVG_W + 3} y={y + 3} fontSize="7" fill="rgba(255,255,255,0.35)">{d}d</text>
                          </g>;
                        })}
                        {curvePoints.length > 1 && (
                          <polygon points={`0,${SVG_H} ${curvePoints.map(p => `${p.x},${p.y}`).join(' ')} ${SVG_W},${SVG_H}`}
                            fill={diffPct > 10 ? 'rgba(255,107,53,0.10)' : diffPct < -5 ? 'rgba(26,147,111,0.10)' : 'rgba(233,196,106,0.10)'} />
                        )}
                        {curvePoints.length > 1 && (
                          <polyline points={curvePoints.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="none" stroke={priceColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                        )}
                        <line x1={medX} y1="0" x2={medX} y2={SVG_H} stroke={MI_POS_COLOR.at} strokeWidth={1} strokeDasharray="3,3" />
                        <line x1={cursorX} y1="0" x2={cursorX} y2={SVG_H} stroke="rgba(255,255,255,0.4)" strokeWidth={1.5} strokeDasharray="3,3" />
                        <circle cx={cursorX} cy={SVG_H - (sim.estDays / 100) * SVG_H} r={5} fill={dayColor} stroke="#0a0a0e" strokeWidth={2} />
                        <text x="0" y={SVG_H + 13} fontSize="7" fill="rgba(255,255,255,0.35)">{formatPrice(slMin)}</text>
                        <text x={SVG_W - 28} y={SVG_H + 13} fontSize="7" fill="rgba(255,255,255,0.35)">{formatPrice(slMax)}</text>
                      </svg>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 12 }}>
                      {[
                        { label: 'Sweet spot',    price: Math.round(median * 0.97), color: MI_POS_COLOR.below },
                        { label: 'Market median', price: median,                    color: MI_POS_COLOR.at },
                        { label: '+5% premium',   price: Math.round(median * 1.05), color: MI_POS_COLOR.above },
                        { label: 'Max gross',     price: Math.round(median * 1.12), color: '#c084fc' },
                      ].map(qp => (
                        <button key={qp.label} onClick={() => setSimPrice(qp.price)} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '8px 12px', background: 'rgba(255,255,255,0.02)', cursor: 'pointer',
                          border: `1px solid ${price === qp.price ? qp.color : 'rgba(255,255,255,0.06)'}`,
                          borderRadius: 10, fontFamily: 'inherit'
                        }}>
                          <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>{qp.label}</span>
                          <span className="v2-num" style={{ fontSize: 12, fontWeight: 700, color: qp.color }}>{formatPrice(qp.price)}</span>
                        </button>
                      ))}
                    </div>

                    <div style={{
                      padding: '12px 14px', borderRadius: 10, fontSize: 12, lineHeight: 1.5,
                      background: positionBg(diffPct > 10 ? 'above' : diffPct < -5 ? 'below' : 'at'),
                      border: `1px solid ${priceColor}30`, color: 'rgba(255,255,255,0.85)'
                    }}>
                      {diffPct > 10 && `⚠ At ${formatPrice(price)}, you're ${diffPct}% above market. ${sim.below} competitor${sim.below !== 1 ? 's' : ''} undercut you. Drop $${(price - sim.sweetPrice).toLocaleString()} to hit the sweet spot and sell ~${Math.round(45 * 0.7)} days faster.`}
                      {diffPct <= 10 && diffPct >= -5 && `✓ ${formatPrice(price)} is competitive. ${sim.below} competitor${sim.below !== 1 ? 's' : ''} price lower, ${sim.above} price higher. Estimated ${sim.estDays} days to sell.${sim.revImpact > 500 ? ` Trim $${sim.revImpact.toLocaleString()} to hit the sweet spot.` : ' You are at or near the optimal price point.'}`}
                      {diffPct < -5 && `🔥 Priced to move - ${Math.abs(diffPct)}% below market. Estimated ${sim.estDays} days to sell. You're leaving $${Math.abs(sim.revImpact).toLocaleString()} on the table vs sweet spot ${formatPrice(sim.sweetPrice)}.`}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportsPage() {
  const [dealers, setDealers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(null);
  const [generating, setGenerating] = useState(null);

  const [form, setForm] = useState({
    dealer_id: '', cadence: 'weekly', day_of_week: 1, day_of_month: 1,
    send_hour: 8, recipients: '', include_summary: true, is_active: true
  });

  const DOW_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function loadData() {
    setLoading(true);
    Promise.all([
      api('/my-dealers').catch(() => ({ dealers: [] })),
      api('/reports/schedules').catch(() => ({ schedules: [] }))
    ]).then(([dRes, sRes]) => {
      setDealers(Array.isArray(dRes.dealers || dRes) ? (dRes.dealers || dRes) : (dRes.dealers || []));
      setSchedules(sRes.schedules || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { loadData(); }, []);

  function openCreate() {
    setEditingSchedule(null);
    setForm({ dealer_id: dealers[0]?.id || '', cadence: 'weekly', day_of_week: 1, day_of_month: 1, send_hour: 8, recipients: '', include_summary: true, is_active: true });
    setShowForm(true);
  }

  function openEdit(s) {
    setEditingSchedule(s);
    setForm({
      dealer_id: s.dealer_id, cadence: s.cadence,
      day_of_week: s.day_of_week ?? 1, day_of_month: s.day_of_month ?? 1,
      send_hour: s.send_hour ?? 8,
      recipients: (s.recipients || []).join(', '),
      include_summary: s.include_summary ?? true, is_active: s.is_active ?? true
    });
    setShowForm(true);
  }

  async function saveSchedule() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        dealer_id: parseInt(form.dealer_id),
        recipients: form.recipients.split(',').map(e => e.trim()).filter(Boolean)
      };
      if (editingSchedule) {
        await api(`/reports/schedules/${editingSchedule.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/reports/schedules', { method: 'POST', body: JSON.stringify(payload) });
      }
      setShowForm(false);
      loadData();
    } catch (e) {}
    setSaving(false);
  }

  async function deleteSchedule(id) {
    if (!window.confirm('Delete this schedule?')) return;
    await api(`/reports/schedules/${id}`, { method: 'DELETE' }).catch(() => {});
    loadData();
  }

  async function sendNow(id) {
    setSendingNow(id);
    try { await api(`/reports/schedules/${id}/send-now`, { method: 'POST' }); } catch (e) {}
    setSendingNow(null);
  }

  async function generateNow(dealerId) {
    setGenerating(dealerId);
    try { await api(`/reports/${dealerId}/generate`, { method: 'POST' }); } catch (e) {}
    setGenerating(null);
  }

  const inputStyle = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  function fmtRelative(ts) {
    if (!ts) return null;
    const diffMs = new Date(ts).getTime() - Date.now();
    const m = Math.floor(diffMs / 60000);
    if (Math.abs(m) < 60) return diffMs >= 0 ? `in ${m}m` : `${-m}m ago`;
    const h = Math.floor(m / 60);
    if (Math.abs(h) < 24) return diffMs >= 0 ? `in ${h}h` : `${-h}h ago`;
    const d = Math.floor(h / 24);
    if (Math.abs(d) < 7) return diffMs >= 0 ? `in ${d}d` : `${-d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // KPI math
  const activeSchedules = schedules.filter(s => s.is_active).length;
  const weeklyCount = schedules.filter(s => s.cadence === 'weekly').length;
  const monthlyCount = schedules.filter(s => s.cadence === 'monthly').length;
  const totalRecipients = schedules.reduce((sum, s) => sum + ((s.recipients || []).length), 0);

  return (
    <div className="page-content">
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          REPORTS · {schedules.length} {schedules.length === 1 ? 'SCHEDULE' : 'SCHEDULES'}{activeSchedules ? ` · ${activeSchedules} ACTIVE` : ''}{totalRecipients ? ` · ${totalRecipients} RECIPIENTS` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Reports.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Ad efficiency, auto-delivered.
            </span>
          </h1>
          <button onClick={openCreate} disabled={!dealers.length} className="v2-chip"
                  style={{ cursor: dealers.length ? 'pointer' : 'not-allowed', opacity: dealers.length ? 1 : 0.5, background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
            <Icons.Plus /> New schedule
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING SCHEDULES…</span>
        </div>
      ) : (
        <>
          {/* KPIs */}
          {schedules.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 22 }}>
              <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>scheduled reports</div>
                <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>{schedules.length.toLocaleString()}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                  {activeSchedules} active · {schedules.length - activeSchedules} paused
                </div>
              </div>

              <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>cadence mix</div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                  <div>
                    <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c3)' }}>WEEKLY</div>
                    <div className="v2-num" style={{ fontSize: 24, fontWeight: 500, color: '#fff' }}>{weeklyCount}</div>
                  </div>
                  <div>
                    <div className="v2-mono" style={{ fontSize: 9, color: 'var(--c2)' }}>MONTHLY</div>
                    <div className="v2-num" style={{ fontSize: 24, fontWeight: 500, color: '#c4b5fd' }}>{monthlyCount}</div>
                  </div>
                </div>
              </div>

              <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>recipients</div>
                <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>{totalRecipients.toLocaleString()}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>email addresses</div>
              </div>

              <div className="v2-tile" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>dealers covered</div>
                <div className="v2-num" style={{ fontSize: 44, fontWeight: 300, color: '#fff' }}>{new Set(schedules.map(s => s.dealer_id)).size}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>of {dealers.length} total</div>
              </div>
            </div>
          )}

          {/* Schedules */}
          {schedules.length === 0 ? (
            <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
              <div className="v2-serif" style={{ fontSize: 28, color: '#fff', marginBottom: 10 }}>
                No report schedules yet.
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>
                Create a schedule to auto-email ad efficiency reports to your clients.
              </div>
              <button onClick={openCreate} disabled={!dealers.length}
                      style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: dealers.length ? 'pointer' : 'not-allowed', opacity: dealers.length ? 1 : 0.5, boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
                <Icons.Plus /> New schedule
              </button>
            </div>
          ) : (
            <div className="v2-tile" style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {['Dealer', 'Cadence', 'Send time', 'Recipients', 'Next send', 'Status', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '12px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((s, idx) => (
                      <tr key={s.id} style={{
                        borderBottom: idx === schedules.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.04)',
                        opacity: s.is_active ? 1 : 0.6,
                        transition: 'background 0.15s'
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{s.dealer_name || '-'}</div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span className="v2-chip" style={{ background: s.cadence === 'weekly' ? 'rgba(34,211,238,0.10)' : 'rgba(122,77,255,0.15)', borderColor: 'transparent', color: s.cadence === 'weekly' ? 'var(--c3)' : '#c4b5fd', fontSize: 10 }}>
                            {s.cadence === 'weekly' ? `Weekly · ${DOW_LABELS[s.day_of_week ?? 1]}` : `Monthly · day ${s.day_of_month ?? 1}`}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
                            {String(s.send_hour ?? 8).padStart(2, '0')}:00 UTC
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', maxWidth: 220 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {(s.recipients || []).slice(0, 2).map((r, i) => (
                              <span key={i} className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.04)', padding: '2px 6px', borderRadius: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                                {r}
                              </span>
                            ))}
                            {(s.recipients || []).length > 2 && (
                              <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', alignSelf: 'center' }}>
                                +{(s.recipients || []).length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span className="v2-mono" style={{ fontSize: 11, color: s.next_send_at ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.25)' }}>
                            {s.next_send_at ? (fmtRelative(s.next_send_at) || new Date(s.next_send_at).toLocaleDateString()) : '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {s.is_active ? (
                            <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                              <span className="v2-pulse-dot" /> Active
                            </span>
                          ) : (
                            <span className="v2-chip" style={{ background: 'rgba(255,200,87,0.10)', borderColor: 'rgba(255,200,87,0.25)', color: '#fde68a', fontSize: 10 }}>
                              Paused
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => sendNow(s.id)} disabled={sendingNow === s.id} className="v2-chip"
                                    style={{ cursor: 'pointer', background: 'rgba(122,77,255,0.10)', borderColor: 'rgba(122,77,255,0.30)', color: '#c4b5fd', fontSize: 10, opacity: sendingNow === s.id ? 0.5 : 1 }}>
                              {sendingNow === s.id ? '…' : 'Send now'}
                            </button>
                            <button onClick={() => openEdit(s)} className="v2-chip"
                                    style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10 }}>
                              Edit
                            </button>
                            <button onClick={() => deleteSchedule(s.id)} className="v2-chip"
                                    title="Delete schedule"
                                    style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10 }}>
                              <Icons.Trash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* On-demand generation */}
          {dealers.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em' }}>ON-DEMAND</div>
                <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, rgba(255,255,255,0.08), transparent)' }} />
              </div>
              <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 14 }}>
                Generate now <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.55)' }}>- skip the schedule.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                {dealers.map(d => (
                  <div key={d.id} className="v2-tile" style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                      {(d.city || d.state) && (
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                          {[d.city, d.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                    <button onClick={() => generateNow(d.id)} disabled={generating === d.id} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 11, opacity: generating === d.id ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                      {generating === d.id ? 'Generating…' : 'Generate'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
             onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="v2-tile" style={{ width: 520, maxWidth: '92vw', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, letterSpacing: '0.06em' }}>
              {editingSchedule ? 'EDITING SCHEDULE' : 'NEW SCHEDULE'}
            </div>
            <h2 className="v2-serif" style={{ fontSize: 26, fontWeight: 400, color: '#fff', margin: 0, marginBottom: 22 }}>
              {editingSchedule ? 'Edit report schedule' : 'New report schedule'}
            </h2>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={labelStyle}>Dealer</label>
                <select value={form.dealer_id} onChange={e => setForm(f => ({ ...f, dealer_id: e.target.value }))} style={inputStyle}>
                  {dealers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Cadence</label>
                  <select value={form.cadence} onChange={e => setForm(f => ({ ...f, cadence: e.target.value }))} style={inputStyle}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {form.cadence === 'weekly' ? (
                  <div>
                    <label style={labelStyle}>Day of week</label>
                    <select value={form.day_of_week} onChange={e => setForm(f => ({ ...f, day_of_week: parseInt(e.target.value) }))} style={inputStyle}>
                      {DOW_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label style={labelStyle}>Day of month</label>
                    <select value={form.day_of_month} onChange={e => setForm(f => ({ ...f, day_of_month: parseInt(e.target.value) }))} style={inputStyle}>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Send hour (UTC)</label>
                <select value={form.send_hour} onChange={e => setForm(f => ({ ...f, send_hour: parseInt(e.target.value) }))} style={inputStyle}>
                  {Array.from({ length: 24 }, (_, i) => i).map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Recipients</label>
                <input type="text" value={form.recipients} onChange={e => setForm(f => ({ ...f, recipients: e.target.value }))}
                       placeholder="client@example.com, another@example.com" style={inputStyle} />
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>comma-separated email addresses</div>
              </div>

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  <input type="checkbox" checked={form.include_summary} onChange={e => setForm(f => ({ ...f, include_summary: e.target.checked }))} style={{ accentColor: 'var(--c3)' }} />
                  Include summary
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} style={{ accentColor: 'var(--c4)' }} />
                  Active
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} className="v2-chip"
                      style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>
                Cancel
              </button>
              <button onClick={saveSchedule} disabled={saving}
                      style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: saving ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                {saving ? 'Saving…' : editingSchedule ? 'Save changes' : 'Create schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsPage() {
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const tab = urlTab || 'aging';
  const [agingData, setAgingData] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loadingAging, setLoadingAging] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [dismissing, setDismissing] = useState(null);
  const [marking, setMarking] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (tab === 'aging') loadAging();
    if (tab === 'price') loadPriceChanges();
  }, [tab]);

  function loadAging() {
    setLoadingAging(true);
    api('/dealers/aging-alerts').then(res => {
      setAgingData(res.data || res.alerts || []);
    }).catch(() => {}).finally(() => setLoadingAging(false));
  }

  function loadPriceChanges() {
    setLoadingPrice(true);
    api('/my-dealers').then(res => {
      const list = res.dealers || res || [];
      setDealers(list);
      return Promise.all(list.map(d => api(`/dealers/${d.id}/alerts/price-changes`).then(r => ({ dealerId: d.id, data: r.data || r.alerts || (Array.isArray(r) ? r : []) })).catch(() => ({ dealerId: d.id, data: [] }))));
    }).then(results => {
      const all = results.flatMap(r => r.data.map(a => ({ ...a, dealerId: r.dealerId })));
      setPriceChanges(all);
      setUnreadCount(all.filter(a => !a.read_at && !a.is_read).length);
    }).catch(() => {}).finally(() => setLoadingPrice(false));
  }

  async function dismissAlert(dealerId, alertId) {
    setDismissing(alertId);
    try {
      await api(`/dealers/${dealerId}/aging/alerts/${alertId}/dismiss`, { method: 'POST' });
      setAgingData(prev => prev.filter(a => a.id !== alertId && a.alert_id !== alertId));
    } catch (e) {}
    setDismissing(null);
  }

  async function markRead(dealerId, alertId) {
    setMarking(alertId);
    try {
      await api(`/dealers/${dealerId}/alerts/price-changes/${alertId}/read`, { method: 'POST' });
      setPriceChanges(prev => prev.map(a => (a.id === alertId || a.alert_id === alertId) ? { ...a, is_read: true, read_at: new Date().toISOString() } : a));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (e) {}
    setMarking(null);
  }

  function severityChip(severity) {
    const map = {
      critical: { bg: 'rgba(255,91,164,0.12)', border: 'rgba(255,91,164,0.30)', fg: 'var(--c1)', label: 'CRITICAL' },
      warning:  { bg: 'rgba(255,200,87,0.12)', border: 'rgba(255,200,87,0.30)', fg: 'var(--c5)', label: 'WARNING' },
      info:     { bg: 'rgba(34,211,238,0.12)', border: 'rgba(34,211,238,0.30)', fg: 'var(--c3)', label: 'INFO' },
    };
    const s = map[severity] || map.info;
    return <span className="v2-mono" style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}`, borderRadius: 6, padding: '1px 7px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>{s.label}</span>;
  }

  const agingByDealer = useMemo(() => {
    const groups = {};
    (agingData || []).forEach(a => {
      const key = a.dealer_id || a.dealerId || 'unknown';
      if (!groups[key]) groups[key] = { name: a.dealer_name || a.dealerName || `Dealer ${key}`, alerts: [] };
      groups[key].alerts.push(a);
    });
    return Object.values(groups);
  }, [agingData]);

  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  const totalAging = agingData.length;
  const criticalAging = agingData.filter(a => a.severity === 'critical').length;
  const warningAging = agingData.filter(a => a.severity === 'warning').length;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          ALERTS · {tab === 'aging' ? `${totalAging} ${totalAging === 1 ? 'AGING ALERT' : 'AGING ALERTS'}` : `${priceChanges.length} PRICE CHANGES${unreadCount ? ` · ${unreadCount} UNREAD` : ''}`}
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Inbox.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Aging stock and rivals' price moves.
          </span>
        </h1>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 22, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
        {[
          { id: 'aging', label: 'Aging inventory' },
          { id: 'price', label: 'Price changes', badge: unreadCount > 0 ? unreadCount : null }
        ].map(t => (
          <button key={t.id} onClick={() => navigate(`/alerts/${t.id}`)}
            style={{
              whiteSpace: 'nowrap',
              background: tab === t.id ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: tab === t.id ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: tab === t.id ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.15s'
            }}>
            {t.label}
            {t.badge && (
              <span className="v2-mono" style={{ background: 'rgba(255,91,164,0.15)', color: 'var(--c1)', borderRadius: 99, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* AGING TAB */}
      {tab === 'aging' && (
        loadingAging ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
            <div className="spinner" />
            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING AGING ALERTS…</span>
          </div>
        ) : agingByDealer.length === 0 ? (
          <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
            <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>All clear.</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Vehicles are moving on time. New alerts appear when inventory ages past configured thresholds.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
              <div className={`v2-tile ${criticalAging > 0 ? 'v2-tile-glow' : ''}`} style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>critical</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: criticalAging > 0 ? 'var(--c1)' : '#fff' }}>{criticalAging}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>over 60 days on lot</div>
              </div>
              <div className="v2-tile v2-tile-amber" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>warning</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c5)' }}>{warningAging}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>aging - needs eyes</div>
              </div>
              <div className="v2-tile" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>total alerts</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{totalAging}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>across {agingByDealer.length} {agingByDealer.length === 1 ? 'dealer' : 'dealers'}</div>
              </div>
            </div>

            {agingByDealer.map(group => {
              const critical = group.alerts.filter(a => a.severity === 'critical').length;
              const warning = group.alerts.filter(a => a.severity === 'warning').length;
              return (
                <div key={group.name} style={{ marginBottom: 22 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <div className="v2-serif" style={{ fontSize: 18, color: '#fff' }}>{group.name}</div>
                    {critical > 0 && severityChip('critical')}
                    {warning > 0 && severityChip('warning')}
                  </div>
                  <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                        <thead><tr>{['VIN', 'Vehicle', 'Days on lot', 'Severity', 'Action', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {group.alerts.map(a => {
                            const id = a.id || a.alert_id;
                            const days = a.days_on_lot;
                            const dayColor = days >= 90 ? 'var(--c1)' : days >= 60 ? 'var(--c5)' : 'rgba(255,255,255,0.7)';
                            return (
                              <tr key={id}>
                                <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{a.vin || '-'}</td>
                                <td style={{ ...td, color: '#fff' }}>{[a.year, a.make, a.model].filter(Boolean).join(' ') || a.vehicle || '-'}</td>
                                <td style={td}>
                                  <span className="v2-num" style={{ fontSize: 18, fontWeight: 700, color: dayColor }}>{days ?? '-'}</span>
                                  {days != null && <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>days</span>}
                                </td>
                                <td style={td}>{severityChip(a.severity)}</td>
                                <td style={{ ...td, fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{a.action || a.action_text || '-'}</td>
                                <td style={td}>
                                  <button onClick={() => dismissAlert(a.dealer_id || a.dealerId, id)} disabled={dismissing === id} className="v2-chip"
                                    style={{ cursor: dismissing === id ? 'not-allowed' : 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10, opacity: dismissing === id ? 0.5 : 1 }}>
                                    {dismissing === id ? 'Dismissing…' : '✕ Dismiss'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )
      )}

      {/* PRICE TAB */}
      {tab === 'price' && (
        loadingPrice ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
            <div className="spinner" />
            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING PRICE CHANGES…</span>
          </div>
        ) : priceChanges.length === 0 ? (
          <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
            <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No price moves yet.</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Competitor price changes in your market will appear here as they're detected.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
              <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>total moves</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{priceChanges.length}</div>
              </div>
              <div className={`v2-tile ${unreadCount > 0 ? 'v2-tile-glow' : ''}`} style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>unread</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: unreadCount > 0 ? 'var(--c1)' : '#fff' }}>{unreadCount}</div>
              </div>
              <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>price drops</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c4)' }}>{priceChanges.filter(a => ((a.new_price || a.price_new || 0) - (a.old_price || a.was_price || a.price_old || 0)) < 0).length}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>competitors discounting</div>
              </div>
            </div>

            <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>{['Competitor', 'Vehicle', 'Was', 'Now', 'Delta', 'Your VIN', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {priceChanges.map(a => {
                      const id = a.id || a.alert_id;
                      const oldP = a.old_price || a.was_price || a.price_old || 0;
                      const newP = a.new_price || a.price_new || 0;
                      const delta = newP - oldP;
                      const isRead = a.is_read || a.read_at;
                      const pct = oldP > 0 ? Math.abs(Math.round(delta / oldP * 100)) : 0;
                      return (
                        <tr key={id} style={{ opacity: isRead ? 0.55 : 1 }}>
                          <td style={{ ...td, color: '#fff', fontWeight: 600 }}>{a.competitor_name || a.dealerName || '-'}</td>
                          <td style={{ ...td, color: 'rgba(255,255,255,0.85)' }}>{[a.year, a.make, a.model].filter(Boolean).join(' ') || a.vehicle || '-'}</td>
                          <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', fontSize: 12 }}>${oldP.toLocaleString()}</td>
                          <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: '#fff' }}>${newP.toLocaleString()}</td>
                          <td style={td}>
                            <span className="v2-mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: delta < 0 ? 'rgba(166,255,84,0.10)' : 'rgba(255,200,87,0.12)', border: `1px solid ${delta < 0 ? 'rgba(166,255,84,0.30)' : 'rgba(255,200,87,0.30)'}`, borderRadius: 99, padding: '2px 9px', fontSize: 11, fontWeight: 700, color: delta < 0 ? 'var(--c4)' : 'var(--c5)' }}>
                              {delta < 0 ? '▼' : '▲'} ${Math.abs(delta).toLocaleString()}
                              {pct > 0 && <span style={{ opacity: 0.7 }}>· {pct}%</span>}
                            </span>
                          </td>
                          <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{a.matching_vin || a.your_vin || '-'}</td>
                          <td style={td}>
                            {!isRead && (
                              <button onClick={() => markRead(a.dealerId, id)} disabled={marking === id} className="v2-chip"
                                style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10, opacity: marking === id ? 0.5 : 1 }}>
                                {marking === id ? '…' : 'Mark read'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

function AlertThresholdsModal({ dealer, onClose, onSaved }) {
  const [warningDays, setWarningDays] = useState(45);
  const [criticalDays, setCriticalDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api(`/dealers/${dealer.id}/aging/config`)
      .then(res => {
        const cfg = res.data || [];
        const warn = cfg.find(c => c.severity === 'warning');
        const crit = cfg.find(c => c.severity === 'critical');
        if (warn) setWarningDays(warn.threshold_days);
        if (crit) setCriticalDays(crit.threshold_days);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealer.id]);

  async function handleSave() {
    setSaving(true);
    try {
      await api(`/dealers/${dealer.id}/aging/config`, {
        method: 'PUT',
        body: JSON.stringify([
          { threshold_days: parseInt(warningDays), action_text: 'Recommend aggressive pricing or increased ad spend.', severity: 'warning' },
          { threshold_days: parseInt(criticalDays), action_text: 'Evaluate for wholesale/auction.', severity: 'critical' }
        ])
      });
      onSaved && onSaved();
      onClose();
    } catch (e) {}
    setSaving(false);
  }

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '11px 14px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: "'JetBrains Mono', monospace", outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="v2-tile" style={{ width: 480, maxWidth: '92vw', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>ALERT THRESHOLDS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, gap: 12 }}>
          <h2 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: 0 }}>{dealer.name}</h2>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING…</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ background: 'rgba(255,200,87,0.06)', border: '1px solid rgba(255,200,87,0.20)', borderRadius: 12, padding: '14px 16px' }}>
              <label style={labelStyle}><span style={{ color: 'var(--c5)' }}>WARNING</span> · threshold (days)</label>
              <input type="number" min={1} value={warningDays} onChange={e => setWarningDays(e.target.value)} style={inp} />
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>recommend aggressive pricing or increased ad spend</div>
            </div>
            <div style={{ background: 'rgba(255,91,164,0.06)', border: '1px solid rgba(255,91,164,0.20)', borderRadius: 12, padding: '14px 16px' }}>
              <label style={labelStyle}><span style={{ color: 'var(--c1)' }}>CRITICAL</span> · threshold (days)</label>
              <input type="number" min={1} value={criticalDays} onChange={e => setCriticalDays(e.target.value)} style={inp} />
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>evaluate for wholesale / auction</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="v2-chip" style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || loading}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (saving || loading) ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
            {saving ? 'Saving…' : 'Save thresholds'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferBuilderPage() {
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState('');
  const [count, setCount] = useState(6);
  const [suggestions, setSuggestions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [tray, setTray] = useState([]);
  const [campaignName, setCampaignName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api('/specials/dealers?group=cochran').then(d => setDealers(d.dealers || [])).catch(() => {}); }, []);

  const dealer = dealers.find(d => String(d.id) === String(dealerId));
  const money = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
  const headline = (o) => o.offer_type === 'lease' && o.lease_payment ? money(o.lease_payment) + '/mo'
    : o.offer_type === 'apr' && o.apr != null ? Number(o.apr).toFixed(Number(o.apr) % 1 ? 1 : 0) + '% APR'
    : o.offer_type === 'cash' && o.cash_amount ? money(o.cash_amount) + ' cash' : '—';
  const TYPE_COLOR = { lease: 'var(--c3)', apr: 'var(--c4)', cash: 'var(--c5)', other: '#c084fc' };

  const getPicks = async () => {
    if (!dealerId) return;
    setLoadingSuggest(true); setSuggestions([]); setCandidates([]); setCreatedLink(null);
    try {
      const d = await api(`/specials/campaigns/suggest?domain_id=${dealerId}&count=${count}`);
      setSuggestions(d.suggestions || []);
      setCandidates(d.scored_top || []);
      setTray((d.suggestions || []).map(s => ({ ...s, targetPayment: '', preview: null })));
      if (dealer) setCampaignName(`${dealer.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\.com$/, '')} — specials`);
    } finally { setLoadingSuggest(false); }
  };

  const inTray = (id) => tray.some(t => t.offer_id === id);
  const addToTray = (o) => { if (!inTray(o.offer_id)) setTray(t => [...t, { ...o, targetPayment: '', preview: null }]); };
  const removeFromTray = (id) => setTray(t => t.filter(x => x.offer_id !== id));

  const recalc = async (id, target) => {
    setTray(t => t.map(x => x.offer_id === id ? { ...x, targetPayment: target } : x));
    if (!target) { setTray(t => t.map(x => x.offer_id === id ? { ...x, preview: null } : x)); return; }
    try {
      const d = await api(`/specials/campaigns/preview?offer_id=${id}&target_payment=${target}`);
      setTray(t => t.map(x => x.offer_id === id ? { ...x, preview: d } : x));
    } catch {}
  };

  const createCampaign = async () => {
    if (!dealerId || !tray.length) return;
    setCreating(true);
    try {
      const camp = await api('/specials/campaigns', { method: 'POST', body: JSON.stringify({ groupKey: 'cochran', dealerDomainId: Number(dealerId), name: campaignName || 'Untitled campaign', periodLabel: '' }) });
      for (const item of tray) {
        const added = await api('/specials/campaigns/items/add', { method: 'POST', body: JSON.stringify({ campaignId: camp.id, offerId: item.offer_id, aiScore: item.ai_score, aiReason: item.ai_reason, qualifyingUnits: item.qualifying_units }) });
        if (item.targetPayment) await api('/specials/campaigns/items/edit', { method: 'POST', body: JSON.stringify({ itemId: added.id, targetPayment: Number(item.targetPayment) }) });
      }
      await api('/specials/campaigns/send', { method: 'POST', body: JSON.stringify({ id: camp.id }) });
      setCreatedLink(`${window.location.origin}/api/specials/approve-page?token=${camp.public_token}`);
    } finally { setCreating(false); }
  };

  const copyLink = () => { try { navigator.clipboard.writeText(createdLink); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} };

  const sel = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '9px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' };
  const btn = (active) => ({ background: active ? 'var(--c4, #5eead4)' : 'rgba(255,255,255,0.05)', color: active ? '#0a0a0f' : '#fff', border: '1px solid ' + (active ? 'transparent' : 'rgba(255,255,255,0.12)'), borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });
  const mono = { fontFamily: "'JetBrains Mono', monospace" };

  const OfferCard = (o, picked) => {
    const color = TYPE_COLOR[o.offer_type] || '#c084fc';
    const added = inTray(o.offer_id);
    return (
      <div key={o.offer_id} style={{ display: 'flex', gap: 12, padding: 12, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid ' + (picked ? 'rgba(94,234,212,0.3)' : 'rgba(255,255,255,0.06)') }}>
        <div style={{ width: 64, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'rgba(255,255,255,0.04)' }}>
          {o.thumbnail ? <img src={o.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} /> : null}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{o.model_year} {o.make} {o.model}</div>
            {picked && <span style={{ ...mono, fontSize: 8, color: '#5eead4', border: '1px solid rgba(94,234,212,0.4)', borderRadius: 999, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>★ AI pick</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' }}>
            <span style={{ ...mono, fontSize: 9, textTransform: 'uppercase', color, border: '1px solid ' + color + '66', borderRadius: 999, padding: '1px 7px' }}>{o.offer_type}</span>
            <span style={{ color, fontWeight: 700, fontSize: 14 }}>{headline(o)}</span>
          </div>
          <div style={{ ...mono, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{o.ai_reason}</div>
        </div>
        <button onClick={() => added ? removeFromTray(o.offer_id) : addToTray(o)} style={{ ...btn(false), alignSelf: 'center', padding: '6px 12px', fontSize: 12, color: added ? '#f87171' : '#5eead4', borderColor: added ? 'rgba(248,113,113,0.3)' : 'rgba(94,234,212,0.3)' }}>
          {added ? 'Remove' : '+ Add'}
        </button>
      </div>
    );
  };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>OFFER BUILDER · #1 COCHRAN</div>
        <h1 style={{ fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Build specials.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>AI picks the mix, you tune the math, the dealer signs off.</span>
        </h1>
      </div>

      <div className="v2-tile" style={{ padding: 16, marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={dealerId} onChange={e => setDealerId(e.target.value)} style={{ ...sel, minWidth: 280 }}>
          <option value="" style={{ background: '#111' }}>Select a dealer…</option>
          {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.url.replace(/^https?:\/\/(www\.)?/, '')} ({d.vehicle_count} cars)</option>)}
        </select>
        <select value={count} onChange={e => setCount(Number(e.target.value))} style={sel}>
          {[3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n} style={{ background: '#111' }}>Top {n}</option>)}
        </select>
        <button onClick={getPicks} disabled={!dealerId || loadingSuggest} style={{ ...btn(true), opacity: (!dealerId || loadingSuggest) ? 0.5 : 1 }}>
          {loadingSuggest ? 'Thinking…' : '✨ Get AI picks'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 18, alignItems: 'start' }}>
        {/* LEFT — suggestions / candidates */}
        <div className="v2-tile" style={{ padding: 16 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            {suggestions.length ? `${candidates.length} in-stock offers · ${suggestions.length} AI-picked` : 'Suggested offers'}
          </div>
          {!suggestions.length && !candidates.length ? (
            <div className="v2-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '30px 0', textAlign: 'center' }}>
              {loadingSuggest ? <div className="spinner" style={{ margin: '0 auto' }} /> : 'Pick a dealer and hit “Get AI picks”.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {candidates.map(o => OfferCard(o, suggestions.some(s => s.offer_id === o.offer_id)))}
            </div>
          )}
        </div>

        {/* RIGHT — campaign tray */}
        <div className="v2-tile" style={{ padding: 16, position: 'sticky', top: 12 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Campaign · {tray.length} offer{tray.length !== 1 ? 's' : ''}</div>
          {tray.length > 0 && <button onClick={() => window.open(`/api/specials/slide?offer_ids=${tray.map(t => t.offer_id).join(',')}`, '_blank')} style={{ ...btn(false), fontSize: 11, padding: '6px 12px', marginBottom: 10, color: '#5eead4', borderColor: 'rgba(94,234,212,0.3)' }}>Preview slides ↗</button>}
          {!tray.length ? (
            <div className="v2-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '30px 0', textAlign: 'center' }}>Add offers to build the campaign.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tray.map(o => {
                const color = TYPE_COLOR[o.offer_type] || '#c084fc';
                return (
                  <div key={o.offer_id} style={{ padding: 12, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{o.model_year} {o.make} {o.model} <span style={{ color, fontWeight: 700 }}>· {headline(o)}</span></div>
                      <button onClick={() => removeFromTray(o.offer_id)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                    </div>
                    {o.offer_type === 'lease' && o.lease_payment ? (
                      <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <label className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Target $/mo</label>
                        <input type="number" value={o.targetPayment} placeholder={String(Math.round(o.lease_payment))} onChange={e => recalc(o.offer_id, e.target.value)}
                          style={{ ...sel, width: 90, padding: '6px 10px', cursor: 'text' }} />
                        {o.preview && (
                          <span className="v2-mono" style={{ fontSize: 11, color: o.preview.method === 'exact' ? '#5eead4' : '#fbbf24' }}>
                            → {money(o.preview.dueAtSigning)} due
                            <span style={{ opacity: 0.7 }}> ({o.preview.method}{o.preview.apr ? `, ${o.preview.apr}% APR` : ''})</span>
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>{o.offer_type === 'lease' ? 'no advertised payment to edit' : 'fixed ' + o.offer_type + ' offer'}</div>
                    )}
                  </div>
                );
              })}

              <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 14 }}>
                <input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="Campaign name" style={{ ...sel, width: '100%', cursor: 'text', marginBottom: 10 }} />
                {createdLink ? (
                  <div style={{ background: 'rgba(94,234,212,0.08)', border: '1px solid rgba(94,234,212,0.3)', borderRadius: 10, padding: 12 }}>
                    <div className="v2-mono" style={{ fontSize: 10, color: '#5eead4', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>✓ Sent · dealer approval link</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input readOnly value={createdLink} style={{ ...sel, flex: 1, fontSize: 11, cursor: 'text' }} onFocus={e => e.target.select()} />
                      <button onClick={copyLink} style={btn(true)}>{copied ? 'Copied' : 'Copy'}</button>
                    </div>
                    <a href={createdLink} target="_blank" rel="noreferrer" className="v2-mono" style={{ fontSize: 11, color: '#5eead4', display: 'inline-block', marginTop: 8 }}>Open approval page →</a>
                  </div>
                ) : (
                  <button onClick={createCampaign} disabled={creating || !tray.length} style={{ ...btn(true), width: '100%', opacity: creating ? 0.6 : 1 }}>
                    {creating ? 'Generating…' : 'Create & generate approval link'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SpecialsPage() {
  const [rows, setRows] = useState([]);
  const [filterOpts, setFilterOpts] = useState({ makes: [], types: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [make, setMake] = useState('');
  const [type, setType] = useState('');
  const [group, setGroup] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [carsFor, setCarsFor] = useState(null);   // row whose qualifying cars are open
  const [cars, setCars] = useState([]);
  const [carsLoading, setCarsLoading] = useState(false);

  useEffect(() => { api('/specials/oem/filters').then(setFilterOpts).catch(() => {}); }, []);

  const openCars = (r) => {
    setCarsFor(r); setCars([]); setCarsLoading(true);
    const p = new URLSearchParams({ make: r.make, model: r.model, year: r.model_year });
    if (group) p.append('group', group);
    api('/specials/oem/vehicles?' + p.toString()).then(d => setCars(d.vehicles || [])).catch(() => setCars([])).finally(() => setCarsLoading(false));
  };

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (make) p.append('make', make);
    if (type) p.append('type', type);
    if (group) p.append('group', group);
    if (search) p.append('search', search);
    api('/specials/oem/rows?' + p.toString()).then(d => setRows(d.rows || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [make, type, group, search]);
  useEffect(() => { load(); }, [load]);

  const money = (n) => (n == null ? null : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
  const offerText = (r) => {
    if (r.lease_payment) return money(r.lease_payment) + '/mo';
    if (r.apr != null) return Number(r.apr).toFixed(1) + '% APR';
    if (r.cash_amount) return money(r.cash_amount);
    return '—';
  };
  const offerSub = (r) => {
    if (r.lease_payment && r.lease_term) return r.lease_term + ' mo lease';
    if (r.apr != null && r.apr_term_months) return r.apr_term_months + ' mo';
    if (r.cash_amount) return 'cash back';
    return '';
  };
  const TYPE_COLOR = { lease: 'var(--c3)', apr: 'var(--c4)', cash: 'var(--c5)', other: '#c084fc' };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const isNew = (d) => d && (Date.now() - new Date(d).getTime()) < 7 * 864e5;

  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', whiteSpace: 'nowrap' };
  const td = { padding: '10px 14px', fontSize: 13, color: 'rgba(255,255,255,0.8)', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' };
  const sel = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' };

  const totalUnits = rows.reduce((s, r) => s + Number(r.qualifying_units || 0), 0);

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          OEM SPECIALS · {rows.length} OFFERS · {totalUnits.toLocaleString()} QUALIFYING UNITS
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Specials.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Live OEM offers, anchored to VINs on your lot.
          </span>
        </h1>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={make} onChange={e => setMake(e.target.value)} style={sel}>
          <option value="" style={{ background: '#111' }}>All makes</option>
          {filterOpts.makes.map(m => <option key={m} value={m} style={{ background: '#111' }}>{m}</option>)}
        </select>
        <select value={type} onChange={e => setType(e.target.value)} style={sel}>
          <option value="" style={{ background: '#111' }}>All types</option>
          {filterOpts.types.map(t => <option key={t} value={t} style={{ background: '#111' }}>{t.toUpperCase()}</option>)}
        </select>
        <select value={group} onChange={e => setGroup(e.target.value)} style={sel}>
          <option value="" style={{ background: '#111' }}>All dealer groups</option>
          <option value="cochran" style={{ background: '#111' }}>#1 Cochran</option>
          <option value="shorkey" style={{ background: '#111' }}>Jim Shorkey</option>
        </select>
        <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); }} style={{ flex: 1, minWidth: 180 }}>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search model…" style={{ ...sel, width: '100%', cursor: 'text' }} />
        </form>
        {(make || type || group || search) && (
          <button onClick={() => { setMake(''); setType(''); setGroup(''); setSearch(''); setSearchInput(''); }} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>Clear</button>
        )}
      </div>

      <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={th}></th>
                <th style={th}>Vehicle</th>
                <th style={th}>Type</th>
                <th style={th}>Offer</th>
                <th style={th}>MSRP</th>
                <th style={th}>Qualifying</th>
                <th style={th}>Groups</th>
                <th style={th}>Expires</th>
                <th style={th}>Change</th>
                <th style={th}>Disclosure</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={{ ...td, textAlign: 'center', padding: 60 }} colSpan={10}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td style={{ ...td, textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }} colSpan={10}>No specials match these filters.</td></tr>
              ) : rows.map((r, i) => {
                const color = TYPE_COLOR[r.offer_type] || '#c084fc';
                return (
                  <tr key={i} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={td}>
                      <div style={{ width: 56, height: 42, borderRadius: 8, overflow: 'hidden', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {r.thumbnail ? <img src={r.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} /> : null}
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ color: '#fff', fontWeight: 500 }}>{r.model_year} {r.make} {r.model}</div>
                      {isNew(r.first_seen_at) && <span className="v2-mono" style={{ fontSize: 9, color: 'var(--c4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>● new</span>}
                    </td>
                    <td style={td}><span className="v2-mono" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: color, border: '1px solid ' + color + '66', borderRadius: 999, padding: '2px 8px' }}>{r.offer_type}</span></td>
                    <td style={td}><span style={{ color: color, fontWeight: 700, fontSize: 15 }}>{offerText(r)}</span><div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{offerSub(r)}</div></td>
                    <td style={td}>{money(r.msrp) || '—'}</td>
                    <td style={td}>
                      <button onClick={() => openCars(r)} title="View the qualifying VINs"
                        style={{ background: 'rgba(94,234,212,0.10)', border: '1px solid rgba(94,234,212,0.35)', color: '#5eead4', fontWeight: 700, fontSize: 13, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {Number(r.qualifying_units).toLocaleString()}
                        <span style={{ fontSize: 10, opacity: 0.7 }}>cars ›</span>
                      </button>
                    </td>
                    <td style={td}><span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>{(r.groups || []).join(', ')}</span></td>
                    <td style={td}>{fmtDate(r.end_date)}</td>
                    <td style={td}>{r.last_change ? <span className="v2-mono" style={{ fontSize: 10, color: 'var(--c5)' }}>{r.last_change.change_type} {r.last_change.old_value}→{r.last_change.new_value}</span> : <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>}</td>
                    <td style={td}>{r.disclaimer ? <details><summary className="v2-mono" style={{ cursor: 'pointer', fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>view</summary><p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4, marginTop: 6, maxWidth: 320, whiteSpace: 'normal' }}>{String(r.disclaimer).slice(0, 500)}</p></details> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 14 }}>
        POC · Stellantis (Jeep / Ram / Chrysler / Dodge) · Pittsburgh · thumbnails from matched inventory · changes tracked per ingest
      </div>

      {carsFor && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }} onClick={() => setCarsFor(null)} />
          <div style={{ position: 'relative', width: 'min(560px, 100%)', height: '100%', background: 'rgba(15,15,22,0.98)', borderLeft: '1px solid rgba(255,255,255,0.10)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Qualifying inventory</div>
                <div style={{ fontSize: 18, color: '#fff', fontWeight: 600 }}>{carsFor.model_year} {carsFor.make} {carsFor.model}</div>
                <div className="v2-mono" style={{ fontSize: 11, color: '#5eead4', marginTop: 4 }}>{offerText(carsFor)} {offerSub(carsFor) ? '· ' + offerSub(carsFor) : ''}</div>
              </div>
              <button onClick={() => setCarsFor(null)} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', width: 34, height: 34, borderRadius: 10, fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {carsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><div className="spinner" /></div>
              ) : cars.length === 0 ? (
                <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, textAlign: 'center', padding: 30 }}>No matching VINs.</div>
              ) : (
                <>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>{cars.length} VIN{cars.length !== 1 ? 's' : ''} on the lot</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {cars.map((c) => (
                      <a key={c.id} href={c.url || '#'} target="_blank" rel="noreferrer"
                        style={{ display: 'flex', gap: 12, padding: 10, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ width: 72, height: 54, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {c.image ? <img src={c.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} /> : null}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{c.year} {c.make} {c.model} {c.trim || ''}</div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'var(--c4)' }}>{c.vin}</div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                            {c.price ? '$' + Number(c.price).toLocaleString() : '—'}{c.mileage ? ' · ' + Number(c.mileage).toLocaleString() + ' mi' : ''}{c.exterior_color ? ' · ' + c.exterior_color : ''}
                          </div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{(c.domain || '').replace(/^https?:\/\/(www\.)?/, '')} · {c.grp}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EngagementPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [quadrants, setQuadrants] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedVin, setExpandedVin] = useState(null);

  useEffect(() => {
    api('/my-dealers').then(res => {
      const list = res.dealers || res || [];
      setDealers(list);
      if (list.length > 0) setSelectedDealer(String(list[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDealer) return;
    setLoading(true);
    Promise.all([
      api(`/dealers/${selectedDealer}/inventory/quadrants`),
      api(`/dealers/${selectedDealer}/inventory/scores`),
    ]).then(([qRes, sRes]) => {
      setQuadrants(qRes.summary || null);
      setScores(sRes.data || sRes.vehicles || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedDealer]);

  const QUADRANT_CFG = [
    { key: 'stars',       label: 'Stars',       color: 'var(--c4)', bg: 'rgba(166,255,84,0.10)',  desc: 'High views, short days on lot' },
    { key: 'fix_it',      label: 'Fix It',      color: 'var(--c1)', bg: 'rgba(255,91,164,0.10)',  desc: 'Low views, long days on lot' },
    { key: 'boost',       label: 'Boost',       color: 'var(--c5)', bg: 'rgba(255,200,87,0.10)',  desc: 'High views, long days on lot' },
    { key: 'push_or_cut', label: 'Push or Cut', color: '#c084fc',   bg: 'rgba(122,77,255,0.10)',  desc: 'Low views, short days on lot' },
  ];

  function engagementColor(score) {
    if (score >= 70) return 'var(--c4)';
    if (score >= 40) return 'var(--c5)';
    return 'var(--c1)';
  }

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  const totalScored = scores.length;
  const avgScore = totalScored > 0 ? Math.round(scores.reduce((s, v) => s + (v.engagement_score ?? v.score ?? 0), 0) / totalScored) : 0;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          ENGAGEMENT · GA4 VDP ANALYTICS{totalScored ? ` · ${totalScored} VEHICLES SCORED` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Engagement.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Which VINs are pulling, which are dead weight.
            </span>
          </h1>
          {dealers.length > 0 && (
            <select value={selectedDealer} onChange={e => setSelectedDealer(e.target.value)} style={{ ...inp, minWidth: 220, cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name || d.dealer_name}</option>)}
            </select>
          )}
        </div>
      </div>

      {dealers.length === 0 && (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No dealers yet.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Add a dealer first to view engagement scores.</div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING ENGAGEMENT DATA…</span>
        </div>
      ) : dealers.length > 0 && (
        <>
          {/* Quadrant tiles */}
          {quadrants && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 22 }}>
              {QUADRANT_CFG.map(q => {
                const entry = quadrants[q.key] || quadrants[q.key.replace('_', '')] || {};
                const count = typeof entry === 'object' ? (entry.count ?? 0) : (entry ?? 0);
                return (
                  <div key={q.key} className="v2-tile" style={{ padding: 18, background: q.bg, border: `1px solid ${q.color}40` }}>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>{q.label.toLowerCase()}</div>
                    <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: q.color }}>{count}</div>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{q.desc}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Average score tile if data exists */}
          {totalScored > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 22 }}>
              <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>vehicles scored</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{totalScored.toLocaleString()}</div>
              </div>
              <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>avg engagement</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: engagementColor(avgScore) }}>{avgScore}</div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden', marginTop: 8 }}>
                  <div style={{ width: `${avgScore}%`, height: '100%', background: engagementColor(avgScore) }} />
                </div>
              </div>
            </div>
          )}

          {scores.length === 0 ? (
            <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
              <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No engagement data yet.</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', maxWidth: 360, margin: '0 auto' }}>Connect GA4 on the GA4 Connect page to start pulling VDP engagement scores for this dealer.</div>
            </div>
          ) : (
            <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>{['VIN', 'Vehicle', 'Engagement', 'Days on lot', 'Quadrant'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {scores.map(v => {
                      const vin = v.vin;
                      const score = v.engagement_score ?? v.score ?? 0;
                      const qKey = v.quadrant || '';
                      const qCfg = QUADRANT_CFG.find(q => q.key === qKey || q.label.toLowerCase().replace(/ /g,'_') === qKey) || null;
                      return (
                        <React.Fragment key={vin}>
                          <tr onClick={() => setExpandedVin(expandedVin === vin ? null : vin)}
                              style={{ cursor: 'pointer', background: expandedVin === vin ? 'rgba(255,255,255,0.04)' : 'transparent', transition: 'background 0.15s' }}
                              onMouseEnter={e => { if (expandedVin !== vin) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                              onMouseLeave={e => { if (expandedVin !== vin) e.currentTarget.style.background = 'transparent'; }}>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{vin}</td>
                            <td style={{ ...td, color: '#fff' }}>{[v.year, v.make, v.model].filter(Boolean).join(' ') || '-'}</td>
                            <td style={td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 99, overflow: 'hidden', maxWidth: 100 }}>
                                  <div style={{ width: `${score}%`, height: '100%', background: engagementColor(score), borderRadius: 99 }} />
                                </div>
                                <span className="v2-num" style={{ fontSize: 13, color: engagementColor(score), fontWeight: 700, minWidth: 28 }}>{score}</span>
                              </div>
                            </td>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{v.days_on_lot ?? '-'}</td>
                            <td style={td}>
                              {qCfg ? (
                                <span className="v2-chip" style={{ background: qCfg.bg, borderColor: 'transparent', color: qCfg.color, fontSize: 10 }}>{qCfg.label}</span>
                              ) : (
                                <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>{qKey || '-'}</span>
                              )}
                            </td>
                          </tr>
                          {expandedVin === vin && (
                            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                              <td colSpan={5} style={{ padding: '16px 24px' }}>
                                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                  {[['Pageviews', v.raw_pageviews], ['Form fills', v.raw_form_fills], ['Click-to-call', v.raw_click_to_call], ['Percentile rank', v.percentile_rank ? `${v.percentile_rank}%` : null], ['Engagement score', score]].map(([label, val]) => (
                                    <div key={label} style={{ minWidth: 120 }}>
                                      <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                                      <div className="v2-num" style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{val ?? '-'}</div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MarketPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [positions, setPositions] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api('/my-dealers').then(res => {
      const list = res.dealers || res || [];
      setDealers(list);
      if (list.length > 0) setSelectedDealer(String(list[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDealer) return;
    setLoading(true);
    Promise.all([
      api(`/dealers/${selectedDealer}/inventory/positions`),
      api(`/dealers/${selectedDealer}/inventory/gaps`),
    ]).then(([pRes, gRes]) => {
      setPositions(pRes.data || pRes.positions || []);
      setGaps(gRes.data || gRes.gaps || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedDealer]);

  function rankColor(rank) {
    if (rank <= 3) return 'var(--c4)';
    if (rank <= 6) return 'var(--c5)';
    return 'var(--c1)';
  }

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  const top3 = positions.filter(p => (p.rank_in_market || p.market_rank || p.rank || 99) <= 3).length;
  const overpriced = positions.filter(p => (p.price_delta_to_avg || p.price_delta || p.delta_to_avg || 0) > 0).length;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          PRICE POSITIONING{positions.length ? ` · ${positions.length} VEHICLES RANKED` : ''}{gaps.length ? ` · ${gaps.length} INVENTORY GAPS` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Position.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Where each VIN sits in its market - and what's missing from the lot.
            </span>
          </h1>
          {dealers.length > 0 && (
            <select value={selectedDealer} onChange={e => setSelectedDealer(e.target.value)} style={{ ...inp, minWidth: 220, cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name || d.dealer_name}</option>)}
            </select>
          )}
        </div>
      </div>

      {dealers.length === 0 && (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No dealers yet.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Add a dealer and run competitor scraping to see pricing data.</div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING MARKET DATA…</span>
        </div>
      ) : dealers.length > 0 && (
        <>
          {/* KPIs */}
          {positions.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 22 }}>
              <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>vehicles ranked</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{positions.length.toLocaleString()}</div>
              </div>
              <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>top-3 in market</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c4)' }}>{top3}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>priced to win</div>
              </div>
              <div className="v2-tile v2-tile-amber" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>above market</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c5)' }}>{overpriced}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>VINs priced over avg</div>
              </div>
              <div className="v2-tile" style={{ padding: 18 }}>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>inventory gaps</div>
                <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c1)' }}>{gaps.length}</div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>models you don't stock</div>
              </div>
            </div>
          )}

          {/* Positions table */}
          <div style={{ marginBottom: 24 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>positions</div>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 12 }}>Where you rank</div>
            {positions.length === 0 ? (
              <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
                <div className="v2-serif" style={{ fontSize: 20, color: '#fff', marginBottom: 6 }}>No positioning data yet.</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Market positions are computed once competitors are scraped for this dealer.</div>
              </div>
            ) : (
              <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                    <thead><tr>{['VIN', 'Vehicle', 'Price', 'Market rank', 'vs. market avg', 'Position'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {positions.map((p, i) => {
                        const rank = p.rank_in_market || p.market_rank || p.rank || 0;
                        const total = p.total_comparables || p.market_total || p.total || '?';
                        const delta = p.price_delta_to_avg || p.price_delta || p.delta_to_avg || 0;
                        return (
                          <tr key={p.vin || i} style={{ transition: 'background 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{p.vin || '-'}</td>
                            <td style={{ ...td, color: '#fff' }}>{[p.year, p.make, p.model].filter(Boolean).join(' ') || '-'}</td>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: '#fff' }}>{p.price ? `$${Number(p.price).toLocaleString()}` : '-'}</td>
                            <td style={td}>
                              <span className="v2-num" style={{ fontSize: 14, fontWeight: 700, color: rankColor(rank) }}>#{rank}</span>
                              <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginLeft: 4 }}>of {total}</span>
                            </td>
                            <td style={td}>
                              {delta === 0 || !delta ? (
                                <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>-</span>
                              ) : (
                                <span className="v2-mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: delta > 0 ? 'rgba(255,200,87,0.10)' : 'rgba(166,255,84,0.10)', border: `1px solid ${delta > 0 ? 'rgba(255,200,87,0.30)' : 'rgba(166,255,84,0.30)'}`, borderRadius: 99, padding: '2px 9px', fontSize: 11, fontWeight: 700, color: delta > 0 ? 'var(--c5)' : 'var(--c4)' }}>
                                  {delta > 0 ? '▲' : '▼'} ${Math.abs(delta).toLocaleString()}
                                </span>
                              )}
                            </td>
                            <td style={{ ...td, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>{p.position_label || p.position || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Inventory gaps */}
          <div>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>gaps</div>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 12 }}>
              Inventory gaps <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>- models rivals sell that you don't.</span>
            </div>
            {gaps.length === 0 ? (
              <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
                <div className="v2-serif" style={{ fontSize: 20, color: '#fff', marginBottom: 6 }}>No inventory gaps found.</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Gap analysis runs once competitor inventory is available.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {gaps.map((g, i) => (
                  <div key={i} className="v2-tile" style={{ padding: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {[g.make, g.model].filter(Boolean).join(' ') || 'Unknown'}
                        </div>
                        {(g.year_range || g.year) && (
                          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{g.year_range || g.year}</div>
                        )}
                      </div>
                      <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.12)', borderColor: 'rgba(255,91,164,0.30)', color: 'var(--c1)', fontSize: 9, padding: '2px 7px', flexShrink: 0 }}>GAP</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <div>
                        <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 3, letterSpacing: '0.06em' }}>MARKET</div>
                        <div className="v2-num" style={{ fontSize: 16, color: '#fff', fontWeight: 600 }}>{g.market_count ?? '-'}</div>
                      </div>
                      <div>
                        <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 3, letterSpacing: '0.06em' }}>YOURS</div>
                        <div className="v2-num" style={{ fontSize: 16, color: '#fff', fontWeight: 600 }}>{g.dealer_count ?? 0}</div>
                      </div>
                      <div>
                        <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 3, letterSpacing: '0.06em' }}>SCORE</div>
                        <div className="v2-num" style={{ fontSize: 16, color: 'var(--c5)', fontWeight: 600 }}>{g.gap_score ?? '-'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function WebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [form, setForm] = useState({ url: '', events: [], secret: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [expandedWebhook, setExpandedWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState({});

  const ALL_EVENTS = ['inventory_added', 'inventory_removed', 'price_changed', 'competitor_price_changed', 'alert_generated', 'feed_updated', 'engagement_score_changed'];

  useEffect(() => { loadWebhooks(); }, []);

  function loadWebhooks() {
    setLoading(true);
    api('/webhooks').then(res => setWebhooks(res.webhooks || res || [])).catch(() => {}).finally(() => setLoading(false));
  }

  function openCreate() {
    setEditingWebhook(null);
    setForm({ url: '', events: [], secret: '', is_active: true });
    setShowModal(true);
  }

  function openEdit(wh) {
    setEditingWebhook(wh);
    setForm({ url: wh.url, events: wh.events || [], secret: wh.secret || '', is_active: wh.is_active !== false });
    setShowModal(true);
  }

  function toggleEvent(ev) {
    setForm(f => ({ ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev] }));
  }

  async function saveWebhook() {
    setSaving(true);
    try {
      if (editingWebhook) {
        await api(`/webhooks/${editingWebhook.id}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await api('/webhooks', { method: 'POST', body: JSON.stringify(form) });
      }
      setShowModal(false);
      loadWebhooks();
    } catch (e) {}
    setSaving(false);
  }

  async function deleteWebhook(id) {
    if (!window.confirm('Delete this webhook?')) return;
    setDeleting(id);
    try {
      await api(`/webhooks/${id}`, { method: 'DELETE' });
      setWebhooks(prev => prev.filter(w => w.id !== id));
    } catch (e) {}
    setDeleting(null);
  }

  async function testWebhook(id) {
    setTesting(id);
    try {
      await api(`/webhooks/${id}/test`, { method: 'POST' });
      setTestResult(prev => ({ ...prev, [id]: 'ok' }));
    } catch (e) {
      setTestResult(prev => ({ ...prev, [id]: 'error' }));
    }
    setTesting(null);
    setTimeout(() => setTestResult(prev => { const n = { ...prev }; delete n[id]; return n; }), 3000);
  }

  function toggleDeliveries(id) {
    if (expandedWebhook === id) { setExpandedWebhook(null); return; }
    setExpandedWebhook(id);
    if (!deliveries[id]) {
      api(`/webhooks/${id}/deliveries`).then(res => setDeliveries(prev => ({ ...prev, [id]: (res.deliveries || res || []).slice(0, 10) }))).catch(() => {});
    }
  }

  function statusColor(status) {
    if (status === 'success' || status === 200 || status === '200') return 'var(--c4)';
    if (status === 'pending') return 'var(--c5)';
    return 'var(--c1)';
  }

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          WEBHOOKS · {webhooks.length} ENDPOINT{webhooks.length === 1 ? '' : 'S'}{webhooks.filter(w => w.is_active !== false).length ? ` · ${webhooks.filter(w => w.is_active !== false).length} ACTIVE` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Webhooks.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Push real-time events to your own endpoints.
            </span>
          </h1>
          <button onClick={openCreate} className="v2-chip"
            style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d' }}>
            <Icons.Plus /> New webhook
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING WEBHOOKS…</span>
        </div>
      ) : webhooks.length === 0 ? (
        <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No webhooks configured.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>Create a webhook to receive real-time event notifications.</div>
          <button onClick={openCreate}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
            <Icons.Plus /> Create webhook
          </button>
        </div>
      ) : (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead><tr>{['Endpoint', 'Events', 'Status', 'Last delivery', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {webhooks.map(wh => (
                  <React.Fragment key={wh.id}>
                    <tr>
                      <td style={td}>
                        <button onClick={() => toggleDeliveries(wh.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, color: 'inherit', fontFamily: 'inherit' }}>
                          <div className="v2-mono" style={{ fontSize: 12, color: 'var(--c3)', wordBreak: 'break-all', maxWidth: 280 }}>{wh.url}</div>
                          {wh.secret && (
                            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>secret · {wh.secret.substring(0, 8)}••••••••</div>
                          )}
                        </button>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 280 }}>
                          {(wh.events || []).length === 0 ? (
                            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>-</span>
                          ) : (wh.events || []).map(ev => (
                            <span key={ev} className="v2-mono" style={{ background: 'rgba(34,211,238,0.10)', color: 'var(--c3)', border: '1px solid rgba(34,211,238,0.20)', borderRadius: 6, padding: '1px 6px', fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap' }}>{ev}</span>
                          ))}
                        </div>
                      </td>
                      <td style={td}>
                        {wh.is_active !== false ? (
                          <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
                            <span className="v2-pulse-dot" /> Active
                          </span>
                        ) : (
                          <span className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>Inactive</span>
                        )}
                      </td>
                      <td style={td}>
                        {wh.last_delivery_status ? (() => {
                          const c = statusColor(wh.last_delivery_status);
                          const isOk = wh.last_delivery_status === 'success' || wh.last_delivery_status === 200 || wh.last_delivery_status === '200';
                          const label = isOk ? 'Success' : wh.last_delivery_status === 'pending' ? 'Pending' : 'Failed';
                          return <span className="v2-mono" style={{ color: c, fontSize: 11, fontWeight: 700 }}>{label}</span>;
                        })() : <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>-</span>}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button onClick={() => openEdit(wh)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10 }}>Edit</button>
                          <button onClick={() => testWebhook(wh.id)} disabled={testing === wh.id} className="v2-chip"
                            style={{ cursor: 'pointer', background: testResult[wh.id] === 'ok' ? 'rgba(166,255,84,0.10)' : testResult[wh.id] === 'error' ? 'rgba(255,91,164,0.10)' : 'rgba(34,211,238,0.10)', borderColor: testResult[wh.id] === 'ok' ? 'rgba(166,255,84,0.30)' : testResult[wh.id] === 'error' ? 'rgba(255,91,164,0.30)' : 'rgba(34,211,238,0.30)', color: testResult[wh.id] === 'ok' ? 'var(--c4)' : testResult[wh.id] === 'error' ? 'var(--c1)' : 'var(--c3)', fontSize: 10 }}>
                            {testing === wh.id ? 'Sending…' : testResult[wh.id] === 'ok' ? '✓ Sent' : testResult[wh.id] === 'error' ? '✗ Failed' : 'Test'}
                          </button>
                          <button onClick={() => deleteWebhook(wh.id)} disabled={deleting === wh.id} title="Delete" className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10 }}>
                            <Icons.Trash />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedWebhook === wh.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div style={{ padding: '14px 18px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <div className="v2-mono" style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.06em' }}>LAST 10 DELIVERIES</div>
                            {!deliveries[wh.id] ? (
                              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Loading…</div>
                            ) : deliveries[wh.id].length === 0 ? (
                              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>No deliveries yet.</div>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead><tr>{['Status', 'Event', 'Response', 'Attempted', 'Error'].map(h => <th key={h} style={{ ...th, padding: '6px 10px' }}>{h}</th>)}</tr></thead>
                                <tbody>
                                  {deliveries[wh.id].map((d, i) => (
                                    <tr key={i}>
                                      <td style={{ ...td, padding: '6px 10px', color: statusColor(d.status), fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{d.status || '-'}</td>
                                      <td style={{ ...td, padding: '6px 10px', color: 'rgba(255,255,255,0.65)' }}>{d.event_type || '-'}</td>
                                      <td style={{ ...td, padding: '6px 10px', color: 'rgba(255,255,255,0.55)', fontFamily: "'JetBrains Mono', monospace" }}>{d.response_code || '-'}</td>
                                      <td style={{ ...td, padding: '6px 10px', color: 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>{d.attempted_at ? new Date(d.attempted_at).toLocaleString() : '-'}</td>
                                      <td style={{ ...td, padding: '6px 10px', color: 'var(--c1)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.error || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && ReactDOM.createPortal(
        <div onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="v2-tile" style={{ width: 540, maxWidth: '92vw', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, letterSpacing: '0.06em' }}>{editingWebhook ? 'EDITING WEBHOOK' : 'NEW WEBHOOK'}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h2 className="v2-serif" style={{ fontSize: 26, fontWeight: 400, color: '#fff', margin: 0 }}>{editingWebhook ? 'Edit webhook' : 'New webhook'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
            </div>

            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={labelStyle}>Endpoint URL</label>
                <input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://example.com/webhook" style={inp} />
              </div>
              <div>
                <label style={labelStyle}>Events</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ALL_EVENTS.map(ev => {
                    const selected = form.events.includes(ev);
                    return (
                      <button key={ev} type="button" onClick={() => toggleEvent(ev)} className="v2-chip"
                        style={{ cursor: 'pointer', background: selected ? 'rgba(122,77,255,0.18)' : 'rgba(255,255,255,0.02)', borderColor: selected ? 'rgba(122,77,255,0.40)' : undefined, color: selected ? '#c4b5fd' : 'rgba(255,255,255,0.55)', fontSize: 11 }}>
                        {ev}
                      </button>
                    );
                  })}
                </div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>click to toggle · select all events you want delivered</div>
              </div>
              <div>
                <label style={labelStyle}>Secret key</label>
                <input value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} placeholder="whsec_… (blank = auto-generate)" style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>used to verify webhook signatures via HMAC-SHA256</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>Active</div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>inactive webhooks won't receive events</div>
                </div>
                <div onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  style={{ width: 44, height: 24, borderRadius: 99, background: form.is_active ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,0.10)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, boxShadow: form.is_active ? '0 0 12px rgba(122,77,255,0.4)' : 'none' }}>
                  <div style={{ position: 'absolute', top: 3, left: form.is_active ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} className="v2-chip" style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>Cancel</button>
              <button onClick={saveWebhook} disabled={saving || !form.url}
                style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (saving || !form.url) ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                {saving ? 'Saving…' : editingWebhook ? 'Save changes' : 'Create webhook'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function QualityPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [qualityData, setQualityData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vPage, setVPage] = useState(1);
  const [vTotal, setVTotal] = useState(0);
  const [vPages, setVPages] = useState(0);
  const [scoreFilter, setScoreFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditMsg, setAuditMsg] = useState(null);

  useEffect(() => {
    api('/my-dealers').then(res => {
      const list = res.dealers || res || [];
      setDealers(list);
      if (list.length > 0) setSelectedDealer(String(list[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDealer) return;
    setLoading(true);
    setVPage(1);
    api(`/dealers/${selectedDealer}/inventory/quality`)
      .then(res => setQualityData(res))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedDealer]);

  useEffect(() => {
    if (!selectedDealer) return;
    const maxScore = scoreFilter === 'poor' ? 50 : scoreFilter === 'needs_work' ? 70 : scoreFilter === 'good' ? 90 : null;
    const minScore = scoreFilter === 'good' ? 70 : scoreFilter === 'excellent' ? 90 : null;
    let url = `/dealers/${selectedDealer}/inventory/quality/vehicles?page=${vPage}&limit=25`;
    if (maxScore !== null) url += `&max_score=${maxScore - 0.1}`;
    if (minScore !== null) url += `&min_score=${minScore}`;
    api(url).then(res => {
      setVehicles(res.data || []);
      setVTotal(res.pagination?.total || 0);
      setVPages(res.pagination?.pages || 0);
    }).catch(() => {});
  }, [selectedDealer, vPage, scoreFilter]);

  async function runAudit() {
    setAuditing(true);
    setAuditMsg(null);
    try {
      const res = await api(`/dealers/${selectedDealer}/inventory/quality/audit`, { method: 'POST' });
      setAuditMsg({ type: 'success', text: res.message || `Audited ${res.audited} vehicles` });
      const q = await api(`/dealers/${selectedDealer}/inventory/quality`);
      setQualityData(q);
    } catch (e) {
      setAuditMsg({ type: 'error', text: 'Audit failed. Please try again.' });
    }
    setAuditing(false);
  }

  const summary = qualityData?.summary;
  const topIssues = qualityData?.top_issues || [];
  const avgScore = summary?.avg_score;

  function scoreColor(s) {
    if (s >= 90) return 'var(--c4)';
    if (s >= 70) return '#a3e635';
    if (s >= 50) return 'var(--c5)';
    return 'var(--c1)';
  }
  function scoreChip(s) {
    if (s == null) return null;
    const color = scoreColor(s);
    const label = s >= 90 ? 'Excellent' : s >= 70 ? 'Good' : s >= 50 ? 'Needs Work' : 'Poor';
    return (
      <span className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'transparent', color, fontSize: 10, fontWeight: 700 }}>
        <span className="v2-num" style={{ fontSize: 11 }}>{Math.round(s)}</span> · {label}
      </span>
    );
  }

  const bands = summary ? [
    { label: 'Excellent',  sub: '90–100', count: summary.excellent_90_plus,  color: 'var(--c4)', key: 'excellent' },
    { label: 'Good',       sub: '70–89',  count: summary.good_70_89,         color: '#a3e635',   key: 'good' },
    { label: 'Needs Work', sub: '50–69',  count: summary.needs_work_50_69,   color: 'var(--c5)', key: 'needs_work' },
    { label: 'Poor',       sub: '<50',    count: summary.poor_under_50,      color: 'var(--c1)', key: 'poor' },
  ] : [];

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          LISTING QUALITY{summary?.total ? ` · ${summary.total.toLocaleString()} VEHICLES SCORED` : ''}{avgScore != null ? ` · AVG ${Math.round(avgScore)}/100` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Quality.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Audit listings for completeness - find what's hurting click-through.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {dealers.length > 0 && (
              <select value={selectedDealer} onChange={e => setSelectedDealer(e.target.value)} style={{ ...inp, minWidth: 200, cursor: 'pointer' }}>
                {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name || d.dealer_name}</option>)}
              </select>
            )}
            <button onClick={runAudit} disabled={auditing || !selectedDealer}
              style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 999, color: '#fff', padding: '9px 22px', fontSize: 12, fontWeight: 700, cursor: (auditing || !selectedDealer) ? 'not-allowed' : 'pointer', opacity: (auditing || !selectedDealer) ? 0.5 : 1, letterSpacing: '0.02em', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
              {auditing ? 'Auditing…' : 'Run audit →'}
            </button>
          </div>
        </div>
      </div>

      {auditMsg && (
        <div style={{ background: auditMsg.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${auditMsg.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 18, fontSize: 13, color: auditMsg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>
          {auditMsg.text}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING QUALITY DATA…</span>
        </div>
      ) : !summary || summary.total === 0 ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No quality scores yet.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>Click "Run Audit" to score your listings now.</div>
          <button onClick={runAudit} disabled={auditing || !selectedDealer}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 12, color: '#fff', padding: '11px 28px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: auditing || !selectedDealer ? 0.6 : 1, boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
            {auditing ? 'Running audit…' : 'Run audit now'}
          </button>
        </div>
      ) : (
        <>
          {/* Score / Distribution / Top issues */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 20 }}>
            <div className="v2-tile v2-tile-violet" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12 }}>avg quality score</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span className="v2-num" style={{ fontSize: 64, fontWeight: 300, color: scoreColor(avgScore), lineHeight: 1 }}>{avgScore != null ? Math.round(avgScore) : '-'}</span>
                <span className="v2-mono" style={{ fontSize: 18, color: 'rgba(255,255,255,0.3)' }}>/100</span>
              </div>
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 8 }}>{summary.total.toLocaleString()} vehicles scored</div>
            </div>

            <div className="v2-tile" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>distribution</div>
              {bands.map(b => {
                const pct = summary.total > 0 ? Math.round(b.count / summary.total * 100) : 0;
                return (
                  <div key={b.key} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: b.color, fontWeight: 600 }}>{b.label} <span className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>{b.sub}</span></span>
                      <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{b.count.toLocaleString()} · {pct}%</span>
                    </div>
                    <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: b.color, opacity: 0.85, borderRadius: 99, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="v2-tile v2-tile-cyan" style={{ padding: 22 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>top issues</div>
              {topIssues.length === 0 ? (
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center', paddingTop: 16 }}>No issues - great listings!</div>
              ) : topIssues.slice(0, 5).map((issue, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10, paddingBottom: 10, borderBottom: i < Math.min(topIssues.length, 5) - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.3 }}>{issue.fix}</div>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{issue.check}</div>
                  </div>
                  <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.12)', borderColor: 'rgba(255,91,164,0.30)', color: 'var(--c1)', fontSize: 10, padding: '2px 8px', flexShrink: 0 }}>{issue.affected_count}</span>
                </div>
              ))}
              {qualityData?.estimated_impact && (
                <div className="v2-mono" style={{ marginTop: 10, background: 'rgba(122,77,255,0.10)', border: '1px solid rgba(122,77,255,0.30)', borderRadius: 10, padding: '10px 12px', fontSize: 11, color: '#c4b5fd' }}>
                  💡 {qualityData.estimated_impact}
                </div>
              )}
            </div>
          </div>

          {/* Vehicle table */}
          <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>vehicles</div>
                <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>By score</div>
              </div>
              <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', padding: 3, borderRadius: 10 }}>
                {[{ k: 'all', l: 'All' }, { k: 'poor', l: 'Poor' }, { k: 'needs_work', l: 'Needs work' }, { k: 'good', l: 'Good' }, { k: 'excellent', l: 'Excellent' }].map(f => (
                  <button key={f.k} onClick={() => { setScoreFilter(f.k); setVPage(1); }}
                    style={{ background: scoreFilter === f.k ? 'rgba(255,255,255,0.10)' : 'transparent', border: 'none', color: scoreFilter === f.k ? '#fff' : 'rgba(255,255,255,0.45)', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    {f.l}
                  </button>
                ))}
              </div>
            </div>
            {vehicles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>No vehicles in this category.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>{['Vehicle', 'VIN', 'Price', 'Score', 'Issues'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {vehicles.map(v => (
                      <tr key={v.vin}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {v.photo ? (
                              <img src={v.photo} alt="" style={{ width: 56, height: 42, borderRadius: 8, objectFit: 'cover', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                            ) : (
                              <div style={{ width: 56, height: 42, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }} />
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{[v.year, v.make, v.model].filter(Boolean).join(' ')}</div>
                              {v.trim && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{v.trim}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{v.vin}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: '#fff' }}>{v.price ? `$${v.price.toLocaleString()}` : '-'}</td>
                        <td style={td}>{scoreChip(v.score)}</td>
                        <td style={td}>
                          {(v.issues || []).slice(0, 2).map((iss, i) => (
                            <div key={i} className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>· {iss.fix || iss.check}</div>
                          ))}
                          {v.issues?.length > 2 && <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>+{v.issues.length - 2} more</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {vPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{vTotal.toLocaleString()} vehicles</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => setVPage(p => Math.max(1, p - 1))} disabled={vPage === 1} className="v2-chip"
                    style={{ cursor: vPage === 1 ? 'not-allowed' : 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10, opacity: vPage === 1 ? 0.4 : 1 }}>← Prev</button>
                  <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', padding: '0 8px' }}>{vPage} / {vPages}</span>
                  <button onClick={() => setVPage(p => Math.min(vPages, p + 1))} disabled={vPage === vPages} className="v2-chip"
                    style={{ cursor: vPage === vPages ? 'not-allowed' : 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10, opacity: vPage === vPages ? 0.4 : 1 }}>Next →</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function GA4Page() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ga4_connected') === 'true') {
      setFlash({ type: 'success', message: 'GA4 connected successfully!' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('ga4_error')) {
      setFlash({ type: 'error', message: `GA4 connection error: ${params.get('ga4_error')}` });
      window.history.replaceState({}, '', window.location.pathname);
    }

    api('/my-dealers').then(res => {
      const list = res.dealers || res || [];
      setDealers(list);
      if (list.length > 0) setSelectedDealer(String(list[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDealer) return;
    setLoading(true);
    Promise.all([
      api(`/ga4/status/${selectedDealer}`),
      api(`/ga4/analytics/${selectedDealer}`),
    ]).then(([sRes, aRes]) => {
      setStatus(sRes);
      setAnalytics(aRes.vehicles || aRes.data || aRes || []);
    }).catch(err => {
      setStatus(null);
      setAnalytics([]);
    }).finally(() => setLoading(false));
  }, [selectedDealer]);

  async function connectGA4() {
    try {
      const res = await api(`/ga4/connect/${selectedDealer}`);
      const url = res.url || res.auth_url || res;
      if (url) window.location.href = url;
    } catch (e) {
      setFlash({ type: 'error', message: 'Failed to get auth URL.' });
    }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      const connectionId = status?.connections?.[0]?.id;
      if (!connectionId) { setFlash({ type: 'error', message: 'No GA4 connection found.' }); setSyncing(false); return; }
      await api(`/ga4/sync/${selectedDealer}/${connectionId}`, { method: 'POST' });
      setFlash({ type: 'success', message: 'Sync started successfully.' });
    } catch (e) {
      setFlash({ type: 'error', message: 'Sync failed.' });
    }
    setSyncing(false);
  }

  async function disconnect() {
    if (!window.confirm('Disconnect GA4 for this dealer?')) return;
    setDisconnecting(true);
    try {
      const connectionId = status?.connections?.[0]?.id;
      if (!connectionId) { setFlash({ type: 'error', message: 'No GA4 connection found.' }); setDisconnecting(false); return; }
      await api(`/ga4/disconnect/${selectedDealer}/${connectionId}`, { method: 'POST' });
      setStatus(prev => ({ ...prev, connections: [], connected: false }));
      setFlash({ type: 'success', message: 'GA4 disconnected.' });
    } catch (e) {
      setFlash({ type: 'error', message: 'Disconnect failed.' });
    }
    setDisconnecting(false);
  }

  const isConnected = (status?.connections?.length > 0) || status?.connected || status?.status === 'connected';
  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          GA4 CONNECT · GOOGLE ANALYTICS 4{isConnected ? ' · CONNECTED' : ' · NOT CONNECTED'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            GA4.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Sync VDP engagement signals into your inventory.
            </span>
          </h1>
          {selectedDealer && (
            <span className="v2-chip" style={{
              background: isConnected ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.10)',
              borderColor: isConnected ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)',
              color: isConnected ? 'var(--c4)' : 'var(--c1)'
            }}>
              {isConnected ? <><span className="v2-pulse-dot" /> Connected</> : 'Not connected'}
            </span>
          )}
        </div>
      </div>

      {flash && (
        <div style={{ background: flash.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${flash.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: flash.type === 'success' ? 'var(--c4)' : 'var(--c1)', fontSize: 13, fontWeight: 500 }}>{flash.message}</span>
          <button onClick={() => setFlash(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      )}

      {dealers.length === 0 ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No dealers yet.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Add a domain to a dealer first, then connect GA4 here.</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 18 }}>
            <select value={selectedDealer} onChange={e => setSelectedDealer(e.target.value)} style={{ ...inp, minWidth: 240, cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name || d.dealer_name}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
              <div className="spinner" />
              <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING GA4 DATA…</span>
            </div>
          ) : (
            <>
              {/* Connection card */}
              {selectedDealer && (
                <div className={`v2-tile ${isConnected ? 'v2-tile-acid' : ''}`} style={{ padding: 22, marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <span className="v2-pulse-dot" style={{ background: isConnected ? 'var(--c4)' : 'var(--c1)' }} />
                        <span className="v2-serif" style={{ fontSize: 18, color: '#fff' }}>{isConnected ? 'GA4 connected' : 'Not connected'}</span>
                      </div>
                      {status && (
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12 }}>
                          {status.connections?.[0]?.ga4_property_id && (
                            <div>
                              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 3 }}>PROPERTY</div>
                              <div style={{ color: '#fff' }}>{status.connections[0].property_display_name || status.connections[0].ga4_property_id}</div>
                            </div>
                          )}
                          {status.last_sync_at && (
                            <div>
                              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 3 }}>LAST SYNC</div>
                              <div style={{ color: '#fff' }} className="v2-mono">{new Date(status.last_sync_at).toLocaleString()}</div>
                            </div>
                          )}
                          {status.total_analytics_rows > 0 && (
                            <div>
                              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 3 }}>ROWS</div>
                              <div style={{ color: '#fff' }} className="v2-num">{status.total_analytics_rows.toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!isConnected ? (
                        <button onClick={connectGA4}
                          style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
                          Connect GA4 →
                        </button>
                      ) : (
                        <>
                          <button onClick={syncNow} disabled={syncing} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: 'var(--c4)', opacity: syncing ? 0.6 : 1 }}>
                            {syncing ? 'Syncing…' : 'Sync now'}
                          </button>
                          <button onClick={disconnect} disabled={disconnecting} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8' }}>
                            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {isConnected && analytics.length === 0 && (
                <div className="v2-tile v2-tile-cyan" style={{ padding: 48, textAlign: 'center' }}>
                  <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 6 }}>No analytics data yet.</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Click "Sync now" to pull the latest VDP analytics from GA4.</div>
                </div>
              )}

              {analytics.length > 0 && (
                <div>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>top vehicles</div>
                  <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 12 }}>
                    By pageviews <span style={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', fontSize: 14 }}>· last 30 days</span>
                  </div>
                  <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                        <thead><tr>{['VIN', 'Pageviews', 'Avg time', 'Bounce rate', 'Form fills', 'Click-to-call'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {analytics.slice(0, 10).map((row, i) => (
                            <tr key={row.vin || i} style={{ transition: 'background 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{row.vin || '-'}</td>
                              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: '#fff', fontWeight: 700 }}>{(row.pageviews || 0).toLocaleString()}</td>
                              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{row.avg_time_on_page ? (row.avg_time_on_page >= 60 ? `${Math.floor(row.avg_time_on_page / 60)}m ${Math.round(row.avg_time_on_page % 60)}s` : `${Math.round(row.avg_time_on_page)}s`) : '-'}</td>
                              <td style={td}>
                                {row.bounce_rate ? (() => {
                                  const pct = Math.round(row.bounce_rate * 100);
                                  const color = pct > 70 ? 'var(--c1)' : pct < 40 ? 'var(--c4)' : 'rgba(255,255,255,0.7)';
                                  return <span className="v2-mono" style={{ color }}>{pct}%</span>;
                                })() : <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>}
                              </td>
                              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{row.form_fills ?? '-'}</td>
                              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{row.click_to_call ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function ScraperHealthPage() {
  const [platforms, setPlatforms] = useState([]);
  const [domains, setDomains] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cmsFilter, setCmsFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainDetail, setDomainDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dismissing, setDismissing] = useState(null);

  function rateColor(rate) {
    const r = parseFloat(rate || 0);
    if (r >= 80) return 'var(--c4)';
    if (r >= 50) return 'var(--c5)';
    return 'var(--c1)';
  }

  function statusChip(status) {
    const map = {
      healthy:  { bg: 'rgba(166,255,84,0.10)', border: 'rgba(166,255,84,0.30)', fg: 'var(--c4)' },
      degraded: { bg: 'rgba(255,200,87,0.12)', border: 'rgba(255,200,87,0.30)', fg: 'var(--c5)' },
      failed:   { bg: 'rgba(255,91,164,0.12)', border: 'rgba(255,91,164,0.30)', fg: 'var(--c1)' },
    };
    const m = map[status] || { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', fg: 'rgba(255,255,255,0.55)' };
    return (
      <span className="v2-mono" style={{ background: m.bg, color: m.fg, border: `1px solid ${m.border}`, borderRadius: 99, padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>
        {(status || 'unknown').toUpperCase()}
      </span>
    );
  }

  function loadData() {
    setLoading(true);
    const params = new URLSearchParams();
    if (cmsFilter) params.set('cms_type', cmsFilter);
    if (statusFilter) params.set('status', statusFilter);
    params.set('limit', '100');

    Promise.all([
      api('/scraper-health/platforms'),
      api(`/scraper-health/domains?${params}`),
      api('/scraper-health/alerts'),
    ]).then(([pRes, dRes, aRes]) => {
      setPlatforms(pRes.platforms || []);
      setDomains(dRes.domains || []);
      setTotal(dRes.total || 0);
      setAlerts(aRes.alerts || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { loadData(); }, [cmsFilter, statusFilter]);

  function openDetail(domain) {
    setSelectedDomain(domain);
    setDetailLoading(true);
    api(`/scraper-health/domains/${domain.domain_id}`)
      .then(res => { setDomainDetail(res); setDetailLoading(false); })
      .catch(() => setDetailLoading(false));
  }

  async function dismissAlert(domainId) {
    setDismissing(domainId);
    try {
      await api(`/scraper-health/alerts/${domainId}/dismiss`, { method: 'POST' });
      setAlerts(prev => prev.filter(a => a.domain_id !== domainId));
      setDomains(prev => prev.map(d => d.domain_id === domainId ? { ...d, consecutive_failures: 0, health_status: 'healthy' } : d));
      if (domainDetail?.stats?.domain_id === domainId) {
        setDomainDetail(prev => prev ? { ...prev, stats: { ...prev.stats, consecutive_failures: 0, health_status: 'healthy' } } : prev);
      }
    } catch (e) {}
    setDismissing(null);
  }

  const cmsList = [...new Set(platforms.map(p => p.cms_type).filter(Boolean))];
  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 12, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          SCRAPER HEALTH{platforms.length ? ` · ${platforms.length} CMS PLATFORMS` : ''}{total ? ` · ${total} DOMAINS` : ''}{alerts.length ? ` · ${alerts.length} NEED ATTENTION` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Health.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Per-platform success rates and failure streaks.
            </span>
          </h1>
          <button onClick={loadData} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
            <Icons.Refresh /> Refresh
          </button>
        </div>
      </div>

      {/* Alert banner */}
      {alerts.length > 0 && (
        <div className="v2-tile v2-tile-glow" style={{ padding: '12px 16px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexDirection: 'row' }}>
          <span className="v2-pulse-dot v2-pulse-dot--magenta" />
          <span style={{ color: 'var(--c1)', fontWeight: 700, fontSize: 13 }}>{alerts.length} domain{alerts.length > 1 ? 's' : ''} need attention</span>
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginLeft: 4 }}>
            - {alerts.map(a => a.domain_name || a.domain_url).slice(0, 3).join(', ')}{alerts.length > 3 ? ` +${alerts.length - 3} more` : ''}
          </span>
        </div>
      )}

      {/* Platform tiles */}
      {platforms.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>
          {platforms.map(p => {
            const rate = parseFloat(p.overall_success_rate || 0);
            const isSelected = cmsFilter === p.cms_type;
            return (
              <div key={p.cms_type} onClick={() => setCmsFilter(isSelected ? '' : p.cms_type)}
                className={`v2-tile ${isSelected ? 'v2-tile-violet' : ''}`}
                style={{ padding: 16, cursor: 'pointer', transition: 'all 0.15s', borderColor: isSelected ? 'rgba(122,77,255,0.40)' : undefined }}>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{p.cms_type}</div>
                <div className="v2-num" style={{ fontSize: 28, fontWeight: 700, color: rateColor(rate) }}>{rate.toFixed(0)}<span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>%</span></div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>success rate</div>
                <div className="v2-mono" style={{ display: 'flex', gap: 10, marginTop: 10, fontSize: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--c4)' }}>{p.healthy_domains} healthy</span>
                  <span style={{ color: 'var(--c5)' }}>{p.degraded_domains} degraded</span>
                  <span style={{ color: 'var(--c1)' }}>{p.failed_domains} failed</span>
                </div>
                <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{p.total_domains} domains · avg {parseFloat(p.avg_vehicles_per_domain || 0).toFixed(0)} vehicles</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter strip */}
      <div className="v2-tile" style={{ padding: 12, marginBottom: 14, display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em' }}>FILTER</span>
        <select value={cmsFilter} onChange={e => setCmsFilter(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="" style={{ background: '#111' }}>All platforms</option>
          {cmsList.map(c => <option key={c} value={c} style={{ background: '#111' }}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="" style={{ background: '#111' }}>All status</option>
          <option value="healthy" style={{ background: '#111' }}>Healthy</option>
          <option value="degraded" style={{ background: '#111' }}>Degraded</option>
          <option value="failed" style={{ background: '#111' }}>Failed</option>
        </select>
        {(cmsFilter || statusFilter) && (
          <button onClick={() => { setCmsFilter(''); setStatusFilter(''); }} className="v2-chip"
            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8' }}>
            ✕ Clear
          </button>
        )}
        <span className="v2-mono" style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{total} domains</span>
      </div>

      {/* Domains table */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING SCRAPER DATA…</span>
        </div>
      ) : domains.length === 0 ? (
        <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No scraper data yet.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Health metrics populate automatically as scrapers run.</div>
        </div>
      ) : (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
              <thead><tr>{['Domain', 'Platform', 'Success rate', 'Avg vehicles', 'Last success', 'Failures', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {domains.map(d => {
                  const hoursAgo = d.last_success_at ? (Date.now() - new Date(d.last_success_at)) / 3600000 : null;
                  const lastColor = !d.last_success_at ? 'var(--c1)' : hoursAgo > 72 ? 'var(--c1)' : hoursAgo > 24 ? 'var(--c5)' : 'rgba(255,255,255,0.55)';
                  const lastLabel = !d.last_success_at ? 'never' : hoursAgo < 1 ? '<1h ago' : hoursAgo < 24 ? `${Math.round(hoursAgo)}h ago` : new Date(d.last_success_at).toLocaleDateString();
                  return (
                    <tr key={d.domain_id} onClick={() => openDetail(d)}
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...td, color: '#fff', fontWeight: 600, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.domain_name || d.domain_url}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{d.cms_type || '-'}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: rateColor(d.success_rate) }}>{parseFloat(d.success_rate || 0).toFixed(0)}%</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{parseFloat(d.avg_vehicles_found || 0).toFixed(0)}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: lastColor }}>{lastLabel}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: d.consecutive_failures >= 3 ? 'var(--c1)' : 'rgba(255,255,255,0.6)' }}>{d.consecutive_failures || 0}</td>
                      <td style={td}>{statusChip(d.health_status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Domain detail modal */}
      {selectedDomain && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) { setSelectedDomain(null); setDomainDetail(null); } }}>
          <div className="v2-tile" style={{ width: '100%', maxWidth: 820, maxHeight: '85vh', overflow: 'auto', padding: 24, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>{selectedDomain.cms_type || 'unknown platform'}</div>
                <div className="v2-serif" style={{ fontSize: 22, color: '#fff' }}>{selectedDomain.domain_name || selectedDomain.domain_url}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(domainDetail?.stats?.consecutive_failures >= (domainDetail?.stats?.alert_threshold || 3)) && (
                  <button onClick={() => dismissAlert(selectedDomain.domain_id)} disabled={dismissing === selectedDomain.domain_id} className="v2-chip"
                    style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.10)', borderColor: 'rgba(255,91,164,0.30)', color: '#fbcfe8', fontSize: 11 }}>
                    {dismissing === selectedDomain.domain_id ? 'Dismissing…' : 'Dismiss alerts'}
                  </button>
                )}
                <button onClick={() => { setSelectedDomain(null); setDomainDetail(null); }} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8 }}>×</button>
              </div>
            </div>

            {detailLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
                <div className="spinner" />
                <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING DETAILS…</span>
              </div>
            ) : domainDetail ? (
              <>
                {domainDetail.stats && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
                    {[
                      { label: 'Total jobs', value: domainDetail.stats.total_jobs || 0, color: '#fff' },
                      { label: 'Success rate', value: `${parseFloat(domainDetail.stats.success_rate || 0).toFixed(1)}%`, color: rateColor(domainDetail.stats.success_rate) },
                      { label: 'Avg vehicles', value: parseFloat(domainDetail.stats.avg_vehicles_found || 0).toFixed(0), color: '#fff' },
                      { label: 'Consec. failures', value: domainDetail.stats.consecutive_failures || 0, color: domainDetail.stats.consecutive_failures >= 3 ? 'var(--c1)' : '#fff' },
                    ].map(s => (
                      <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 14 }}>
                        <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.label}</div>
                        <div className="v2-num" style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                )}

                {domainDetail.stats?.last_error && (
                  <div style={{ background: 'rgba(255,91,164,0.08)', border: '1px solid rgba(255,91,164,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#fbcfe8' }}>
                    <strong className="v2-mono" style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 8, color: 'var(--c1)' }}>LAST ERROR</strong>
                    {domainDetail.stats.last_error}
                  </div>
                )}

                {domainDetail.sitemap && (
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14, marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12 }}>
                      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sitemap source</div>
                      {domainDetail.sitemap.last_known_good_url && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Clear last-known-good sitemap? The next scrape will re-walk the candidate list from scratch.')) return;
                            try {
                              await api(`/scraper-health/domains/${selectedDomain.domain_id}/clear-sitemap-source`, { method: 'POST' });
                              setDomainDetail(prev => prev ? { ...prev, sitemap: { ...prev.sitemap, last_known_good_url: null, last_known_good_at: null, last_known_good_url_count: null } } : prev);
                            } catch (e) {}
                          }}
                          className="v2-chip"
                          style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', fontSize: 10, padding: '3px 9px' }}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {domainDetail.sitemap.last_known_good_url ? (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', wordBreak: 'break-all', fontFamily: "'JetBrains Mono', monospace" }}>
                          <a href={domainDetail.sitemap.last_known_good_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--c5)', textDecoration: 'none' }}>
                            {domainDetail.sitemap.last_known_good_url}
                          </a>
                        </div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                          worked last on {new Date(domainDetail.sitemap.last_known_good_at).toLocaleString()} · produced {domainDetail.sitemap.last_known_good_url_count} URLs
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10, fontStyle: 'italic' }}>
                        No sitemap has succeeded yet for this domain.
                      </div>
                    )}

                    {/* 14d outcome sparkline */}
                    {domainDetail.sitemap.history?.length > 0 && (
                      <>
                        <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>14d outcomes (most recent first)</div>
                        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          {domainDetail.sitemap.history.map(h => {
                            const color = h.sitemap_outcome === 'success' ? 'var(--c4)'
                              : h.sitemap_outcome === 'fallback' ? 'var(--c5)'
                              : h.sitemap_outcome === 'parser_miss' ? 'var(--c5)'
                              : h.sitemap_outcome === 'blocked' ? 'var(--c1)'
                              : h.sitemap_outcome === 'not_found' ? 'rgba(255,255,255,0.35)'
                              : 'rgba(255,255,255,0.20)';
                            return (
                              <div key={h.id}
                                title={`${new Date(h.created_at).toLocaleString()}\n${h.sitemap_outcome}\n${h.sitemap_url || '(no winning URL)'}\nurls_found: ${h.urls_found ?? '-'}`}
                                style={{ width: 12, height: 22, background: color, borderRadius: 3, cursor: 'help' }} />
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono', monospace" }}>
                          {['success', 'fallback', 'parser_miss', 'blocked', 'not_found'].map(k => {
                            const count = domainDetail.sitemap.history.filter(h => h.sitemap_outcome === k).length;
                            if (!count) return null;
                            return <span key={k}>{k}: {count}</span>;
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recent jobs</div>
                {domainDetail.history?.length === 0 ? (
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, padding: 16 }}>No job history yet.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead><tr>{['Time', 'Status', 'Vehicles', 'Duration', 'Error'].map(h => <th key={h} style={{ ...th, padding: '8px 10px' }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {domainDetail.history.map(row => (
                          <tr key={row.id}>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{new Date(row.recorded_at).toLocaleString()}</td>
                            <td style={td}>{statusChip(row.status)}</td>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.7)' }}>{row.vehicles_found ?? '-'}</td>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,0.55)' }}>{row.duration_ms ? `${(row.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                            <td style={{ ...td, color: 'var(--c1)', fontSize: 11, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.error || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>No data available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DealersPage() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDealer, setEditDealer] = useState(null);
  const [showThresholds, setShowThresholds] = useState(null);
  const [flash, setFlash] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', website: '', city: '', state: '', phone: '', oem_brand: '', domain_id: '' });

  function showFlash(type, message) {
    setFlash({ type, message });
    setTimeout(() => setFlash(null), 4000);
  }

  function loadDealers() {
    setLoading(true);
    api('/my-dealers')
      .then(res => {
        setDealers(Array.isArray(res) ? res : (res.dealers || []));
      })
      .catch(() => setDealers([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadDealers(); }, []);

  function openAdd() {
    setEditDealer(null);
    setForm({ name: '', website: '', city: '', state: '', phone: '', oem_brand: '', domain_id: '' });
    setShowAddModal(true);
  }

  function openEdit(dealer) {
    setEditDealer(dealer);
    setForm({
      name: dealer.name || '',
      website: dealer.website || '',
      city: dealer.city || '',
      state: dealer.state || '',
      phone: dealer.phone || '',
      oem_brand: dealer.oem_brand || '',
      domain_id: dealer.domain_id != null ? String(dealer.domain_id) : ''
    });
    setShowAddModal(true);
  }

  async function saveDealer() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        domain_id: form.domain_id !== '' ? parseInt(form.domain_id) : undefined
      };
      if (editDealer) {
        await api(`/my-dealers/${editDealer.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        showFlash('success', 'Dealer updated successfully.');
      } else {
        await api('/my-dealers', { method: 'POST', body: JSON.stringify(payload) });
        showFlash('success', 'Dealer added successfully.');
      }
      setShowAddModal(false);
      loadDealers();
    } catch (e) {
      showFlash('error', 'Failed to save dealer. Please try again.');
    }
    setSaving(false);
  }

  async function deleteDealer(dealer) {
    if (!window.confirm(`Delete "${dealer.name}"? This cannot be undone.`)) return;
    try {
      await api(`/my-dealers/${dealer.id}`, { method: 'DELETE' });
      showFlash('success', 'Dealer deleted.');
      loadDealers();
    } catch (e) {
      showFlash('error', 'Failed to delete dealer.');
    }
  }

  const inputStyle = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };

  function fmtRelative(ts) {
    if (!ts) return 'never';
    const diffMs = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Aggregate stats from real data
  const totalVehicles = dealers.reduce((sum, d) => sum + (Number(d.vehicle_count) || 0), 0);
  const linkedDomains = dealers.filter(d => d.domain_url).length;
  const recentlyScraped = dealers.filter(d => d.last_scraped && (Date.now() - new Date(d.last_scraped).getTime()) < 24 * 3600 * 1000).length;
  const brands = Array.from(new Set(dealers.map(d => d.oem_brand).filter(Boolean)));
  const primaryDealer = dealers[0];

  return (
    <div className="page-content">
      {/* Flash message */}
      {flash && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: flash.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)',
          border: `1px solid ${flash.type === 'success' ? 'rgba(166,255,84,0.4)' : 'rgba(255,91,164,0.4)'}`,
          color: flash.type === 'success' ? 'var(--c4)' : 'var(--c1)',
          borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 500,
          backdropFilter: 'blur(12px)'
        }}>
          {flash.message}
        </div>
      )}

      {/* Hero */}
      <div style={{ marginBottom: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          DEALERS · {dealers.length} ACTIVE{brands.length ? ` · ${brands.length} BRAND${brands.length > 1 ? 'S' : ''}` : ''}{primaryDealer && primaryDealer.last_scraped ? ` · LAST CRAWL ${fmtRelative(primaryDealer.last_scraped).toUpperCase()}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            The roster.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              {dealers.length === 0 ? 'Drop a domain. We detect the rest.' : dealers.length === 1 ? 'One lot, full coverage, one console.' : `${dealers.length} lots, one console.`}
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={openAdd} className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.35)', color: '#d9f99d', cursor: 'pointer' }}>
              ＋ Add dealer
            </button>
            {dealers.length > 0 && linkedDomains < dealers.length && (
              <span className="v2-chip" style={{ background: 'rgba(255,200,87,0.10)', borderColor: 'rgba(255,200,87,0.30)', color: '#fde68a' }}>
                ⚠ {dealers.length - linkedDomains} unlinked
              </span>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING ROSTER…</span>
        </div>
      ) : dealers.length === 0 ? (
        <div className="v2-bento">
          <div className="v2-tile v2-tile-acid" style={{ gridColumn: 'span 8', gridRow: 'span 4', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>onboard your first lot</div>
            <div>
              <div className="v2-serif" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', lineHeight: 1.05, marginBottom: 14 }}>
                Drop a domain.<br />
                <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>We detect the rest.</span>
              </div>
              <button onClick={openAdd} style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 12, color: '#fff', padding: '12px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
                ＋ Add your first dealer
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span className="v2-chip" style={{ fontSize: 10, padding: '3px 8px' }}>CMS auto-detect</span>
              <span className="v2-chip" style={{ fontSize: 10, padding: '3px 8px' }}>first scrape ~6m</span>
              <span className="v2-chip" style={{ fontSize: 10, padding: '3px 8px' }}>no contract</span>
            </div>
          </div>
          <div className="v2-tile v2-tile-cyan" style={{ gridColumn: 'span 4', gridRow: 'span 4' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12 }}>what you'll get</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--c3)', marginTop: 6, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Hourly inventory snapshots</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>VIN-level diffs · price drops · sold detection</div>
                </div>
              </li>
              <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--c1)', marginTop: 6, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Self-healing scrapers</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>CMS changes? We patch and ship</div>
                </div>
              </li>
              <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--c4)', marginTop: 6, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Smart VLA feeds</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Google-ready, auto-published</div>
                </div>
              </li>
              <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--c5)', marginTop: 6, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Competitive watch</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Track rival lots in your market</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="v2-bento">
          {/* HERO - primary dealer */}
          {primaryDealer && (
            <div className="v2-tile v2-tile-glow" style={{ gridColumn: 'span 8', gridRow: 'span 4', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.18)', borderColor: 'rgba(255,91,164,0.4)', color: '#fbcfe8' }}>
                    <span className="v2-pulse-dot v2-pulse-dot--magenta" />
                    PRIMARY · agency
                  </span>
                  <span className="v2-chip">dealer #{primaryDealer.id}</span>
                  {primaryDealer.oem_brand && (
                    <span className="v2-chip" style={{ background: 'rgba(122,77,255,0.18)', borderColor: 'rgba(122,77,255,0.35)', color: '#c4b5fd' }}>
                      {primaryDealer.oem_brand}
                    </span>
                  )}
                </div>
                {primaryDealer.domain_url && (
                  <a href={primaryDealer.domain_url} target="_blank" rel="noopener noreferrer" className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>
                    {primaryDealer.domain_url.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
                  </a>
                )}
              </div>

              <div className="v2-serif" style={{ fontSize: 'clamp(28px, 3.2vw, 42px)', lineHeight: 1, color: '#fff', marginBottom: 6 }}>
                {primaryDealer.name}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 22 }}>
                {[primaryDealer.city, primaryDealer.state].filter(Boolean).join(', ') || 'Location not set'}
                {primaryDealer.oem_brand ? ` · ${primaryDealer.oem_brand} franchise` : ''}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, marginBottom: 'auto' }}>
                <div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>vehicles</div>
                  <div className="v2-num" style={{ fontSize: 32, fontWeight: 300, color: '#fff' }}>
                    {primaryDealer.vehicle_count != null ? Number(primaryDealer.vehicle_count).toLocaleString() : '-'}
                  </div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>in inventory</div>
                </div>
                <div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>last scrape</div>
                  <div className="v2-num" style={{ fontSize: 32, fontWeight: 300, color: '#fff' }}>
                    {primaryDealer.last_scraped ? fmtRelative(primaryDealer.last_scraped).replace(' ago', '') : '-'}
                  </div>
                  <div className="v2-mono" style={{ fontSize: 10, color: primaryDealer.last_scraped ? 'var(--c4)' : 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                    {primaryDealer.last_scraped ? 'fresh' : 'never run'}
                  </div>
                </div>
                <div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>domain</div>
                  <div className="v2-num" style={{ fontSize: 32, fontWeight: 300, color: '#fff' }}>
                    {primaryDealer.domain_id ? '✓' : '-'}
                  </div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                    {primaryDealer.domain_id ? `linked · #${primaryDealer.domain_id}` : 'not linked'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 22, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <button onClick={() => openEdit(primaryDealer)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
                  Edit details
                </button>
                <button onClick={() => setShowThresholds(primaryDealer)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}>
                  Alert thresholds
                </button>
                <button onClick={() => deleteDealer(primaryDealer)} className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.08)', borderColor: 'rgba(255,91,164,0.25)', color: '#fbcfe8', marginLeft: 'auto' }}>
                  Delete
                </button>
              </div>
            </div>
          )}

          {/* ROSTER SUMMARY */}
          <div className="v2-tile v2-tile-cyan" style={{ gridColumn: 'span 4', gridRow: 'span 2' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12 }}>total roster</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
              <div className="v2-num" style={{ fontSize: 64, fontWeight: 300, color: '#fff' }}>{dealers.length}</div>
              <div style={{ paddingBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>{dealers.length === 1 ? 'dealership tracked' : 'dealerships tracked'}</div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'var(--c4)', marginTop: 4 }}>
                  {totalVehicles.toLocaleString()} vehicles total
                </div>
              </div>
            </div>
          </div>

          {/* ONBOARD CTA */}
          <button onClick={openAdd} className="v2-tile v2-tile-acid" style={{ gridColumn: 'span 4', gridRow: 'span 2', textAlign: 'left', cursor: 'pointer', color: '#fff', display: 'block' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>onboard a new lot</div>
              <span className="v2-mono" style={{ fontSize: 11, color: 'var(--c4)' }}>＋ add</span>
            </div>
            <div className="v2-serif" style={{ fontSize: 24, marginTop: 10, lineHeight: 1.15 }}>
              Drop a domain.<br />
              <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>We detect the rest.</span>
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 12 }}>
              <span className="v2-chip" style={{ fontSize: 10, padding: '2px 7px' }}>CMS auto-detect</span>
              <span className="v2-chip" style={{ fontSize: 10, padding: '2px 7px' }}>~6m to first scrape</span>
            </div>
          </button>

          {/* DEALER ROSTER */}
          <div className="v2-tile" style={{ gridColumn: 'span 8', gridRow: `span ${Math.max(3, Math.ceil(dealers.length / 4) + 2)}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>roster · all dealers</div>
                <div className="v2-serif" style={{ fontSize: 22, marginTop: 2 }}>The full lineup</div>
              </div>
              <span className="v2-chip">{dealers.length} of {dealers.length}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {dealers.map((dealer, idx) => (
                <div key={dealer.id} style={{
                  background: idx === 0 ? 'rgba(255,91,164,0.05)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${idx === 0 ? 'rgba(255,91,164,0.30)' : 'rgba(255,255,255,0.07)'}`,
                  borderRadius: 14,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {dealer.name}
                      </div>
                      {(dealer.city || dealer.state) && (
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                          {[dealer.city, dealer.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                    {dealer.oem_brand && (
                      <span className="v2-chip" style={{ fontSize: 9, padding: '2px 6px', background: 'rgba(122,77,255,0.15)', borderColor: 'rgba(122,77,255,0.3)', color: '#c4b5fd' }}>
                        {dealer.oem_brand}
                      </span>
                    )}
                  </div>

                  {dealer.domain_url ? (
                    <a href={dealer.domain_url} target="_blank" rel="noopener noreferrer" className="v2-mono"
                       style={{ fontSize: 10, color: 'var(--c3)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {dealer.domain_url.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
                    </a>
                  ) : (
                    <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>no domain linked</span>
                  )}

                  <div style={{ display: 'flex', gap: 14, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div>
                      <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>VEHICLES</div>
                      <div className="v2-num" style={{ fontSize: 18, fontWeight: 500, color: '#fff' }}>
                        {dealer.vehicle_count != null ? Number(dealer.vehicle_count).toLocaleString() : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>LAST SCRAPE</div>
                      <div className="v2-mono" style={{ fontSize: 11, color: dealer.last_scraped ? 'var(--c4)' : 'rgba(255,255,255,0.3)', paddingTop: 4 }}>
                        {dealer.last_scraped ? fmtRelative(dealer.last_scraped) : 'never'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 5, marginTop: 'auto' }}>
                    <button onClick={() => openEdit(dealer)} className="v2-chip"
                            style={{ flex: 1, justifyContent: 'center', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', fontSize: 10 }}>
                      Edit
                    </button>
                    <button onClick={() => setShowThresholds(dealer)} className="v2-chip"
                            style={{ flex: 1, justifyContent: 'center', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', fontSize: 10 }}>
                      Alerts
                    </button>
                    <button onClick={() => deleteDealer(dealer)} title="Delete" className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10, padding: '4px 8px' }}>
                      <Icons.Trash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* COVERAGE SNAPSHOT */}
          <div className="v2-tile v2-tile-violet" style={{ gridColumn: 'span 4', gridRow: 'span 3', display: 'flex', flexDirection: 'column' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>coverage snapshot</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>scraped in last 24h</div>
                  <div className="v2-num" style={{ fontSize: 16, color: '#fff' }}>{recentlyScraped}/{dealers.length}</div>
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${dealers.length ? (recentlyScraped / dealers.length) * 100 : 0}%`, background: 'var(--c4)', borderRadius: 99 }} />
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>domains linked</div>
                  <div className="v2-num" style={{ fontSize: 16, color: '#fff' }}>{linkedDomains}/{dealers.length}</div>
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${dealers.length ? (linkedDomains / dealers.length) * 100 : 0}%`, background: 'var(--c3)', borderRadius: 99 }} />
                </div>
              </div>

              {brands.length > 0 && (
                <div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>brands tracked</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {brands.map(b => (
                      <span key={b} className="v2-chip" style={{ fontSize: 10, padding: '2px 7px', background: 'rgba(122,77,255,0.15)', borderColor: 'rgba(122,77,255,0.3)', color: '#c4b5fd' }}>
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>
                {totalVehicles.toLocaleString()}
              </div>
              <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                vehicles across all lots
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
             onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div className="v2-tile" style={{ width: 520, maxWidth: '92vw', padding: 28, background: 'linear-gradient(145deg, rgba(20,20,28,0.98), rgba(15,15,22,0.98))', borderRadius: 20 }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, letterSpacing: '0.06em' }}>
              {editDealer ? `EDITING DEALER #${editDealer.id}` : 'ONBOARDING'}
            </div>
            <h2 className="v2-serif" style={{ fontSize: 26, fontWeight: 400, color: '#fff', margin: 0, marginBottom: 22 }}>
              {editDealer ? 'Edit dealer' : 'Add a new dealer'}
            </h2>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={labelStyle}>Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                       placeholder="Dealer name" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Website</label>
                <input type="text" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                       placeholder="https://example.com" style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                         placeholder="City" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>State</label>
                  <input type="text" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                         placeholder="CA" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input type="text" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                       placeholder="(555) 000-0000" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>OEM Brand</label>
                <input type="text" value={form.oem_brand} onChange={e => setForm(f => ({ ...f, oem_brand: e.target.value }))}
                       placeholder="e.g. Toyota, Ford" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Domain ID (optional)</label>
                <input type="number" value={form.domain_id} onChange={e => setForm(f => ({ ...f, domain_id: e.target.value }))}
                       placeholder="Linked domain ID" style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAddModal(false)} className="v2-chip"
                      style={{ cursor: 'pointer', padding: '9px 18px', fontSize: 12, background: 'rgba(255,255,255,0.04)' }}>
                Cancel
              </button>
              <button onClick={saveDealer} disabled={saving || !form.name.trim()}
                      style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 22px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (saving || !form.name.trim()) ? 0.5 : 1, boxShadow: '0 4px 20px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                {saving ? 'Saving…' : editDealer ? 'Save changes' : 'Add dealer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Thresholds Modal */}
      {showThresholds && (
        <AlertThresholdsModal
          dealer={showThresholds}
          onClose={() => setShowThresholds(null)}
          onSaved={() => showFlash('success', 'Alert thresholds saved.')}
        />
      )}
    </div>
  );
}

function VLAFeedPage() {
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const activeTab = urlTab || 'overview';
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [feedInfo, setFeedInfo] = useState(null);
  const [config, setConfig] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [reactiveConfig, setReactiveConfig] = useState(null);
  const [searchTerms, setSearchTerms] = useState([]);
  const [searchTermsTotal, setSearchTermsTotal] = useState(0);
  const [labels, setLabels] = useState(null);
  const [decayCurve, setDecayCurve] = useState([]);

  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [flash, setFlash] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const [configForm, setConfigForm] = useState(null);
  const [reactiveForm, setReactiveForm] = useState(null);
  const [decayForm, setDecayForm] = useState([]);

  const [overrideVin, setOverrideVin] = useState('');
  const [overrideForceInclude, setOverrideForceInclude] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');
  const [addingOverride, setAddingOverride] = useState(false);

  function showFlash(type, message) {
    setFlash({ type, message });
    setTimeout(() => setFlash(null), 4500);
  }

  useEffect(() => {
    api('/my-dealers').then(res => {
      const list = Array.isArray(res) ? res : (res.dealers || []);
      setDealers(list);
      if (list.length > 0) setSelectedDealerId(list[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedDealerId) return;
    setLoading(true);
    Promise.allSettled([
      api(`/vla/dealers/${selectedDealerId}/feeds/vla`),
      api(`/vla/dealers/${selectedDealerId}/feeds/vla/config`),
      api(`/vla/dealers/${selectedDealerId}/feeds/vla/overrides`),
      api(`/dealers/${selectedDealerId}/inventory/boosts`),
      api(`/dealers/${selectedDealerId}/feeds/reactive-boost/config`),
      api(`/dealers/${selectedDealerId}/feeds/search-terms`),
      api(`/vla/dealers/${selectedDealerId}/inventory/labels`),
      api(`/dealers/${selectedDealerId}/feeds/time-decay/config`),
    ]).then(([feedRes, cfgRes, ovRes, boostRes, reactRes, stRes, lblRes, decayRes]) => {
      const safe = r => r.status === 'fulfilled' ? r.value : null;
      const feedData = safe(feedRes);
      const cfgData = safe(cfgRes);
      const ovData = safe(ovRes);
      const boostData = safe(boostRes);
      const reactData = safe(reactRes);
      const stData = safe(stRes);
      const decayData = safe(decayRes);
      setFeedInfo(feedData);
      setConfig(cfgData);
      setConfigForm(cfgData ? { ...cfgData } : null);
      setOverrides(Array.isArray(ovData) ? ovData : (ovData?.overrides || []));
      setBoosts(boostData?.data || boostData || []);
      const reactiveRaw = reactData?.data || reactData || null;
      const reactive = reactiveRaw ? { ...reactiveRaw, bid_multiplier: reactiveRaw.bid_multiplier ?? reactiveRaw.boost_bid_modifier, enabled: reactiveRaw.enabled ?? reactiveRaw.is_active } : null;
      setReactiveConfig(reactive);
      setReactiveForm(reactive ? { ...reactive } : null);
      setSearchTerms(stData?.data || stData || []);
      setSearchTermsTotal(stData?.total || 0);
      setLabels(safe(lblRes));
      const curve = Array.isArray(decayData) ? decayData : (decayData?.data?.curve_points || decayData?.points || (Array.isArray(decayData?.data) ? decayData.data : []));
      setDecayCurve(curve);
      setDecayForm(curve.map(p => ({ ...p })));
    }).finally(() => setLoading(false));
  }, [selectedDealerId]);

  async function regenerateFeed() {
    setRegenerating(true);
    try {
      await api(`/vla/dealers/${selectedDealerId}/feeds/vla/generate`, { method: 'POST' });
      showFlash('success', 'Feed regeneration triggered.');
      const res = await api(`/vla/dealers/${selectedDealerId}/feeds/vla`);
      setFeedInfo(res);
    } catch (e) { showFlash('error', 'Failed to trigger regeneration.'); }
    setRegenerating(false);
  }

  async function saveConfig() {
    if (!configForm) return;
    setSaving(true);
    try {
      const updates = { include_stars: configForm.include_stars, include_fix_it: configForm.include_fix_it, include_boost: configForm.include_boost, include_push_or_cut: configForm.include_push_or_cut, exclude_above_market: configForm.exclude_above_market, refresh_interval_hours: Number(configForm.refresh_interval_hours), is_active: configForm.is_active };
      await api(`/vla/dealers/${selectedDealerId}/feeds/vla/config`, { method: 'PUT', body: JSON.stringify(updates), headers: { 'Content-Type': 'application/json' } });
      setConfig({ ...configForm });
      if (reactiveForm) {
        const reactiveUpdates = {
          is_active: !!reactiveForm.enabled,
          boost_duration_days: Number(reactiveForm.boost_duration_days),
          boost_bid_modifier: Number(reactiveForm.bid_multiplier),
        };
        await api(`/dealers/${selectedDealerId}/feeds/reactive-boost/config`, { method: 'PUT', body: JSON.stringify(reactiveUpdates), headers: { 'Content-Type': 'application/json' } });
        setReactiveConfig({ ...reactiveForm });
      }
      showFlash('success', 'Settings saved.');
    } catch (e) { showFlash('error', 'Failed to save.'); }
    setSaving(false);
  }

  async function saveDecayCurve() {
    setSaving(true);
    try {
      const curve_points = decayForm.map(p => ({ ...p, bid_modifier: Number(p.bid_modifier), day_min: Number(p.day_min), day_max: Number(p.day_max) }));
      await api(`/dealers/${selectedDealerId}/feeds/time-decay/config`, { method: 'PUT', body: JSON.stringify({ curve_points }), headers: { 'Content-Type': 'application/json' } });
      setDecayCurve(decayForm.map(p => ({ ...p })));
      showFlash('success', 'Time decay curve saved.');
    } catch (e) { showFlash('error', 'Failed to save decay curve.'); }
    setSaving(false);
  }

  async function addOverride() {
    if (!overrideVin.trim()) return;
    setAddingOverride(true);
    try {
      await api(`/vla/dealers/${selectedDealerId}/feeds/vla/overrides`, { method: 'POST', body: JSON.stringify({ vin: overrideVin.trim(), force_include: overrideForceInclude, reason: overrideReason.trim() }), headers: { 'Content-Type': 'application/json' } });
      const res = await api(`/vla/dealers/${selectedDealerId}/feeds/vla/overrides`);
      setOverrides(Array.isArray(res) ? res : (res?.overrides || []));
      setOverrideVin(''); setOverrideReason('');
      showFlash('success', 'Override added.');
    } catch (e) { showFlash('error', 'Failed to add override.'); }
    setAddingOverride(false);
  }

  async function removeOverride(vin) {
    try {
      await api(`/vla/dealers/${selectedDealerId}/feeds/vla/overrides/${vin}`, { method: 'DELETE' });
      setOverrides(prev => prev.filter(o => o.vin !== vin));
    } catch (e) { showFlash('error', 'Failed to remove override.'); }
  }

  function copyFeedUrl() {
    const url = feedInfo?.content_url || feedInfo?.url || '';
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); });
  }

  // v2 tokens
  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '9px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  function Toggle({ value, onChange }) {
    return (
      <div onClick={onChange} style={{ width: 42, height: 24, borderRadius: 99, background: value ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,0.10)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0, boxShadow: value ? '0 0 12px rgba(122,77,255,0.4)' : 'none' }}>
        <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'config',   label: 'Settings' },
    { id: 'boosts',   label: 'Boosts' },
    { id: 'labels',   label: 'Labels' },
    { id: 'search',   label: 'Search Terms' },
  ];

  const feedUrl = feedInfo?.content_url || feedInfo?.url || '';
  const stats = feedInfo?.last_stats;
  const includedPct = stats?.total_vehicles > 0 ? Math.round(stats.included_count / stats.total_vehicles * 100) : 0;
  const selectedDealer = dealers.find(d => d.id === selectedDealerId);

  function fmtRelative(ts) {
    if (!ts) return null;
    const diffMs = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <div className="page-content">
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          VLA · GOOGLE VEHICLE LISTING ADS{selectedDealer ? ` · ${(selectedDealer.name || '').toUpperCase()}` : ''}{stats?.included_count != null ? ` · ${formatNumber(stats.included_count)} VEHICLES IN FEED` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            VLA feed.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Smart inclusion, reactive boosts, decay-aware bidding.
            </span>
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {dealers.length > 1 && (
              <select value={selectedDealerId || ''} onChange={e => setSelectedDealerId(Number(e.target.value))}
                style={{ ...inp, minWidth: 180, cursor: 'pointer', padding: '8px 12px' }}>
                {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name || d.dealer_name}</option>)}
              </select>
            )}
            <button onClick={regenerateFeed} disabled={regenerating || !selectedDealerId} className="v2-chip"
              style={{ cursor: 'pointer', background: 'rgba(122,77,255,0.10)', borderColor: 'rgba(122,77,255,0.35)', color: '#c4b5fd', opacity: regenerating ? 0.6 : 1 }}>
              {regenerating ? '⟳ Regenerating…' : <><Icons.Refresh /> Regenerate</>}
            </button>
          </div>
        </div>
      </div>

      {/* Flash */}
      {flash && (
        <div style={{ background: flash.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${flash.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backdropFilter: 'blur(8px)' }}>
          <span style={{ color: flash.type === 'success' ? 'var(--c4)' : 'var(--c1)', fontSize: 13, fontWeight: 500 }}>{flash.message}</span>
          <button onClick={() => setFlash(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>
      )}

      {/* Feed URL banner */}
      {feedUrl && !loading && (
        <div className="v2-tile" style={{ padding: '12px 16px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flexDirection: 'row' }}>
          {feedInfo?.is_active ? (
            <span className="v2-chip" style={{ background: 'rgba(166,255,84,0.10)', borderColor: 'rgba(166,255,84,0.30)', color: 'var(--c4)', fontSize: 10 }}>
              <span className="v2-pulse-dot" /> LIVE
            </span>
          ) : (
            <span className="v2-chip" style={{ background: 'rgba(255,91,164,0.10)', borderColor: 'rgba(255,91,164,0.30)', color: 'var(--c1)', fontSize: 10 }}>
              INACTIVE
            </span>
          )}
          <code className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{feedUrl}</code>
          <button onClick={copyFeedUrl} className="v2-chip"
            style={{ cursor: 'pointer', background: copiedUrl ? 'rgba(166,255,84,0.10)' : 'rgba(255,255,255,0.04)', borderColor: copiedUrl ? 'rgba(166,255,84,0.35)' : undefined, color: copiedUrl ? 'var(--c4)' : 'rgba(255,255,255,0.85)' }}>
            {copiedUrl ? <><Icons.Check /> Copied</> : <><Icons.Copy /> Copy URL</>}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 22, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => navigate(`/vla/${t.id}`)}
            className={activeTab === t.id ? 'v2-chip' : ''}
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              whiteSpace: 'nowrap',
              background: activeTab === t.id ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: activeTab === t.id ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: activeTab === t.id ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
              transition: 'all 0.15s'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING FEED DATA…</span>
        </div>
      ) : !selectedDealerId ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No dealers found.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Add dealers to manage VLA feeds.</div>
        </div>
      ) : (
        <>
          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <>
              {/* Stats KPIs */}
              {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
                  <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>total vehicles</div>
                    <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{formatNumber(stats.total_vehicles)}</div>
                  </div>
                  <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>included in feed</div>
                    <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c4)' }}>{formatNumber(stats.included_count)}</div>
                  </div>
                  <div className="v2-tile" style={{ padding: 18 }}>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>excluded</div>
                    <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c1)' }}>{formatNumber(stats.excluded_count)}</div>
                  </div>
                  <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
                    <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>inclusion rate</div>
                    <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: includedPct >= 70 ? 'var(--c4)' : includedPct >= 40 ? 'var(--c5)' : 'var(--c1)' }}>{includedPct}%</div>
                    <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden', marginTop: 8 }}>
                      <div style={{ width: `${includedPct}%`, height: '100%', background: includedPct >= 70 ? 'var(--c4)' : includedPct >= 40 ? 'var(--c5)' : 'var(--c1)' }} />
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginBottom: 14 }}>
                {/* Generation history */}
                <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>history</div>
                      <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Generation timeline</div>
                    </div>
                    {feedInfo?.last_generated && (
                      <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>last {fmtRelative(feedInfo.last_generated)}</span>
                    )}
                  </div>
                  {feedInfo?.history?.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>{['Generated', 'Included', 'Total', 'Rate'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {feedInfo.history.slice(0, 6).map((h, i) => {
                            const rate = h.total_vehicles > 0 ? Math.round(h.included_count / h.total_vehicles * 100) : 0;
                            return (
                              <tr key={i}>
                                <td style={{ ...td, color: 'rgba(255,255,255,0.55)', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{fmtRelative(h.generated_at)}</td>
                                <td style={{ ...td, color: 'var(--c4)', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{formatNumber(h.included_count)}</td>
                                <td style={{ ...td, color: 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono', monospace" }}>{formatNumber(h.total_vehicles)}</td>
                                <td style={td}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                                      <div style={{ width: `${rate}%`, height: '100%', background: rate >= 70 ? 'var(--c4)' : rate >= 40 ? 'var(--c5)' : 'var(--c1)', borderRadius: 99 }} />
                                    </div>
                                    <span className="v2-mono" style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.55)', minWidth: 28 }}>{rate}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ padding: '32px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>No generation history yet</div>
                  )}
                </div>

                {/* Label summary (label_0) */}
                <div className="v2-tile v2-tile-cyan">
                  <div style={{ marginBottom: 14 }}>
                    <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>quadrant</div>
                    <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Inventory labels</div>
                  </div>
                  {labels?.distributions?.label_0 ? (() => {
                    const dist = labels.distributions.label_0;
                    const entries = Object.entries(dist).filter(([, v]) => v > 0).sort(([, a], [, b]) => b - a);
                    const total = entries.reduce((s, [, v]) => s + v, 0);
                    const cfg = { stars: { color: 'var(--c5)', label: 'Stars' }, boost: { color: 'var(--c3)', label: 'Boost' }, fix_it: { color: 'var(--c1)', label: 'Fix It' }, push_or_cut: { color: '#c084fc', label: 'Push or Cut' } };
                    return entries.map(([k, v]) => {
                      const c = cfg[k] || { color: 'rgba(255,255,255,0.4)', label: k };
                      const pct = total > 0 ? Math.round(v / total * 100) : 0;
                      return (
                        <div key={k} style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: c.color, fontWeight: 600 }}>{c.label}</span>
                            <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{formatNumber(v)} · {pct}%</span>
                          </div>
                          <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: c.color, opacity: 0.85, borderRadius: 99, transition: 'width 0.5s ease' }} />
                          </div>
                        </div>
                      );
                    });
                  })() : <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 13, textAlign: 'center', padding: 16 }}>No label data</div>}
                </div>
              </div>

              {/* VIN Overrides */}
              <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>vin overrides</div>
                    <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Force include or exclude</div>
                  </div>
                  <span className="v2-chip">{overrides.length} {overrides.length === 1 ? 'override' : 'overrides'}</span>
                </div>

                {/* Add form */}
                <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input value={overrideVin} onChange={e => setOverrideVin(e.target.value.toUpperCase())} placeholder="VIN (17 chars)" maxLength={17}
                    style={{ ...inp, width: 200, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }} />
                  <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Reason (optional)"
                    style={{ ...inp, flex: 1, minWidth: 140 }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Toggle value={overrideForceInclude} onChange={() => setOverrideForceInclude(v => !v)} />
                    <span className="v2-mono" style={{ fontSize: 11, color: overrideForceInclude ? 'var(--c4)' : 'var(--c1)', fontWeight: 600, minWidth: 100, letterSpacing: '0.04em' }}>{overrideForceInclude ? 'FORCE INCLUDE' : 'FORCE EXCLUDE'}</span>
                  </div>
                  <button onClick={addOverride} disabled={addingOverride || !overrideVin.trim()}
                    style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 999, color: '#fff', padding: '9px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (addingOverride || !overrideVin.trim()) ? 0.5 : 1, letterSpacing: '0.02em', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
                    {addingOverride ? 'Adding…' : '+ Add override'}
                  </button>
                </div>

                {overrides.length === 0 ? (
                  <div style={{ padding: '32px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>No overrides configured</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                      <thead><tr>{['VIN', 'Type', 'Reason', 'Added', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {overrides.map((o, i) => (
                          <tr key={o.vin || i}>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--c4)' }}>{o.vin}</td>
                            <td style={td}>
                              <span className="v2-chip" style={{ background: o.force_include ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.10)', borderColor: o.force_include ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)', color: o.force_include ? 'var(--c4)' : 'var(--c1)', fontSize: 10 }}>
                                {o.force_include ? '✓ Include' : '✕ Exclude'}
                              </span>
                            </td>
                            <td style={{ ...td, color: 'rgba(255,255,255,0.55)' }}>{o.reason || <span style={{ color: 'rgba(255,255,255,0.2)' }}>-</span>}</td>
                            <td style={{ ...td, fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace" }}>{o.created_at ? fmtRelative(o.created_at) : '-'}</td>
                            <td style={td}>
                              <button onClick={() => removeOverride(o.vin)} className="v2-chip"
                                style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10 }}>Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* SETTINGS */}
          {activeTab === 'config' && !configForm && (
            <div className="v2-tile v2-tile-violet" style={{ padding: 48, textAlign: 'center' }}>
              <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No feed configured yet.</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18 }}>Regenerate the feed to initialize configuration.</div>
              <button onClick={regenerateFeed} disabled={regenerating}
                style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 12, color: '#fff', padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: regenerating ? 0.6 : 1, boxShadow: '0 8px 32px -8px rgba(122,77,255,0.5)' }}>
                {regenerating ? 'Regenerating…' : 'Initialize feed'}
              </button>
            </div>
          )}
          {activeTab === 'config' && configForm && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, alignItems: 'start' }}>
              {/* Inclusion Rules */}
              <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>inclusion rules</div>
                  <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Which segments ship</div>
                </div>
                <div>
                  {[
                    { key: 'include_stars',       label: 'Stars',       desc: 'Top performers · high demand · good price', color: 'var(--c5)' },
                    { key: 'include_boost',       label: 'Boost',       desc: 'Vehicles that need a visibility push',       color: 'var(--c3)' },
                    { key: 'include_fix_it',      label: 'Fix It',      desc: 'Needs price or quality attention',          color: 'var(--c1)' },
                    { key: 'include_push_or_cut', label: 'Push or Cut', desc: 'Aging - push harder or remove',             color: '#c084fc' },
                  ].map(({ key, label, desc, color }) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.04)', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
                        <div style={{ width: 4, height: 32, borderRadius: 99, background: color, flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{label}</div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{desc}</div>
                        </div>
                      </div>
                      <Toggle value={!!configForm[key]} onChange={() => setConfigForm(prev => ({ ...prev, [key]: !prev[key] }))} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Right column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Feed controls */}
                <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>feed controls</div>
                    <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Master switches</div>
                  </div>
                  <div>
                    {[
                      { key: 'is_active',             label: 'Feed active',            desc: 'Enable or disable the VLA feed entirely' },
                      { key: 'exclude_above_market',  label: 'Exclude above-market',   desc: 'Drop vehicles priced above market median' },
                    ].map(({ key, label, desc }) => (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.04)', gap: 12 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{label}</div>
                          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{desc}</div>
                        </div>
                        <Toggle value={!!configForm[key]} onChange={() => setConfigForm(prev => ({ ...prev, [key]: !prev[key] }))} />
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>Refresh interval</div>
                        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>auto-regenerate cadence</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="number" min={1} max={168} value={configForm.refresh_interval_hours || 24}
                          onChange={e => setConfigForm(prev => ({ ...prev, refresh_interval_hours: e.target.value }))}
                          style={{ ...inp, width: 64, textAlign: 'center', padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace" }} />
                        <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>hrs</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reactive Boost */}
                {reactiveForm && (
                  <div className="v2-tile v2-tile-violet" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>reactive boost</div>
                      <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Auto-pump on rivals</div>
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>Enable reactive boost</div>
                        <Toggle value={!!reactiveForm.enabled} onChange={() => setReactiveForm(prev => ({ ...prev, enabled: !prev.enabled }))} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                        {[
                          { label: 'Boost duration', sub: 'days', key: 'boost_duration_days', min: 1,   max: 90, step: 1,   def: 7   },
                          { label: 'Bid multiplier', sub: '×',    key: 'bid_multiplier',      min: 0.1, max: 10, step: 0.1, def: 2.0 },
                        ].map((f, i) => (
                          <div key={f.key} style={{ padding: '14px 18px', borderRight: i === 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{f.label}</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              <input type="number" min={f.min} max={f.max} step={f.step} value={reactiveForm[f.key] || f.def}
                                onChange={e => setReactiveForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                style={{ ...inp, width: 80, padding: '7px 10px', fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }} />
                              <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>{f.sub}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Save */}
                <button onClick={saveConfig} disabled={saving}
                  style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 12, color: '#fff', padding: '12px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1, width: '100%', boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
                  {saving ? 'Saving…' : 'Save settings →'}
                </button>
              </div>
            </div>
          )}

          {/* BOOSTS */}
          {activeTab === 'boosts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Active boosts */}
              <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>active boosts</div>
                    <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Bid multipliers in flight</div>
                  </div>
                  <span className="v2-chip">{boosts.length} active</span>
                </div>
                {boosts.length === 0 ? (
                  <div style={{ padding: '36px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>No active boosts</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                      <thead><tr>{['VIN', 'Type', 'Bid modifier', 'Reason', 'Expires'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {boosts.map((b, i) => {
                          const typeConfig = {
                            competitive_reactive: { label: 'Reactive',   color: '#c084fc', bg: 'rgba(192,132,252,0.12)' },
                            time_decay:           { label: 'Time decay', color: 'var(--c3)', bg: 'rgba(34,211,238,0.10)' },
                          };
                          const tc = typeConfig[b.type] || { label: b.type || 'Manual', color: 'rgba(255,255,255,0.55)', bg: 'rgba(255,255,255,0.06)' };
                          const isExpired = b.expires_at && new Date(b.expires_at) < new Date();
                          return (
                            <tr key={b.vin || i} style={{ opacity: isExpired ? 0.45 : 1 }}>
                              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{b.vin || '-'}</td>
                              <td style={td}>
                                <span className="v2-chip" style={{ background: tc.bg, borderColor: 'transparent', color: tc.color, fontSize: 10 }}>{tc.label}</span>
                              </td>
                              <td style={td}>
                                <span className="v2-num" style={{ fontSize: 16, fontWeight: 700, color: isExpired ? 'rgba(255,255,255,0.3)' : 'var(--c5)' }}>{b.bid_modifier ? `${Number(b.bid_modifier).toFixed(2)}×` : '-'}</span>
                              </td>
                              <td style={{ ...td, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{b.reason || <span style={{ color: 'rgba(255,255,255,0.2)' }}>-</span>}</td>
                              <td style={{ ...td, fontSize: 11, color: isExpired ? 'var(--c1)' : 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono', monospace" }}>
                                {b.expires_at ? (isExpired ? 'expired' : fmtRelative(b.expires_at)?.replace('ago', 'left') || '-') : <span style={{ color: 'rgba(255,255,255,0.2)' }}>-</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Time Decay Curve */}
              <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>time decay curve</div>
                  <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Bid by days on lot</div>
                </div>
                {decayForm.length === 0 ? (
                  <div style={{ padding: '36px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>No decay curve configured</div>
                ) : (
                  <div style={{ padding: 18 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
                      {decayForm.map((point, i) => {
                        const modifier = Number(point.bid_modifier) || 1;
                        const color = modifier >= 2 ? 'var(--c4)' : modifier >= 1 ? 'var(--c5)' : 'var(--c1)';
                        return (
                          <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 14 }}>
                            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Tier {i + 1}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                              <div>
                                <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>DAY MIN</div>
                                <input type="number" min={0} value={point.day_min ?? ''} onChange={e => setDecayForm(prev => prev.map((p, j) => j === i ? { ...p, day_min: Number(e.target.value) } : p))}
                                  style={{ ...inp, width: '100%', padding: '7px 10px', boxSizing: 'border-box', fontFamily: "'JetBrains Mono', monospace" }} />
                              </div>
                              <div>
                                <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>DAY MAX</div>
                                <input type="number" min={0} value={point.day_max ?? ''} onChange={e => setDecayForm(prev => prev.map((p, j) => j === i ? { ...p, day_max: Number(e.target.value) } : p))}
                                  style={{ ...inp, width: '100%', padding: '7px 10px', boxSizing: 'border-box', fontFamily: "'JetBrains Mono', monospace" }} />
                              </div>
                            </div>
                            <div>
                              <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>BID MODIFIER</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="number" min={0.1} max={10} step={0.1} value={point.bid_modifier ?? ''} onChange={e => setDecayForm(prev => prev.map((p, j) => j === i ? { ...p, bid_modifier: e.target.value } : p))}
                                  style={{ ...inp, flex: 1, padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace" }} />
                                <span className="v2-num" style={{ fontSize: 18, fontWeight: 700, color, minWidth: 50, textAlign: 'right' }}>{modifier.toFixed(2)}×</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button onClick={saveDecayCurve} disabled={saving}
                      style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 999, color: '#fff', padding: '10px 24px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1, letterSpacing: '0.02em', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
                      {saving ? 'Saving…' : 'Save decay curve'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* LABELS */}
          {activeTab === 'labels' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
              {[
                { key: 'label_0', title: 'Quadrant',        sub: 'core inventory classification', colorMap: { stars: 'var(--c5)', boost: 'var(--c3)', fix_it: 'var(--c1)', push_or_cut: '#c084fc' },                       labelMap: { stars: 'Stars', boost: 'Boost', fix_it: 'Fix It', push_or_cut: 'Push or Cut' } },
                { key: 'label_1', title: 'Aging tier',      sub: 'inventory age classification',   colorMap: { fresh: 'var(--c4)', aging: 'var(--c5)', stale: 'var(--c1)', critical: '#ef4444' },                       labelMap: { fresh: 'Fresh', aging: 'Aging', stale: 'Stale', critical: 'Critical' } },
                { key: 'label_2', title: 'Price position',  sub: 'market price benchmark',         colorMap: { below_market: 'var(--c4)', at_market: 'var(--c3)', above_market: 'var(--c1)' },                          labelMap: { below_market: 'Below market', at_market: 'At market', above_market: 'Above market' } },
                { key: 'label_3', title: 'Engagement tier', sub: 'buyer intent from VDP analytics',colorMap: { high: 'var(--c4)', medium: 'var(--c5)', low: 'var(--c1)', none: 'rgba(255,255,255,0.3)' },              labelMap: { high: 'High', medium: 'Medium', low: 'Low', none: 'None' } },
              ].map(({ key, title, sub, colorMap, labelMap }) => {
                const dist = labels?.distributions?.[key] || {};
                const entries = Object.entries(dist).filter(([, v]) => v > 0).sort(([, a], [, b]) => b - a);
                const total = entries.reduce((s, [, v]) => s + v, 0);
                return (
                  <div key={key} className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{key}</div>
                      <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>{title}</div>
                      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{sub}</div>
                    </div>
                    <div style={{ padding: 18 }}>
                      {entries.length === 0 ? (
                        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No data available</div>
                      ) : (
                        <>
                          {entries.map(([k, v]) => {
                            const pct = total > 0 ? Math.round(v / total * 100) : 0;
                            const color = colorMap[k] || 'rgba(255,255,255,0.4)';
                            const label = labelMap[k] || k;
                            return (
                              <div key={k} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, color, fontWeight: 600 }}>{label}</span>
                                  <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{formatNumber(v)} · {pct}%</span>
                                </div>
                                <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: color, opacity: 0.8, borderRadius: 99, transition: 'width 0.5s ease' }} />
                                </div>
                              </div>
                            );
                          })}
                          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between' }}>
                            <span className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</span>
                            <span className="v2-num" style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{formatNumber(total)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SEARCH TERMS */}
          {activeTab === 'search' && (
            <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>search terms</div>
                  <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Top demand signals</div>
                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>queries driving traffic to your inventory</div>
                </div>
                {searchTermsTotal > 0 && (
                  <span className="v2-chip">{formatNumber(searchTermsTotal)} total</span>
                )}
              </div>
              {searchTerms.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>No search terms data available yet</div>
              ) : (() => {
                const sorted = [...searchTerms].sort((a, b) => (b.sessions || 0) - (a.sessions || 0));
                const maxSessions = sorted[0]?.sessions || 1;
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                      <thead><tr>{['#', 'Search term', 'Make / Model', 'Sessions', 'Volume'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {sorted.map((st, i) => {
                          const sessions = st.sessions || 0;
                          const barPct = Math.round(sessions / maxSessions * 100);
                          return (
                            <tr key={st.term || i}>
                              <td style={{ ...td, color: 'rgba(255,255,255,0.3)', fontSize: 11, width: 36, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace" }}>{i + 1}</td>
                              <td style={{ ...td, fontWeight: 600, color: '#fff' }}>{st.term || st.search_term || '-'}</td>
                              <td style={{ ...td, color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{[st.make, st.model].filter(Boolean).join(' ') || <span style={{ color: 'rgba(255,255,255,0.2)' }}>-</span>}</td>
                              <td style={td}>
                                <span className="v2-num" style={{ fontSize: 14, fontWeight: 700, color: 'var(--c5)' }}>{formatNumber(sessions)}</span>
                              </td>
                              <td style={{ ...td, minWidth: 120 }}>
                                <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                                  <div style={{ width: `${barPct}%`, height: '100%', background: 'linear-gradient(90deg, var(--c2), var(--c3))', borderRadius: 99 }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TradePage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [prefs, setPrefs] = useState({ opt_in: false, wants_makes: '', wants_models: '', geographic_radius_miles: 50 });
  const [matches, setMatches] = useState([]);
  const [matchTotal, setMatchTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    api('/my-dealers').then(d => {
      const list = d.dealers || d || [];
      setDealers(list);
      if (list.length) setSelectedDealerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedDealerId) return;
    setLoading(true);
    Promise.all([
      api(`/dealers/${selectedDealerId}/trade/preferences`),
      api(`/dealers/${selectedDealerId}/trade/matches`)
    ]).then(([prefData, matchData]) => {
      const p = prefData.data || {};
      setPrefs({
        opt_in: !!p.opt_in,
        wants_makes: (p.wants_makes || []).join(', '),
        wants_models: (p.wants_models || []).join(', '),
        geographic_radius_miles: p.geographic_radius_miles || 50
      });
      setMatches(matchData.data || []);
      setMatchTotal((matchData.pagination || {}).total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedDealerId]);

  const handleSavePrefs = async () => {
    setPrefsLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await api(`/dealers/${selectedDealerId}/trade/preferences`, {
        method: 'PUT',
        body: JSON.stringify({
          opt_in: prefs.opt_in,
          geographic_radius_miles: parseInt(prefs.geographic_radius_miles) || 50,
          wants_makes: prefs.wants_makes.split(',').map(s => s.trim()).filter(Boolean),
          wants_models: prefs.wants_models.split(',').map(s => s.trim()).filter(Boolean)
        })
      });
      setMessage({ type: 'success', text: 'Preferences saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save.' });
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleMatchAction = async (matchId, action) => {
    try {
      await api(`/dealers/${selectedDealerId}/trade/matches/${matchId}/${action}`, { method: 'POST' });
      setMatches(prev => prev.map(m => m.id === matchId ? { ...m, status: action === 'contact' ? 'contacted' : 'declined' } : m));
    } catch (err) {
      alert(err.message || 'Action failed');
    }
  };

  const statusChip = (status) => {
    const map = {
      pending:   { fg: 'var(--c5)', bg: 'rgba(255,200,87,0.12)', border: 'rgba(255,200,87,0.30)', label: 'PENDING' },
      contacted: { fg: 'var(--c3)', bg: 'rgba(34,211,238,0.12)', border: 'rgba(34,211,238,0.30)', label: 'CONTACTED' },
      declined:  { fg: 'var(--c1)', bg: 'rgba(255,91,164,0.12)', border: 'rgba(255,91,164,0.30)', label: 'DECLINED' },
    };
    const s = map[status] || { fg: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.10)', label: (status || '').toUpperCase() };
    return (
      <span className="v2-mono" style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}`, borderRadius: 99, padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>{s.label}</span>
    );
  };

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '9px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          TRADE MATCHES{matchTotal ? ` · ${matchTotal} TOTAL` : ''}{prefs.opt_in ? ' · OPTED IN' : ' · OPTED OUT'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Trade swaps.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Match dealers based on what each side actually wants.
            </span>
          </h1>
          {dealers.length > 0 && (
            <select value={selectedDealerId || ''} onChange={e => setSelectedDealerId(parseInt(e.target.value))} style={{ ...inp, minWidth: 220, width: 'auto', cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {message.text && (
        <div style={{ background: message.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${message.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 18, color: message.type === 'success' ? 'var(--c4)' : 'var(--c1)', fontSize: 13 }}>
          {message.text}
        </div>
      )}

      {/* Preferences */}
      <div className="v2-tile" style={{ padding: 24, marginBottom: 14 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>preferences</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginTop: 2, marginBottom: 18 }}>What you want to swap</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>Trade matching</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '9px 12px', background: prefs.opt_in ? 'rgba(166,255,84,0.10)' : 'rgba(255,255,255,0.04)', border: `1px solid ${prefs.opt_in ? 'rgba(166,255,84,0.30)' : 'rgba(255,255,255,0.10)'}`, borderRadius: 10, transition: 'all 0.15s' }}>
              <input type="checkbox" checked={prefs.opt_in} onChange={e => setPrefs({ ...prefs, opt_in: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--c4)' }} />
              <span style={{ color: prefs.opt_in ? 'var(--c4)' : 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600 }}>{prefs.opt_in ? 'Opted in' : 'Opted out'}</span>
            </label>
          </div>
          <div>
            <label style={labelStyle}>Geographic radius</label>
            <div style={{ position: 'relative' }}>
              <input type="number" value={prefs.geographic_radius_miles} onChange={e => setPrefs({ ...prefs, geographic_radius_miles: e.target.value })} placeholder="50" min={1}
                style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
              <span className="v2-mono" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'rgba(255,255,255,0.4)', pointerEvents: 'none' }}>miles</span>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Wants makes</label>
            <input type="text" value={prefs.wants_makes} onChange={e => setPrefs({ ...prefs, wants_makes: e.target.value })} placeholder="Toyota, Honda, Ford" style={inp} />
            <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>comma-separated</div>
          </div>
          <div>
            <label style={labelStyle}>Wants models</label>
            <input type="text" value={prefs.wants_models} onChange={e => setPrefs({ ...prefs, wants_models: e.target.value })} placeholder="Camry, Civic, F-150" style={inp} />
            <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>comma-separated</div>
          </div>
        </div>
        <button onClick={handleSavePrefs} disabled={prefsLoading}
          style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 999, color: '#fff', padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: prefsLoading ? 'wait' : 'pointer', opacity: prefsLoading ? 0.6 : 1, letterSpacing: '0.02em', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
          {prefsLoading ? 'Saving…' : 'Save preferences →'}
        </button>
      </div>

      {/* Matches */}
      <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>matches</div>
          <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Trade candidates {matchTotal > 0 && <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, fontStyle: 'italic' }}>· {matchTotal} total</span>}</div>
        </div>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 14 }}>
            <div className="spinner" />
            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING MATCHES…</span>
          </div>
        ) : matches.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 6 }}>No trade matches yet.</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', maxWidth: 380, margin: '0 auto' }}>Enable opt-in and complete your preferences to start matching with other dealers.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead><tr>{['Partner dealer', 'My VIN', 'Their VIN', 'Match type', 'Status', 'Date', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {matches.map((m, i) => (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                    <td style={{ ...td, color: '#fff', fontWeight: 600 }}>{m.dealer_b_name || '-'}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{m.dealer_a_vin || '-'}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{m.dealer_b_vin || '-'}</td>
                    <td style={td}>{m.match_type || '-'}</td>
                    <td style={td}>{statusChip(m.status)}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{formatDate(m.created_at)}</td>
                    <td style={td}>
                      {m.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleMatchAction(m.id, 'contact')} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(34,211,238,0.10)', borderColor: 'rgba(34,211,238,0.30)', color: 'var(--c3)', fontSize: 10 }}>Contact</button>
                          <button onClick={() => handleMatchAction(m.id, 'decline')} className="v2-chip"
                            style={{ cursor: 'pointer', background: 'rgba(255,91,164,0.06)', borderColor: 'rgba(255,91,164,0.20)', color: '#fbcfe8', fontSize: 10 }}>Decline</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ImbalancePage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [imbalances, setImbalances] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api('/my-dealers').then(d => {
      const list = d.dealers || d || [];
      setDealers(list);
      if (list.length) setSelectedDealerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedDealerId) return;
    setLoading(true);
    api(`/dealers/${selectedDealerId}/market/imbalances`)
      .then(res => { setImbalances(res.data || []); setTotal((res.pagination || {}).total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedDealerId]);

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };

  const scoreChip = (score) => {
    const val = parseFloat(score) || 0;
    const isHigh = val > 0.66, isMed = val > 0.33;
    const fg = isHigh ? 'var(--c1)' : isMed ? 'var(--c5)' : 'var(--c4)';
    const bg = isHigh ? 'rgba(255,91,164,0.12)' : isMed ? 'rgba(255,200,87,0.12)' : 'rgba(166,255,84,0.10)';
    const border = isHigh ? 'rgba(255,91,164,0.30)' : isMed ? 'rgba(255,200,87,0.30)' : 'rgba(166,255,84,0.30)';
    const label = isHigh ? 'HIGH' : isMed ? 'MEDIUM' : 'LOW';
    return <span className="v2-mono" style={{ background: bg, color: fg, border: `1px solid ${border}`, borderRadius: 99, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>{label} · {(val * 100).toFixed(0)}%</span>;
  };

  const ppChip = (pp) => {
    const val = parseFloat(pp) || 0;
    const fg = val > 1.05 ? 'var(--c4)' : val < 0.95 ? 'var(--c1)' : 'var(--c5)';
    return <span className="v2-num" style={{ fontSize: 12, fontWeight: 700, color: fg }}>{(val * 100).toFixed(0)}%</span>;
  };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          MARKET IMBALANCES{total ? ` · ${total} SEGMENTS` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Imbalances.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Where supply and demand don't match - and what to do about it.
            </span>
          </h1>
          {dealers.length > 0 && (
            <select value={selectedDealerId || ''} onChange={e => setSelectedDealerId(parseInt(e.target.value))} style={{ ...inp, minWidth: 220, cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING IMBALANCES…</span>
        </div>
      ) : imbalances.length === 0 ? (
        <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No imbalance data.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Imbalance analysis runs after competitor inventory is scraped.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
          {imbalances.map((item, i) => (
            <div key={i} className="v2-tile" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="v2-serif" style={{ fontSize: 18, color: '#fff' }}>{item.segment_make} {item.segment_model}</div>
                  <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 3 }}>{item.segment_condition || 'all conditions'}</div>
                </div>
                {scoreChip(item.imbalance_score)}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>PRICING POWER</span>
                {ppChip(item.pricing_power)}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 14, lineHeight: 1.5 }}>{item.recommendation || '-'}</div>
              <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
                <div>
                  <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', marginBottom: 3 }}>LOCAL SUPPLY</div>
                  <div className="v2-num" style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{formatNumber(item.local_supply_count || 0)}</div>
                </div>
                <div>
                  <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', marginBottom: 3 }}>YOUR SUPPLY</div>
                  <div className="v2-num" style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{formatNumber(item.dealer_supply_count || 0)}</div>
                </div>
                <div>
                  <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', marginBottom: 3 }}>DEMAND SCORE</div>
                  <div className="v2-num" style={{ color: 'var(--c4)', fontWeight: 700, fontSize: 14 }}>{parseFloat(item.demand_score || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeasonalPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [segments, setSegments] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);

  const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  useEffect(() => {
    api('/my-dealers').then(d => {
      const list = d.dealers || d || [];
      setDealers(list);
      if (list.length) setSelectedDealerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedDealerId) return;
    setLoading(true);
    api(`/dealers/${selectedDealerId}/market/seasonal`)
      .then(res => {
        setSegments(res.data || []);
        if (res.current_month) setCurrentMonth(res.current_month);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedDealerId]);

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };

  const renderBars = (months) => {
    const BAR_MAX_HEIGHT = 56;
    const velocities = months.map(m => parseFloat(m.relative_velocity) || 0);
    const maxVel = Math.max(...velocities, 1);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: BAR_MAX_HEIGHT + 22 }}>
        {MONTH_LABELS.map((label, idx) => {
          const monthNum = idx + 1;
          const monthData = months.find(m => m.month === monthNum) || { relative_velocity: 0 };
          const vel = parseFloat(monthData.relative_velocity) || 0;
          const barH = Math.max(4, Math.round((vel / maxVel) * BAR_MAX_HEIGHT));
          const isCurrent = monthNum === currentMonth;
          const barColor = isCurrent ? 'linear-gradient(180deg, var(--c2), var(--c3))' : vel > 1 ? 'rgba(166,255,84,0.7)' : 'rgba(255,255,255,0.18)';
          return (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{ height: BAR_MAX_HEIGHT, display: 'flex', alignItems: 'flex-end', width: '100%' }}>
                <div title={`${label}: ${vel.toFixed(2)}x`} style={{ width: '100%', minWidth: 8, height: barH, background: barColor, borderRadius: '4px 4px 0 0' }} />
              </div>
              <div className="v2-mono" style={{ fontSize: 9, color: isCurrent ? 'var(--c3)' : 'rgba(255,255,255,0.35)', marginTop: 4, fontWeight: isCurrent ? 700 : 400 }}>{label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          SEASONAL TRENDS · {MONTH_LABELS[currentMonth - 1]?.toUpperCase()} HIGHLIGHTED{segments.length ? ` · ${segments.length} SEGMENTS` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Seasonality.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Stock for the curve, not the average.
            </span>
          </h1>
          {dealers.length > 0 && (
            <select value={selectedDealerId || ''} onChange={e => setSelectedDealerId(parseInt(e.target.value))} style={{ ...inp, minWidth: 220, cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING SEASONAL DATA…</span>
        </div>
      ) : segments.length === 0 ? (
        <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No seasonal data.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Seasonal trends compute after enough historical sales data accumulates.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
          {segments.map((seg, i) => {
            const confRaw = seg.confidence;
            let confLabel;
            if (typeof confRaw === 'number') {
              const confPct = confRaw > 1 ? confRaw : confRaw * 100;
              confLabel = `${confPct.toFixed(0)}% confidence`;
            } else {
              confLabel = confRaw ? `${confRaw} confidence` : 'confidence unknown';
            }
            return (
              <div key={i} className="v2-tile" style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
                  <div className="v2-serif" style={{ fontSize: 18, color: '#fff' }}>{seg.make} {seg.model}</div>
                  <span className="v2-chip" style={{ background: 'rgba(122,77,255,0.15)', borderColor: 'rgba(122,77,255,0.30)', color: '#c4b5fd', fontSize: 10 }}>{confLabel}</span>
                </div>
                {renderBars(seg.months || [])}
                <div className="v2-mono" style={{ marginTop: 10, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                  bar height = relative demand velocity (1.0 = baseline) · current month highlighted
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FIPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api('/my-dealers').then(d => {
      const list = d.dealers || d || [];
      setDealers(list);
      if (list.length) setSelectedDealerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedDealerId) return;
    setLoading(true);
    api(`/dealers/${selectedDealerId}/fi/benchmarks`)
      .then(res => setBenchmarks(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedDealerId]);

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  const fmtCurrency = (val) => val != null ? `$${parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '-';
  const fmtNum = (val, dec = 1) => val != null ? parseFloat(val).toFixed(dec) : '-';

  const isEstimated = benchmarks.length > 0 && benchmarks.every(b => parseFloat(b.avg_apr) === 6.5);

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          F&I BENCHMARKS{benchmarks.length ? ` · ${benchmarks.length} SEGMENTS` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            F&amp;I.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Finance &amp; insurance benchmarks by segment.
            </span>
          </h1>
          {dealers.length > 0 && (
            <select value={selectedDealerId || ''} onChange={e => setSelectedDealerId(parseInt(e.target.value))} style={{ ...inp, minWidth: 220, cursor: 'pointer' }}>
              {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {isEstimated && (
        <div className="v2-mono" style={{ background: 'rgba(255,200,87,0.10)', border: '1px solid rgba(255,200,87,0.30)', borderRadius: 12, padding: '11px 16px', marginBottom: 18, fontSize: 11, color: 'var(--c5)', display: 'flex', alignItems: 'center', gap: 8 }}>
          ⓘ Estimated benchmarks based on vehicle prices (10% down · 6.5% APR · 60-month term). Updated when actual financing data is scraped.
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING F&I DATA…</span>
        </div>
      ) : benchmarks.length === 0 ? (
        <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No F&amp;I benchmark data.</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Benchmarks compute after vehicle pricing data is available for this dealer.</div>
        </div>
      ) : (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr>{['Segment', 'Avg payment', 'Median payment', 'Avg term', 'Avg APR', 'Sample size'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {benchmarks.map((b, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                    <td style={{ ...td, color: '#fff', fontWeight: 600 }}>{b.segment_make} {b.segment_model}</td>
                    <td style={{ ...td, color: 'var(--c4)', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{fmtCurrency(b.avg_payment)}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{fmtCurrency(b.median_payment)}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(b.avg_term_months, 0)} mo</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(b.avg_apr, 2)}%</td>
                    <td style={{ ...td, color: 'rgba(255,255,255,0.45)', fontFamily: "'JetBrains Mono', monospace" }}>{formatNumber(b.sample_size || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function McpPage() {
  const [apiTokens, setApiTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState('');

  const MCP_TOOLS = [
    { group: 'Vehicles', tools: ['search_vehicles', 'get_vehicle_by_vin', 'get_vehicle_price_history', 'get_vehicle_stats'] },
    { group: 'Dealers & Domains', tools: ['search_dealerships', 'get_dealership_details', 'add_dealer', 'list_dealers', 'list_domains', 'add_domain', 'rescrape_domain'] },
    { group: 'Competitive Intel', tools: ['create_competitor_group', 'list_competitor_groups', 'get_competitor_group', 'discover_competitors', 'get_competitor_map_data', 'get_market_share', 'get_pricing_comparison', 'get_competitor_heatmap', 'get_competitor_activity', 'get_competitor_timeline'] },
    { group: 'Alerts & Feeds', tools: ['list_alerts', 'mark_alert_read', 'list_feeds', 'get_feed', 'list_jobs'] },
  ];

  useEffect(() => {
    api('/tokens').then(res => {
      const tokens = res.tokens || res || [];
      setApiTokens(tokens);
      if (tokens.length > 0) setSelectedToken(tokens[0].token_preview || tokens[0].token || tokens[0].key || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function copy(text, key) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 2000);
    });
  }

  const apiKey = selectedToken || 'YOUR_API_KEY';
  const configJson = JSON.stringify({
    mcpServers: {
      motorsdynamic: {
        command: 'npx',
        args: ['-y', '@motorsdynamic/mcp-server'],
        env: {
          MOTORSDYNAMIC_API_URL: 'https://api.motorsdynamic.com/api',
          MOTORSDYNAMIC_API_KEY: apiKey
        }
      }
    }
  }, null, 2);

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const codeBlock = { background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: 'var(--c4)', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-all', position: 'relative' };
  const totalTools = MCP_TOOLS.reduce((s, g) => s + g.tools.length, 0);

  return (
    <div className="page-content" style={{ maxWidth: 960 }}>
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          MCP SERVER · {totalTools} TOOLS · MODEL CONTEXT PROTOCOL
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          MCP server.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Plug Claude Desktop into your MotorsDynamic data.
          </span>
        </h1>
      </div>

      {/* Step 1 - API key */}
      <div className="v2-tile v2-tile-violet" style={{ padding: 22, marginBottom: 14 }}>
        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>STEP 1</div>
        <div className="v2-serif" style={{ fontSize: 20, color: '#fff', marginBottom: 14 }}>Pick your API key</div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : apiTokens.length === 0 ? (
          <div className="v2-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
            No API keys yet. Create one on the <Link to="/api" style={{ color: 'var(--c2)', textDecoration: 'none', fontWeight: 700 }}>API Explorer →</Link> page.
          </div>
        ) : (
          <div>
            <select value={selectedToken} onChange={e => setSelectedToken(e.target.value)} style={{ ...inp, minWidth: 320, width: 'auto', cursor: 'pointer', marginBottom: 10 }}>
              {apiTokens.map((t, i) => <option key={i} value={t.token_preview || t.token || t.key || ''} style={{ background: '#111' }}>{t.name || t.label || `API Key ${i+1}`}</option>)}
            </select>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 10 }}>
              full keys are only shown once at creation - paste yours below if you've saved it
            </div>
            <input type="text" placeholder="Paste your full API key here…"
              value={selectedToken.includes('...') ? '' : selectedToken}
              onChange={e => setSelectedToken(e.target.value)}
              style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", width: '100%', boxSizing: 'border-box' }} />
          </div>
        )}
      </div>

      {/* Step 2 - Claude Desktop config */}
      <div className="v2-tile" style={{ padding: 22, marginBottom: 14 }}>
        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>STEP 2</div>
        <div className="v2-serif" style={{ fontSize: 20, color: '#fff', marginBottom: 6 }}>Configure Claude Desktop</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
          Add this to <code className="v2-mono" style={{ color: 'var(--c4)', fontSize: 11, background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: 4 }}>~/Library/Application Support/Claude/claude_desktop_config.json</code> (macOS) or <code className="v2-mono" style={{ color: 'var(--c4)', fontSize: 11, background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: 4 }}>%APPDATA%\Claude\claude_desktop_config.json</code> (Windows).
        </div>
        <div style={{ position: 'relative' }}>
          <pre style={codeBlock}>{configJson}</pre>
          <button onClick={() => copy(configJson, 'config')} className="v2-chip"
            style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer', background: copiedKey === 'config' ? 'rgba(166,255,84,0.10)' : 'rgba(255,255,255,0.04)', borderColor: copiedKey === 'config' ? 'rgba(166,255,84,0.35)' : undefined, color: copiedKey === 'config' ? 'var(--c4)' : 'rgba(255,255,255,0.85)', fontSize: 10 }}>
            {copiedKey === 'config' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {/* CLI alternative */}
      <div className="v2-tile v2-tile-cyan" style={{ padding: 22, marginBottom: 14 }}>
        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>ALTERNATIVE</div>
        <div className="v2-serif" style={{ fontSize: 20, color: '#fff', marginBottom: 6 }}>Run via CLI</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 14 }}>Or run the MCP server directly from your terminal:</div>
        <div style={{ position: 'relative' }}>
          <pre style={codeBlock}>{`MOTORSDYNAMIC_API_URL=https://api.motorsdynamic.com/api \\
MOTORSDYNAMIC_API_KEY=${apiKey} \\
npx -y @motorsdynamic/mcp-server`}</pre>
          <button onClick={() => copy(`MOTORSDYNAMIC_API_URL=https://api.motorsdynamic.com/api MOTORSDYNAMIC_API_KEY=${apiKey} npx -y @motorsdynamic/mcp-server`, 'cli')} className="v2-chip"
            style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer', background: copiedKey === 'cli' ? 'rgba(166,255,84,0.10)' : 'rgba(255,255,255,0.04)', borderColor: copiedKey === 'cli' ? 'rgba(166,255,84,0.35)' : undefined, color: copiedKey === 'cli' ? 'var(--c4)' : 'rgba(255,255,255,0.85)', fontSize: 10 }}>
            {copiedKey === 'cli' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Tools reference */}
      <div className="v2-tile" style={{ padding: 22 }}>
        <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>AVAILABLE TOOLS · {totalTools}</div>
        <div className="v2-serif" style={{ fontSize: 20, color: '#fff', marginBottom: 16 }}>What Claude can do</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
          {MCP_TOOLS.map(group => (
            <div key={group.group}>
              <div className="v2-mono" style={{ fontSize: 10, fontWeight: 700, color: '#c4b5fd', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{group.group}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {group.tools.map(tool => (
                  <div key={tool} className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--c2)', fontSize: 9 }}>⬡</span>
                    {tool}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AmmoCardsPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardDetail, setCardDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filter, setFilter] = useState('');
  const LIMIT = 20;

  useEffect(() => {
    api('/my-dealers').then(res => {
      const list = res.dealers || [];
      setDealers(list);
      if (list.length > 0) setSelectedDealer(String(list[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDealer) return;
    setLoading(true);
    setPage(1);
    api(`/cards/dealers/${selectedDealer}/cards?limit=${LIMIT}&offset=0`).then(res => {
      setCards(res.data || []);
      setTotal(res.pagination?.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedDealer]);

  function loadPage(p) {
    setLoading(true);
    setPage(p);
    api(`/cards/dealers/${selectedDealer}/cards?limit=${LIMIT}&offset=${(p-1)*LIMIT}`).then(res => {
      setCards(res.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }

  async function openCard(vin) {
    setSelectedCard(vin);
    setCardDetail(null);
    setLoadingDetail(true);
    try {
      const res = await api(`/cards/dealers/${selectedDealer}/cards/${vin}`);
      setCardDetail(res.data || res);
    } catch (e) {}
    setLoadingDetail(false);
  }

  const quadrantColor = { stars: 'var(--c4)', fix_it: 'var(--c1)', boost: 'var(--c5)', push_or_cut: '#c084fc' };
  const quadrantLabel = { stars: 'Star', fix_it: 'Fix It', boost: 'Boost', push_or_cut: 'Push or Cut' };

  const filtered = filter ? cards.filter(c => `${c.year} ${c.make} ${c.model} ${c.vin}`.toLowerCase().includes(filter.toLowerCase())) : cards;

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          AMMO CARDS{total ? ` · ${total.toLocaleString()} VEHICLES` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
            Ammo cards.<br />
            <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
              Sales-floor talking points, per VIN.
            </span>
          </h1>
        </div>
      </div>

      <div className="v2-tile" style={{ padding: 14, marginBottom: 14, display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={selectedDealer} onChange={e => { setSelectedDealer(e.target.value); setSelectedCard(null); }} style={{ ...inp, minWidth: 240, cursor: 'pointer' }}>
          {dealers.map(d => <option key={d.id} value={d.id} style={{ background: '#111' }}>{d.name}</option>)}
        </select>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search VIN, make, model…"
          style={{ ...inp, flex: 1, minWidth: 200 }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedCard ? 'minmax(0, 1fr) 380px' : 'minmax(0, 1fr)', gap: 14 }}>
        <div>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
              <div className="spinner" />
              <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING AMMO CARDS…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="v2-tile v2-tile-cyan" style={{ padding: 60, textAlign: 'center' }}>
              <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>No ammo cards yet.</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Cards are generated from market positioning data - make sure your competitor scraper has run.</div>
            </div>
          ) : (
            <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead><tr>{['Vehicle', 'VIN', 'Price', 'Days', 'Quadrant', 'Signals'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filtered.map(c => {
                      const qColor = quadrantColor[c.quadrant] || 'rgba(255,255,255,0.4)';
                      const isSelected = selectedCard === c.vin;
                      return (
                        <tr key={c.vin} onClick={() => openCard(c.vin)}
                          style={{ cursor: 'pointer', background: isSelected ? 'rgba(122,77,255,0.08)' : 'transparent', transition: 'background 0.15s' }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                          <td style={{ ...td, color: '#fff', fontWeight: 600 }}>{[c.year, c.make, c.model, c.trim].filter(Boolean).join(' ') || 'Unknown'}</td>
                          <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{c.vin}</td>
                          <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: '#fff' }}>{c.price ? `$${parseInt(c.price).toLocaleString()}` : '-'}</td>
                          <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: c.days_on_lot > 45 ? 'var(--c1)' : 'rgba(255,255,255,0.7)' }}>{c.days_on_lot ?? '-'}</td>
                          <td style={td}>
                            {c.quadrant && (
                              <span className="v2-chip" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'transparent', color: qColor, fontSize: 10 }}>
                                {quadrantLabel[c.quadrant] || c.quadrant}
                              </span>
                            )}
                          </td>
                          <td style={{ ...td, fontSize: 11 }}>
                            {c.has_market_data && <span className="v2-mono" style={{ color: 'var(--c4)', marginRight: 8 }}>✓ market</span>}
                            {c.has_engagement_data && <span className="v2-mono" style={{ color: 'var(--c3)' }}>✓ ga4</span>}
                            {!c.has_market_data && !c.has_engagement_data && <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => loadPage(p)} className="v2-chip"
                      style={{ cursor: 'pointer', background: page === p ? 'rgba(122,77,255,0.18)' : 'rgba(255,255,255,0.04)', borderColor: page === p ? 'rgba(122,77,255,0.40)' : undefined, color: page === p ? '#c4b5fd' : 'rgba(255,255,255,0.6)', fontSize: 11, minWidth: 30, justifyContent: 'center' }}>{p}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {selectedCard && (
          <div className="v2-tile" style={{ padding: 18, height: 'fit-content', position: 'sticky', top: 80 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em' }}>AMMO CARD</div>
              <button onClick={() => setSelectedCard(null)} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, width: 26, height: 26, borderRadius: 8 }}>×</button>
            </div>

            {loadingDetail ? (
              <div style={{ padding: 30, textAlign: 'center' }}><div className="spinner" /></div>
            ) : cardDetail ? (() => {
              const v = cardDetail.vehicle || {};
              const mp = cardDetail.market_position || {};
              const q = cardDetail.quadrant || {};
              const qColor = quadrantColor[q.quadrant] || 'rgba(255,255,255,0.4)';
              return (
                <>
                  {v.image && <img src={v.image} alt="vehicle" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 14 }} onError={e => e.target.style.display='none'} />}
                  <div className="v2-serif" style={{ fontWeight: 400, color: '#fff', fontSize: 22, marginBottom: 2 }}>{[v.year, v.make, v.model].filter(Boolean).join(' ')}</div>
                  <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginBottom: 14 }}>{[v.trim, v.exterior_color].filter(Boolean).join(' · ') || '-'}</div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                      <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 3, letterSpacing: '0.06em' }}>PRICE</div>
                      <div className="v2-num" style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{v.price ? `$${parseInt(v.price).toLocaleString()}` : '-'}</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                      <div className="v2-mono" style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 3, letterSpacing: '0.06em' }}>DAYS ON LOT</div>
                      <div className="v2-num" style={{ fontSize: 18, fontWeight: 700, color: q.days_on_lot > 45 ? 'var(--c1)' : '#fff' }}>{q.days_on_lot ?? '-'}</div>
                    </div>
                  </div>

                  {q.quadrant && (
                    <div style={{ marginBottom: 14, background: qColor + '15', border: `1px solid ${qColor}33`, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: qColor }} />
                      <span style={{ color: qColor, fontWeight: 700, fontSize: 13 }}>{quadrantLabel[q.quadrant] || q.quadrant}</span>
                      <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: '0.06em' }}>QUADRANT</span>
                    </div>
                  )}

                  {cardDetail.talking_points?.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Talking points</div>
                      {cardDetail.talking_points.map((tp, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                          <span style={{ color: 'var(--c4)', marginTop: 2, flexShrink: 0, fontSize: 12 }}>✓</span>
                          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: 1.4 }}>{tp}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {mp.is_unique_in_market && (
                    <div className="v2-mono" style={{ background: 'rgba(166,255,84,0.10)', border: '1px solid rgba(166,255,84,0.30)', borderRadius: 10, padding: '8px 12px', fontSize: 11, color: 'var(--c4)', marginBottom: 12, fontWeight: 600 }}>
                      ✦ Exclusive - no direct comparables in your market
                    </div>
                  )}
                  {mp.rank && (
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                      <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4, letterSpacing: '0.06em' }}>MARKET RANK</div>
                      <div className="v2-num" style={{ fontSize: 16, fontWeight: 700, color: mp.rank <= 3 ? 'var(--c4)' : mp.rank <= 6 ? 'var(--c5)' : 'var(--c1)' }}>#{mp.rank} of {mp.total}</div>
                    </div>
                  )}

                  <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 12 }}>{selectedCard}</div>
                </>
              );
            })() : null}
          </div>
        )}
      </div>
    </div>
  );
}

function PortfolioPage() {
  const [summary, setSummary] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api('/portfolio/summary'),
      api('/portfolio/dealers'),
      api('/portfolio/benchmarks'),
    ]).then(([s, d, b]) => {
      setSummary(s);
      setDealers(Array.isArray(d) ? d : []);
      setBenchmarks(Array.isArray(b) ? b : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '12px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING PORTFOLIO…</span>
        </div>
      </div>
    );
  }

  const health = summary?.portfolio_health || {};
  const aging = summary?.aging_distribution || {};
  const agingTotal = (aging.fresh_0_14 || 0) + (aging.moderate_15_30 || 0) + (aging.aging_31_45 || 0) + (aging.stale_46_plus || 0);
  const avgDays = summary?.avg_days_on_lot || 0;
  const avgDaysColor = avgDays > 45 ? 'var(--c1)' : avgDays > 30 ? 'var(--c5)' : 'var(--c4)';

  const healthBars = [
    { label: 'Stars',       pct: health.stars_pct       || 0, color: 'var(--c4)' },
    { label: 'Boost',       pct: health.boost_pct       || 0, color: 'var(--c5)' },
    { label: 'Push or Cut', pct: health.push_or_cut_pct || 0, color: '#c084fc' },
    { label: 'Fix It',      pct: health.fix_it_pct      || 0, color: 'var(--c1)' },
  ];

  const agingBars = [
    { label: '0–14 days',  count: aging.fresh_0_14    || 0, color: 'var(--c4)' },
    { label: '15–30 days', count: aging.moderate_15_30 || 0, color: '#a3e635' },
    { label: '31–45 days', count: aging.aging_31_45    || 0, color: 'var(--c5)' },
    { label: '46+ days',   count: aging.stale_46_plus  || 0, color: 'var(--c1)' },
  ];

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          PORTFOLIO · {summary?.total_dealers || 0} DEALERS · {formatNumber(summary?.total_active_vehicles || 0)} ACTIVE VEHICLES
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          The portfolio.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            Aggregate performance across every lot you manage.
          </span>
        </h1>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>total dealers</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{summary?.total_dealers || 0}</div>
        </div>
        <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>active vehicles</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>{formatNumber(summary?.total_active_vehicles || 0)}</div>
        </div>
        <div className="v2-tile" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>avg days on lot</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: avgDaysColor }}>{avgDays.toFixed(1)}<span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>d</span></div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>portfolio average</div>
        </div>
        <div className={`v2-tile ${(summary?.total_alerts_unread || 0) > 0 ? 'v2-tile-glow' : ''}`} style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>open alerts</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: (summary?.total_alerts_unread || 0) > 0 ? 'var(--c1)' : 'var(--c4)' }}>{summary?.total_alerts_unread || 0}</div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>last 7 days</div>
        </div>
      </div>

      {/* Health + Aging */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 22 }}>
        <div className="v2-tile" style={{ padding: 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>inventory health</div>
              <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Quadrant mix</div>
            </div>
          </div>
          {healthBars.map(b => (
            <div key={b.label} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: b.color, fontWeight: 600 }}>{b.label}</span>
                <span className="v2-num" style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{b.pct}%</span>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${b.pct}%`, height: '100%', background: b.color, opacity: 0.85, borderRadius: 99, transition: 'width 0.5s' }} />
              </div>
            </div>
          ))}
        </div>

        <div className="v2-tile v2-tile-cyan" style={{ padding: 22 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 16 }}>aging distribution</div>
          {agingBars.map(b => {
            const pct = agingTotal > 0 ? (b.count / agingTotal * 100) : 0;
            return (
              <div key={b.label} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                  <span style={{ fontSize: 12, color: b.color, fontWeight: 600 }}>{b.label}</span>
                  <span className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{b.count.toLocaleString()} units</span>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: b.color, opacity: 0.85, borderRadius: 99, transition: 'width 0.5s' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dealer comparison */}
      {dealers.length > 0 && (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden', marginBottom: 18 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>dealers</div>
            <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Comparison</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead><tr>{['Dealer', 'Location', 'Active units', 'Avg days', 'Stars', 'Push or cut', 'Last scrape'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {dealers.map(d => (
                  <tr key={d.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...td, color: '#fff', fontWeight: 600 }}>{d.name}</td>
                    <td style={{ ...td, color: 'rgba(255,255,255,0.55)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{[d.city, d.state].filter(Boolean).join(', ') || '-'}</td>
                    <td style={{ ...td, color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{formatNumber(d.active_vehicles)}</td>
                    <td style={{ ...td, color: (d.avg_days_on_lot || 0) > 45 ? 'var(--c1)' : (d.avg_days_on_lot || 0) > 30 ? 'var(--c5)' : 'var(--c4)', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{(d.avg_days_on_lot || 0).toFixed(1)}d</td>
                    <td style={{ ...td, color: 'var(--c4)', fontFamily: "'JetBrains Mono', monospace" }}>{d.stars_count}</td>
                    <td style={{ ...td, color: d.push_or_cut_count > 0 ? 'var(--c5)' : 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace" }}>{d.push_or_cut_count}</td>
                    <td style={{ ...td, color: 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{d.last_scraped ? new Date(d.last_scraped).toLocaleDateString() : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Benchmarks */}
      {benchmarks.length > 0 && (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>rankings</div>
            <div className="v2-serif" style={{ fontSize: 18, marginTop: 2 }}>Performance leaderboard</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead><tr>{['Rank', 'Dealer', 'Avg days', 'Stars %', 'GA4 coverage', 'At/below market'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {benchmarks.map(b => {
                  const rankColors = b.rank === 1
                    ? { bg: 'rgba(255,215,0,0.10)', fg: '#FFD700', border: 'rgba(255,215,0,0.40)' }
                    : b.rank === 2
                      ? { bg: 'rgba(192,192,192,0.10)', fg: '#C0C0C0', border: 'rgba(192,192,192,0.40)' }
                      : b.rank === 3
                        ? { bg: 'rgba(205,127,50,0.10)', fg: '#CD7F32', border: 'rgba(205,127,50,0.40)' }
                        : { bg: 'rgba(255,255,255,0.04)', fg: 'rgba(255,255,255,0.45)', border: 'rgba(255,255,255,0.10)' };
                  return (
                    <tr key={b.dealer_id}>
                      <td style={td}>
                        <span className="v2-mono" style={{ background: rankColors.bg, color: rankColors.fg, border: `1px solid ${rankColors.border}`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>#{b.rank}</span>
                      </td>
                      <td style={{ ...td, color: '#fff', fontWeight: 600 }}>{b.dealer_name}</td>
                      <td style={{ ...td, color: (b.avg_days_on_lot || 0) > 45 ? 'var(--c1)' : 'var(--c4)', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{(b.avg_days_on_lot || 0).toFixed(1)}d</td>
                      <td style={{ ...td, color: b.stars_pct > 20 ? 'var(--c4)' : 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono', monospace" }}>{b.stars_pct}%</td>
                      <td style={{ ...td, color: b.engagement_coverage_pct > 50 ? 'var(--c4)' : 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono', monospace" }}>{b.engagement_coverage_pct}%</td>
                      <td style={{ ...td, color: b.market_positioning_pct > 60 ? 'var(--c4)' : b.market_positioning_pct > 40 ? 'var(--c5)' : 'var(--c1)', fontFamily: "'JetBrains Mono', monospace" }}>{b.market_positioning_pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function OnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [dealerName, setDealerName] = useState('');
  const [oemBrand, setOemBrand] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [dealerId, setDealerId] = useState(null);
  const [domainId, setDomainId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function createDealer() {
    if (!dealerName.trim()) { setError('Dealer name is required'); return; }
    setError(''); setLoading(true);
    try {
      const res = await api('/my-dealers', { method: 'POST', body: JSON.stringify({ name: dealerName.trim(), oem_brand: oemBrand.trim() || null }) });
      if (res.error) throw new Error(res.error);
      setDealerId(res.dealer.id);
      setStep(2);
    } catch (e) { setError(e.message || 'Failed to create dealer'); }
    setLoading(false);
  }

  async function addDomain() {
    if (!websiteUrl.trim()) { setError('Website URL is required'); return; }
    setError(''); setLoading(true);
    try {
      const dRes = await api('/domains', { method: 'POST', body: JSON.stringify({ url: websiteUrl.trim() }) });
      const domain = dRes.domain || (dRes.error === 'Domain already exists' ? dRes.domain : null);
      if (dRes.error && dRes.error !== 'Domain already exists') throw new Error(dRes.error);
      const did = domain?.id || (dRes.domain?.id);
      if (!did) throw new Error('Failed to get domain ID');
      setDomainId(did);
      await api(`/my-dealers/${dealerId}`, { method: 'PUT', body: JSON.stringify({ domain_id: did, website: websiteUrl.trim() }) });
      await api(`/domains/${did}/scan`, { method: 'POST' }).catch(() => {});
      setStep(3);
    } catch (e) { setError(e.message || 'Failed to add domain'); }
    setLoading(false);
  }

  const inp = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, color: '#fff', padding: '11px 14px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono', monospace" };
  const btnPrimary = { background: 'linear-gradient(135deg, var(--c2), var(--c3))', border: 'none', borderRadius: 999, color: '#fff', padding: '12px 28px', fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%', letterSpacing: '0.02em', boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)' };
  const btnSecondary = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 999, color: 'rgba(255,255,255,0.65)', padding: '8px 18px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' };

  const Shell = ({ children }) => (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 700px at 88% -10%, rgba(122,77,255,.30), transparent 60%),
        radial-gradient(900px 600px at -10% 110%, rgba(34,211,238,.16), transparent 60%),
        radial-gradient(700px 400px at 50% 130%, rgba(255,91,164,.12), transparent 60%)
      `,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, backdropFilter: 'blur(8px)',
      fontFamily: 'Inter, system-ui, sans-serif',
      color: 'var(--ink, #FAFAF6)'
    }}>
      <div className="v2-grain" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1 }} />
      <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 540 }}>
        {children}
      </div>
    </div>
  );

  function StepDots() {
    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
        {[1,2,3].map(s => (
          <div key={s}
            style={{ width: s <= step ? 28 : 8, height: 6, borderRadius: 99, background: s <= step ? 'linear-gradient(90deg, var(--c2), var(--c3))' : 'rgba(255,255,255,0.10)', transition: 'all 0.3s' }} />
        ))}
      </div>
    );
  }

  if (step === 0) return (
    <Shell>
      <div className="v2-tile v2-tile-violet" style={{ padding: 40 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <MDLogo size={56} radius={16} style={{ margin: '0 auto 18px', boxShadow: '0 12px 32px -8px rgba(122,77,255,0.6)', display: 'block' }} />
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', marginBottom: 8 }}>WELCOME TO MOTORSDYNAMIC</div>
          <h1 className="v2-serif" style={{ fontSize: 32, fontWeight: 400, color: '#fff', margin: '0 0 12px', lineHeight: 1.1 }}>Let's get you set up.</h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.6, margin: 0 }}>Three quick steps and you're tracking inventory, pricing, and market intel.</p>
        </div>
        <div style={{ display: 'grid', gap: 10, marginBottom: 28 }}>
          {[
            { color: 'var(--c1)', title: 'Add your dealership',  desc: 'Tell us your dealer name and brand' },
            { color: 'var(--c3)', title: 'Connect your website', desc: "We'll scrape your live inventory automatically" },
            { color: 'var(--c4)', title: 'See your data',        desc: 'Instant insights on pricing, quality, and more' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px' }}>
              <div className="v2-num" style={{ width: 30, height: 30, borderRadius: 10, background: `${f.color}18`, border: `1px solid ${f.color}40`, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
              <div>
                <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{f.title}</div>
                <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <button style={btnPrimary} onClick={() => setStep(1)}>Get started →</button>
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button onClick={onComplete} className="v2-mono" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.04em' }}>skip for now</button>
        </div>
      </div>
    </Shell>
  );

  if (step === 1) return (
    <Shell>
      <div className="v2-tile" style={{ padding: 36 }}>
        <StepDots />
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>STEP 1 OF 3</div>
        <h2 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: '0 0 22px' }}>Add your dealership.</h2>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Dealer name *</label>
          <input style={inp} value={dealerName} onChange={e => setDealerName(e.target.value)} placeholder="e.g. Sunshine Motors" onKeyDown={e => e.key === 'Enter' && createDealer()} autoFocus />
        </div>
        <div style={{ marginBottom: 22 }}>
          <label style={labelStyle}>OEM brand <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, marginLeft: 4 }}>OPTIONAL</span></label>
          <input style={inp} value={oemBrand} onChange={e => setOemBrand(e.target.value)} placeholder="e.g. Toyota, Honda, Ford" />
        </div>
        {error && <div style={{ background: 'rgba(255,91,164,0.10)', border: '1px solid rgba(255,91,164,0.30)', color: 'var(--c1)', borderRadius: 12, padding: '10px 14px', fontSize: 12, marginBottom: 14 }}>{error}</div>}
        <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} disabled={loading} onClick={createDealer}>{loading ? 'Creating…' : 'Continue →'}</button>
      </div>
    </Shell>
  );

  if (step === 2) return (
    <Shell>
      <div className="v2-tile" style={{ padding: 36 }}>
        <StepDots />
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 6 }}>STEP 2 OF 3</div>
        <h2 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: '0 0 6px' }}>Connect your website.</h2>
        <p className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginBottom: 22 }}>we'll scrape your live inventory from your dealer site</p>
        <div style={{ marginBottom: 22 }}>
          <label style={labelStyle}>Dealer website URL *</label>
          <input style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://www.yourdealer.com" onKeyDown={e => e.key === 'Enter' && addDomain()} autoFocus />
          <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 6 }}>supports Dealer Inspire, Dealer.com, DealerOn, and many more</div>
        </div>
        {error && <div style={{ background: 'rgba(255,91,164,0.10)', border: '1px solid rgba(255,91,164,0.30)', color: 'var(--c1)', borderRadius: 12, padding: '10px 14px', fontSize: 12, marginBottom: 14 }}>{error}</div>}
        <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} disabled={loading} onClick={addDomain}>{loading ? 'Setting up…' : 'Start scanning →'}</button>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button style={btnSecondary} onClick={onComplete}>Skip website setup</button>
        </div>
      </div>
    </Shell>
  );

  if (step === 3) return (
    <Shell>
      <div className="v2-tile v2-tile-acid" style={{ padding: 36 }}>
        <StepDots />
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(166,255,84,0.15)', border: '1px solid rgba(166,255,84,0.40)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', color: 'var(--c4)' }}>
            <Icons.Check />
          </div>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', marginBottom: 8 }}>STEP 3 OF 3 · ALMOST DONE</div>
          <h2 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: '0 0 12px' }}>Scanning your inventory.</h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            We've kicked off the first scan for <strong style={{ color: '#fff' }}>{dealerName}</strong>. This usually takes 5–15 minutes depending on inventory size.
          </p>
          <p className="v2-mono" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 12 }}>
            track progress on the <Link to="/jobs" style={{ color: 'var(--c3)', textDecoration: 'none', fontWeight: 700 }}>Jobs</Link> page
          </p>
        </div>
        <div style={{ display: 'grid', gap: 8, marginBottom: 24 }}>
          {[
            { done: true,  text: 'Dealer created' },
            { done: true,  text: 'Website connected' },
            { done: false, text: 'Inventory scan in progress…' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', background: s.done ? 'rgba(166,255,84,0.06)' : 'rgba(255,200,87,0.06)', border: `1px solid ${s.done ? 'rgba(166,255,84,0.20)' : 'rgba(255,200,87,0.20)'}`, borderRadius: 10, padding: '10px 14px' }}>
              {s.done ? (
                <span style={{ color: 'var(--c4)', display: 'flex' }}><Icons.Check /></span>
              ) : (
                <span className="v2-pulse-dot v2-pulse-dot--amber" />
              )}
              <span style={{ color: s.done ? 'rgba(255,255,255,0.85)' : 'var(--c5)', fontSize: 13 }}>{s.text}</span>
            </div>
          ))}
        </div>
        <button style={btnPrimary} onClick={onComplete}>Go to dashboard →</button>
      </div>
    </Shell>
  );

  return null;
}

// Per-status visual metadata for the healing inbox + session views.
// `live` marks statuses where the agent is actively working, drives a
// pulsing dot in HealingBadge and faster polling in HealingSession.
const HEALING_STATUS_META = {
  researching:        { label: 'researching',     fg: '#7A4DFF', bg: 'rgba(122,77,255,0.10)', live: true  },
  proposed:           { label: 'proposed',        fg: '#22D3EE', bg: 'rgba(34,211,238,0.10)' },
  awaiting_approval:  { label: 'awaiting approval', fg: '#FFC857', bg: 'rgba(255,200,87,0.10)' },
  approved:           { label: 'approved',        fg: '#22D3EE', bg: 'rgba(34,211,238,0.10)', live: true  },
  executing:          { label: 'executing',       fg: '#7A4DFF', bg: 'rgba(122,77,255,0.10)', live: true  },
  testing:            { label: 'testing',         fg: '#22D3EE', bg: 'rgba(34,211,238,0.10)', live: true  },
  review_pr_open:     { label: 'PR open',         fg: '#A6FF54', bg: 'rgba(166,255,84,0.10)', live: true  },
  merging:            { label: 'merging',         fg: '#A6FF54', bg: 'rgba(166,255,84,0.10)', live: true  },
  deploying:          { label: 'deploying',       fg: '#7A4DFF', bg: 'rgba(122,77,255,0.10)', live: true  },
  verifying:          { label: 'verifying',       fg: '#22D3EE', bg: 'rgba(34,211,238,0.10)', live: true  },
  completed:          { label: 'completed',       fg: '#A6FF54', bg: 'rgba(166,255,84,0.10)' },
  declined:           { label: 'declined',        fg: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.04)' },
  failed:             { label: 'failed',          fg: '#FF5BA4', bg: 'rgba(255,91,164,0.10)' },
  cap_exceeded:       { label: 'cap exceeded',    fg: '#FF5BA4', bg: 'rgba(255,91,164,0.10)' },
  expired:            { label: 'expired',         fg: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.04)' },
  paused:             { label: 'paused',          fg: '#FFC857', bg: 'rgba(255,200,87,0.10)' },
  rolled_back:        { label: 'rolled back',     fg: '#FF5BA4', bg: 'rgba(255,91,164,0.10)' }
};

// "5m ago", "3h ago", "yesterday", "Jan 4", used in the healing inbox table.
// Accepts ISO strings or Date instances; returns '-' for invalid input.
function relativeTime(input) {
  if (!input) return '-';
  const t = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (!Number.isFinite(t)) return '-';
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60)        return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60)        return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)         return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7)         return `${day}d ago`;
  if (day < 30)        return `${Math.round(day / 7)}w ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Catch render-time exceptions inside the healing subtree so the page never
// goes fully blank, surface the error inline instead. Class component because
// React error boundaries can't be functional.
class HealingErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[HealingErrorBoundary]', err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div className="page-content">
          <div style={{ padding: 24, borderRadius: 14, background: 'rgba(255,91,164,0.08)', border: '1px solid rgba(255,91,164,0.30)', color: '#fff' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'var(--c1)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>HEALING UI CRASHED</div>
            <div style={{ fontSize: 14, marginBottom: 12, color: 'rgba(255,255,255,0.85)' }}>The healing page hit a runtime error. The data layer is fine, this is a UI bug.</div>
            <pre style={{ background: 'rgba(0,0,0,0.4)', padding: 12, borderRadius: 8, fontSize: 11, color: '#fbcfe8', overflow: 'auto', maxHeight: 200 }}>{String(this.state.err?.stack || this.state.err?.message || this.state.err)}</pre>
            <button onClick={() => this.setState({ err: null })} className="v2-chip" style={{ cursor: 'pointer', marginTop: 12 }}>Try again</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function HealingBadge({ status }) {
  const m = HEALING_STATUS_META[status] || { label: status, fg: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.05)' };
  return (
    <span className="v2-mono" style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 99,
      background: m.bg, color: m.fg,
      border: `1px solid ${m.fg}33`,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase'
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: 99, background: m.fg,
        animation: m.live ? 'v2-pulse 1.5s ease-out infinite' : 'none'
      }}/>
      {m.label}
    </span>
  );
}

function CostMeter({ used, cap }) {
  const u = Number(used) || 0;
  const c = Number(cap) || 1;
  const pct = Math.min(100, Math.round((u / c) * 100));
  const color = pct < 50 ? 'var(--c4)' : pct < 80 ? 'var(--c5)' : 'var(--c1)';
  return (
    <span title={`$${u.toFixed(4)} / $${c.toFixed(2)} (${pct}%)`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: "'JetBrains Mono', monospace", fontFeatureSettings: '"tnum"' }}>
      <svg width="14" height="14" viewBox="0 0 14 14" style={{ display: 'block' }}>
        <circle cx="7" cy="7" r="5.5" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="2"/>
        <circle cx="7" cy="7" r="5.5" fill="none" stroke={color} strokeWidth="2"
          strokeDasharray={`${(pct/100)*34.5} 34.5`} strokeLinecap="round"
          transform="rotate(-90 7 7)"/>
      </svg>
      <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>${u.toFixed(u < 1 ? 4 : 2)}</span>
      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>/ ${c.toFixed(2)}</span>
    </span>
  );
}

function TrialBanner() {
  const [billing, setBilling] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('md_trial_banner_dismissed') === '1');

  useEffect(() => {
    api('/billing/summary').then(setBilling).catch(() => {});
  }, []);

  if (!billing || dismissed) return null;

  const inTrial = billing.trial?.in_trial;
  const isExpired = billing.billing_status === 'expired' || billing.billing_status === 'cancelled';
  const isPastDue = billing.billing_status === 'past_due';

  if (!inTrial && !isExpired && !isPastDue) return null;

  const days = billing.trial?.days_remaining ?? 0;
  const urgent = inTrial && days <= 2;

  let title, sub, cta, ctaText, accent;
  if (isExpired) {
    title = 'Your trial has ended.';
    sub = `Add a payment method to keep ${billing.domains?.billable || 0} ${(billing.domains?.billable || 0) === 1 ? 'domain' : 'domains'} active.`;
    ctaText = 'Add payment method';
    cta = '/settings/billing';
    accent = 'var(--c1)';
  } else if (isPastDue) {
    title = 'Payment failed.';
    sub = 'Update your card to keep the platform running. Reads continue but writes are paused.';
    ctaText = 'Update payment';
    cta = '/settings/billing';
    accent = 'var(--c5)';
  } else {
    title = days === 1 ? 'Trial ends tomorrow.' : days === 0 ? 'Trial ends today.' : `${days} days left in your trial.`;
    sub = `After your trial, you'll be billed $${((billing.price_per_domain_cents || 4900) / 100).toFixed(0)}/domain/mo. Add a card any time.`;
    ctaText = 'Add payment method';
    cta = '/settings/billing';
    accent = urgent ? 'var(--c1)' : 'var(--c5)';
  }

  return (
    <div style={{
      background: `linear-gradient(90deg, ${accent}1A, transparent 60%), rgba(255,255,255,.02)`,
      borderBottom: `1px solid ${accent}3A`,
      padding: '10px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 14, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 60,
      backdropFilter: 'blur(12px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span style={{
          width: 8, height: 8, borderRadius: 99, background: accent,
          boxShadow: `0 0 8px ${accent}`, flexShrink: 0,
        }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 13, letterSpacing: '-.01em' }}>{title}</div>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', marginTop: 2 }}>{sub}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <Link to={cta}
          style={{
            background: 'linear-gradient(135deg, var(--c2), var(--c3))',
            color: '#fff', textDecoration: 'none',
            padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 700,
            letterSpacing: '.02em', whiteSpace: 'nowrap',
            boxShadow: '0 4px 14px -4px rgba(122,77,255,.5)',
          }}>
          {ctaText}
        </Link>
        <button onClick={() => { sessionStorage.setItem('md_trial_banner_dismissed', '1'); setDismissed(true); }}
          aria-label="Dismiss"
          style={{
            background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)',
            color: 'rgba(255,255,255,.6)', cursor: 'pointer', fontSize: 14,
            width: 28, height: 28, borderRadius: 8, lineHeight: 1, padding: 0,
          }}>×</button>
      </div>
    </div>
  );
}

function BillingPanel() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = () => api('/billing/summary').then(d => { setSummary(d); setLoading(false); }).catch(e => { setMsg({ type: 'error', text: e.message }); setLoading(false); });
  useEffect(() => { load(); }, []);

  async function startCheckout() {
    setWorking(true); setMsg({ type: '', text: '' });
    try {
      const r = await api('/billing/start-checkout', { method: 'POST' });
      if (r.checkout_url) window.location.href = r.checkout_url;
      else setMsg({ type: 'error', text: r.error || 'Checkout not available' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally { setWorking(false); }
  }
  async function openPortal() {
    setWorking(true);
    try {
      const r = await api('/billing/portal', { method: 'POST' });
      if (r.portal_url) window.location.href = r.portal_url;
      else setMsg({ type: 'error', text: r.error || 'Portal not available' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }
  async function cancelSub() {
    if (!window.confirm('Cancel subscription at the end of the current period? You keep access until then.')) return;
    setWorking(true);
    try {
      await api('/billing/cancel', { method: 'POST' });
      setMsg({ type: 'success', text: 'Cancellation scheduled.' });
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }

  if (loading) return (
    <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto 14px' }} />
      <span className="v2-mono" style={{ color: 'rgba(255,255,255,.4)', fontSize: 11, letterSpacing: '.04em' }}>LOADING BILLING…</span>
    </div>
  );
  if (!summary) return null;

  const planLabel = {
    trial: 'Trial', professional: 'Professional', enterprise: 'Enterprise', cancelled: 'Cancelled'
  }[summary.plan_tier] || summary.plan_tier;
  const statusColor = {
    trial: 'var(--c5)', active: 'var(--c4)', past_due: 'var(--c1)',
    cancelled: 'rgba(255,255,255,.5)', expired: 'var(--c1)'
  }[summary.billing_status] || 'rgba(255,255,255,.6)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {msg.text && (
        <div style={{
          background: msg.type === 'success' ? 'rgba(166,255,84,.10)' : 'rgba(255,91,164,.12)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(166,255,84,.30)' : 'rgba(255,91,164,.30)'}`,
          borderRadius: 12, padding: '11px 16px', fontSize: 13,
          color: msg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{msg.text}</div>
      )}

      {/* Plan summary */}
      <div className={`v2-tile ${summary.billing_status === 'trial' ? 'v2-tile-amber' : summary.billing_status === 'active' ? 'v2-tile-acid' : ''}`} style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>current plan</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <div className="v2-serif" style={{ fontSize: 30, color: '#fff', letterSpacing: '-.02em' }}>{planLabel}</div>
          <span className="v2-chip" style={{ color: statusColor, borderColor: `${statusColor}40`, background: `${statusColor}10` }}>
            {summary.billing_status.toUpperCase().replace('_', ' ')}
          </span>
        </div>
        {summary.trial?.in_trial && (
          <div className="v2-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,.65)' }}>
            {summary.trial.days_remaining} {summary.trial.days_remaining === 1 ? 'day' : 'days'} remaining · ends {new Date(summary.trial.ends_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Domain breakdown + estimated bill */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>billing</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Per-domain pricing</div>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 18 }}>$49 per active domain per month · pause any time</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)' }}>billable domains</div>
            <div className="v2-num" style={{ fontSize: 30, fontWeight: 300, color: '#fff' }}>{summary.domains?.billable ?? 0}</div>
          </div>
          <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)' }}>paused</div>
            <div className="v2-num" style={{ fontSize: 30, fontWeight: 300, color: 'rgba(255,255,255,.6)' }}>{summary.domains?.paused ?? 0}</div>
          </div>
          <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)' }}>estimated / mo</div>
            <div className="v2-num" style={{ fontSize: 30, fontWeight: 300, color: 'var(--c4)' }}>${summary.estimated_monthly_usd ?? 0}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="v2-tile" style={{ padding: 24 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>payment method</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>
          {summary.payment_method_added ? 'Card on file' : 'No card yet'}
        </div>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 18 }}>
          {summary.payment_method_added ? 'manage your payment method and invoices' : 'add a card to continue after the trial ends'}
        </div>
        {summary.stripe?.note && (
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(255,200,87,.08)', border: '1px solid rgba(255,200,87,.20)', fontSize: 12, color: 'var(--c5)', marginBottom: 14 }}>
            {summary.stripe.note}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {!summary.payment_method_added && (
            <button onClick={startCheckout} disabled={working || !summary.stripe?.enabled}
              style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: working ? 'not-allowed' : 'pointer', opacity: working || !summary.stripe?.enabled ? 0.5 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,.5)', letterSpacing: '.02em' }}>
              {working ? 'Working…' : 'Add payment method'}
            </button>
          )}
          {summary.payment_method_added && (
            <button onClick={openPortal} disabled={working}
              style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: working ? 'not-allowed' : 'pointer', opacity: working ? 0.5 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,.5)' }}>
              {working ? 'Working…' : 'Manage in portal'}
            </button>
          )}
          {summary.billing_status !== 'cancelled' && summary.stripe_customer_id && (
            <button onClick={cancelSub} disabled={working}
              style={{ background: 'rgba(255,91,164,.06)', color: '#fbcfe8', border: '1px solid rgba(255,91,164,.20)', borderRadius: 999, padding: '10px 20px', fontSize: 12, cursor: 'pointer', opacity: working ? 0.5 : 1 }}>
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {/* API USAGE, current period meter + history + hard cap controls */}
      <UsagePanel />
    </div>
  );
}

function UsagePanel() {
  const [usage, setUsage] = useState(null);
  const [daily, setDaily] = useState([]);
  const [history, setHistory] = useState([]);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [hardCapInput, setHardCapInput] = useState('');

  async function loadAll() {
    try {
      const [u, d, h] = await Promise.all([
        api('/billing/usage/current'),
        api('/billing/usage/daily?days=30'),
        api('/billing/usage/history?months=6'),
      ]);
      setUsage(u);
      setDaily(d.daily || []);
      setHistory(h.periods || []);
      if (u.hard_cap != null) setHardCapInput(String(u.hard_cap));
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function saveHardCap(value) {
    setWorking(true); setMsg({ type: '', text: '' });
    try {
      await api('/billing/usage/config', { method: 'PUT', body: JSON.stringify({ hard_cap: value === '' ? null : Number(value) }) });
      setMsg({ type: 'success', text: value === '' ? 'Hard cap removed.' : `Hard cap set to ${Number(value).toLocaleString()}.` });
      loadAll();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }

  async function toggleAlerts() {
    setWorking(true);
    try {
      await api('/billing/usage/config', { method: 'PUT', body: JSON.stringify({ alerts_enabled: !usage.alerts_enabled }) });
      loadAll();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }

  if (!usage) {
    return (
      <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 14px' }} />
        <span className="v2-mono" style={{ color: 'rgba(255,255,255,.4)', fontSize: 11 }}>LOADING USAGE…</span>
      </div>
    );
  }

  const u = usage.usage;
  const pct = u.pct_used;
  const tier = pct == null ? null
    : pct >= 120 ? 'critical'
    : pct >= 100 ? 'over'
    : pct >= 80 ? 'warn'
    : 'ok';
  const accent =
    tier === 'critical' ? 'var(--c1)' :
    tier === 'over' ? 'var(--c1)' :
    tier === 'warn' ? 'var(--c5)' :
    'var(--c4)';

  // Sparkline geometry
  const maxDaily = daily.length > 0 ? Math.max(1, ...daily.map(d => Number(d.request_count))) : 1;
  const W = 100, H = 30;
  const sparkPoints = daily.map((d, i) => {
    const x = (i / Math.max(1, daily.length - 1)) * W;
    const y = H - (Number(d.request_count) / maxDaily) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return (
    <>
      {msg.text && (
        <div style={{
          background: msg.type === 'success' ? 'rgba(166,255,84,.10)' : 'rgba(255,91,164,.12)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(166,255,84,.30)' : 'rgba(255,91,164,.30)'}`,
          borderRadius: 12, padding: '11px 16px', fontSize: 13, marginBottom: 14,
          color: msg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{msg.text}</div>
      )}

      {/* Current period meter */}
      <div className={`v2-tile ${tier === 'warn' ? 'v2-tile-amber' : tier === 'over' || tier === 'critical' ? 'v2-tile-glow' : ''}`}
        style={{ padding: 24, marginBottom: 14 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>api usage · this period</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div className="v2-num" style={{ fontSize: 38, color: '#fff', fontWeight: 300, letterSpacing: '-.02em' }}>
            {u.requests.toLocaleString()}
          </div>
          <div className="v2-mono" style={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}>
            {u.included_quota > 0
              ? `of ${u.included_quota.toLocaleString()} included`
              : usage.is_internal ? '· unlimited (internal)' : '· no quota (not metered)'}
          </div>
          {pct != null && (
            <span style={{ padding: '3px 10px', borderRadius: 999, background: `${accent}1A`, border: `1px solid ${accent}40`, color: accent, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
              {Math.round(pct)}%
            </span>
          )}
        </div>

        {/* Progress bar */}
        {u.included_quota > 0 && (
          <div style={{ height: 6, background: 'rgba(255,255,255,.06)', borderRadius: 99, overflow: 'hidden', marginBottom: 10, position: 'relative' }}>
            <div style={{
              width: `${Math.min(100, pct || 0)}%`,
              height: '100%',
              background: tier === 'ok' ? `linear-gradient(90deg, var(--c4), var(--c3))` : accent,
              transition: 'width .3s ease',
            }} />
            {pct > 100 && (
              <div style={{
                position: 'absolute', top: 0, left: '100%',
                width: `${Math.min(100, pct - 100)}%`, height: '100%',
                background: 'var(--c1)',
                transform: 'translateX(-100%)',
                marginLeft: -2,
              }} />
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 14 }}>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>overage so far</div>
            <div className="v2-num" style={{ fontSize: 22, color: u.overage_count > 0 ? 'var(--c1)' : '#fff', marginTop: 4 }}>
              {u.overage_count.toLocaleString()}
            </div>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>billed at period end</div>
            <div className="v2-num" style={{ fontSize: 22, color: u.overage_cents > 0 ? 'var(--c5)' : '#fff', marginTop: 4 }}>
              ${u.overage_usd.toFixed(2)}
            </div>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>rate / 1K reqs</div>
            <div className="v2-num" style={{ fontSize: 22, color: '#fff', marginTop: 4 }}>
              ${(u.overage_price_cents_per_1k / 100).toFixed(2)}
            </div>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>days remaining</div>
            <div className="v2-num" style={{ fontSize: 22, color: '#fff', marginTop: 4 }}>
              {usage.period.days_remaining}
            </div>
          </div>
        </div>

        {/* 30-day sparkline */}
        {daily.length > 1 && (
          <div style={{ marginTop: 18 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', marginBottom: 6 }}>last 30 days</div>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 48, display: 'block' }}>
              <polyline points={sparkPoints} fill="none" stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points={`0,${H} ${sparkPoints} ${W},${H}`} fill={`${accent}22`} stroke="none" />
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,.4)', marginTop: 2 }}>
              <span>{daily[0]?.day?.slice(0, 10)}</span>
              <span>peak {maxDaily.toLocaleString()}</span>
              <span>{daily[daily.length - 1]?.day?.slice(0, 10)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Hard cap + alerts */}
      <div className="v2-tile" style={{ padding: 24, marginBottom: 14 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>controls</div>
        <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Hard cap &amp; alerts</div>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 18 }}>cap usage to avoid surprise bills · turn alerts on/off</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>
              Hard cap (requests / period)
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input type="number" min="0" value={hardCapInput} onChange={e => setHardCapInput(e.target.value)} placeholder="No cap"
                style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: "'JetBrains Mono', monospace", outline: 'none' }} />
              <button onClick={() => saveHardCap(hardCapInput)} disabled={working}
                className="v2-chip" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Save</button>
              {usage.hard_cap != null && (
                <button onClick={() => { setHardCapInput(''); saveHardCap(''); }} disabled={working}
                  className="v2-chip" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Clear</button>
              )}
            </div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginTop: 6 }}>
              429s once exceeded · only applies to X-API-Key traffic
            </div>
          </div>

          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>
              Threshold alerts
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(255,255,255,.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,.06)', marginTop: 6 }}>
              <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, cursor: 'pointer', flexShrink: 0 }}>
                <input type="checkbox" checked={!!usage.alerts_enabled} onChange={toggleAlerts} style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', inset: 0, background: usage.alerts_enabled ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,.10)', borderRadius: 24, transition: '.2s' }}>
                  <span style={{ position: 'absolute', top: 3, left: usage.alerts_enabled ? 21 : 3, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: '.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                </span>
              </label>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>
                Fire webhook + email at 80% / 100% / 120%
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {[
                { pct: 80, ts: usage.thresholds.pct_80, color: 'var(--c5)' },
                { pct: 100, ts: usage.thresholds.pct_100, color: 'var(--c1)' },
                { pct: 120, ts: usage.thresholds.pct_120, color: 'var(--c1)' },
              ].map(t => (
                <span key={t.pct} className="v2-chip"
                  style={{ background: t.ts ? `${t.color}1A` : 'rgba(255,255,255,.02)', borderColor: t.ts ? `${t.color}40` : 'rgba(255,255,255,.06)', color: t.ts ? t.color : 'rgba(255,255,255,.4)' }}>
                  {t.pct}% {t.ts ? '✓' : '–'}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="v2-tile" style={{ padding: 24 }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>history</div>
          <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 18 }}>Prior periods</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Period</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Requests</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Included</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Overage</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Billed</th>
              </tr>
            </thead>
            <tbody>
              {history.map(p => (
                <tr key={p.period_start} style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                  <td style={{ padding: '10px 0', color: '#fff' }}>{p.period_start?.slice(0, 7)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.85)' }}>
                    {Number(p.request_count).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255,255,255,.6)' }}>
                    {Number(p.included_quota).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: Number(p.overage_count) > 0 ? 'var(--c5)' : 'rgba(255,255,255,.6)' }}>
                    {Number(p.overage_count).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: Number(p.overage_cents) > 0 ? 'var(--c4)' : 'rgba(255,255,255,.6)' }}>
                    ${(Number(p.overage_cents) / 100).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function TotpSetupPanel() {
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const { user, refresh } = useAuth();

  async function start() {
    setWorking(true); setMsg({ type: '', text: '' });
    try {
      const d = await api('/auth/2fa/totp/setup', { method: 'POST' });
      setSetupData(d);
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }

  async function confirm(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setMsg({ type: 'error', text: 'Enter the 6-digit code from your app' });
    setWorking(true); setMsg({ type: '', text: '' });
    try {
      const d = await api('/auth/2fa/totp/confirm', { method: 'POST', body: JSON.stringify({ code }) });
      setRecoveryCodes(d.recovery_codes);
      setSetupData(null);
      setMsg({ type: 'success', text: 'Authenticator app enrolled. Save your recovery codes now.' });
      refresh?.();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }

  async function disable() {
    const password = window.prompt('Confirm your password to disable TOTP:');
    if (!password) return;
    setWorking(true); setMsg({ type: '', text: '' });
    try {
      await api('/auth/2fa/totp/disable', { method: 'POST', body: JSON.stringify({ password }) });
      setMsg({ type: 'success', text: 'TOTP disabled.' });
      refresh?.();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setWorking(false); }
  }

  // Inline QR rendering via Google Charts API (no extra dep). Falls back to
  // a copy-the-secret block.
  const qrUrl = setupData?.otpauth_uri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(setupData.otpauth_uri)}`
    : null;

  const enrolled = user?.totp_enabled || user?.two_factor_enabled;

  return (
    <div className={`v2-tile ${enrolled ? 'v2-tile-acid' : ''}`} style={{ padding: 24 }}>
      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>authenticator app</div>
      <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>TOTP (Google Authenticator, 1Password, Authy)</div>
      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginBottom: 18 }}>
        {enrolled ? 'protected by an authenticator app' : 'scan a QR code with your phone to enable'}
      </div>

      {msg.text && (
        <div style={{
          background: msg.type === 'success' ? 'rgba(166,255,84,.10)' : 'rgba(255,91,164,.12)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(166,255,84,.30)' : 'rgba(255,91,164,.30)'}`,
          borderRadius: 12, padding: '11px 16px', fontSize: 13, marginBottom: 14,
          color: msg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{msg.text}</div>
      )}

      {!enrolled && !setupData && !recoveryCodes && (
        <button onClick={start} disabled={working}
          className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(166,255,84,.10)', borderColor: 'rgba(166,255,84,.30)', color: 'var(--c4)' }}>
          {working ? 'Starting…' : 'Set up authenticator app'}
        </button>
      )}

      {setupData && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            {qrUrl && <img alt="QR code" src={qrUrl} style={{ width: 180, height: 180, borderRadius: 12, border: '1px solid rgba(255,255,255,.10)', background: '#fff', padding: 6 }} />}
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginTop: 8 }}>scan with your authenticator</div>
          </div>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginBottom: 6 }}>or paste this secret manually</div>
            <code style={{ display: 'block', padding: '10px 12px', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, fontSize: 12, wordBreak: 'break-all', marginBottom: 14, color: 'var(--c3)' }}>{setupData.secret}</code>
            <form onSubmit={confirm} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: "'JetBrains Mono', monospace" }}>6-digit code from your app</label>
              <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000"
                style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 10, color: '#fff', padding: '12px 14px', fontSize: 20, letterSpacing: '.4em', fontFamily: "'JetBrains Mono', monospace", textAlign: 'center' }} />
              <button type="submit" disabled={working}
                style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: working ? 0.6 : 1 }}>
                {working ? 'Verifying…' : 'Enable authenticator'}
              </button>
            </form>
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div>
          <div style={{ padding: '14px 16px', background: 'rgba(255,200,87,.08)', border: '1px solid rgba(255,200,87,.20)', borderRadius: 12, fontSize: 13, color: 'var(--c5)', marginBottom: 14 }}>
            Save these recovery codes somewhere safe. Each can only be used once and we will not show them again.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 14 }}>
            {recoveryCodes.map((c, i) => (
              <div key={i} style={{ padding: '10px 14px', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, color: '#fff', letterSpacing: '.1em' }}>{c}</div>
            ))}
          </div>
          <button onClick={() => { navigator.clipboard.writeText(recoveryCodes.join('\n')); setMsg({ type: 'success', text: 'Recovery codes copied to clipboard.' }); }}
            className="v2-chip" style={{ marginTop: 14, cursor: 'pointer' }}>Copy all to clipboard</button>
        </div>
      )}

      {enrolled && !setupData && (
        <button onClick={disable} disabled={working}
          className="v2-chip" style={{ cursor: 'pointer', background: 'rgba(255,91,164,.06)', borderColor: 'rgba(255,91,164,.20)', color: '#fbcfe8' }}>
          {working ? 'Working…' : 'Disable authenticator'}
        </button>
      )}
    </div>
  );
}

function HealingInbox() {
  const navigate = useNavigate();
  const location = useLocation();
  // Tab is in the pathname (/healing/history) rather than a route param so we
  // don't have to register two near-identical routes; the React Router config
  // routes /healing/history explicitly to this component.
  const tab = location.pathname.endsWith('/history') ? 'history' : 'active';
  const [data, setData] = useState({ incidents: [], stats: {}, agency: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api(`/healing/incidents?tab=${tab}`)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.message); setLoading(false); } });
    const i = setInterval(() => {
      api(`/healing/incidents?tab=${tab}`).then(d => !cancelled && setData(d)).catch(() => {});
    }, 15000);
    return () => { cancelled = true; clearInterval(i); };
  }, [tab]);

  const stats = data.stats || {};
  const enabled = data.agency?.healing_enabled;

  const th = { padding: '12px 14px', textAlign: 'left', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '14px 16px', fontSize: 13, color: 'rgba(255,255,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.04)' };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 22 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, letterSpacing: '0.04em' }}>
          SELF-HEALING · {stats.open_count || 0} OPEN{stats.resolved_this_month != null ? ` · ${stats.resolved_this_month} RESOLVED THIS MONTH` : ''}
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>
          Self-healing.<br />
          <span className="v2-serif" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)', fontSize: '0.78em' }}>
            The agent's queue of broken scrapers and unknown sites.
          </span>
        </h1>
      </div>

      {!enabled && (
        <div className="v2-mono" style={{ padding: '11px 16px', borderRadius: 12, background: 'rgba(255,200,87,0.10)', border: '1px solid rgba(255,200,87,0.30)', color: 'var(--c5)', marginBottom: 18, fontSize: 12 }}>
          ⓘ Self-healing is disabled for this agency. <Link to="/settings/healing" style={{ color: 'var(--c5)', textDecoration: 'underline' }}>Enable in Settings →</Link>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {[['active', 'Active', stats.open_count], ['history', 'History']].map(([key, label, count]) => (
          <button key={key} onClick={() => navigate(`/healing/${key === 'active' ? '' : key}`)}
            style={{
              whiteSpace: 'nowrap',
              background: tab === key ? 'rgba(255,255,255,0.07)' : 'transparent',
              border: tab === key ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: 10,
              color: tab === key ? '#fff' : 'rgba(255,255,255,0.45)',
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6
            }}>
            {label}
            {count > 0 && (
              <span className="v2-mono" style={{ background: 'rgba(255,91,164,0.15)', color: 'var(--c1)', borderRadius: 99, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>{count}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className={`v2-tile ${(stats.open_count || 0) > 0 ? 'v2-tile-glow' : ''}`} style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>open incidents</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: (stats.open_count || 0) > 0 ? 'var(--c1)' : '#fff' }}>{stats.open_count || 0}</div>
        </div>
        <div className="v2-tile v2-tile-acid" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>resolved this month</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c4)' }}>{stats.resolved_this_month || 0}</div>
        </div>
        <div className="v2-tile v2-tile-violet" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>spend this month</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: '#fff' }}>${Number(stats.spend_this_month || 0).toFixed(2)}</div>
          {data.agency?.healing_monthly_cap_usd && (
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>of ${Number(data.agency.healing_monthly_cap_usd).toFixed(0)} cap</div>
          )}
        </div>
        <div className="v2-tile v2-tile-cyan" style={{ padding: 18 }}>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>auto-resolved</div>
          <div className="v2-num" style={{ fontSize: 36, fontWeight: 300, color: 'var(--c3)' }}>{stats.auto_resolved_this_month || 0}</div>
          <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>no $ spent</div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 14 }}>
          <div className="spinner" />
          <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING…</span>
        </div>
      ) : err ? (
        <div style={{ padding: 16, color: 'var(--c1)', borderRadius: 12, border: '1px solid rgba(255,91,164,0.30)', background: 'rgba(255,91,164,0.08)', fontSize: 13 }}>Failed to load: {err}</div>
      ) : (data.incidents || []).length === 0 ? (
        <div className="v2-tile v2-tile-acid" style={{ padding: 60, textAlign: 'center' }}>
          <div className="v2-serif" style={{ fontSize: 26, color: '#fff', marginBottom: 8 }}>{tab === 'active' ? 'All scrapers are healthy.' : 'No history yet.'}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>When something breaks or a new site can't be parsed, it shows up here.</div>
        </div>
      ) : (
        <div className="v2-tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead><tr>{['Status','Dealer / site','Kind','Opened','Cost'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {data.incidents.map(inc => (
                  <tr key={inc.id} onClick={() => navigate(`/healing/${inc.id}`)}
                    style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={td}><HealingBadge status={inc.status}/></td>
                    <td style={td}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{inc.dealer_name || inc.domain_name || (inc.domain_url || '').replace(/^https?:\/\//, '')}</div>
                      {inc.domain_url && <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 2 }}>{(inc.domain_url || '').replace(/^https?:\/\//, '')}</div>}
                    </td>
                    <td style={{ ...td, color: 'rgba(255,255,255,0.55)', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em' }}>{inc.kind.replace('_', ' ')}</td>
                    <td style={{ ...td, color: 'rgba(255,255,255,0.55)', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>{relativeTime(inc.created_at)}</td>
                    <td style={td}><CostMeter used={inc.cost_used_usd} cap={inc.cost_cap_usd}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value, hint }) {
  return (
    <div style={{ padding: 18, borderRadius: 14, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ color: '#a0a0b8', fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase' }}>{title}</div>
      <div style={{ color: '#fff', fontSize: 26, fontWeight: 700, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint ? <div style={{ color: '#71717a', fontSize: 12, marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}

// ----- Session view (timeline + chat) ----------------------------------------

const PHASE_ORDER = ['research','plan','execute','snapshot_test','live_test','open_pr','document'];

function HealingSession() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    function pull() {
      api(`/healing/incidents/${incidentId}`)
        .then(d => { if (!cancelled) { setData(d); setErr(null); } })
        .catch(e => { if (!cancelled) setErr(e.message); });
    }
    pull();
    function loop() {
      const live = data?.incident && HEALING_STATUS_META[data.incident.status]?.live;
      const interval = live ? 3000 : 30000;
      timer = setTimeout(() => { if (!cancelled) { pull(); loop(); } }, interval);
    }
    loop();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [incidentId, data?.incident?.status]);

  if (err) return (
    <div className="page-content">
      <div className="v2-tile" style={{ padding: 24, color: 'var(--c1)', fontSize: 13, background: 'rgba(255,91,164,0.08)', borderColor: 'rgba(255,91,164,0.30)' }}>Failed: {err}</div>
    </div>
  );
  if (!data) return (
    <div className="page-content">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14 }}>
        <div className="spinner" />
        <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING INCIDENT…</span>
      </div>
    </div>
  );

  const { incident, runs, messages, proposals } = data;
  const latestProposal = (proposals || [])[0];
  const isLive = HEALING_STATUS_META[incident.status]?.live;
  const isClosed = ['completed','declined','expired','failed','cap_exceeded'].includes(incident.status);

  async function send(e) {
    e?.preventDefault?.();
    if (!chatInput.trim() || sending) return;
    setSending(true);
    try {
      await api(`/healing/incidents/${incidentId}/messages`, {
        method: 'POST', body: JSON.stringify({ content: chatInput.trim() })
      });
      setChatInput('');
    } catch (e) { setErr(e.message); }
    setSending(false);
  }

  return (
    <div className="page-content">
      <button onClick={() => navigate('/healing')}
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontSize: 12, marginBottom: 18, padding: '7px 14px', fontFamily: 'inherit' }}>
        ← Back to self-healing
      </button>

      <div className="v2-tile" style={{ padding: '20px 22px', marginBottom: 18 }}>
        <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, letterSpacing: '0.06em' }}>
          INCIDENT #{incident.id}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10, flexWrap: 'wrap' }}>
          <HealingBadge status={incident.status}/>
          <h2 className="v2-serif" style={{ margin: 0, fontSize: 22, color: '#fff' }}>{incident.dealer_name || incident.domain_name || incident.domain_url || `Incident #${incident.id}`}</h2>
          <span className="v2-mono" style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>{(incident.domain_url || '').replace(/^https?:\/\//, '')}</span>
        </div>
        <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{incident.kind === 'broken_scraper' ? 'broken scraper' : 'unknown site'}</span>
          <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
          <span>started {relativeTime(incident.created_at)}</span>
          <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
          <CostMeter used={incident.cost_used_usd} cap={incident.cost_cap_usd}/>
          {incident.pr_url && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
              <a href={incident.pr_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c2)', textDecoration: 'none', fontWeight: 700 }}>PR #{incident.pr_number} ↗</a>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.6fr)', gap: 18, alignItems: 'start' }}>
        <HealingTimeline runs={runs}/>
        <HealingChat
          messages={messages}
          proposals={proposals}
          latestProposal={latestProposal}
          incident={incident}
          chatInput={chatInput}
          setChatInput={setChatInput}
          onSend={send}
          sending={sending}
          isClosed={isClosed}
          isLive={isLive}
        />
      </div>
    </div>
  );
}

function HealingTimeline({ runs }) {
  const byPhase = {};
  for (const r of (runs || [])) {
    const list = byPhase[r.phase] || (byPhase[r.phase] = []);
    list.push(r);
  }
  return (
    <div className="v2-tile" style={{ padding: 18 }}>
      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14 }}>Timeline</div>
      {PHASE_ORDER.map((phase, idx) => {
        const rs = byPhase[phase] || [];
        const last = rs[rs.length - 1];
        const status = last?.status || 'pending';
        const dotColor = status === 'succeeded' ? 'var(--c4)' : status === 'failed' ? 'var(--c1)' : status === 'running' ? 'var(--c5)' : 'rgba(255,255,255,0.18)';
        const isLast = idx === PHASE_ORDER.length - 1;
        return (
          <div key={phase} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{
              width: 10, height: 10, borderRadius: 99, marginTop: 6, flexShrink: 0,
              background: dotColor,
              boxShadow: status === 'running' ? `0 0 12px ${dotColor}` : 'none',
              animation: status === 'running' ? 'v2-pulse 1.5s ease-out infinite' : 'none'
            }}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{phase.replace('_', ' ')}</span>
                {last && (
                  <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>${Number(last.cost_usd || 0).toFixed(4)}</span>
                )}
              </div>
              <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 3, lineHeight: 1.5 }}>
                {last ? (last.output_summary?.slice(0, 120) || status) : 'pending'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HealingChat({ messages, latestProposal, chatInput, setChatInput, onSend, sending, isClosed, isLive }) {
  const scroller = React.useRef(null);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages?.length]);

  return (
    <div className="v2-tile" style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 600, overflow: 'hidden' }}>
      <div ref={scroller} style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
        {(messages || []).length === 0 && !isLive && (
          <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, textAlign: 'center', padding: 28, letterSpacing: '0.04em' }}>NO CONVERSATION YET</div>
        )}
        {(messages || []).map(m => <ChatBubble key={m.id} m={m}/>)}
        {isLive && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px' }}>
            <span className="v2-pulse-dot v2-pulse-dot--amber" />
            <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontStyle: 'italic', fontSize: 11 }}>agent is working…</span>
          </div>
        )}
        {latestProposal && !latestProposal.approved_at && !latestProposal.declined_at && !latestProposal.superseded_at && (
          <ProposalCard proposal={latestProposal}/>
        )}
      </div>
      <form onSubmit={onSend} style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.20)' }}>
        <textarea
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') onSend(e); }}
          placeholder={isClosed ? 'This incident is closed.' : 'Ask the agent…'}
          disabled={isClosed}
          rows={2}
          style={{
            flex: 1, resize: 'none', padding: '10px 12px',
            background: 'rgba(255,255,255,0.04)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
            fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0
          }}/>
        <button type="submit" disabled={isClosed || sending || !chatInput.trim()}
          style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 10, padding: '0 18px', fontSize: 12, fontWeight: 700, cursor: (isClosed || sending || !chatInput.trim()) ? 'not-allowed' : 'pointer', opacity: (isClosed || sending || !chatInput.trim()) ? 0.5 : 1, minWidth: 80, letterSpacing: '0.02em', boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)' }}>
          {sending ? '…' : 'Send →'}
        </button>
      </form>
    </div>
  );
}

function ChatBubble({ m }) {
  if (m.role === 'system') {
    return <div className="v2-mono" style={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', fontSize: 11, padding: '8px 0' }}>{m.content}</div>;
  }
  const isUser = m.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      <div style={{
        maxWidth: '80%',
        padding: '10px 14px', borderRadius: 14,
        background: isUser ? 'rgba(34,211,238,0.08)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${isUser ? 'rgba(34,211,238,0.25)' : 'rgba(255,255,255,0.08)'}`,
        color: 'rgba(255,255,255,0.9)', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
      }}>
        <div className="v2-mono" style={{ fontSize: 9, color: isUser ? 'var(--c3)' : 'rgba(255,255,255,0.5)', marginBottom: 5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {isUser ? 'YOU' : 'AGENT'} · {relativeTime(m.created_at)}
        </div>
        {m.content}
      </div>
    </div>
  );
}

function ProposalCard({ proposal }) {
  const plan = proposal.plan || {};
  return (
    <div className="v2-tile v2-tile-violet" style={{ marginTop: 14, padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="v2-mono" style={{ background: 'rgba(122,77,255,0.18)', color: '#c4b5fd', border: '1px solid rgba(122,77,255,0.30)', borderRadius: 99, padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>PROPOSAL</span>
          <strong style={{ color: '#fff', fontSize: 14 }}>v{proposal.version}</strong>
        </div>
        <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>est ${Number(plan.estimated_cost_usd || 0).toFixed(2)}</span>
      </div>
      <div style={{ color: '#fff', fontSize: 13, marginBottom: 10, lineHeight: 1.55 }}>
        <strong className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 8 }}>GOAL</strong>
        {plan.goal}
      </div>
      {plan.change_md && (
        <details style={{ marginTop: 8 }}>
          <summary className="v2-mono" style={{ cursor: 'pointer', color: 'var(--c3)', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>▸ Change details</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 8, fontFamily: "'JetBrains Mono', monospace", background: 'rgba(0,0,0,0.30)', padding: 12, borderRadius: 10, lineHeight: 1.5 }}>{plan.change_md}</pre>
        </details>
      )}
      {plan.risk_md && (
        <details style={{ marginTop: 8 }}>
          <summary className="v2-mono" style={{ cursor: 'pointer', color: 'var(--c5)', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>▸ Risk notes</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 8, fontFamily: "'JetBrains Mono', monospace", background: 'rgba(0,0,0,0.30)', padding: 12, borderRadius: 10, lineHeight: 1.5 }}>{plan.risk_md}</pre>
        </details>
      )}
      <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, marginTop: 12, padding: '8px 12px', background: 'rgba(0,0,0,0.20)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, letterSpacing: '0.02em' }}>
        ⓘ Approve via the link in your email · in-app one-click approval is on the v2 roadmap
      </div>
    </div>
  );
}

function HealingAutoLogin() {
  const navigate = useNavigate();
  const [err, setErr] = useState(null);
  useEffect(() => {
    const frag = (window.location.hash || '').replace(/^#/, '');
    const params = new URLSearchParams(frag);
    const token = params.get('token');
    const incident = params.get('incident');
    const action = params.get('action') || '';
    const declineToken = params.get('declineToken');
    const proposalId = params.get('proposalId');

    if (!token || !incident) {
      setErr('Bad link - no token in URL.');
      return;
    }

    (async () => {
      try {
        const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Auto-login failed');
        const me = await res.json();
        localStorage.setItem('access_token', token);
        if (me.user) localStorage.setItem('user', JSON.stringify(me.user));
        const search = (action === 'decline' && declineToken && proposalId)
          ? `?action=decline&declineToken=${encodeURIComponent(declineToken)}&proposalId=${proposalId}`
          : action === 'approve' ? '?action=approve' : '';
        window.location.href = `/healing/${incident}${search}`;
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 700px at 88% -10%, rgba(122,77,255,.22), transparent 60%),
        radial-gradient(900px 600px at -10% 110%, rgba(34,211,238,.12), transparent 60%)
      `,
      color: 'var(--ink, #FAFAF6)',
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px'
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28, justifyContent: 'center' }}>
          <MDLogo size={44} radius={12} style={{ boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)' }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>MotorsDynamic</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>self-healing</div>
          </div>
        </div>

        {err ? (
          <div className="v2-tile v2-tile-glow" style={{ padding: 32, textAlign: 'center' }}>
            <div className="v2-mono" style={{ fontSize: 11, color: 'var(--c1)', letterSpacing: '0.06em', marginBottom: 6 }}>AUTO-LOGIN ERROR</div>
            <h2 className="v2-serif" style={{ fontSize: 24, fontWeight: 400, color: '#fff', margin: '0 0 12px' }}>Couldn't sign you in.</h2>
            <div className="v2-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 18 }}>{err}</div>
            <Link to="/login"
              style={{ display: 'inline-block', background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', textDecoration: 'none', borderRadius: 999, padding: '10px 22px', fontSize: 13, fontWeight: 700, boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
              Sign in manually →
            </Link>
          </div>
        ) : (
          <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }} />
            <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.06em' }}>SIGNING YOU IN…</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>One sec, opening the incident.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function HealingExpired() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper, #0a0a0e)',
      backgroundImage: `
        radial-gradient(1100px 700px at 88% -10%, rgba(255,200,87,.18), transparent 60%),
        radial-gradient(900px 600px at -10% 110%, rgba(255,91,164,.10), transparent 60%)
      `,
      color: 'var(--ink, #FAFAF6)',
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px'
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28, justifyContent: 'center' }}>
          <MDLogo size={44} radius={12} style={{ boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)' }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>MotorsDynamic</div>
            <div className="v2-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>self-healing</div>
          </div>
        </div>

        <div className="v2-tile v2-tile-amber" style={{ padding: 36, textAlign: 'center' }}>
          <div className="v2-mono" style={{ fontSize: 11, color: 'var(--c5)', letterSpacing: '0.06em', marginBottom: 6 }}>⌛ EXPIRED</div>
          <h2 className="v2-serif" style={{ fontSize: 28, fontWeight: 400, color: '#fff', margin: '0 0 12px' }}>This link's no longer valid.</h2>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 20 }}>
            It may have expired, been used already, or been superseded by a newer proposal.
          </div>
          <Link to="/healing"
            style={{ display: 'inline-block', background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', textDecoration: 'none', borderRadius: 999, padding: '10px 22px', fontSize: 13, fontWeight: 700, boxShadow: '0 8px 24px -8px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
            Go to self-healing →
          </Link>
        </div>
      </div>
    </div>
  );
}

function HealingSettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  useEffect(() => {
    api('/healing/settings').then(d => setS(d.settings || {})).catch(e => setMsg({ type: 'error', text: e.message }));
  }, []);
  if (!s) return (
    <div className="v2-tile" style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto 14px' }} />
      <span className="v2-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.04em' }}>LOADING SETTINGS…</span>
    </div>
  );
  async function save() {
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const updated = await api('/healing/settings', { method: 'PUT', body: JSON.stringify(s) });
      setS(updated.settings || s);
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    setSaving(false);
  }
  return (
    <div className="v2-tile" style={{ padding: 24 }}>
      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>self-healing</div>
      <div className="v2-serif" style={{ fontSize: 22, color: '#fff', marginBottom: 4 }}>Agent settings</div>
      <div className="v2-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>monitor scraper health and propose fixes for review</div>

      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(166,255,84,0.10)' : 'rgba(255,91,164,0.12)', border: `1px solid ${msg.type === 'success' ? 'rgba(166,255,84,0.30)' : 'rgba(255,91,164,0.30)'}`, borderRadius: 12, padding: '11px 16px', marginBottom: 14, fontSize: 13, color: msg.type === 'success' ? 'var(--c4)' : 'var(--c1)' }}>{msg.text}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Toggle label="Enable self-healing for this agency"
          desc="When on, threshold-crossing scraper failures and unknown-site onboardings open incidents."
          value={s.healing_enabled} onChange={v => setS({ ...s, healing_enabled: v })}/>
        <NumberField label="Daily spend cap (USD)"
          desc="Hard ceiling per UTC day. New incidents are refused once reached."
          value={s.healing_daily_cap_usd} min={1} max={500} step={1}
          onChange={v => setS({ ...s, healing_daily_cap_usd: v })}/>
        <NumberField label="Monthly spend cap (USD)"
          desc="Hard ceiling per UTC month. Healing pauses agency-wide once reached."
          value={s.healing_monthly_cap_usd} min={5} max={5000} step={1}
          onChange={v => setS({ ...s, healing_monthly_cap_usd: v })}/>
        <TextField label="Notify email"
          desc="Where proposals and summaries are sent."
          value={s.healing_notify_email || ''} onChange={v => setS({ ...s, healing_notify_email: v })}/>
        <div style={{ marginTop: 8 }}>
          <button onClick={save} disabled={saving}
            style={{ background: 'linear-gradient(135deg, var(--c2), var(--c3))', color: '#fff', border: 'none', borderRadius: 999, padding: '10px 24px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: saving ? 0.6 : 1, boxShadow: '0 4px 16px -4px rgba(122,77,255,0.5)', letterSpacing: '0.02em' }}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, desc, value, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{label}</div>
        {desc && <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>}
      </div>
      <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, cursor: 'pointer', flexShrink: 0 }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }}/>
        <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: value ? 'linear-gradient(135deg, var(--c2), var(--c3))' : 'rgba(255,255,255,0.10)', borderRadius: 24, transition: '0.2s', boxShadow: value ? '0 0 12px rgba(122,77,255,0.4)' : 'none' }}>
          <span style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 18, height: 18, background: '#fff', borderRadius: 99, transition: '0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}/>
        </span>
      </label>
    </div>
  );
}

function NumberField({ label, desc, value, min, max, step, onChange }) {
  return (
    <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{label}</div>
      {desc && <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2, marginBottom: 12, lineHeight: 1.5 }}>{desc}</div>}
      <input type="number" value={value ?? ''} min={min} max={max} step={step}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ background: 'rgba(0,0,0,0.30)', color: '#fff', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", width: 160, outline: 'none' }}/>
    </div>
  );
}

function TextField({ label, desc, value, onChange }) {
  return (
    <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{label}</div>
      {desc && <div className="v2-mono" style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2, marginBottom: 12, lineHeight: 1.5 }}>{desc}</div>}
      <input value={value} onChange={e => onChange(e.target.value)}
        style={{ background: 'rgba(0,0,0,0.30)', color: '#fff', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', width: '100%', maxWidth: 360, boxSizing: 'border-box', outline: 'none' }}/>
    </div>
  );
}

function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if agency needs onboarding (no primary dealers + not dismissed)
  useEffect(() => {
    if (!user) return;
    const key = `motorsdynamic_onboarding_done_${user.agencyId || user.agency_id || 'default'}`;
    if (localStorage.getItem(key)) return;
    api('/my-dealers').then(res => {
      const dealers = res.dealers || [];
      if (dealers.length === 0) setShowOnboarding(true);
    }).catch(() => {});
  }, [user]);

  function completeOnboarding() {
    const key = `motorsdynamic_onboarding_done_${user?.agencyId || user?.agency_id || 'default'}`;
    localStorage.setItem(key, '1');
    setShowOnboarding(false);
  }

  return (
    <ChangelogProvider>
      <div className="app" role="application">
        {showOnboarding && <OnboardingWizard onComplete={completeOnboarding} />}
        <MobileHeader onMenuClick={() => setMobileMenuOpen(true)} />
        <Sidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
        <main className="main" id="main-content" role="main" aria-label="Main content">
          <TrialBanner />
          <Routes>
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/domains" element={<ProtectedRoute><Domains /></ProtectedRoute>} />
            <Route path="/vehicles" element={<ProtectedRoute><Vehicles /></ProtectedRoute>} />
            <Route path="/feeds" element={<ProtectedRoute><FeedBuilder /></ProtectedRoute>} />
            <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
            <Route path="/api/:tab" element={<ProtectedRoute><ApiPage /></ProtectedRoute>} />
            <Route path="/api" element={<ProtectedRoute><ApiPage /></ProtectedRoute>} />
            <Route path="/organization/:tab" element={<ProtectedRoute requiredRole="admin"><OrganizationPage /></ProtectedRoute>} />
            <Route path="/organization" element={<ProtectedRoute requiredRole="admin"><OrganizationPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute requiredRole="admin"><UsersPage /></ProtectedRoute>} />
            <Route path="/settings/:tab" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/competitive/setup" element={<ProtectedRoute><CompetitiveSetupPage /></ProtectedRoute>} />
            <Route path="/competitive/group/:groupId/:tab" element={<ProtectedRoute><CompetitiveDashboard /></ProtectedRoute>} />
            <Route path="/competitive/group/:groupId" element={<ProtectedRoute><CompetitiveDashboard /></ProtectedRoute>} />
            <Route path="/competitive/:tab" element={<ProtectedRoute><CompetitiveDashboard /></ProtectedRoute>} />
            <Route path="/competitive" element={<ProtectedRoute><CompetitiveDashboard /></ProtectedRoute>} />
            <Route path="/specials" element={<ProtectedRoute><SpecialsPage /></ProtectedRoute>} />
            <Route path="/specials/builder" element={<ProtectedRoute><OfferBuilderPage /></ProtectedRoute>} />
            <Route path="/market-intelligence/group/:groupId" element={<ProtectedRoute><MarketIntelligencePage /></ProtectedRoute>} />
            <Route path="/market-intelligence" element={<ProtectedRoute><MarketIntelligencePage /></ProtectedRoute>} />
            <Route path="/scraper-health" element={<ProtectedRoute><ScraperHealthPage /></ProtectedRoute>} />
            <Route path="/alerts/:tab" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
            <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
            <Route path="/engagement" element={<ProtectedRoute><EngagementPage /></ProtectedRoute>} />
            <Route path="/market" element={<ProtectedRoute><MarketPage /></ProtectedRoute>} />
            <Route path="/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
            <Route path="/quality" element={<ProtectedRoute><QualityPage /></ProtectedRoute>} />
            <Route path="/ga4" element={<ProtectedRoute><GA4Page /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/dealers" element={<ProtectedRoute><DealersPage /></ProtectedRoute>} />
            <Route path="/vla/:tab" element={<ProtectedRoute><VLAFeedPage /></ProtectedRoute>} />
            <Route path="/vla" element={<ProtectedRoute><VLAFeedPage /></ProtectedRoute>} />
            <Route path="/trade" element={<ProtectedRoute><TradePage /></ProtectedRoute>} />
            <Route path="/imbalance" element={<ProtectedRoute><ImbalancePage /></ProtectedRoute>} />
            <Route path="/seasonal" element={<ProtectedRoute><SeasonalPage /></ProtectedRoute>} />
            <Route path="/fi" element={<ProtectedRoute><FIPage /></ProtectedRoute>} />
            <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
            <Route path="/ammo-cards" element={<ProtectedRoute><AmmoCardsPage /></ProtectedRoute>} />
            <Route path="/mcp" element={<ProtectedRoute><McpPage /></ProtectedRoute>} />
            <Route path="/healing/auto-login" element={<HealingAutoLogin />} />
            <Route path="/healing/expired" element={<HealingExpired />} />
            {/* /healing/history must come BEFORE /healing/:incidentId or it
                gets matched as a numeric id and /api/.../incidents/history
                500s on the parseInt(NaN) bigint cast */}
            <Route path="/healing/history" element={<ProtectedRoute requiredRole="super_admin"><HealingErrorBoundary><HealingInbox /></HealingErrorBoundary></ProtectedRoute>} />
            <Route path="/healing/:incidentId" element={<ProtectedRoute requiredRole="super_admin"><HealingErrorBoundary><HealingSession /></HealingErrorBoundary></ProtectedRoute>} />
            <Route path="/healing" element={<ProtectedRoute requiredRole="super_admin"><HealingErrorBoundary><HealingInbox /></HealingErrorBoundary></ProtectedRoute>} />
          </Routes>
        </main>
        <ChangelogPopup />
      </div>
    </ChangelogProvider>
  );
}

// Main App
function App() {
  // Remove initial loader when React mounts
  React.useEffect(() => {
    const loader = document.querySelector('.app-loader');
    if (loader) loader.remove();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/auth/magic-link" element={<LoginPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/auth/invite" element={<InviteAcceptPage />} />
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
